/**
 * English messages (ADR 0175).
 *
 * Typed as `Record<MessageKey, string>` against the key set `th.ts` defines, so
 * a key added there and forgotten here fails `tsc` rather than rendering blank
 * for an English user weeks later. That guarantee is the reason this project
 * writes its own i18n instead of using a library — see ADR 0175 D1.
 */
import type { MessageKey } from "./th";

export const en: Record<MessageKey, string> = {
  // ── common ─────────────────────────────────────────────────────────────
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.viewPhoto": "View photo",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.search": "Search",
  "common.filter": "Filter",
  "common.clear": "Clear",
  "common.now": "Now",
  "common.selectTime": "Select time",
  "common.refresh": "Refresh",
  "common.export": "Export",
  "common.import": "Import",
  "common.back": "Back",
  "common.next": "Next",
  "common.loading": "Loading…",
  "common.saving": "Saving…",
  "common.noData": "No data",
  "common.all": "All",
  "common.noOptions": "No options in this category yet",
  "common.none": "None",
  "common.yes": "Yes",
  "common.no": "No",
  "common.total": "Total",
  "common.status": "Status",
  "common.actions": "Actions",
  "common.date": "Date",
  "common.time": "Time",
  "common.name": "Name",
  "common.note": "Note",
  "common.required": "Required",
  "common.sortBy": "Sort by",
  "common.errorTitle": "Something went wrong",
  "common.saveFailed": "Could not save",
  "common.loadFailed": "Could not load",
  "time.justNow": "just now",
  "time.minAgo": "{n} min ago",
  "time.hrAgo": "{n} hr ago",
  "time.dAgo": "{n} d ago",
  "ops.nCompleted": "{n} completed",
  "fleet.nActiveOf": "{n} active of {total}",
  "common.loadFailedWith": "Failed to load data: {message}",
  // hunt fix-load (2026-10-25) — generic finance load-failure message; pages with no better-fitting
  // key use this instead of leaving a blank area or endless skeleton that reads as "no data".
  "finance.loadFailed": "Could not load — no figures are shown yet. Try again.",
  "common.confirmDelete": "Delete this?",

  // ── language switcher ──────────────────────────────────────────────────
  // Each language is named in itself: someone who cannot read the current UI
  // language still recognises their own.
  "common.language": "Language",
  "common.language.th": "ไทย",
  "common.language.en": "English",

  // ── login ──────────────────────────────────────────────────────────────
  "login.tagline": "Transport Management System",
  "login.email": "Email",
  "login.methodOtp": "OTP (Passwordless)",
  "login.methodPassword": "Password",
  "login.continue": "Continue",
  "login.sendOtp": "Send OTP",
  "login.otpSentTo": "OTP sent to",
  "login.otpRef": "Ref:",
  "login.otpPlaceholder": "Enter 6-digit OTP",
  "login.verify": "Verify & Login",
  "login.backToEmail": "← Back to email",
  "login.password": "Password",
  "login.passwordPlaceholder": "Password",
  "login.signIn": "Sign in",
  "login.noAccount": "Don't have an account?",
  "login.signUp": "Sign up",

  // ── register / invite / select-org ─────────────────────────────────────
  "register.title": "Create Account",
  "register.createAccount": "Create Account",
  "register.failed": "Registration failed",
  "register.verifyFailed": "Verification failed",
  "login.otpSendFailed": "Failed to send OTP",
  "login.otpVerifyFailed": "OTP verification failed",
  "login.failed": "Login failed",
  "login.setNewPasswordHeading": "Set a new password",
  "login.newPassword": "New password",
  "login.confirmNewPassword": "Confirm new password",
  "login.saveNewPassword": "Save new password",
  "login.newPasswordHint": "At least 8 characters",
  "login.newPasswordTooShort": "The new password must be at least 8 characters",
  "login.newPasswordMismatch": "The two passwords do not match",
  "login.newPasswordSameAsCurrent": "The new password must differ from the current one",
  "login.setNewPasswordFailed": "Could not set the new password",
  "login.sessionExpiredRelogin": "Your session expired — please sign in again",
  "login.cancel": "Cancel",
  "hr.password.adminResetNotice": "The user will be required to set their own password at next sign-in",
  "hr.password.mustChangeBadge": "Awaiting password change",
  "register.subtitle": "DeepTrack Transport Management",
  "register.displayName": "Display Name",
  "register.namePlaceholder": "Your name",
  "register.passwordHint": "Min 8 characters",
  "register.fillAll": "Please fill in all fields",
  "register.passwordMin": "Password must be at least 8 characters",
  "register.passwordWeak": "Password is too weak — mix upper/lowercase, numbers, and symbols",
  "nav.notificationsUnread": "Notifications ({count} unread)",
  "register.haveAccount": "Already have an account?",
  "register.redirecting": "Redirecting to dashboard…",

  "invite.invitedBy": "Invited by",
  "invite.role": "Role",
  "invite.expires": "Expires",

  "createOrg.descPlaceholder": "Brief description of your organization or project",

  "selectOrg.title": "Select Organization",
  // ADR 0418 frame 3 — wrong domain for this account
  "wrongHost.title": "This account is not part of {org}",
  "wrongHost.body": "Please sign in on your own organization's domain, or contact your administrator.",
  // ADR 0418 D11 — super admin org form, brand by domain
  "hostBrand.section": "Brand by domain",
  "hostBrand.domains": "Domains",
  "hostBrand.domainPlaceholder": "e.g. cca.deepinnoline.com",
  "hostBrand.addDomain": "Add domain",
  "hostBrand.domainInvalid": "Not a usable domain name",
  "hostBrand.domainsHint": "A domain belongs to one organization · DNS and the Firebase custom domain must be set up too",
  "hostBrand.loginTitle": "Login page name",
  "hostBrand.browserTitle": "Browser tab title",
  "hostBrand.fallbackHint": "Empty = use the organization name",
  "hostBrand.logo": "Logo",
  "hostBrand.favicon": "Tab icon (favicon)",
  "hostBrand.upload": "Upload",
  "hostBrand.replace": "Replace",
  "hostBrand.remove": "Remove",
  "hostBrand.pngOnly": "PNG only · logo 512×512 · icon 64×64",
  "hostBrand.legacyLogoUrl": "Logo URL (legacy — used when no file is uploaded)",
  "hostBrand.loginOtp": "OTP sign-in button",
  "hostBrand.poweredBy": "\"Powered by\" and version line",
  "hostBrand.flagDefault": "Same as the web app",
  "hostBrand.flagShow": "Show",
  "hostBrand.flagHide": "Hide",
  "hostBrand.behaviourHint": "Applies to this organization's login page — no rebuild needed. \"Same as the web app\" keeps the app's own setting.",
  "selectOrg.searchPlaceholder": "Search organizations",

  // ── analytics ──────────────────────────────────────────────────────────
  "analytics.loadFailed": "Failed to load reports",
  "analytics.routeOnTime": "Route On-Time Performance (Live)",
  "analytics.driverPerf": "Driver Performance (Live)",
  "analytics.fleetBreakdown": "Fleet Status Breakdown (Live)",
  "analytics.backToReports": "Back to Reports",
  "analytics.newTemplate": "New template",
  "analytics.reportName": "Report name",
  "analytics.removeFilter": "Remove filter",
  "analytics.thinking": "Thinking…",
  "analytics.sendMessage": "Send message",

  // ── safety ─────────────────────────────────────────────────────────────
  "safety.title": "Safety & Cases",
  "safety.loadFailed": "Failed to load safety cases",
  "safety.description": "Description",
  "safety.linkedRecord": "Linked Record",
  "safety.workflow": "Case Workflow",
  "safety.sendToHr": "Send to HR",
  "safety.classifyWarn": "Classify & Issue Warning",
  "safety.markSigned": "Mark as Signed",
  "safety.recordPayment": "Record Payment",
  "safety.addEvidence": "Add Evidence",
  "safety.addNote": "Add Note",
  "safety.newCase": "New Case",
  "safety.caseTitle": "Title",
  "safety.titlePlaceholder": "Brief case title",
  "safety.titleRequired": "Title is required",
  "safety.category": "Category",
  "safety.severity": "Severity",
  "safety.describeIncident": "Describe the incident…",
  "safety.searchPlaceholder": "Search case#, employee, offense…",
  "safety.offense": "Offense",
  "safety.evidence": "Evidence",
  "safety.link": "Link",
  "safety.noMatch": "No cases match your filters",

  "handover.title": "Vehicle Handover / Return",
  "handover.loadFailed": "Failed to load handover records",
  "handover.newInspection": "New Inspection",
  "handover.vehicle": "Vehicle",
  "handover.selectVehicle": "Select vehicle…",
  "handover.plateNumber": "Plate Number",
  "handover.driver": "Driver",
  "handover.selectDriver": "Select driver…",
  "handover.type": "Type",
  "handover.handover": "Handover",
  "handover.return": "Return",
  "handover.notes": "Notes",
  "handover.notesPlaceholder": "Inspection notes…",
  "handover.checklist": "Inspection Checklist",
  "handover.damageSummary": "Damage Summary",
  "handover.signatures": "Digital Signatures",
  "handover.searchPlaceholder": "Search vehicle, driver, inspector…",
  "handover.inspector": "Inspector",

  // ── system ─────────────────────────────────────────────────────────────
  "common.dismiss": "Dismiss",
  "common.description": "Description",
  "common.remove": "Remove",
  "common.duplicate": "Duplicate",
  "common.label": "Label",
  "common.code": "Code",
  "common.color": "Color",
  "common.select": "Select…",
  "common.closeForm": "Close form",
  "settings.thresholds": "Operational Thresholds",
  "settings.booking": "Booking Configuration",
  "settings.idPattern": "ID Pattern Configuration",
  "settings.entity": "Entity",
  "settings.pattern": "Pattern",
  "settings.preview": "Preview",
  "settings.integrations": "External Integrations",
  "settings.featureFlags": "Feature Flags",
  "settings.notifChannels": "Notification Channels",
  "settings.event": "Event",
  "settings.push": "Push",
  "settings.sms": "SMS",
  "settings.prefix": "Prefix",
  "settings.prefixLabel": "Prefix label",
  "settings.organization": "Organization",
  "settings.slug": "Slug",
  "settings.module": "Module",
  "settings.plan": "Plan",
  "roles.title": "Roles & Permissions",
  "roles.loadFailed": "Failed to load roles",
  "roles.name": "Role Name",
  "roles.briefDesc": "Brief description",
  "roles.permissions": "Permissions",
  "roles.editPermissions": "Edit Permissions",
  "roles.newRole": "New Role",
  "integ.title": "Integration Hub",
  "integ.subtitle": "API Keys, Webhooks, Notification Templates",
  "integ.generateKey": "Generate Key",
  "integ.keyCreated": "API Key Created",
  "integ.copyNow": "Copy the key now. It will not be shown again.",
  "integ.revoke": "Revoke",
  "integ.addWebhook": "Add Webhook",
  "integ.webhookSecret": "Webhook Secret",
  "integ.rotateSecret": "Rotate secret",
  "integ.deleteWebhook": "Delete webhook",
  "masterData.newCategory": "New category",
  "masterData.protected": "Protected category — reserved, cannot be edited",
  "members.cancelInvite": "Cancel invite",

  // ── monitoring ─────────────────────────────────────────────────────────
  "common.retry": "Retry",
  "common.details": "Details",
  "common.clearSearch": "Clear search",
  "common.closeSearch": "Close search",
  "common.searching": "Searching…",
  "warroom.loadingOverview": "Loading overview…",
  "warroom.noPositions": "No vehicle positions yet",
  "warroom.positionsHint": "Positions appear once drivers start sending GPS",
  "warroom.mode": "Mode",
  "warroom.searchBoth": "Search plate · driver · job · route",
  "warroom.quickEdit": "Quick-edit job",
  "warroom.lateThreshold": "Late threshold (min)",
  "warroom.assignment": "Assignment",
  "warroom.searchVehicle": "Search vehicle by plate, ID, type…",
  "warroom.searchDriver": "Search driver by name, ID, license…",
  "warroom.routeStops": "Route & stops",
  "warroom.searchTemplate": "Search routes…",
  "warroom.jobLabel": "Optional job label…",
  "warroom.bookingSeats": "Booking & seats",
  "warroom.boarding": "Boarding",
  "warroom.tracking": "Tracking",
  "warroom.payRevenue": "Pay & revenue",
  "warroom.pb.nJobs": "{n} jobs",
  "warroom.pb.all": "All",
  "warroom.pb.search": "Search job · driver · plate",
  "warroom.pb.searchNone": "Nothing matches \"{q}\" on {day}",
  "warroom.pb.searchHead": "Results for \"{q}\"",
  "warroom.pb.searchMin": "Type at least 2 characters",
  "warroom.pb.searchMore": "Showing the first {n} jobs · type more to narrow",
  "warroom.pb.searchError": "Search failed",
  "warroom.pb.lineLegend": "Map lines",
  "warroom.pb.lineDriven": "Driven",
  "warroom.pb.lineAhead": "Still to drive",
  "warroom.pb.linePlan": "Planned route",
  "warroom.pb.noneInScope": "No job matches the filters",
  "warroom.dtc.title.live": "Today",
  "warroom.dtc.subtitle": "The vehicle from its GPS box",
  "warroom.dtc.lane.jobs": "Jobs",
  "warroom.dtc.lane.real": "Vehicle GPS",
  "warroom.dtc.noJobs": "No jobs today",
  "warroom.dtc.noJobsPast": "No jobs on the chosen day",
  "warroom.dtc.sum.distance": "Distance all day",
  "warroom.dtc.sum.job": "On a job",
  "warroom.dtc.sum.near": "Before and after a job",
  "warroom.dtc.sum.off": "Off job",
  "warroom.dtc.sum.starts": "Started {n} times",
  "warroom.dtc.sum.idle": "Idling",
  "warroom.dtc.sum.over": "Over {limit} km/h",
  "warroom.dtc.sum.spans": "{n} stretches",
  "warroom.dtc.km": "{n} km",
  "warroom.dtc.hm": "{h} h {m} min",
  "warroom.dtc.minutes": "{n} min",
  "warroom.dtc.list.show": "Show the list",
  "warroom.dtc.list.hide": "Hide the list",
  "warroom.dtc.list.label": "Events by time",
  "warroom.dtc.ev.job": "Job {id}",
  "warroom.dtc.ev.drive.job": "Drove on a job, {km} km",
  "warroom.dtc.ev.drive.near": "Drove before or after a job, {km} km",
  "warroom.dtc.ev.drive.off": "Drove off job, {km} km",
  "warroom.dtc.ev.idle": "Idling for {min} min",
  "warroom.dtc.ev.start": "Started the vehicle",
  "warroom.dtc.ev.startOff": "Started the vehicle off job",
  "warroom.dtc.ev.over": "Over {limit} km/h, up to {max} km/h",
  "warroom.dtc.ev.gps_lost": "No GPS signal",
  "warroom.dtc.ev.backup_battery": "On the spare battery",
  "warroom.dtc.ev.card_in": "Driver card in",
  "warroom.dtc.ev.card_out": "Driver card out",
  "warroom.dtc.ev.unidentified": "Driver not identified",
  "warroom.dtc.bucket.job": "On a job",
  "warroom.dtc.bucket.near": "Before or after a job",
  "warroom.dtc.bucket.off": "Off job",
  "warroom.dtc.kind.drive": "Driving",
  "warroom.dtc.kind.idle": "Idling",
  "warroom.dtc.kind.off": "Parked, engine off",
  "warroom.dtc.kind.gap": "No data from the box",
  "warroom.dtc.card.range": "{from} to {to}",
  "warroom.dtc.card.job": "Job {id}",
  "warroom.dtc.card.route": "From {from} to {to}",
  "warroom.dtc.card.top": "Top speed {n} km/h",
  "warroom.dtc.card.close": "Close the card",
  "warroom.dtc.plate": "Vehicle {plate}",
  "warroom.dtc.box": "GPS box",
  "warroom.dtc.tip.appNone": "None, too few points",
  "warroom.dtc.state.loading": "Loading the vehicle's day from its GPS box",
  "warroom.dtc.state.error": "Could not load the vehicle's day from its GPS box. The map and the boards work as usual.",
  "warroom.dtc.state.nobox": "This vehicle has no GPS box fitted",
  "warroom.dtc.state.nobox.link": "Fit a box on the GPS box page",
  "warroom.dtc.state.empty": "The GPS box has sent no points for this vehicle today",
  "warroom.dtc.state.emptyPast": "The GPS box sent no points for this vehicle on the chosen day",
  "warroom.dtc.state.silent": "The GPS box has been silent for {min} minutes. The system does not guess the vehicle is parked.",
  "warroom.dtc.state.fresh": "Last point {min} minutes ago",
  "warroom.dtc.warn.plate": "This GPS box reports a plate that does not match the vehicle it is fitted to. The lines on the map may belong to another vehicle.",
  "warroom.dtc.warn.plate.link": "Check the GPS box page",
  "warroom.dtc.legend": "Vehicle lines",
  "warroom.dtc.legend.job": "On a job",
  "warroom.dtc.legend.near": "Before or after a job",
  "warroom.dtc.legend.off": "Off job",
  "warroom.dtc.legend.overspeed": "Over the speed",
  "warroom.dtc.legend.idle": "Idling for long",
  "warroom.dtc.sum.trips": "Trips",
  "warroom.dtc.sum.tripCount": "{n} trips",
  "warroom.dtc.trip.group": "Choose a trip",
  "warroom.dtc.trip.prev": "Previous trip",
  "warroom.dtc.trip.next": "Next trip",
  "warroom.dtc.trip.of": "Trip {n} of {total}",
  "warroom.dtc.trip.allDay": "Whole day, {total} trips",
  "warroom.dtc.trip.showDay": "Show whole day",
  "warroom.dtc.trip.mark": "The chosen trip",
  "warroom.dtc.legend.other": "Other trips today",
  "warroom.dtc.legend.running": "Running now",
  "warroom.dtc.legend.plan": "Planned route",
  "warroom.dtc.kmh": "{n} km/h",
  "warroom.dtc.card.label.distance": "Distance",
  "warroom.dtc.card.label.top": "Top speed",
  "warroom.dtc.card.label.driver": "Driver from the card",
  "warroom.dtc.card.label.jobDriver": "Driver",
  "warroom.dtc.card.label.route": "Route",
  "warroom.dtc.card.label.app": "Distance by the app GPS",
  "warroom.dtc.card.label.box": "Distance by the GPS box",
  "warroom.dtc.pin.idle": "Idling for {min} min",
  "warroom.dtc.end.start": "Start {time}",
  "warroom.dtc.end.end": "End {time}",
  "warroom.dtc.pin.last": "Last position from the GPS box",
  "warroom.dtc.viewDay": "View the vehicle's day",
  "warroom.pb.head": "Playback",
  "warroom.pb.clearSelectedJob": "Clear selected job",
  "warroom.pb.loadingMore": "Loading…",
  "warroom.pb.vehicleLabel": "Vehicle",
  "warroom.pb.noVehiclesDay": "No vehicle ran on {day}",
  "warroom.pb.vehiclesError": "Could not load the vehicles",
  "warroom.pb.emptyDay": "No jobs on {day}",
  "warroom.pb.changeDay": "Pick another day",
  "warroom.pb.dayOutOfRange": "The day in the link has no data — back to today",
  "warroom.pb.pickDay": "Day to play back",
  "warroom.pb.clearFilters": "Clear filters",
  "warroom.pb.vehiclesOfDay": "Vehicles on {day} · {n}",
  "warroom.pb.listError": "Could not load the jobs",
  "warroom.pb.loadingRoutes": "Loading routes…",
  "warroom.pb.noGpsToday": "No GPS data today",
  "warroom.pb.noGpsOn": "No GPS data on {day}",
  "warroom.pb.routesError": "Could not load the routes",
  "warroom.pb.noGpsJob": "This job has no GPS history",
  "warroom.pb.noGpsVehicle": "This vehicle has no GPS history this day",
  "warroom.pb.notFound": "That vehicle or job is not on the chosen day",
  "warroom.pb.replay": "Replay",
  "warroom.pb.jump": "GPS jumped {km} km in {min} min — not drawn as a route",
  "warroom.pb.points": "{n} points",
  "warroom.pb.hideList": "Hide list",
  "warroom.pb.showList": "Show list · {n} jobs",
  "warroom.pb.backToList": "← Jobs",
  "warroom.pb.speed": "Speed",
  "warroom.pb.breadcrumb": "Level",
  "warroom.pb.timeline": "Timeline",
  "warroom.pb.stop": "Stop {n}",
  "warroom.pb.noArrivals": "No stop arrival times were recorded for this job",
  "warroom.pb.dayActivity": "Activity over the day",
  "warroom.pb.stUnsettled": "Left open",
  "warroom.pb.stNotRun": "Never ran",
  "warroom.pb.noneInFilter": "No jobs with the chosen status",
  "warroom.pb.othersExist": "{n} jobs have another status",
  "warroom.pb.showAllStatuses": "Show every status",
  "warroom.pb.outsideFilter": "Outside the filter",
  "warroom.pb.today": "Today",
  "warroom.pb.noJob": "Ran without a job",
  "warroom.pb.nVehicles": "{n} vehicles",
  "warroom.pb.truncated": "Data thinned for speed",
  "warroom.pb.playAll": "▶ See all on the map",
  "warroom.searchAll": "Search route, job, vehicle, driver… e.g. “70-33”, “JT-”",
  "warroom.clearVehicleFilter": "Clear vehicle filter",
  "warroom.clearFocusedJob": "Clear focused job",
  "warroom.fleetStats": "Fleet stats",
  "warroom.tripsToday": "Trips today",
  "warroom.plateDriverJobRoute": "Plate · driver · job · route",
  "warroom.allContracts": "All contracts",
  "warroom.allRoutes": "All routes",
  "warroom.noStopData": "No stop data",
  "warroom.loadingTicket": "Loading ticket…",
  "warroom.liveStatus": "Live status",
  "warroom.call": "Call",
  "warroom.chat": "Chat",
  "warroom.contract": "Contract",
  "warroom.route": "Route",
  "warroom.schedule": "Schedule",
  "warroom.fleet": "Fleet",
  "warroom.draft": "Draft",

  // ── fleet ──────────────────────────────────────────────────────────────
  "common.previous": "Previous",
  "common.priority": "Priority",
  "common.category": "Category",
  "common.photo": "Photo",
  "common.province": "Province",
  "common.notes": "Notes",
  "common.itemsPerPage": "Items per page",
  "common.optionalNote": "Optional note",
  "common.additionalDetails": "Additional details…",
  "common.keep": "Keep",
  "fleet.backToFleet": "Back to Fleet",
  "fleet.searchVehicle": "Search vehicle by plate, ID…",
  "fleet.searchAll": "Search ID, plate, driver, province, type…",
  "fleet.licensePlate": "License Plate",
  "fleet.plateRequired": "License plate is required",
  "fleet.vehicleId": "Vehicle ID",
  "fleet.vehicleIdRequired": "Vehicle ID is required",
  "fleet.willBeSavedAs": "Will be saved as",
  "fleet.vehicleType": "Vehicle Type",
  "fleet.seats": "Seats",
  "fleet.ownership": "Ownership Type",
  "fleet.primaryDriver": "Primary Driver",
  "fleet.searchDriver": "Search driver by name or ID…",
  "fleet.searchDepot": "Search depot / parking location…",
  "fleet.selectProvince": "Select province",
  "fleet.dueDate": "Due Date",
  "fleet.shop": "Shop",
  "fleet.workshopName": "Workshop name",
  "fleet.endAfterStart": "End date must be on or after the start date",
  "fleet.expiryAfterStart": "Expiry date must be on or after the start date",
  "fleet.deviceId": "Device ID",
  "ops.driverLabel": "Driver",
  "fleet.deviceModel": "Device Model",
  "fleet.simNumber": "SIM Number",
  "fleet.billingCycle": "Billing Cycle",
  "fleet.gpsContracts": "GPS Contracts",
  "fleet.editContract": "Edit contract",
  "fleet.issueExpiryRequired": "Issue and expiry date are required",
  "fleet.providerRequired": "Provider is required",
  "fleet.policyRequired": "Policy number is required",
  "fleet.insurerRequired": "Insurer is required",
  "fleet.coverageType": "Coverage Type",
  "fleet.premiumThb": "Premium (THB)",
  "fleet.coverageClass": "Coverage Class",
  "fleet.insClass1": "Class 1",
  "fleet.insClass2Plus": "Class 2+",
  "fleet.insClass2": "Class 2",
  "fleet.insClass3Plus": "Class 3+",
  "fleet.insClass3": "Class 3",
  "fleet.sumInsured": "Sum Insured",
  "fleet.sumInsuredThb": "Sum Insured (THB)",
  "fleet.deductible": "Deductible",
  "fleet.deductibleThb": "Deductible (THB)",
  "fleet.repairType": "Repair Type",
  "fleet.repairDealer": "Dealer workshop",
  "fleet.repairGarage": "General garage",
  "fleet.namedDriver": "Named Driver",
  "fleet.namedDriverYes": "Named drivers only",
  "fleet.namedDriverNo": "Any driver",
  "fleet.coverUnspecified": "Not specified",
  "fleet.coverDetails": "Cover",
  "fleet.insurancePolicies": "Insurance Policies",
  "fleet.cmiPolicies": "Compulsory insurance (พ.ร.บ.)",
  "fleet.addCmi": "Add CMI",
  "fleet.noCmi": "No compulsory insurance on record for this vehicle",
  "fleet.commercialPolicies": "Commercial insurance",
  "nav.cmi": "CMI (พ.ร.บ.)",
  "fleet.editPolicy": "Edit policy",
  "fleet.deletePolicy": "Delete policy",
  "fleet.editWorkOrder": "Edit work order",
  "fleet.searchPmPlaceholder": "Search vehicle, service type, shop…",
  "fleet.pmTitle": "Preventive Maintenance",
  "fleet.pmLoadFailed": "Could not load PM schedules",
  "fleet.pmEdit": "Edit PM schedule",
  "fleet.pmDelete": "Delete PM schedule",
  "fleet.pmDeleteQ": "Delete this PM schedule?",
  "fleet.deleteWorkOrderQ": "Delete this work order?",
  "fleet.woDeleteFailed": "Could not delete the work order",
  "toast.workOrderDeleted": "Work order deleted",
  "fleet.pmDeleteFailed": "Could not delete the PM schedule",
  "toast.pmDeleted": "PM schedule deleted",
  "fleet.pmNone": "No PM schedules found",
  "fleet.addRow": "Add row",
  "fleet.removeRow": "Remove row",
  "fleet.addColumn": "Add column",
  "fleet.removeColumn": "Remove column",
  "fleet.removeGroup": "Remove group",
  "fleet.removeItem": "Remove item",

  // ── people ─────────────────────────────────────────────────────────────
  "common.prev": "Prev",
  "common.type": "Type",
  "common.reason": "Reason",
  "common.effectivePeriod": "Effective period",
  "common.openEnded": "Open-ended",
  "common.amount": "Amount",
  "common.source": "Source",
  "common.item": "Item",
  "common.restricted": "Restricted",
  "common.reject": "Reject",
  "common.verified": "Verified",
  "common.rejected": "Rejected",
  "common.pending": "Pending",
  "hr.fullName": "Full name",
  "hr.nameRequired": "Name is required",
  "hr.nameEn": "Name (EN)",
  "hr.nameEnPlaceholder": "English name (optional)",
  "hr.age": "Age",
  "hr.gender": "Gender",
  "hr.phone": "Phone",
  "hr.branch": "Branch",
  "hr.department": "Department",
  "hr.position": "Position",
  "hr.zone": "Zone",
  "hr.minChars8": "Minimum 8 characters",
  "hr.selectEmployee": "Select employee",
  "hr.removeMember": "Remove member",
  "hr.deductions": "Deductions",
  "hr.noSalaryHistory": "No salary history yet",
  "hr.pipeline": "Pipeline",
  "hr.deleteDocument": "Delete Document?",
  "hr.idCards": "National ID Cards",
  "hr.payeeInfo": "Payee Information",
  "hr.addBehavior": "Add Behavior Record",
  "hr.behaviorHistory": "Behavior History",
  "hr.issueDate": "Issue Date",
  "hr.notApprovedExcluded": "Not approved yet — excluded from Net",
  "hr.removeCondition": "Remove condition",

  // ── operations ─────────────────────────────────────────────────────────
  "common.prevPage": "Previous page",
  "common.nextPage": "Next page",
  "common.saveChanges": "Save Changes",
  "common.saved": "Saved",
  "common.customer": "Customer",
  "common.start": "Start",
  "common.end": "End",
  "ops.routes": "Routes",
  // ADR 0235 — route time presets
  "ops.timePresets": "Frequently Used Times",
  "ops.addTimePreset": "Add time set",
  "ops.noTimePresets": "No frequently used times yet",
  "ops.timePresetNamePlaceholder": "e.g. Morning run",
  "ops.timePresetNameRequired": "Time set needs a name",
  "ops.timePresetDuplicate": "Duplicate time set name: {name}",
  "ops.timePresetTimeInvalid": "Invalid times on {name}",
  "ops.timePresetLateInvalid": "Late threshold on {name} must be 1-240 minutes",
  "ops.lateThresholdMin": "Late threshold (min)",
  "ops.timeGates": "Time rules",
  "ops.acceptOpenBefore": "Accept opens before the accept deadline (min)",
  "ops.cancelCutoffBefore": "Cancel closes before start (min)",
  "ops.timePresetPickRouteFirst": "Pick a route first",
  "ops.timePresetPick": "Frequently used times",
  "ops.timePresetPickPlaceholder": "Choose a time set…",
  "ops.timePresetLockedHint": "This column has job tickets — times cannot change",
  "common.enable": "Enable",
  "common.disable": "Disable",
  "ops.stopsPickDrop": "Pick-up / Drop-off",
  "ops.viaPoints": "Via points",
  "ops.stops": "Stops",
  "ops.checkinMethod": "Check-in Method",
  "ops.jobType": "Job Type",
  "ops.bookingMode": "Booking Mode",
  "ops.renameBoard": "Rename board",
  "ops.deleteTrip": "Delete trip",
  "ops.deposit": "Deposit",
  "ops.searchContracts": "Search contracts…",
  "ops.booked": "Booked",
  "ops.contactPerson": "Contact Person",
  "ops.jobLabel": "Job Label",
  "ops.beforeCutoff": "Before cutoff (2h before departure)",
  "ops.searchEmployee": "Search Employee",

  "common.firstPage": "First page",
  "common.lastPage": "Last page",
  "common.sort": "Sort",
  "common.key": "Key",
  "common.value": "Value",
  "common.file": "File name",
  "common.uploaded": "Uploaded",
  "common.failed": "Failed",
  "common.documents": "Documents",
  "common.unsavedChanges": "Unsaved changes",
  "common.generalInfo": "General Information",
  "common.information": "Information",
  "common.optionalDesc": "Optional description…",
  "common.saveAnyway": "Save anyway",
  "ops.searchByNameId": "Search by name, ID, department…",
  "ops.searchPassenger": "Search Passenger",
  "ops.guestName": "Guest Name",
  "ops.nameOptional": "Name (optional)",
  "ops.idOrPhone": "ID / Phone",
  "ops.optionalRef": "Optional ref",
  "ops.documentName": "Document name",
  "ops.searchPlace": "Search place or address…",
  "ops.moveUp": "Move up",
  "ops.moveDown": "Move down",
  "ops.removeStop": "Remove stop",
  "ops.latitude": "Latitude",
  "ops.longitude": "Longitude",
  // ADR 0222 — say why a coordinate paste was refused (it used to fail silently)
  "ops.pasteSwapped": "The pasted coordinates look swapped",
  "ops.pasteSwapAction": "Use {lat}, {lng}",
  "ops.pasteUnreadable": "Could not read coordinates from what you pasted — expected latitude, longitude",
  "ops.speedLimit": "Speed Limit (km/h)",
  "ops.checkinRadius": "Check-in Radius (m)",
  "ops.serviceDuration": "Service Duration (min)",
  "ops.revenue": "Revenue",
  "ops.driverCompensation": "Driver Compensation",
  "ops.searchChargeType": "Search charge type…",
  "ops.detailsPlaceholder": "Details…",
  "ops.direction": "Direction",
  "ops.selectDirection": "Select direction…",
  "ops.receipt": "Receipt",
  "ops.clearSelectedFile": "Clear selected file",
  "ops.selectStop": "Select stop…",
  "ops.cancelBooking": "Cancel booking",
  "ops.actual": "Actual",
  "ops.noJobsFromBoard": "No jobs have been created from this board yet",
  "ops.fitAllRoutes": "Fit all routes",
  "ops.period": "Period",
  "ops.routeTerms": "Route Terms",
  "ops.pricePerTrip": "Price/Trip",
  "ops.effective": "Effective",
  "ops.searchRoutes": "Search routes…",
  "ops.jobId": "Job ID",
  "ops.noTicketsForDate": "No tickets for this date",
  "ops.searchNameNotes": "Search name, code, contact, phone, email, notes…",
  "ops.customerCode": "Customer Code",
  "ops.customerNo": "Customer No.",
  "ops.ticketCustomers": "All customers",
  "ops.sharedContracts": "Shared contracts",
  "ops.customerNoColumn": "No.",
  "ops.activeContractsShort": "Active {n}",
  "ops.endedContractsShort": "Ended {n}",
  "ops.noContracts": "No contracts",
  "ops.contractCount": "Contract Count",
  "ops.expiringContractsShort": "Expiring {n}",
  "ops.expiringContractsCard": "Expiring Contracts",
  "ops.pctOfAll": "{pct}% of all",
  "ops.inclNoContracts": "incl. {n} without contracts",
  "ops.expiringWithin30": "contract ends within 30 days",
  "ops.unitCustomers": "customers",
  "ops.editContractSoon": "Edit contract — available in next release",
  "ops.extendContractSoon": "Extend contract — available in next release",
  "ops.suspendContractSoon": "Suspend contract — available in next release",

  "common.members": "Members",
  "common.contracts": "Contracts",
  "common.title": "Title",
  "hr.jobTitle": "Job title",
  "hr.hireDateRequired": "Hire date is required",
  "hr.assignedVehicle": "Assigned Vehicle",
  "hr.searchVehicleIdPlate": "Search vehicle by ID or plate…",
  "hr.searchEmployeeAll": "Search name, email, ID, phone, position, vehicle…",
  "hr.teamLoadFailed": "Failed to load driver team",
  "hr.teamsLoadFailed": "Failed to load driver teams",
  "hr.searchDriverNameId": "Search driver by name, ID…",
  "hr.driverId": "Driver ID",
  "hr.noMembersYet": "No members in this team yet",
  "hr.removeMemberQ": "Remove member?",
  "hr.driverTeams": "Driver Teams",
  "hr.contractIds": "Contract IDs",
  "hr.teamDetails": "Team details…",
  "hr.searchTeam": "Search team, zone, description…",
  "hr.editTeam": "Edit team",
  "hr.deactivateTeam": "Deactivate team",
  "hr.deactivate": "Deactivate",
  "hr.deactivateTeamQ": "Deactivate driver team?",
  "hr.importSalaryCsv": "Import salary CSV",
  "hr.importEmployeesCsv": "Import Employees (CSV)",
  "hr.clickReplaceFile": "Click to replace file",
  "hr.dropCsv": "Drop a CSV file here",
  "hr.currentRules": "Current Rules",
  "hr.allowances": "Allowances",
  "hr.salaryHistory": "Salary History",
  "hr.salaryLoadFailed": "Failed to load salary data",
  "hr.salaryHrOnly": "Salary Rules are available to HR admins only.",
  "hr.salaryRules": "Salary Rules",
  "hr.searchNameEmpId": "Search name, employee ID…",
  "hr.editRules": "Edit Rules",
  "hr.noSalaryMatch": "No salary rules match your filters",
  "hr.noCandidates": "No candidates",
  "hr.rejectCandidate": "Reject Candidate",
  "hr.deleteCandidateQ": "Delete Candidate?",
  "hr.offerBeforeOnboarding": "The offer must be accepted before advancing to onboarding.",
  "hr.applicationInfo": "Application Info",
  "hr.interview": "Interview",
  "hr.notYetScheduled": "Not yet scheduled",
  "hr.offer": "Offer",
  "hr.markDeclined": "Mark Declined",
  "hr.noOfferYet": "No offer yet",
  "hr.rejectionReason": "Rejection Reason",
  "hr.editPosting": "Edit posting",
  "hr.addCandidate": "Add Candidate",
  "hr.deletePostingQ": "Delete Posting?",
  "hr.recruitmentPipeline": "Recruitment Pipeline",
  "hr.searchPosting": "Search title, location, department…",

  "common.address": "Address",
  "common.view": "View",
  "common.replace": "Replace",
  "common.titleRequired": "Title is required",
  "fleet.loadFailed": "Failed to load vehicles",
  "fleet.row": "Row",
  "fleet.col": "Col",
  "fleet.cellType": "Cell Type",
  "fleet.clickCell": "Click a cell to change its type.",
  "fleet.seatAutoLabel": "Seats auto-label (A1, B2, etc.)",
  "fleet.deviceSerial": "Device serial or IMEI",
  "fleet.simPhone": "SIM / phone no.",
  "fleet.costThb": "Cost (THB)",
  "fleet.costExclVat": "Cost excl. VAT (THB)",
  "fleet.gpsLoadFailed": "Failed to load GPS data",
  "fleet.addContract": "Add Contract",
  "fleet.searchGps": "Search vehicle, provider, model…",
  "fleet.noGpsContracts": "No GPS contracts found",
  "fleet.noGpsForVehicle": "No GPS contracts for this vehicle.",
  "fleet.deactivateVehicleQ": "Deactivate Vehicle?",
  "fleet.deactivateVehicleHint": "This will mark the vehicle as inactive. You can reactivate it later.",
  "fleet.generalInfo": "General Info",
  "fleet.depotLocation": "Depot Location",
  "fleet.coordinates": "Coordinates",
  "fleet.registrationHistory": "Registration History",
  "fleet.noRegistrationHistory": "No registration history.",
  "fleet.addRegistration": "Add Registration",
  "fleet.currentRegistration": "Current registration",
  "fleet.uploadPhoto": "Upload Photo",
  "fleet.clickChooseImage": "Click to choose an image",
  "fleet.photos": "Photos",
  "fleet.addPhoto": "Add photo",
  "fleet.viewPhoto": "View photo",
  "fleet.replacePhoto": "Replace photo",
  "fleet.deletePhoto": "Delete photo",
  "fleet.deletePhotoQ": "Delete Photo?",
  "fleet.deviceIdRequired": "Device ID is required",
  "fleet.installationDate": "Installation Date",
  "fleet.startEndRequired": "Start and end date are required",
  "fleet.startExpiryRequired": "Start and expiry date are required",
  "fleet.describeWork": "Describe the work to be done…",
  "fleet.cancelContract": "Cancel contract",
  "fleet.reactivateContract": "Reactivate contract",
  "fleet.cancelGpsQ": "Cancel GPS Contract?",
  "fleet.reactivateGpsQ": "Reactivate GPS Contract?",

  "common.loadingShort": "Loading",
  "common.today": "Today",
  "common.selectDate": "Select date",
  "common.openCalendar": "Open calendar",
  "common.userMenu": "User menu",
  "common.closePreview": "Close preview",
  "common.timeline": "Timeline",
  "common.unassigned": "Unassigned",
  "ops.boardingStop": "Boarding Stop",
  "ops.selectBoardingStop": "Select boarding stop…",
  // ADR 0411 — passengers are a pair: board → drop ("board/drop" for people, "pickup/deliver" for cargo)
  "ops.selectDropStop": "Select drop-off stop…",
  "ops.pair.boardAt": "Boards at",
  "ops.pair.dropAt": "Gets off at",
  "ops.pair.chooseStop": "Choose a stop",
  "ops.dropStop": "Drop-off stop",
  // ADR 0412 — the single "Route" column that holds both ends
  "ops.route.col": "Route",
  "ops.route.noFrom": "No pickup stop",
  "ops.route.noTo": "No delivery stop",
  "ops.route.noBoard": "No boarding stop",
  "ops.route.noDrop": "No drop-off stop",
  "ops.pair.thisStop": "This stop",
  "ops.pair.why.beforePickup": "Before boarding",
  "ops.pair.noDropPair": "No drop-off stop (older record)",
  "ops.pair.lastStopNoBoard": "This is the final stop — nobody boards here",
  "ops.pair.lastStopHow": "To record someone getting off here, add them at an earlier stop and set \u201Cdrop at\u201D to this stop",
  "ops.dropoffStop": "Dropoff Stop",
  "ops.optionalDropoff": "Optional dropoff stop…",
  "ops.passengerName": "Passenger Name",
  "ops.passengerNamePlaceholder": "Passenger name…",
  "ops.searchPassengerAll": "Search passenger by name, ID, email…",
  "portal.noPortals": "No portals available",
  "portal.yourPortals": "Your portals",
  "portal.accessRestricted": "Access restricted",
  "portal.ticketsThisWeek": "Job Tickets this week",
  "portal.recentTickets": "Recent job tickets",
  "portal.bookingsLoadFailed": "Failed to load bookings",
  "portal.bookings": "Bookings",
  "portal.prevDay": "Previous day",
  "portal.operatingDate": "Operating date",
  "portal.nextDay": "Next day",
  "portal.allTickets": "All job tickets",
  "portal.searchBooking": "Search passenger name or booking ID…",
  "portal.noBookings": "No bookings found",
  "portal.noBoardingData": "No boarding data available",
  "portal.noShowPassengers": "No-Show Passengers",
  "portal.noNoShows": "No no-shows recorded",
  "portal.totalCancelled": "Total Cancelled",

  "common.from": "From",
  "common.progress": "Progress",
  "ops.stopProgress": "Stop Progress",
  "ops.stopProgress.ours": "In the system",
  "ops.stopProgress.locus": "In Locus",
  "ops.stopTime.planned": "Planned {time}",
  // ADR 0500 D3 — grouped map pins
  "map.group.title": "{n} stops at the same spot",
  "map.group.depotTitle": "Store (out and back)",
  "map.group.depotLabel": "Out·Back",
  "ops.stopTime.locusDone": "Delivered {time} (Locus)",
  "ops.stopTime.nextDay": "(+{n} day)",
  "ops.stopPlaceholder": "Temporary",
  "ops.locusAwaitingOrders": "Locus has not sent the drop-offs yet ({n} orders) — the next pull tries again",
  "ops.roadLineFailed": "Could not draw the road line — showing straight lines",
  "ops.roadLineDrawing": "Drawing the road line…",
  "ops.passengers": "Passengers",
  "ops.driverPay": "Driver Pay",
  "ops.needsAttention": "Needs attention",
  "ops.newTicket": "New Ticket",
  "ops.ticketsLoadFailed": "Failed to load tickets",
  "ops.searchTickets": "Search: job#, job name, route, driver, plate, contract, notes…",
  "ops.notScheduled": "Not scheduled",
  "ops.noStops": "No stops",
  "ops.createNewTicket": "Create New Ticket",
  "ops.selectContract": "Select contract…",
  "ops.selectContractFirst": "Select a contract first…",
  "ops.routeTemplate": "Route",
  "ops.assignTicket": "Assign Ticket",
  "ops.assignDriverVehicle": "Assign driver and vehicle to",
  "ops.searchDriverNameId": "Search driver by name, ID…",
  "ops.searchVehiclePlateId": "Search vehicle by plate, ID…",
  "ops.cancelRate": "Cancel Rate",
  "ops.totalBookings": "Total Bookings",
  "ops.cancelPermissions": "Cancellation Permissions",
  "ops.adminOps": "Admin / Ops",
  "ops.cancelAnyAnytime": "Cancel any booking anytime",
  "ops.bulkCancel": "Bulk cancel (trip cancel)",
  "ops.overrideCutoff": "Override cutoff time",
  "ops.portalFactory": "Portal (Factory)",
  "ops.cancelOwnEmployees": "Cancel own employees only",
  "ops.mustProvideReason": "Must provide reason",
  "ops.passenger": "Passenger",
  "ops.cancelOwnBooking": "Cancel own booking only",
  "ops.viaMobileApp": "Via mobile app",
  "ops.cancelledBookings": "Cancelled Bookings",
  "ops.noCancellations": "No cancellations",
  "ops.cancelBookingTitle": "Cancel Booking",
  "ops.reasonRequired": "Reason (required)",
  "ops.markNoShow": "Mark as No-Show",
  "ops.newBooking": "New Booking",
  "ops.jobTicket": "Job Ticket",
  "ops.searchJobTicket": "Search job ticket…",
  "ops.editContractUnavailable": "Edit contract (unavailable)",
  "ops.extendContractUnavailable": "Extend contract (unavailable)",
  "ops.suspendContractUnavailable": "Suspend contract (unavailable)",

  "hr.editPayee": "Edit Payee",
  "hr.paymentMethod": "Payment Method",
  "hr.expiryDate": "Expiry Date",
  "hr.setTripRate": "Set trip rate",
  "hr.searchContract": "Search contract…",
  "hr.rateNoteHint": "Optional note shown when picking this rate",
  "hr.newLeaveRequest": "New Leave Request",
  "hr.selectType": "Select type",
  "hr.uploadDocument": "Upload Document",
  "hr.documentType": "Document Type",
  "hr.editBaseComp": "Edit Base Compensation",
  "hr.annualReview": "Annual review…",
  "hr.deleteChargeQ": "Delete Charge?",
  "hr.overrideTicketRate": "Override ticket rate",
  "hr.endAfterStart": "End must be after start.",
  "hr.startDateRequired": "Start date is required.",
  "hr.licenseNumber": "Licence Number",
  "hr.issuer": "Issuer",
  "hr.deactivateEmployeeQ": "Deactivate Employee?",
  "hr.deleteRecordQ": "Delete Record?",
  "hr.deleteLicenseQ": "Delete Licence?",
  "hr.deleteIdCardQ": "Delete ID Card?",
  "hr.cancelLeaveQ": "Cancel Leave Request?",
  "hr.safetyScore": "Safety score",
  "hr.noDocuments": "No documents uploaded yet",
  "hr.drivingLicenses": "Driving Licenses",
  "hr.noLicenses": "No licenses on file",
  "hr.noIdCards": "No ID cards on file",
  "hr.editIdCard": "Edit ID card",
  "hr.deleteIdCard": "Delete ID card",
  "hr.voidVersion": "Void this version",
  "hr.voidRateQ": "Void this rate?",
  "hr.ticketOverrides": "Ticket Overrides (Manual)",
  "hr.noTripsWindow": "No trips in this window",
  "hr.periodLocked": "Period locked",

  "common.rangeOf": "{label} range",
  "common.reset": "Reset",
  "common.createdBy": "Created By",
  "common.noteOptional": "Note (optional)",
  "common.reasonOptional": "Reason (optional)",
  "common.fromDate": "From date",
  "common.toDate": "To date",
  "ops.viaPointRouting": "Via point — routing only",
  "ops.passengerType": "Passenger Type",
  "ops.pickupStop": "Pickup Stop",
  "ops.whyCancelled": "Why is this cancelled?",
  "ops.ticketNotFound": "Job ticket not found",
  "ops.financials": "Financials",
  "ops.customRate": "Custom rate",
  // ADR 0267 — rate provenance comes from rate_tier, not value comparison
  "ops.rateSourceRoute": "Route's standard pay",
  "ops.rateSourceRouteInternal": "Route's standard pay · internal driver",
  "ops.rateSourceRouteExternal": "Route's standard pay · external driver",
  "ops.rateSourceRouteDriver": "Personal route rate",
  "ops.rateSourceContractDriver": "Personal contract rate",
  "ops.rateSourceDriverDefault": "Personal default rate",
  "ops.rateSourceManual": "Set on the ticket",
  // ADR 0267 P2 — province rate on the ticket · does not affect pay yet
  "ops.provinceRate": "Province rate",
  "ops.rateAutoOnAssign": "The rate is calculated automatically from the route rate for the driver kind, once a driver is assigned.",
  "ops.provinceRatePlaceholder": "Not selected",
  "ops.kindIncome": "Income",
  "ops.kindExpense": "Expense",
  "hr.driverPayPersonalRateHint": "Per-driver rates are managed in",
  "ops.routeRate": "Route rate",
  "ops.routeBonusLegacy": "Route bonus (legacy)",
  "ops.operationalCharges": "Operational Charges",
  "ops.addOperationalCharge": "Add Operational Charge",
  "ops.deleteCharge": "Delete charge",
  "ops.editCharge": "Edit charge",
  "ops.receiptKept": "Current receipt is kept unless replaced",
  "ops.jobSettings": "Job Settings",
  "ops.timing": "TIMING",
  "ops.assignmentCaps": "ASSIGNMENT",
  "ops.detailsCaps": "DETAILS",
  "ops.notesCaps": "NOTES",
  "ops.boardingCaps": "BOARDING",
  "ops.whenFull": "When full",
  "ops.passengerManifest": "Passenger Manifest",
  "ops.searchNameBookingId": "Search name or booking ID…",
  "ops.allStops": "All Stops",
  "ops.addPassenger": "Add Passenger",
  "ops.markNoShowShort": "Mark no-show",
  "ops.editPassenger": "Edit Passenger",
  "ops.editSchedule": "Edit Schedule",
  "ops.stopName": "Stop Name",
  "ops.scheduled": "Scheduled",
  "ops.walkIn": "Walk-in",
  "ops.checkin": "Check-in",
  "fleet.totalFuelCost": "Total Fuel Cost",

  "common.clearAll": "Clear all",
  "common.clearAllFilters": "Clear all filters",
  "common.rows": "Rows",
  "common.discard": "Discard",
  "common.changeStatus": "Change status",
  "common.viewAll": "View all",
  "common.done": "Done",
  "common.verify": "Verify",
  "common.selectAll": "Select all",
  "common.selectAllVisible": "Select all visible",
  "common.tryAgain": "Try again",
  "common.noItems": "No items found",
  "common.home": "Home",
  "common.notifications": "Notifications",
  "common.markAllRead": "Mark all read",
  "common.noNotifications": "No notifications",
  "common.dangerZone": "Danger Zone",
  "common.role": "Role",
  "common.soon": "Soon",
  "common.pageNotFound": "Page not found",
  "common.pageNotFoundHint": "The link may be broken or the page may have been moved.",
  "common.backToDashboard": "Back to Dashboard",
  "hr.title": "HR & Workforce",
  "ops.keepBooking": "Keep Booking",
  "ops.backToContracts": "Back to Contracts",
  "ops.backToCustomers": "Back to Customers",
  "ops.backToTickets": "Back to Job Tickets",
  "ops.amountKept": "Jobs already created keep the amount they were given.",
  "ops.createBoard": "Create Board",
  "ops.noDatesMatch": "No dates match the day-of-week filter",
  "ops.backToPool": "Back to pool",
  "ops.vehiclesNeeded": "Vehicles needed",
  "ops.noVehiclesSelected": "No vehicles selected yet",
  "portal.goToMyPortals": "Go to my portals",
  "masterData.codeFormat": "Code must be lowercase letters, digits, or underscores.",
  "settings.noOrgsMatch": "No organizations match that search",

  "common.attributes": "Attributes",
  "common.result": "Result",
  "common.startDate": "Start Date",
  "common.endDate": "End Date",
  "ops.undoPublish": "Undo publish",
  "ops.deleteRoute": "Delete route",
  "ops.deleteRoutePair": "Delete route pair",
  "ops.createPortal": "Create Portal",
  "ops.createPassenger": "Create Passenger",
  "ops.extend": "Extend",
  "ops.suspend": "Suspend",
  "ops.jobs": "Jobs",
  "ops.customersAdminOnly": "Customers is visible to admins only.",
  "ops.contractTerms": "Contract Terms",
  "ops.contractStatus": "Contract Status",
  "ops.financial": "Financial",
  "ops.estimateColumn": "Estimate/Deposit",
  "ops.remainingTerm": "Remaining Term",
  "ops.remainDaysLeft": "{n} days left",
  "ops.remainOverdue": "{n} days overdue",
  "ops.remainLastDay": "Last day today",
  "ops.contractDateHistory": "Period Change History",
  "ops.contractNoColumn": "ID",
  "ops.reminders": "Reminders",
  "ops.reminderAdd": "Add reminder",
  "ops.reminderPlaceholder": "Describe what needs to be done…",
  "ops.reminderDue": "Due {date}",
  "ops.reminderDueIn": "{n} days left · {date}",
  "ops.reminderDueToday": "Due today",
  "ops.reminderOverdueDue": "{n} days overdue · {date}",
  "ops.reminderOverdueN": "Overdue {n}",
  "ops.reminderOpenN": "Open {n}",
  "ops.reminderColumn": "Notes",
  "ops.reminderSoonN": "Due soon {n}",
  "ops.reminderDoneBy": "Done by {name} · {date}",
  "ops.reminderEmpty": "No reminders yet",
  "ops.reminderDeleteConfirm": "Delete this reminder?",
  "ops.historyNoChanges": "No period changes yet",
  "ops.historyWhen": "Edited At",
  "ops.historyWho": "Edited By",
  "ops.manageContractTypesHint": "Add or edit types in Master Data → Contract Type",
  "ops.monthlyValue": "Contract Value (THB)",
  "ops.filterByCustomer": "All customers",
  "ops.depositThb": "Deposit (THB)",
  "ops.depositStatus": "Deposit Status",
  "ops.contractsLoadFailed": "Failed to load contracts",
  "ops.routeTemplates": "Routes",
  "ops.newRouteTemplate": "New Route",
  "ops.routeCode": "Route Code",
  "ops.routeName": "Route Name",
  "ops.searchRouteCodeName": "Search route code or name…",
  "ops.saveRoute": "Save Route",
  "ops.routeOptimizer": "Route Optimizer",
  "ops.addToRoute": "Add to route",
  "ops.optimizeFor": "Optimize for",
  "ops.avoidance": "Avoidance",
  "ops.returnToDepot": "Return to depot",
  "ops.returnToDepotHint": "Vehicle returns to its start location after the last stop",
  "ops.serviceDurationPerStop": "Service duration per stop",
  "ops.summaryLanguage": "Summary language",
  "ops.capacityPerVehicle": "Capacity per vehicle",
  "ops.saveAsTemplate": "Save as Route Template",
  "ops.routeDescPlaceholder": "Brief description of this route...",
  "ops.addStop": "Add Stop",
  "ops.stopNamePlaceholder": "Stop name",

  "common.approve": "Approve",
  "common.history": "History",
  "common.changes": "Changes",
  "common.record": "Record",
  "common.actor": "Actor",
  "settings.noDetailRecorded": "No detail recorded",
  "settings.teamMembers": "Team Members",
  "settings.gpsInterval": "GPS Refresh Interval",
  // ADR 0219 — map presets (org setting, shared with the driver app)
  "settings.map": "Map",
  "settings.mapPreset": "Map detail",
  "settings.mapPreset.clean": "Clean — data only",
  "settings.mapPreset.standard": "Standard — place names",
  "settings.mapPreset.detailed": "Detailed — everything",
  "settings.mapPreset.clean.desc": "No place names, icons, stations or airports. The base map stays a plain canvas so stops and vehicles are the only things on it.",
  "settings.mapPreset.standard.desc": "Place names and icons — shops, schools, hospitals, temples, parks, stations, airports. Rail and bus lines stay hidden because they look too much like a route line.",
  "settings.mapPreset.detailed.desc": "Everything Standard shows, plus rail and bus lines, soi names and neighbourhood names.",
  "settings.mapPresetHint": "Applies to every map here and in the driver app. Drivers see it on the next job they open.",
  "settings.gpsIntervalHint": "Seconds between GPS positions. New jobs inherit this.",
  "settings.usernamePrefixes": "Username Prefixes",
  "settings.separator": "Separator",
  "settings.labelOptional": "Label (optional)",
  "settings.noPrefixes": "No prefixes yet.",
  "settings.recentDeliveries": "Recent Deliveries",
  "settings.noDeliveries": "No deliveries yet",
  "settings.noWebhooks": "No webhooks yet",
  "settings.addTemplate": "Add Template",
  "settings.auditTitle": "Audit Log",
  "settings.auditSubtitle": "All system mutations with full trail (Asia/Bangkok)",
  "settings.auditSearch": "Search actor, entity, action…",
  "settings.auditLoading": "Loading audit log…",
  "settings.auditFilterRecord": "Filter the full trail of this record",
  "settings.auditNoMatch": "No audit entries match this filter",
  "settings.auditDetail": "Audit entry detail",
  "settings.simulate": "Simulate",
  "settings.outgoingTransitions": "Outgoing Transitions",
  "settings.noOutgoing": "No outgoing transitions (end state)",
  "settings.workflowEngine": "Workflow Engine",
  "settings.newWorkflow": "New Workflow",
  "settings.membersSubtitle": "Manage members and invitations for your organization",
  "settings.inviteMember": "Invite Member",
  "settings.emailAddress": "Email address",
  "hr.charges": "Charges",

  "common.saveChangesLower": "Save changes",
  "common.viewDetails": "View details",
  "common.backToList": "Back to list",
  "common.completed": "Completed",
  "common.inProgress": "In progress",
  "warroom.queueUpcoming": "Queue — upcoming jobs today",
  "warroom.noVehicles": "No vehicles",
  "warroom.noVehiclesMatch": "No vehicles match",
  "warroom.vehicles": "Vehicles",
  // ADR 0498 D4 · D5
  "warroom.overdueShort": "+{overrun}",
  "warroom.overdueTip": "{overrun} past the expected end",
  "warroom.overdueTipEnd": "Due {when}",
  "warroom.dur.min": "{n} min",
  "warroom.dur.hr": "{n} hr",
  "warroom.dur.day": "{n}d",
  "warroom.locusDelivered": "In Locus: {done}/{total} stops delivered",
  "warroom.roadFailed": "Could not work out the road line",
  "warroom.rewind5min": "Rewind 5 minutes",
  "warroom.rewind30s": "Rewind 30 seconds",
  "warroom.forward30s": "Skip forward 30 seconds",
  "warroom.forward5min": "Skip forward 5 minutes",
  "warroom.scrubber": "Playback timeline scrubber",
  "fleet.seatMapDesigner": "Seat Map Designer",
  "fleet.selectVehicleToSave": "Select a vehicle above to enable saving.",
  "fleet.backToFleetLower": "Back to fleet",
  "fleet.plateRequiredShort": "Plate is required",
  "fleet.fuelEntries": "Fuel Entries",
  "fleet.noFuelLogs": "No fuel logs for this vehicle yet.",
  "fleet.noInsurance": "No insurance policies for this vehicle.",
  "fleet.deleteInsuranceQ": "Delete Insurance Policy?",
  "fleet.taxType": "Tax Type",
  "fleet.amountThb": "Amount (THB)",
  "fleet.expiryAfterIssue": "Expiry date must be on or after the issue date",
  "fleet.receiptNumber": "Receipt Number",
  "fleet.taxRecords": "Tax records",
  "fleet.noTaxRecords": "No tax records for this vehicle.",

  // ── status ─────────────────────────────────────────────────────────────
  "status.draft": "Draft",
  "status.assigned": "Assigned",
  "status.approved": "Approved",
  "status.accepted": "Accepted",
  "status.started": "Started",
  "status.inProgress": "In Progress",
  "status.completed": "Completed",
  "status.closed": "Closed",
  /** ADR 0331 — ต่างกันเฉพาะภาษาไทย (ใบงาน = "ไม่ใช้งาน") อังกฤษใช้คำเดียวกัน */
  "status.ticketClosed": "Closed",
  "status.cancelled": "Cancelled",
  "status.scheduled": "Scheduled",
  "status.active": "Active",
  "status.inactive": "Inactive",
  "status.pending": "Pending",
  "status.expired": "Expired",
  "status.suspended": "Suspended",
  "status.confirmed": "Confirmed",
  "status.reserved": "Reserved",
  "status.boarded": "Boarded",
  "status.noShow": "No-show",

  "common.general": "General",
  "common.critical": "Critical",
  "common.created": "Created",
  "common.ok": "OK",
  "common.manual": "Manual",
  "common.yesterday": "Yesterday",
  "common.ready": "Ready",
  "common.high": "High",
  "common.medium": "Medium",
  "common.low": "Low",
  "common.submitted": "Submitted",
  "common.open": "Open",
  "common.archived": "Archived",
  "common.late": "Late",
  "ops.pickupDropPoint": "Stop",
  "common.guest": "Guest",
  "status.stored": "Stored",
  "status.inRepair": "In repair",
  "status.walkIn": "Walk-In",
  "status.noShowCaps": "No-Show",
  "ops.noBooking": "No booking",
  "ops.utilShort": "Util",
  "ops.deadhead": "Deadhead",
  "ops.chained": "Chained",
  "ops.backToBoards": "Back to boards",
  "warroom.todayNDrivers": "today · {n} drivers",
  "warroom.callNow": "call now",
  "ops.problemsGone": "Those {n} problems are gone.",
  "ops.nothingWasChanged": "Nothing was changed.",
  "ops.datesLocked": "{n} date(s) have live job tickets and cannot be removed: {dates}",
  "ops.saveDatesFailed": "Failed to save dates",
  "ops.removeDateFailed": "Failed to remove date",
  "ops.dateRemoved": "Removed date · {n} trips deleted",
  "ops.contractLockedPaired": "Contract is locked while the route is paired — change {jobType} to {general}/{charter} in the {card} card first.",
  "passengers.tempPasswordNote": "All current sessions are signed out, and {name} must change this password at next sign-in. Share it with them directly — it is shown only here, never emailed or stored.",
  "passengers.reactivate": "Reactivate",
  "passengers.noBookingsYet": "No bookings yet",
  "passengers.noBookingsMatch": "No bookings match the filter",
  "err.passenger.emailExists": "A passenger with this email already exists in this organization",
  "err.passenger.emailExistsWith": "A passenger with this email already exists in this organization ({id} · {status})",
  "err.passenger.samePerson": "This person already belongs to this organization (under another email)",
  "err.passenger.samePersonWith": "This person already belongs to this organization — {id} (under another email)",
  "err.portal.memberExists": "This person is already a member of this portal",
  "passengers.noAccountYet": "No account yet — the invitation is still pending.",
  "passengers.setPassword": "Set Password",
  "passengers.errRemove": "Could not remove passenger",
  "passengers.errUpdateStatus": "Could not update status",
  "passengers.errResendInvite": "Could not resend invitation",
  "passengers.errSetTempPassword": "Could not set temporary password",
  "passengers.errSaveChanges": "Could not save changes",
  "passengers.errCreate": "Could not create passenger",
  "ops.markStopForRemoval": "Mark this stop for removal? It will be deleted when you save.",
  "common.create": "Create",
  "common.outOf": "of",
  "portal.countsLine": "{contracts} contracts · {members} members ({admin} admin)",
  "portal.deleteConfirmTitle": "Delete this portal?",
  "portal.domainNote": " The {{domain}} part becomes your real passenger app address once it's set up.",
  "portal.noOrgSlug": "This organization has no slug configured yet — the access URL is unavailable.",
  "portal.slugAutoPlaceholder": "auto-from-name",
  "portal.slugFormatError": "Only lowercase letters, numbers and hyphens",
  "portal.slugTaken": "“{slug}” is already taken",
  "portal.errChangeStatus": "Could not change status",
  "portal.errAddMember": "Could not add member",
  "portal.errUpdateRole": "Could not update role",
  "portal.errRemoveMember": "Could not remove member",
  "portal.errUpdatePortal": "Could not update portal",
  "portal.errDeletePortal": "Could not delete portal",
  "portal.errCreatePortal": "Could not create portal",
  "portal.errSaveChanges": "Could not save changes",
  "portal.scopeSaved": "Scope saved",
  "portal.scopeSearch": "Search the scope — contract or route",
  "portal.nContracts": "{n} contracts",
  "portal.scopeCustomerTitle": "Customer / contract title",
  "portal.scopeNoMatch": "No contract or route in this scope matches your search",
  "portal.scopeEmpty": "This portal has no contracts yet — use “Add contracts” to start",
  "portal.contractRange": "Contracts {from}–{to} of {total}",
  "portal.draftUnsaved": "Unsaved",
  "portal.draftAdd": "add {n} contracts",
  "portal.draftRemove": "remove {n} contracts",
  "portal.draftRoutes": "{n} route changes",
  "portal.removeContractFromPortal": "Remove contract from portal",
  "portal.noRoutesOpenWarn": "No route is open yet — passengers cannot see this contract",
  "portal.selectShown": "Select shown ({n})",
  "portal.selectAllN": "Select all ({n})",
  "portal.openWord": "Open",
  "portal.shownOfContract": "{n} shown · {total} in contract",
  "portal.searchRoutesInContract": "Search routes in this contract",
  "portal.noRoutesMatch": "No route in this contract matches your search",
  "portal.nStops": "{n} stops",
  "portal.routeRange": "Routes {from}–{to} of {total}",
  "portal.addContractsTitle": "Add contracts to the portal",
  "portal.addContractsNote": "Contracts only — narrow the routes afterwards in the main table · searching by route name works too, and says which contract it sits in",
  "portal.pickSearch": "Code / customer name, or route name",
  "portal.nRoutes": "{n} routes",
  "portal.matchedRoutes": "matches routes {routes}",
  "portal.allMatchedAlreadyIn": "Every contract matching your search is already in this portal",
  "portal.noContractsFound": "No contract matches your search",
  "portal.alreadyInPortal": "Already in this portal: {list}",
  "portal.pickedSummary": "{n} contracts picked · {routes} routes will become visible",
  "portal.nothingPicked": "Nothing picked yet",
  "warroom.editableAnyStatus": "editable in any status",
  "warroom.editStops": "Edit stops",
  "warroom.tripRateCharges": "Trip rate & charges",
  "warroom.openInJobDetail": "Open in job detail",
  "warroom.saveJobFailed": "Couldn't save this job",
  "warroom.noRouteBlankJob": "No route — blank job",
  "fuel.proof": "Proof",
  "warroom.notSet": "not set",
  "warroom.notStarted": "not started",
  "warroom.expectedWord": "expected",
  "warroom.actAt": "act {time}",
  "ops.visitedBadge": "visited",
  "ops.visitedNoEdit": "Already visited — cannot be edited",
  "ops.visitedNoRemove": "Already visited — cannot be removed",
  "hr.editCandidate": "Edit Candidate",
  "hr.nJobPostings": "{n} job postings",
  "hr.noPostingsYet": "No job postings yet. Click \"New Posting\" to create one.",
  "hr.offerAccepted": "Accepted",
  "hr.offerDeclined": "Declined",
  "hr.srcWebsite": "Website",
  "hr.srcReferral": "Referral",
  "hr.srcAgency": "Agency",
  "hr.srcLinkedin": "LinkedIn",
  "hr.srcWalkIn": "Walk-in",
  "toast.actionFailed": "Action failed",
  "toast.advancedStage": "Advanced to next stage",
  "toast.rejectFailed": "Reject failed",
  "toast.deleteFailed": "Delete failed",
  "toast.interviewSaved": "Interview saved",
  "toast.offerSaved": "Offer saved",
  "toast.offerAccepted": "Offer accepted",
  "toast.offerDeclined": "Offer declined",
  "ops.noStopsInRoute": "No stops in this route",
  "ops.noStopsAddLeft": "No stops yet — add one on the left",
  "ops.backhaulRadiusHint": "Distance treated as “nearby” when chaining a vehicle's consecutive trips.",
  "ops.driverMinRestHint": "Min rest enforced between a driver's cross-day trips (0 = not enforced).",
  "toast.statusArrow": "Status → {status}",
  "login.authFailed": "Authentication failed",
  "login.otpInvalidExpired": "Invalid or expired code",
  "login.otpTooMany": "Too many attempts. Please request a new code.",
  "login.invalidCredentials": "Invalid email or password",
  "login.accountLocked": "Account temporarily locked. Please try again later.",
  "login.networkError": "Network error. Please try again.",
  "portal.tempPasswordSet": "A temporary password is set on this account. Please set a permanent password in the passenger app before using the portal.",
  "ops.origin": "Origin",
  "ops.destination": "Destination",
  "ops.via": "Via",
  // ADR 0255 — plural handled by key selection; see th.ts for why
  "ops.stopCountOne": "{n} stop",
  "ops.stopsCount": "{n} stops",
  "ops.viaCount": "{n} via",
  // ADR 0255 — "Destination" overflows the 64px badge column; abbreviate here only.
  //             ops.destination stays full — it is a heading elsewhere (finance/driver-pay).
  "ops.destinationBadge": "Dest.",
  "hr.factorySide": "Factory side",
  "hr.homeSide": "Home side",
  "hr.middleStops": "Middle stops",
  "hr.checkedIn": "Checked",
  "hr.notCheckedIn": "Not checked",
  "hr.anyMethod": "Any method",
  "hr.gpsCheckin": "GPS check-in",
  "hr.manualCheckin": "Manual check-in",
  "hr.requirement": "Requirement",
  "toast.categoryCreated": "Category created",
  "toast.categoryUpdated": "Category updated",
  "toast.categoryDeleted": "Category deleted",
  "toast.itemAdded": "Item added",
  "toast.itemUpdated": "Item updated",
  "toast.itemDeleted": "Item deleted",
  "ops.viaLetter": "Via {letter}",
  "ops.viaLetterNamed": "Via {letter} — {name}",
  "ops.routingOnly": "routing only",
  "ops.viaPointRoutingOnly": "Via point — routing only.",
  "common.kmValue": "{n} km",
  "common.minValue": "{n} min",
  "common.minApprox": "~{n} min",
  "ops.hasDepot": "has depot",
  "common.nSeconds": "{n} seconds",
  "toast.exportFailed": "Export failed",
  "toast.inviteCancelled": "Invite cancelled.",
  "toast.couldNotCancelInvite": "Could not cancel invite.",
  "toast.couldNotRemoveMember": "Could not remove member.",
  "toast.assigned": "Assigned",
  "toast.tripDeleted": "Trip deleted",
  "toast.jobsPublished": "Jobs published",
  "toast.portalCreated": "Portal created",
  "toast.contractLinked": "Contract linked",
  "toast.contractUnlinked": "Contract unlinked",
  "toast.memberAdded": "Member added",
  "toast.roleUpdated": "Role updated",
  "toast.memberRemoved": "Member removed",
  "toast.portalUpdated": "Portal updated",
  "toast.portalDeleted": "Portal deleted",
  "toast.accessUrlCopied": "Access URL copied",
  "toast.couldNotCopy": "Could not copy",
  "toast.coordinatesCopied": "Coordinates copied",
  "toast.changesSaved": "Changes saved",
  "toast.passengerAddedTheyCan": "Passenger added — they can sign in with the initial password",
  "toast.invitationSent": "Invitation sent",
  "toast.passengerRemovedFromOrganization": "Passenger removed from organization",
  "toast.invitationResent": "Invitation resent",
  "toast.temporaryPasswordSetThe": "Temporary password set — the passenger must change it at next sign-in",
  "toast.resolveFailed": "Resolve failed",
  "toast.reopenFailed": "Reopen failed",
  "toast.autoresolveFailed": "Auto-resolve failed",
  "toast.refreshedOverflowData": "Refreshed overflow data",
  "toast.autoresolveConfigSaved": "Auto-resolve config saved",
  "toast.importCompleteListRefreshed": "Import complete — list refreshed",
  "toast.salaryRulesUpdated": "Salary rules updated",
  "toast.candidateRejected": "Candidate rejected",
  "toast.candidateRemoved": "Candidate removed",
  "toast.postingDeleted": "Posting deleted",
  "toast.documentDeleted": "Document deleted",
  "toast.licenseDeleted": "License deleted",
  "toast.idCardDeleted": "ID card deleted",
  "toast.recordDeleted": "Record deleted",
  "toast.leaveRequestCancelled": "Leave request cancelled",
  "toast.documentUploaded": "Document uploaded",
  "toast.licenseAdded": "License added",
  "toast.licenseUpdated": "License updated",
  "toast.idCardAdded": "ID card added",
  "toast.idCardUpdated": "ID card updated",
  "toast.leaveRequestSubmitted": "Leave request submitted",
  "toast.baseCompensationUpdated": "Base compensation updated",
  "toast.payeeUpdated": "Payee updated",
  "toast.recordAdded": "Record added",
  "toast.newRateSaved": "New rate saved",
  "toast.manualRateSaved": "Manual rate saved",
  "toast.decisionSaved": "Decision saved",
  "toast.provinceRateDeleted": "Province rate deleted",
  "toast.provinceRateAdded": "Province rate added",
  "toast.provinceRateUpdated": "Province rate updated",
  "toast.bookingCreated": "Booking created",
  "toast.bookingCancelled": "Booking cancelled",
  "toast.markedAsNoshow": "Marked as no-show",
  "toast.failedToSaveDocuments": "Failed to save documents",
  "toast.savedButSomeFiles": "Saved, but some files failed to upload",
  "toast.documentsSaved": "Documents saved",
  "toast.stopsSaved": "Stops saved",
  "toast.routeTemplateSaved": "Route template saved",
  "toast.routeAssignmentsSaved": "Route assignments saved",
  "toast.bookingUpdated": "Booking updated",
  "toast.exportIsNotEnabled": "Export is not enabled yet",
  "toast.financialsSaved": "Financials saved",
  "toast.chargeAdded": "Charge added",
  "toast.chargeDeleted": "Charge deleted",
  "toast.chargeUpdated": "Charge updated",
  "toast.settingsSaved": "Settings saved",
  "toast.passengerAdded": "Passenger added",
  "toast.bookingNotFound": "Booking not found",
  "toast.routeStopsSaved": "Route stops saved",
  "toast.perstopScheduleEditingIsnt": "Per-stop schedule editing isn't persisted yet",
  "toast.jobSaved": "Job saved",
  "toast.ticketCancelledCreateThe": "Ticket cancelled — create the replacement trip from the tickets page",
  "toast.couldntOpenTheFile": "Couldn't open the file — try again",
  "toast.profileImageUpdated": "Profile image updated",
  "toast.profileImageRemoved": "Profile image removed",
  "toast.pleaseSelectACharge": "Please select a charge type",
  "toast.pleaseEnterAValid": "Please enter a valid amount",
  "toast.unsupportedFileJpgPng": "Unsupported file — JPG, PNG, WebP, GIF",
  "toast.vehiclePhotoUpdated": "Vehicle photo updated",
  "toast.vehiclePhotoRemoved": "Vehicle photo removed",
  "fleet.noVehicle": "No vehicle",
  "ops.removeFromOrgNote": "The passenger loses access to this organization. Their account and booking history are kept — they can be re-invited later.",
  "ops.tempPasswordPending": "Temporary — change pending on next login",
  "ops.accountOwnedByPassenger": "Owned by the passenger — profile and password are managed in the passenger app.",
  "portal.accessLinkNote": "Members open this link in the passenger app and sign in to access the portal.",
  "warroom.totalUpper": "total",
  "warroom.freeUpper": "free",
  "warroom.jobsUpper": "jobs",
  "warroom.noJobsToday": "No jobs run today",
  "warroom.paletteHint": "Type at least 2 characters to search jobs · routes · vehicles · drivers",
  "warroom.noVehicleAssigned": "⚠ No vehicle assigned",
  "warroom.routeStopsLine": "Route {code} · {n} stops",
  "warroom.stopsPassed": "{done}/{total} stops passed",
  "warroom.showingOfMatched": "⚠ Showing {shown} of {total} — search, or pick a filter to see the rest",
  "hr.driverTeam": "Driver Team",
  "fleet.inspection": "Inspection",
  "safety.case": "Case",
  "common.submission": "Submission",
  "ops.teamAddedWarn": "Team {team}: added {added} vehicle(s). {missing} driver(s) have no bound vehicle:",
  "ops.driversNoVehicle": "{n} driver(s) have no bound vehicle:",
  "ops.nDriversShort": "{n} drivers",
  "ops.editableWhileDraft": "Editable only while the ticket is draft / assigned (before the trip starts)",
  "hr.noChargesForJob": "No operational charges for this job ·",
  "hr.addFirstCharge": "+ Add first charge",
  "hr.noOperationalCharges": "No operational charges",
  "common.byActor": "by {name}",
  "masterData.nItems": "{n} items",
  "masterData.lockedProtected": "· Locked (protected)",
  "masterData.dragToReorder": "· Drag rows to reorder",
  "masterData.cat.expense_category": "Expense Category",
  "masterData.cat.fuel_type": "Fuel Type",
  "masterData.cat.job_status": "Job Status",
  "masterData.cat.maintenance_type": "Maintenance Type",
  "masterData.cat.province": "Province",
  "masterData.cat.vehicle_type": "Vehicle Type",
  "masterData.cat.vehicle_id_prefix": "Vehicle ID Prefix",
  "masterData.cat.vehicle_status": "Vehicle Status",
  "masterData.cat.ownership_type": "Ownership Type",
  "masterData.cat.insurance_type": "Insurance Type",
  "masterData.cat.insurance_coverage_class": "Coverage Class",
  "masterData.cat.insurance_repair_type": "Repair Type",
  "masterData.cat.work_order_category": "Work Order Category",
  "masterData.cat.work_order_status": "Work Order Status",
  "masterData.cat.work_order_priority": "Work Order Priority",
  "masterData.cat.inspection_purpose": "Inspection Purpose",
  "masterData.cat.inspection_category": "Inspection Category",
  "masterData.cat.employee_type": "Employee Type",
  "masterData.cat.maintenance_report_status": "Maintenance Report Status",
  "masterData.cat.employee_status": "Employee Status",
  "masterData.cat.gps_billing_type": "GPS Billing Type",
  "masterData.cat.department": "Department",
  "masterData.cat.tax_type": "Tax Type",
  "masterData.cat.branch": "Branch",
  "masterData.cat.finance_type": "Finance Type",
  "masterData.cat.document_type": "Document Type",
  "masterData.cat.doc_verification_status": "Document Verification Status",
  "masterData.cat.salary_pay_type": "Salary Pay Type",
  "masterData.cat.payment_method": "Payment Method",
  "masterData.cat.behavior_record_type": "Behavior Record Type",
  "masterData.cat.leave_type": "Leave Type",
  "masterData.cat.leave_request_status": "Leave Request Status",
  "masterData.cat.recruitment_stage": "Recruitment Stage",
  "masterData.cat.job_posting_status": "Job Posting Status",
  "masterData.cat.recruitment_source": "Recruitment Source",
  "masterData.cat.behavior_category": "Behavior Category",
  "masterData.cat.employment_type": "Employment Type",
  "masterData.cat.offer_status": "Offer Status",
  "masterData.cat.charge_direction": "Charge Direction",
  "masterData.cat.compensation_type": "Compensation Type",
  "masterData.cat.rate_tier": "Rate Tier",
  "masterData.cat.overflow_action": "Overflow Action",
  "masterData.cat.stop_checkin_method": "Stop Check-in Method",
  "masterData.cat.route_status": "Route Status",
  "masterData.cat.booking_mode": "Booking Mode",
  "masterData.cat.booking_status": "Booking Status",
  "masterData.cat.booking_checkin_method": "Booking Check-in Method",
  "masterData.cat.passenger_status": "Passenger Status",
  "masterData.cat.passenger_type": "Passenger Type",
  "masterData.cat.pay_ticket_status": "Pay Ticket Status",
  "masterData.cat.contract_status": "Contract Status",
  "masterData.cat.deposit_status": "Deposit Status",
  "masterData.cat.contract_service_type": "Contract Service Type",
  "masterData.cat.job_type": "Job Type",
  "masterData.cat.safety_case_category": "Safety Case Category",
  "masterData.cat.safety_case_status": "Safety Case Status",
  "masterData.cat.severity_level": "Severity Level",
  "masterData.cat.announcement_audience": "Announcement Audience",
  "masterData.cat.announcement_priority": "Announcement Priority",
  "masterData.cat.announcement_status": "Announcement Status",
  "masterData.cat.alert_type": "Alert Type",
  "masterData.cat.alert_severity": "Alert Severity",
  "masterData.cat.alert_status": "Alert Status",
  "masterData.cat.feedback_source": "Feedback Source",
  "masterData.cat.feedback_category": "Feedback Category",
  "masterData.cat.feedback_status": "Feedback Status",
  "masterData.cat.contract_document_type": "Contract Document Type",
  "masterData.defaultColon": "default:",
  "fuel.sourceDriver": "driver",
  "fuel.gaugeBefore": "Gauge (before)",
  "fuel.pumpMeter": "Pump meter",
  "fuel.gaugeAfter": "Gauge (after)",
  "fuel.receiptSlip": "Receipt / slip",
  "fuel.approvedCostBaht": "Approved cost (฿)",
  "fuel.changeLaterLogged": "You can change this later — every change is logged.",
  "fuel.currentlyStatusLogged": "Currently {status}. Changing it is logged.",
  "fuel.perLiter": "฿ / liter",
  "settings.openMembers": "Open Members →",
  "fleet.platePlaceholder": "e.g. กก 1234",
  "ops.jobTypeGeneral": "General",
  "ops.jobTypeOutbound": "Inbound to plant",
  "ops.jobTypeReturn": "Outbound from plant",
  "ops.jobTypeCharter": "Charter",
  "ops.jobTypeDistance": "Distance-based",
  "fleet.kmplSpecIs": "Spec value on file: {n} km/L",
  "fleet.fuelEffShort": "Fuel Eff.",
  "portal.footerBrand": "DeepTrack TMS · Customer Portal",
  "roles.nDefined": "{n} roles defined",
  "settings.nWorkflows": "{n} workflow definitions",
  "common.allCount": "All ({n})",
  "common.activeCount": "Active ({n})",
  "common.inactiveCount": "Inactive ({n})",
  "status.submittedCount": "Submitted ({n})",
  "status.closedCount": "Closed ({n})",
  "common.template": "Template",
  "fleet.nVehiclesTotal": "{n} vehicles total",
  "fleet.fleetSize": "fleet size",
  "fleet.nPoliciesAcrossFleet": "{n} policies across fleet",
  "fleet.nWorkOrdersTotal": "{n} work orders total",
  "settings.removePrefix": "Remove {prefix}",
  "settings.phase2Formulas": "Phase 2 -- planned feature. Configurable payroll formula engine with version control.",
  "settings.phase2Workflow": "Phase 2 -- planned feature. Visual workflow builder with state machine engine.",
  "settings.nFormulaSets": "{n} formula sets",
  "audit.actionAssignBulk": "Assign (bulk)",
  "hr.driverTeamsTotal": "{n} driver teams total",
  "hr.leaveRequestsCount": "{n} leave requests",
  "ops.bookingTrend30": "30-Day Booking Trend",
  "ops.nJobTickets": "{n} job ticket(s)",
  "ops.utilizationColon": "· Utilization:",
  "fleet.nScheduledServices": "{n} scheduled services",
  "fleet.overdueCount": "Overdue ({n})",
  "fleet.dueSoonCount": "Due Soon ({n})",
  "fleet.okCount": "OK ({n})",
  "fleet.pmOpenCount": "Outstanding ({n})",
  "fleet.pmClosedCount": "Closed ({n})",
  "fleet.gridRowsCols": "{rows} rows x {cols} cols",
  "fuel.standard": "Standard",
  "import.previewDryRun": "Preview (dry-run)",
  "import.unsupportedFile": "Unsupported file — export to CSV first (.csv or .txt)",
  "import.encodingUnreadable": "This file could not be read as text. Re-save it from Excel as \u201cCSV UTF-8\u201d and try again.",
  "import.encodingConverted": "Read as {encoding} and converted to Unicode — check the Thai text in the preview below before importing.",
  "import.readFailed": "Could not read the file",
  "import.uploadFirst": "Upload a CSV first",
  "hr.importAdminOnly": "Only an organisation admin can import employees.",
  "fleet.importAdminOnly": "Only an organisation admin can import vehicles.",
  "import.previewFirst": "Run the preview first",
  "import.noValidRows": "No valid rows to import",
  "import.dryRunFailed": "Preview failed",
  "import.importFailed": "Import failed",
  "import.importedRows": "Imported {n} rows",
  "import.importedSkipped": "Imported {n}, skipped {skipped} — see below",
  "import.finishedPartial": "Import finished — {n} imported, {skipped} skipped",
  "import.finishedClean": "Import complete — {n} rows imported",
  "import.skippedRowsTitle": "Skipped rows:",
  "import.errorRowsTitle": "Errors (these rows will be skipped):",
  "hr.expiringExpiredCount": "Expiring / Expired ({n})",
  "settings.usernamePrefixHelp": "A username is prefix + separator + the person's name — e.g. prefix ps with separator - gives ps-somchai; separator none gives pssomchai. Each prefix must be unique across the whole system — if it's taken you'll be asked to choose another.",
  "settings.withSeparator": "with separator",
  "settings.entityJobTicket": "Job Ticket",
  "settings.entityRequest": "Request",
  "settings.evTripLate": "Trip Late",
  "settings.evSos": "SOS Alert",
  "settings.evGpsLost": "GPS Signal Lost",
  "settings.evFuelAnomaly": "Fuel Anomaly",
  "settings.evOverbooking": "Overbooking Detected",
  "settings.evSchedulePublished": "Schedule Published",
  "settings.evMaintenanceDue": "Maintenance Due",
  "settings.featNlInput": "Natural language input for route planning",
  "settings.lateThreshold": "Late Threshold",
  "settings.lateThresholdDesc": "Grace period after a job's planned end before the War Room flags it as late. New jobs inherit this.",
  "settings.acceptOpenBefore": "Accept opens before the accept deadline (min)",
  "settings.acceptOpenBeforeDesc": "How many minutes before the ticket's accept deadline a driver can accept the job (check-in style — the counter opens before it closes, not before departure). A ticket with no accept deadline can be accepted at any time. New jobs inherit this; each ticket can override.",
  "settings.cancelCutoffBefore": "Cancel closes before start (min)",
  "settings.cancelCutoffBeforeDesc": "The driver's cancel button disappears when less than this remains before start. New jobs inherit this; each ticket can override.",
  "settings.fuelStandard": "Fuel Standard (km/L)",
  "settings.fuelStandardDesc": "Below this triggers a fuel anomaly alert.",
  "settings.fuelStationRadius": "Fuel station radius (m)",
  "settings.fuelStationRadiusDesc": "How close a fuel entry must be to count as that station — shared by every station",
  "settings.idleTimeout": "Idle Timeout",
  "settings.idleTimeoutDesc": "Minutes before a vehicle is marked idle.",
  "settings.speedLimit": "Speed Limit",
  "settings.speedLimitDesc": "km/h. Pre-filled into every new stop, and also the speed limit for GPS box data in the War Room.",
  // ADR 0224
  "settings.checkinRadius": "Check-in Radius",
  "settings.checkinRadiusDesc": "metres — pre-filled into every new stop, and used for older stops that never set one.",
  "settings.pinSamePlace": "Same-place distance",
  "settings.pinSamePlaceDesc": "metres — pins this close reuse the existing pin; farther but inside the check-in radius, the add-cargo dialog asks first (0 = exact match only).",
  "settings.pinSamePlaceOver": "Larger than the check-in radius — every pin within reach is reused without asking.",
  "settings.sosSla": "SOS Response SLA",
  "settings.sosSlaDesc": "Minutes to acknowledge an SOS alert.",
  "settings.cutoffTime": "Cutoff Time (hours)",
  "settings.cutoffTimeDesc": "Hours before departure to close booking",
  "settings.advanceDays": "Advance Days",
  "settings.advanceDaysDesc": "How many days in advance passengers can book",
  "settings.seatHold": "Seat Hold (minutes)",
  "settings.seatHoldDesc": "Hold duration for unpaid bookings",
  "settings.maxOverbooking": "Max Overbooking %",
  "settings.maxOverbookingDesc": "Allowed overbooking percentage before alert",
  "settings.allowOverbooking": "Allow Overbooking",
  "settings.allowOverbookingDesc": "Enable overbooking beyond vehicle capacity",
  "settings.walkInBooking": "Walk-in Booking",
  "settings.walkInBookingDesc": "Allow admin to add walk-in passengers",
  "settings.qrRequired": "QR Code Required",
  "settings.qrRequiredDesc": "Require QR scan for boarding confirmation",
  "hr.employeesTotal": "{n} employees total",
  "hr.hireDateFuture": "Hire date is in the future — confirm this",
  "roles.member": "Member",
  "roles.driver": "Driver",
  "common.password": "Password",
  "hr.leaveBlankKeep": "(leave blank to keep current)",
  "settings.gpsProvider": "GPS Provider",
  "settings.gpsProviderDesc": "Default GPS provider for new vehicles",
  "settings.googleMapsKey": "Google Maps API Key",
  "settings.googleMapsKeyDesc": "Used for route planning and live tracking",
  "settings.geminiKey": "Gemini API Key",
  "settings.geminiKeyDesc": "Used for AI route suggestions and NL input parsing",
  "settings.featRouteOpt": "Route Optimization",
  "settings.featRouteOptDesc": "AI-powered route planning with OR-Tools",
  "settings.featOverbooking": "Overbooking Management",
  "settings.featOverbookingDesc": "Automatic overflow detection and AI suggestions",
  "settings.featLiveTracking": "Live Tracking",
  "settings.featLiveTrackingDesc": "Real-time GPS vehicle tracking on map",
  "settings.featCommsHub": "Communication Hub",
  "settings.featCommsHubDesc": "In-app chat, announcements, and request system",
  "settings.featPortal": "Portal Access",
  "settings.featPortalDesc": "Allow customers to view their contract data",
  "settings.featDriverAppDesc": "Mobile app for drivers (WebView)",
  "settings.featPassengerAppDesc": "Mobile app for passenger booking (WebView)",
  "common.noneOption": "— None —",
  "hr.noVehicleOption": "— No vehicle —",
  "hr.changeSalaryNote": "Change salary from the Money Management tab (keeps effective-dated history).",
  "hr.oneVehicleNote": "One vehicle per driver — assigning a vehicle already held by someone else moves it here.",
  "hr.contractRateNote": "Beats the driver’s default rate · read-only here — manage in",
  "hr.noContractRatesYet": "No contract rates yet — jobs fall back to route bonus / driver default / route standard rate",
  "ops.vehiclesCount": "Vehicles ({n})",
  "ops.startTimeBangkok": "Start time · Asia/Bangkok",
  "common.secUnit": "sec",
  "ops.minPerStop": "≈ {n} min per stop",
  "ops.stopsPerVehicle": "≈ {per} stops/vehicle ({stops} stops ÷ {veh} vehicles)",
  "ops.autoOnlyTwoPlus": "Auto only applies when 2+ vehicles are selected",
  "ops.runsHistoryHint": "History of all optimization runs — click a template to view details",
  "ops.nRuns": "{n} run(s)",
  "common.thai": "Thai",
  "common.english": "English",
  "common.auto": "Auto",
  "ops.selectedUpper": "Selected",
  "ops.availableUpper": "Available",
  "common.lat": "Lat",
  "common.lng": "Lng",
  "hr.noRouteRateSetInHr": "No route rate — set the driver's rate in HR › Money Management",
  "hr.moneyManagementPath": "HR › Employee › Money Management",
  "hr.noRouteRatesYet": "No personal route rate yet — the next step is used",
  "hr.noRouteRateSetManually": "No route rate — set manually",
  "hr.noDriverAssigned": "No driver assigned",
  "common.endOfList": "{n} loaded · end of list",
  "common.searchPlaceholder": "Search...",
  "common.typeToSearch": "Type to search…",
  "common.noResultsFor": "No results for \"{q}\"",
  "common.topMatches": "Top {n} matches • Type to search all",
  "common.nOfTotalTypeToSearch": "{n} of {total} • Type to search",
  "common.loadedScrollMore": "{n} loaded · scroll for more · type to narrow",
  "common.noDocumentsAttach": "No documents — click \"Add Files\" to attach (documents & images, ≤{mb}MB)",
  "common.collapseSidebar": "Collapse sidebar",
  "common.expandSidebar": "Expand sidebar",
  "common.collapse": "Collapse",
  "common.expand": "Expand",
  "portal.demoFigures": "Showing demo figures for",
  "portal.liveSoon": "Live aggregates coming soon.",
  "ops.totalJobTickets": "Total Job Tickets",
  "status.late": "Late",
  "ops.limited": "Limited",
  "ops.unlimited": "Unlimited",
  "ops.loadBookingsFailed": "Failed to load bookings",
  "ops.tripNotFound": "Trip not found",
  "ops.readOnlySuffix": " (read-only)",
  "ops.boardFreely": "Board freely, no reservation.",
  "ops.mustBookCapped": "Must book. Seats are capped.",
  "ops.mustBookExtra": "Must book. Extra seats allowed.",
  "ops.bookingLimitedSeats": "Booking · Limited seats",
  "ops.bookingUnlimitedSeats": "Booking · Unlimited seats",
  "common.restore": "Restore",
  "copilot.welcome": "Hi! I'm DeepTrack Copilot, here to help with the TMS. Try asking:\n\n• Today's status summary\n• Which drivers are free\n• Documents expiring soon\n• How to handle overflow",
  "copilot.error": "Sorry — could not process that right now. Please try again.",
  "copilot.placeholder": "Ask Copilot anything… (e.g. today's summary, free drivers)",
  "copilot.cmdSummaryToday": "Summarize today's status",
  "copilot.cmdFreeDrivers": "Which drivers are free?",
  "copilot.cmdExpiringDocs": "Which documents are expiring soon?",
  "copilot.cmdFleetStatus": "What is the status of all vehicles?",
  "copilot.cmdFuelToday": "Summarize today's fuel",
  "copilot.cmdScheduleTomorrow": "What is on tomorrow's schedule?",
  "feedback.commentPlaceholder": "Tell us how your experience was…",
  "feedback.emptyTitle": "No feedback yet",
  "feedback.emptyBody": "Feedback sent from the passenger app will appear here",
  "reports.templateSaved": "Report template saved",
  "reports.editSaved": "Changes saved",
  "reports.deleteTemplateQ": "Delete template \"{name}\"?",
  "common.deleted": "Deleted",
  "reports.customIntro": "Build a custom report — pick a source, filters, group-by, then run",
  "reports.noTemplates": "No templates yet",
  "reports.pressPlusToCreate": "Press + to create one",
  "reports.pickTemplateTitle": "Pick a template on the left, or create a new one",
  "reports.pickTemplateBody": "Custom reports let you compose the query yourself — pick a source, filter, group-by, see results",
  "reports.descPlaceholder": "Describe this report briefly",
  "reports.noRows": "No data matches the criteria",
  "integrations.noApiKeyTitle": "No API keys yet",
  "integrations.noApiKeyBody": "Create a key so external systems (Zapier, n8n, other integrations) can read TMS data or call webhooks",
  "reports.r1": "Trip Summary",
  "reports.r2": "Route Performance",
  "reports.r3": "Driver Performance",
  "reports.r4": "Vehicle Status",
  "reports.r5": "Maintenance Log",
  "reports.r6": "Insurance Expiry",
  "reports.r7": "Booking Summary",
  "reports.r8": "Overflow Report",
  "reports.r9": "Fuel Consumption",
  "reports.r10": "Revenue & Cost",
  "reports.r11": "Contract Billing",
  "audit.timestamp": "Timestamp",
  "audit.summary": "Summary",
  "audit.entityId": "Entity ID",
  "audit.moreRowsExport": "+{n} more rows — Export .xlsx for the full list",
  "hr.safetyChip": "Safety {score}/100",
  "safety.stepDeptReview": "Dept Review",
  "safety.stepSentToHr": "Sent to HR",
  "safety.stepHrReview": "HR Review & Classify",
  "safety.stepWarningIssued": "Warning Issued",
  "safety.stepESign": "E-Sign / Acknowledge",
  "safety.stepClosed": "Closed",
  "common.system": "System",
  "masterData.deleteCategoryQ": "Delete category?",
  "masterData.deleteItemQ": "Delete item?",
  "masterData.deleteCategoryNote": "\"{name}\" and all its items will be deactivated.",
  "masterData.editCategory": "Edit Category",
  "masterData.protectedCatEdit": "Protected category — cannot edit",
  "masterData.protectedCatDelete": "Protected category — cannot delete",
  "masterData.addNewItem": "Add New Item",
  "masterData.editItem": "Edit \"{name}\"",
  "masterData.usedIn": "Used in:",
  "masterData.protectedItemEdit": "Protected item — cannot edit",
  "masterData.protectedItemDelete": "Protected item — cannot delete",
  "masterData.protectedItemLocked": "Protected item — locked",
  "masterData.defaultItem": "Default item",
  "masterData.setAsDefault": "Set as default",
  "masterData.groupFleet": "Fleet",
  "masterData.groupHr": "HR & People",
  "masterData.groupRecruitment": "Recruitment",
  "masterData.groupJobs": "Jobs & Dispatch",
  "masterData.groupRoutes": "Routes & Booking",
  "masterData.groupFinance": "Finance & Contracts",
  "masterData.groupSafety": "Safety",
  "masterData.groupComms": "Comms & Feedback",
  "masterData.groupShared": "Shared",
  "audit.actionCreate": "Create",
  "audit.actionUpdate": "Update",
  "audit.actionDelete": "Delete",
  "audit.actionStatusChange": "Status Change",
  "audit.actionVoid": "Void",
  "audit.fin.bill_move": "Bill Move",
  "audit.fin.bill_return": "Bill Return",
  "audit.fin.pay_batch_create": "Batch Created",
  "audit.fin.pay_batch_add": "Bills Added",
  "audit.fin.pay_batch_remove": "Bills Removed",
  "audit.fin.pay_batch_freeze": "Batch Frozen",
  "audit.fin.pay_batch_unfreeze": "Batch Sent Back",
  "audit.fin.pay_batch_approve": "Batch Approved",
  "audit.fin.pay_batch_export": "Payout File",
  "audit.fin.pay_batch_export_accounting": "Accounting File",
  "audit.fin.pay_batch_paid": "Batch Paid",
  "audit.fin.pay_batch_delete": "Batch Deleted",
  "audit.fin.payout_settings_update": "Payout Settings",
  "audit.fin.payee_update": "Bank Details",
  "audit.fin.finance_item_create": "Added a money item",
  "audit.fin.finance_item_update": "Changed a money item",
  "audit.fin.finance_item_delete": "Deleted a money item",
  "audit.fin.finance_config_set": "Set a finance value",
  "audit.fin.finance_config_cancel": "Cancelled a coming value",
  "audit.fin.finance_txn_create": "Recorded a money row",
  "audit.fin.finance_txn_edit": "Edited a money row",
  "audit.fin.finance_txn_relink": "Moved a row's linked document",
  "audit.fin.finance_txn_reverse": "Reversed a money row",
  "audit.fin.finance_txn_void": "Voided a money row",
  "audit.fin.finance_txn_status": "Changed a row's status",
  "audit.fin.person_finance_update": "Changed a person's finance details",
  "hr.unlimited": "Unlimited",
  "hr.ofDays": "of {n} days",
  "feedback.submit": "Submit Feedback",
  "feedback.rateStars": "Rate {n} star(s)",
  "feedback.catSafety": "Safety",
  "analytics.periodLast30": "Period: Last 30 days",
  "common.rolesColon": "Roles:",
  "safety.showingCases": "Showing {n} of {total} cases",
  "handover.showingRecords": "Showing {n} of {total} records",
  "hr.leaveAnnual": "Annual leave",
  "hr.leaveSick": "Sick leave",
  "hr.leavePersonal": "Personal leave",
  "hr.leaveMaternity": "Maternity leave",
  "hr.leavePaternity": "Paternity leave",
  "hr.leaveUnpaid": "Unpaid leave",
  "hr.leaveOther": "Other",
  // ── leave: CSV export column headers ──
  "hr.employeeCode": "Employee ID",
  "hr.leaveType": "Leave type",
  "hr.leaveFirstDayHalf": "First day half",
  "hr.leaveLastDayHalf": "Last day half",
  "hr.leaveHalfMorning": "Morning half",
  "hr.leaveHalfAfternoon": "Afternoon half",
  "hr.leaveFullDay": "Full day",
  "hr.leaveSubmittedAt": "Submitted",
  "hr.leaveDecidedAt": "Decided",
  "hr.leaveDecisionNotes": "Decision notes",
  "hr.leaveAttachmentCount": "Attachments",
  "hr.catGeneral": "General",
  "hr.presetContractors": "External Drivers",
  "common.issueDate": "Issue Date",
  "hr.idNumber": "ID Number",
  "hr.issuedBy": "Issued By",
  "common.image": "Image",
  "hr.usedDays": "Used: {n} day(s)",
  "hr.sensitiveMasked": "Sensitive numbers are masked. Open Edit to view/change full values.",
  "common.opening": "Opening…",
  "import.vehicleRequired": "plate and display_id are both required (ADR 0138 — vehicle codes are no longer generated).",
  "hr.scoreWorking": "Working",
  "hr.scoreSafety": "Safety",
  "hr.scoreAdmin": "Admin",
  "hr.hired": "Hired",
  "hr.idCard": "ID Card",
  "hr.docOther": "Other",
  "hr.tripRate": "Trip Rate",
  "common.ticket": "Ticket",
  "hr.change": "Change",
  "hr.changedAt": "Changed At",
  "hr.houseRegistration": "House Registration",
  "hr.driverLicense": "Driver License",
  "hr.medicalCertificate": "Medical Certificate",
  "hr.newAccount": "New Account",
  "hr.newAccountBody": "A new OAuth account will be created — user must set a password on first login",
  "hr.oauthUserFound": "OAuth User Found",
  "hr.oauthFoundMultiOrg": "OAuth user found — this user already belongs to multiple orgs",
  "hr.oauthFoundFill": "OAuth user found — fill in details and click Add Employee",
  "hr.foundName": "Found: {name}",
  "hr.alreadyInSystem": "Already in system",
  "hr.checkFailed": "Check failed",
  "hr.unableCheckEmail": "Unable to check email — please try again",
  "hr.checkingAvailability": "Checking availability…",
  "hr.usernameTaken": "This username is taken — choose another",
  "hr.selectValidPrefix": "Select a valid prefix",
  "hr.restoreEmployee": "Restore Employee",
  "hr.createEmployee": "Create Employee",
  "hr.editEmployee": "Edit Employee",
  "hr.nextAvailable": "Next available:",
  "hr.setNewPassword": "Set a new password",
  "hr.setPasswordForEmployee": "Set a password for this employee",
  "common.allGender": "All Gender",
  "common.male": "Male",
  "common.female": "Female",
  "hr.documentVerified": "Document verified",
  "hr.documentRejected": "Document rejected",
  "hr.leaveApproved": "Leave approved",
  "hr.leaveRejected": "Leave rejected",
  "hr.employeeReactivated": "Employee reactivated",
  "hr.employeeDeactivated": "Employee deactivated",
  "hr.allowancesUpdated": "Allowances updated",
  "hr.deductionsUpdated": "Deductions updated",
  "hr.verifyDocumentQ": "Verify Document?",
  "hr.rejectDocumentQ": "Reject Document?",
  "hr.approveLeaveQ": "Approve Leave?",
  "hr.rejectLeaveQ": "Reject Leave?",
  "hr.rateVoided": "Rate voided",
  "hr.voidedRepriced": "Voided — {n} ticket(s) re-priced",
  "hr.tripRateDriverDefault": "Personal default rate",
  "hr.tripRateRoute": "Personal route rate",
  "hr.tripRateContract": "Personal contract rate",
  "hr.driverDefaultRateHint": "This driver's own per-trip rate for any trip without a more specific rate — append-only ledger, history is never lost",
  "hr.newRate": "New Rate",
  "hr.newRouteRate": "New Route Rate",
  "hr.newContractRate": "New Contract Rate",
  "hr.notPayableYet": "Not payable yet — net counts only payable trips",
  "hr.tripRatePlusCharges": "Trip rate ± operational charges",
  "hr.setManualRate": "Set a manual rate for this ticket",
  "common.enabled": "Enabled",
  "hr.leaveBalanceYear": "Leave Balance ({year})",
  "hr.bankTransfer": "Bank Transfer",
  "hr.addPayee": "Add Payee",
  "hr.belowFloorThreshold": "Below floor threshold ({n}/100) — review required",
  "hr.editDriverTeam": "Edit Driver Team",
  "hr.newDriverTeam": "New Driver Team",
  "hr.createTeam": "Create Team",
  "hr.showingTeams": "Showing {n} of {total} driver teams",
  "common.exportXlsx": "Export .xlsx",
  "hr.driverGroup": "Driver group",
  "hr.leaveLegend": "Solid = approved · faded = pending",
  "hr.documents": "Documents",
  "hr.document": "Document",
  "hr.presetActiveDrivers": "Active Drivers",
  "hr.presetTrialPeriod": "Trial Period",
  "hr.presetLicenseExpiring": "License Expiring",
  "common.maxSize": "max {mb} MB",
  "fleet.owner": "Owner",
  "fleet.odometer": "Odometer",
  "fleet.dueCompleted": "Due / Completed",
  "fleet.woMaintenance": "Maintenance",
  "fleet.woRepair": "Repair",
  "fleet.woInspection": "Inspection",
  "fleet.woFuel": "Fuel",
  "common.importRows": "Import {rows}",
  "common.nRows": "{n} rows",
  "import.hint": "Upload CSV — preview and fix errors before committing.",
  "import.downloadSample": "Download sample CSV",
  "import.clickToSelect": "Click to select a CSV (or drag and drop)",
  "fleet.uploadFailed": "Upload failed",
  "common.uploading": "Uploading…",
  "fleet.removePhotoConfirm": "Remove the {label} photo? This cannot be undone.",
  "fleet.saveGpsContract": "Save GPS Contract",
  "fleet.saveTaxRecord": "Save Tax Record",
  "fleet.saveFinanceRecord": "Save Finance Record",
  "fleet.roadTax": "Road Tax",
  "common.play": "Play",
  "common.pause": "Pause",
  "warroom.playbackDate": "Date:",
  "warroom.playbackVehicle": "Vehicle:",
  "warroom.noGpsToday": "No GPS data for today",
  "warroom.fromVehiclesWithJobs": "from vehicles with jobs",
  "warroom.points": "points",
  "fleet.presetActiveVansBkk": "Active Vans (BKK)",
  "fleet.presetNoGps": "No GPS",
  "fleet.editVehicle": "Edit Vehicle",
  "fleet.createVehicle": "Create Vehicle",
  "fleet.addPartAfterPrefix": "Add the part after {prefix}",
  "common.expectedHeader": "Expected header:",
  "fleet.rented": "Rented",
  "fleet.editGpsContract": "Edit GPS Contract",
  "fleet.addGpsContract": "Add GPS Contract",
  "fleet.saveContract": "Save Contract",
  "common.allProviders": "All Providers",
  "fleet.expiredDaysAgo": "Expired {n}d ago",
  "fleet.overdueDaysAgo": "{n} days overdue",
  "fleet.daysRemaining": "{n}d remaining",
  "fleet.editInsurancePolicy": "Edit Insurance Policy",
  "fleet.addInsurancePolicy": "Add Insurance Policy",
  "fleet.addCmiPolicy": "Add CMI policy",
  "fleet.editCmiPolicy": "Edit CMI policy",
  "fleet.savePolicy": "Save Policy",
  "fleet.createWorkOrder": "Create Work Order",
  "fleet.editPmSchedule": "Edit PM schedule",
  "fleet.saveSchedule": "Save Schedule",
  "fleet.apiSeats": "API Seats: {n}",
  "common.savedBang": "Saved!",
  "fleet.createTemplate": "Create Template",
  "common.enterValue": "Enter value",
  "fleet.submitInspection": "Submit Inspection",
  "common.submitting": "Submitting…",
  "common.closing": "Closing…",
  "fleet.noTemplatesYet": "No templates yet. Create one to get started.",
  "fleet.noTemplatesMatch": "No templates match this filter.",
  "fleet.noSubmissionsYet": "No inspections submitted yet.",
  "fleet.noSubmissionsMatch": "No submissions match this filter.",
  "fleet.searchTemplates": "Search template name, description, group…",
  "fleet.searchSubmissions": "Search template, vehicle, inspector…",
  "fleet.submittedAt": "Submitted at",
  "fleet.closedAt": "Closed at",
  "hr.provinceRatesSubtitle": "{n} rates · per-job compensation by destination province",
  "hr.noProvinceRatesMatch": "No province rates match your search",
  "hr.noProvinceRatesYet": "No province rates yet — add one to set the per-job compensation for a destination province",
  "fleet.createActiveTemplateFirst": "Create an active template first",
  "fleet.showingTemplates": "Showing {n} of {total} template(s)",
  "fleet.showingSubmissions": "Showing {n} of {total} submission(s)",
  "comms.mockBanner": "API endpoints are under development. Showing mock data for preview.",
  "comms.selectChat": "Select a chat",
  "status.unassigned": "Unassigned",
  "common.noChanges": "No changes",
  "common.comingSoon": "Coming soon",
  "common.on": "On",
  "common.off": "Off",
  "common.unpin": "Unpin",
  "common.pinToTop": "Pin to top",
  "warroom.latePlus": "Late +{min}",
  "warroom.couldNotLoadOverview": "Could not load overview: {error}",
  "warroom.cancelTicketConfirm": "Cancel ticket {id}?",
  "warroom.confirmCancel": "Confirm cancel",
  "warroom.openDetails": "Open details",
  "warroom.viewRoute": "View route",
  "warroom.zoomToVehicle": "Zoom to vehicle",
  "warroom.viewProfile": "View profile",
  "warroom.jobs": "Jobs",
  "warroom.routes": "Routes",
  "warroom.drivers": "Drivers",
  "warroom.pausePlayback": "Pause playback",
  "warroom.playPlayback": "Play playback",
  "warroom.stateOnTime": "On time",
  "warroom.stateLate": "Late",
  "warroom.stateCritical": "Critical",
  "warroom.stateNoGps": "No GPS",
  "warroom.stateFree": "Free",
  "warroom.live": "Live",
  "ops.confirmBy": "Confirm by",
  "ops.lateThreshold": "Late threshold",
  "ops.seats": "Seats",
  "comms.featureComingSoon": "{feature} — Coming soon",
  "comms.readBy": "Read by: {names}",
  "comms.newAnnouncement": "New Announcement",
  "comms.enterTitle": "Please enter an announcement title",
  "comms.draftSaved": "Draft saved",
  "comms.errorRetry": "Something went wrong, please try again",
  "comms.published": "Announcement published",
  "common.joined": "Joined",
  "ops.bookedAt": "Booked At",
  "portal.noContractsMatch": "No contracts match your filters.",
  "portal.noContractsLinked": "No contracts linked to this portal.",
  "ops.contractId": "Contract ID",
  "ops.customer": "Customer",
  "common.linked": "Linked",
  "common.addedBy": "Added by",
  "common.added": "Added",
  "portal.noMembersMatch": "No members match your filters.",
  "portal.noMembersYet": "No members in this portal yet.",
  "ops.autoResolve": "Auto-Resolve",
  "common.activate": "Activate",
  "ops.noPendingOverflow": "No pending overflow",
  "ops.noResolvedItems": "No resolved items",
  "common.deselectAll": "Deselect All",
  "ops.resolveDetourTo": "Resolve: Detour to {id}",
  "ops.resolveDetourToMove": "Resolve: Detour to {id} (move {n})",
  "ops.routePreview": "Route Preview: {title}",
  "ops.canDetourCount": "Can Detour ({n})",
  "ops.autoResolveAll": "Auto-Resolve All ({n})",
  "ops.projectedSavings": "Projected savings: {amount}/month",
  "ops.detourFirst": "Detour First",
  "ops.newVehicleFirst": "New Vehicle First",
  "ops.detourFirstDesc": "Try rerouting existing vehicles before dispatching new ones",
  "ops.newVehicleFirstDesc": "Dispatch new vehicles as priority, fall back to detour",
  "ops.detourModeExistingDesc": "Passengers walk to it",
  "ops.detourModeOriginalDesc": "Vehicle diverts to the original stop",
  "ops.detourModeCustomDesc": "Choose a meeting point",
  "ops.detourModeExisting": "Use an existing stop",
  "ops.detourModeOriginal": "Detour to the original stop",
  "ops.detourModeCustom": "Custom pin",
  "status.available": "Available",
  "ops.passengerAddedLinked": "Passenger added — linked to their existing account",
  "ops.suspendPassengerConfirm": "Suspend this passenger? They will not be able to book or check in.",
  "ops.passengerSuspended": "Passenger suspended",
  "ops.passengerReactivated": "Passenger reactivated",
  "ops.setByPassenger": "Set by passenger",
  "ops.notAvailableMultiOrg": "Not available — this account belongs to {n} organizations",
  "portal.activated": "Portal activated",
  "portal.deactivated": "Portal deactivated",
  "portal.activateConfirm": "Activate this portal?",
  "portal.deactivateConfirm": "Deactivate this portal?",
  "portal.activateNote": "The portal becomes active. (Once the passenger app ships, active portals are visible to their members.)",
  "portal.deactivateNote": "The portal is turned off. It stays in your list but will not be served to the passenger app.",
  "portal.deleteNote": "Unlinks {contracts} contract(s) and removes {members} member(s). The contracts and passengers themselves are kept.",
  "portal.deleteConfirmNote": "This unlinks {contracts} contract(s) and removes {members} member(s). This cannot be undone.",
  "ops.driverPassedPoint": "The driver has already passed this point",
  "hr.deduct": "Deduct",
  "hr.ticketNet": "Ticket net",
  "hr.pendingNotCounted": "{n} awaiting approval — not counted",
  "hr.setOnFinanceCard": "Set on the person finance card",
  "hr.payBasisSsoElsewhere": "Pay basis and social security are set in one place —",
  "opening.title": "Opening balances",
  "opening.lead": "Used once, when you start paying through the system — what was paid the old way must not be paid again, and debts still being paid back start from the right balance",
  "opening.chip": "Carried over",
  "opening.cut.title": "1 · Close what came before the start day",
  "opening.cut.day": "Paying through the system from {day}",
  "opening.cut.none": "Nothing before {day} is waiting to be paid",
  "opening.cut.found": "Before that day {n} rows of {people} people are waiting to be paid ({from} – {to})",
  "opening.cut.out": "The company pays",
  "opening.cut.in": "The company takes back",
  "opening.cut.kindLine": "{n} rows · pay {out} · take {in}",
  "opening.cut.after": "All of them become \"Done\" (paid the old way) and reach no bill — rows from {day} go to bills as usual",
  "opening.cut.evidence": "Evidence",
  "opening.cut.evidenceHint": "e.g. the old payroll summary of the last round — kept on every row closed",
  "opening.cut.note": "Note",
  "opening.cut.notePh": "e.g. paid with Excel up to 30 Sep",
  "opening.cut.go": "Close {n} rows as \"Done\"",
  "opening.cut.done": "{code} done — {n} rows closed",
  "opening.cut.failed": "Could not close the history",
  "opening.cut.made": "Done so far",
  "opening.cut.rowLine": "before {day} · {n} rows · {people} people",
  "opening.cut.undone": "undone {n} rows ({reason})",
  "opening.confirm.title": "Close what came before the start day?",
  "opening.confirm.body": "{n} rows of {people} people before {day} (pay {out} · take {in}) become \"Done\" and reach no bill. You can undo the whole batch while none of them is in a pay round",
  "opening.confirm.go": "Close",
  "opening.undo.button": "Undo",
  "opening.undo.title": "Undo {code}",
  "opening.undo.what": "{n} rows before {day} go back to waiting to be paid — rows already in a pay round stay",
  "opening.undo.reason": "Reason",
  "opening.undo.confirm": "Undo the batch",
  "opening.undo.done": "{code} undone — {n} rows back · {kept} kept",
  "opening.undo.failed": "Could not undo",
  "opening.debts.title": "2 · Debts still being paid back",
  "opening.debts.lead": "Loans, advances, deposits or damages still being paid back on the start day — enter the full amount, what came back and the instalments left. Only the instalments left reach a bill",
  "opening.debts.add": "Carry over a debt",
  "opening.debts.none": "No debt carried over yet",
  "opening.debts.line": "back {back} · left {left}",
  "opening.debt.title": "Carry over a debt",
  "opening.debt.save": "Save",
  "opening.debt.saved": "{code} carried over",
  "opening.debt.failed": "Could not carry the debt over",
  "opening.debt.who": "Whose · what kind of debt",
  "opening.debt.kind": "What kind of debt",
  "opening.debt.kind.loan": "The company lent / advanced it, being paid back",
  "opening.debt.kind.plan": "The company deducts it in instalments, e.g. a deposit or damages",
  "opening.debt.item": "Item",
  "opening.debt.q1.loan": "How much was lent in all",
  "opening.debt.q1.plan": "The full amount to deduct",
  "opening.debt.interestHint": "A fixed interest (if any) is part of the full amount",
  "opening.debt.total": "Full amount",
  "opening.debt.began": "Started {day}",
  "opening.debt.q2.loan": "How much came back already (before the start day)",
  "opening.debt.q2.plan": "How much was deducted already (before the start day)",
  "opening.debt.returned": "Already back",
  "opening.debt.left": "{left} left",
  "opening.debt.q3": "Instalments left, and the next one's day",
  "opening.debt.next": "Next instalment {day}",
  "opening.debt.evidence": "Evidence",
  "opening.debt.evidenceHint": "e.g. the loan contract or the old debt book page",
  "opening.debt.what": "What it is (required)",
  "opening.debt.notePh": "e.g. house repair loan (old book)",
  "opening.debt.sum.incomplete": "Pick the person and item, then the full amount",
  "opening.debt.sum.loan": "{who} borrowed {item} {total} in all, {back} back — {left} left in {n} instalments of {per} until {end}",
  "opening.debt.sum.plan": "{who} {item} {total} in all, {back} deducted — {left} left in {n} instalments of {per} until {end}",
  "finance.err.opening_evidence": "Attach the evidence",
  "finance.err.opening_total": "The full amount must be above zero (and above the interest)",
  "finance.err.opening_returned": "The amount already back is not right",
  "finance.err.opening_nothing_left": "All of it came back — nothing to carry over",
  "finance.err.opening_count": "Instalments left: 1–60 and no more than what is left",
  "finance.err.opening_began": "The start cannot be in the future",
  "finance.err.opening_start": "The next instalment cannot be before the start",
  "hold.action": "Hold",
  "hold.actionRow": "Hold this row",
  "hold.title": "Hold this row",
  "hold.save": "Hold",
  "hold.saved": "{doc} held — back to be paid on {day}",
  "hold.failed": "Could not hold",
  "hold.why": "Why hold it",
  "hold.whyPh": "e.g. waiting for OP's confirmation",
  "hold.when": "Back to be paid on",
  "hold.time": "Time",
  // NIGHT-1023 N5 — everything of one person held
  "holdAll.open": "Hold everything of this person",
  "holdAll.title": "Hold everything of {name}",
  "holdAll.what": "Every row of this person not in a pay round becomes \"held\", and none of their bills can go into a pay round until released. Rows that come in meanwhile wait in their bills and cannot go into a round either.",
  "holdAll.whyPh": "e.g. cannot be reached / damage to settle / bank details missing",
  "holdAll.when": "Held until (released by the system on that day)",
  "holdAll.while": "On the appointment day the system releases it: every row goes back to the bill of its own day — or release it earlier here",
  "holdAll.save": "Hold everything",
  "holdAll.saved": "Held everything of {name}: {n} rows until {day}",
  "holdAll.banner": "Everything of this person is held until {day}",
  "holdAll.bannerWhy": "{reason} · by {by} · {n} rows held · none of their bills can go into a round meanwhile",
  "releaseAll.open": "Release everything",
  "releaseAll.title": "Release everything of {name}",
  "releaseAll.what": "The rows this hold put on hold ({n}) go back to the bill of their own day, and this person's bills can go into a round again",
  "releaseAll.save": "Release",
  "releaseAll.saved": "Released everything of {name}: {n} rows",
  "hold.whenPast": "The appointment must be after now",
  "hold.evidence": "Evidence",
  "hold.while": "While held this row counts nowhere — no bill, no round, no base of tax or fees. A corner alert shows when the day is near and when it comes",
  "hold.wasHeld": "Was held: \"{reason}\"",
  "release.title": "Release",
  "release.save": "Release",
  "release.saved": "{doc} released",
  "release.failed": "Could not release",
  "release.where": "Which bill it goes to",
  "release.to.original": "Back to its own round",
  "release.to.originalHint": "The bill of the row's own day (the next open one if that bill went out)",
  "release.to.latest": "With the latest round",
  "release.to.latestHint": "The bill open today",
  "release.to.future": "A round in the future",
  "release.to.futureHint": "The bill that holds the day you pick",
  "release.on": "The bill of {day}",
  "release.why": "Reason (required)",
  "release.whyPh": "e.g. OP confirmed",
  "holds.people": "{n} people held as a whole",
  "holds.personLine": "until {day} · {reason} · {n} rows (released by the system on the day)",
  "holds.title": "Held rows",
  "holds.lead": "A held row counts nowhere until it is released — nearest appointment first",
  "holds.totals": "{n} held · to pay {out} · to take {in}",
  "holds.empty": "Nothing is held",
  "holds.until": "Back {day} {time}",
  "holds.due": "Due {day} {time}",
  "holds.noDay": "No appointment",
  "holds.by": "Held by {name} {day}",
  "holds.release": "Release",
  "finHome.act.openHolds": "See held rows",
  "settle.action": "Settled outside",
  "settle.actionRow": "Mark settled outside",
  "settle.title": "Done (settled outside)",
  "settle.save": "Save",
  "settle.saved": "{doc} done — it is not paid again",
  "settle.failed": "Could not save",
  "settle.how": "How it was settled",
  "settle.how.paid_outside": "Paid / received outside the round (cash · own transfer)",
  "settle.how.done": "Settled another way (netted off · cancelled by agreement)",
  "settle.why": "Note",
  "settle.whyPh": "e.g. paid in cash on site on 12 Oct",
  "settle.after": "This row reaches no bill or pay round again, but stays in this person's history and in the ledger",
  "fixRow.void.action": "Cancel row",
  "fixRow.void.title": "Cancel this row",
  "fixRow.void.after": "This row no longer counts in any bill or pay round, but stays in the history",
  "fixRow.void.afterDebt": "The instalments of this debt not paid yet are cancelled with it",
  "fixRow.reverse.action": "Reverse",
  "fixRow.reverse.title": "Reverse a paid row",
  "fixRow.reverse.after": "The system writes the same amount the other way into {who}'s next bill",
  "fixRow.thisPerson": "this person",
  "fixRow.why": "Reason (required)",
  "fixRow.whyPh": "e.g. entered twice · wrong person · wrong amount",
  "fixRow.voided": "{doc} cancelled",
  "fixRow.reversed": "{doc} reversed — {newDoc} goes into the next bill",
  "fixRow.failed": "Could not save",
  "fixRow.at.ticket": "Edit ticket",
  "fixRow.at.profile": "Edit employee",
  "fixRow.at.debt": "Edit debt",
  "review.open": "Approver view",
  "review.notFound": "Round not found",
  "review.sub": "Paid {day} · {people} people · {bills} bills",
  "review.total": "This round",
  "review.vsPrev": "Last round ({name}) {total} · {delta} ({pct})",
  "review.noPrev": "No paid round yet to compare with",
  "review.signals": "Worth a look before approving",
  "review.noSignals": "Nothing out of the ordinary",
  // APPROVER-DASHBOARD — the approver's page as a dashboard
  "board.title": "Check the pay round before approving",
  "board.q": "Where this round's money comes from, who to look at first, and whether it is ready",
  "board.meta": "{name} · pay on {day} · {people} people · {bills} bills",
  "board.metaPrev": "against {name}",
  "board.memo": "Print approval memo",
  "board.memoPrint": "Save as PDF",
  "board.memoTitle": "Pay round approval memo",
  "board.baht": "baht",
  "board.people": "{n} people",
  "board.unitPeople": "people",
  "board.rows": "{n} rows",
  "board.charts.open": "This round in charts",
  "board.charts.fold": "Fold the charts",
  "board.hero.label": "To be sent this round",
  "board.hero.from": "from {name}",
  "board.hero.rounds": "Paid rounds against this one",
  "board.hero.thisRound": "This round",
  "board.story.a": "This round pays {n} people",
  "board.story.up": "more than last round",
  "board.story.down": "less than last round",
  "board.story.because": ", mostly from ",
  "board.story.tickets": " · trip pay from tickets ",
  "board.story.ticketsShare": " — {pct}% of the round's total before system deductions (not a share of the transfer amount)",
  "board.story.look": "Look first:",
  "board.ready.title": "How ready to approve",
  "board.ready.q": "Counted from the people in the round, checked and with nothing open",
  "board.ready.ring": "{n} of {of} people checked",
  "board.ready.checked": "Checked {n}/{of}",
  "board.ready.clean": "Checked, can be trusted",
  "board.ready.withIssue": "Checked, something to look at",
  "board.ready.unchecked": "Not checked",
  "board.ready.kvChecked": "Bills checked",
  "board.ready.kvSample": "Sample",
  "board.ready.kvIssues": "To look at",
  "board.ready.kvDebt": "Debt taken back",
  "board.wf.title": "From last round to this one",
  "board.wf.q": "What the difference is made of — people gone · people in both · new people",
  "board.wf.focal": "{step} moved the total most",
  "board.wf.prev": "Last round",
  "board.wf.gone": "Gone {n}",
  "board.wf.changed": "In both {n}",
  "board.wf.new": "New {n}",
  "board.wf.now": "This round",
  "board.wf.total": "Round total",
  "board.wf.up": "Up",
  "board.wf.down": "Down",
  "board.src.title": "Where every baht comes from",
  "board.src.q": "The more from tickets, the more it can be trusted",
  "board.src.focal": "from tickets",
  "board.src.evidence": "See evidence",
  "board.src.tickets": "Closed tickets",
  "board.src.hand": "Typed by a person (debts, advances, hand rows)",
  "board.src.profile": "Set in the profile",
  "board.src.computed": "Worked out by the system (tax, fees, carry, daily wage)",
  "board.days.title": "Which working days the money comes from",
  "board.days.q": "By working day · days more than 15% above average in orange · point at a bar for the day",
  "board.days.perDay": "/day",
  "board.days.focal": "average of {n} days with work",
  "board.days.thatDay": "That day",
  "board.days.avg": "Average a day",
  "board.days.rows": "Rows",
  "board.days.foot": "Dashed line = average a day",
  "board.look.q": "Pick a line to filter the people below",
  "board.look.focal": "of {n} people",
  "board.look.overdueS": "No round has them — see the round page",
  "board.look.trustedS": "{total} in all — checked · paid before · near usual",
  "board.look.negative": "Below zero",
  "board.look.negativeS": "Cannot be approved until fixed",
  "board.look.bank": "Bank details missing",
  "board.look.bankS": "The bank file cannot pay them",
  "board.look.shared": "One account for several people",
  "board.look.sharedS": "Check whose account it is",
  "board.look.payee": "Bank changed after the lock",
  "board.look.payeeS": "Confirm with the person before sending",
  "board.look.flagged": "Bill problem open",
  "board.look.flaggedS": "Not closed yet",
  "board.look.stale": "Bill changed after check",
  "board.look.staleS": "Rows came in or changed after the check",
  "board.look.unchecked": "Not checked before the lock",
  "board.look.uncheckedS": "Open the bill from the list",
  "board.look.high": "Well above usual",
  "board.look.highS": "Half again usual or more",
  "board.look.jump": "More than 20% off usual",
  "board.look.jumpS": "See the chart below",
  "board.look.first": "New — never paid before",
  "board.look.firstS": "Nothing to compare with",
  "board.spread.title": "Who gets how much, against usual",
  "board.spread.q": "People paid before: ring = usual (last 3 rounds) · dot = this round · new people have nothing to compare",
  "board.spread.focal": "more than 20% off usual",
  "board.spread.usual": "Usual",
  "board.spread.now": "This round",
  "board.spread.jump": "More than 20% off",
  "board.spread.noBank": "New, no bank account",
  "board.spread.new": "New {n}",
  "board.memo.figures": "The round",
  "board.memo.prev": "Last round ({name})",
  "board.memo.heldOut": "Held out of the round: {n}",
  "board.memo.steps": "Approval steps",
  "board.memo.approver": "Approver",
  "board.memo.step": "Step {n} · {name}",
  "board.memo.doneBy": "Approved by {name} on {at}",
  "board.memo.sample": "sample right {ok} wrong {bad} of {of}",
  "board.memo.waiting": "Waiting",
  "board.memo.sign": "Signature / date",
  "board.memo.printed": "Printed {at}",
  // NIGHT-1023 N4 — the approver's dashboard
  "review.kpi.people": "People in the round",
  "review.kpi.peopleSub": "new {first} · held out {held}",
  "review.kpi.checked": "Bills checked (people)",
  "review.kpi.checkedSub": "{pct}% of the round",
  "review.kpi.checkedNone": "Locked before bill checking existed",
  "review.kpi.debts": "Debt taken back this round",
  "review.kpi.debtsLent": "lent with the round {lent} · {n} debts · {people} people",
  "review.kpi.debtsSub": "{n} debts · {people} people",
  "review.look.negative": "{n} below zero — cannot be approved until fixed",
  "review.look.bank": "{n} with a bank problem (no account / a bank the file cannot pay / a bad number)",
  "review.look.shared_account": "{n} paid into an account shared with others",
  "review.look.payee_changed": "{n} with bank details changed after the lock",
  "review.look.flagged": "{n} with a bill problem still open",
  "review.look.stale": "{n} whose bill changed after the check",
  "review.look.unchecked": "{n} not checked before the lock",
  "review.look.high": "{n} well above usual",
  "review.look.jump": "{n} more than 20% off usual",
  "review.look.first": "{n} new — never paid before",
  "review.look.show": "Show",
  "review.look.showing": "Showing",
  "review.lookShort.negative": "Below zero",
  "review.lookShort.bank": "Bank problem",
  "review.lookShort.shared_account": "Shared account",
  "review.lookShort.payee_changed": "Bank changed",
  "review.lookShort.flagged": "Bill problem",
  "review.lookShort.stale": "Changed after check",
  "review.lookShort.unchecked": "Not checked",
  "review.lookShort.high": "Above usual",
  "review.lookShort.jump": "Off usual",
  "review.lookShort.first": "New",
  "review.trust.title": "Can be trusted",
  "review.trust.body": "{n} people · {total} ({share}% of the round)",
  "review.trust.none": "Nobody passes every check yet",
  "review.trust.rule": "Bills checked · paid before · near usual (within 20%) · on no list above",
  "review.filter.trusted": "Trusted {n}",
  "review.sort.change": "Sort: biggest change",
  "review.sort.total": "Sort: highest total",
  "review.sort.name": "Sort: name",
  "review.change": "{sign}{amount} from usual",
  "review.trend": "Totals of the last {n} rounds and this one",
  "review.comp.title": "What the money is made of",
  "review.comp.items": "By kind of money",
  "review.comp.contracts": "By contract",
  "review.comp.noContract": "No contract (salary / other items)",
  "review.comp.rows": "{n} rows · {people} people",
  "review.comp.more": "and {n} more",
  "review.heldOut": "Held out of this round: {n} people · {total}",
  "review.signal.high": "{n} get clearly more than usual",
  "review.signal.payee_changed": "{n} had their bank details changed after the freeze",
  "review.signal.negative": "{n} below zero — send the round back and freeze it again to carry the shortfall",
  "review.signal.overdue": "{n} due instalments no round took",
  "review.flag.high": "Above usual",
  "review.flag.payee_changed": "Bank changed",
  "review.flag.negative": "Below zero",
  "review.flag.first": "First round",
  "review.usual": "usually {total}",
  "review.hold": "Hold",
  "review.holdTitle": "Hold {name} out of this round",
  "review.holdWhat": "This person's bills ({total}) leave the round and every row is held until the appointment — the rest can still be approved",
  "review.holdWhyPh": "e.g. waiting for HR to confirm the debt",
  "review.holdSave": "Hold",
  "review.held": "{name} held — the round is recounted",
  "review.holdFailed": "Could not hold",
  "review.approve": "Approve {total}",
  "review.sendBack": "Send back / details",
  "finHome.act.review": "Review and approve",
  "bills.computedLine": "{rate}% of {base} · worked out",
  "financeTxn.kind.computed": "Worked out by the system",
  "bills.computedBase": "of {base} · worked out",
  "bills.part.earn": "adds {amount}",
  "bills.part.given": "handed over (not income, untaxed) {amount}",
  "bills.part.pre_tax": "taken before tax {amount}",
  "bills.part.post_tax": "taken after tax (base unchanged) {amount}",
  "bills.monthlyTitle": "Monthly pay {month}",
  "payBatches.fee": "Transfer fee {fee} taken (once a round)",
  "finHome.todo.unpriced": "{n} completed tickets ({people} people) have no trip pay",
  "finHome.todo.unpricedDetail": "People paid per trip get nothing from these tickets until a trip pay is set",
  "finHome.act.unpriced": "Set trip pay",
  "unpriced.title": "Tickets with no trip pay",
  "unpriced.lead": "Completed tickets of people paid per trip (last 62 days) with a trip pay of zero — the driver gets nothing from them. Set it here: it is written on the ticket and reaches the ledger at the next sync",
  "unpriced.empty": "No completed ticket is missing its trip pay",
  "unpriced.tickets": "{n} tickets",
  "unpriced.all": "Same for every ticket of this person",
  "unpriced.applyAll": "Set all",
  "unpriced.savePerson": "Save {n} tickets",
  "unpriced.save": "Save",
  "unpriced.saved": "Trip pay set on {n} tickets",
  "unpriced.failed": "Could not save",
  "unpriced.rate": "Trip pay",
  "unpriced.routeRate": "route rate {rate}",
  "hr.netToDriver": "Net to driver",
  "ops.passengerAdded": "Passenger added: {name}",
  "ops.createFailed": "Create failed",
  "ops.ticketAssigned": "Ticket assigned",
  "ops.assignFailed": "Assign failed",
  "ops.searchRoutesInContract": "Search routes in this contract…",
  "ops.routeNoContract": "No contract",
  "ops.searchRoutesInContracts": "Search routes in the {n} picked contracts…",
  "ops.contractAdd": "+ Add contract…",
  "ops.contractSearch": "Search code, title or customer…",
  "ops.contractEnds": "ends",
  "ops.contractRemove": "Remove",
  "ops.moreCustomers": "+{n} customers",
  "ops.nameAuto": "Automatic",
  "ops.nameUseAuto": "Use the automatic name",
  "ops.nameFromRoute": "The ticket has a route, so the name is the route's",
  "ops.createTicket": "Create Ticket",
  "ops.overlapNotAcceptedNote": "The driver hasn't accepted that job yet, so this can still be saved — but they can only run one of them.",
  "ops.overlapAcceptedNote": "This overlaps another job in time, so it can't be saved. Change the driver, vehicle, or time.",
  "ops.overlapManyNote": "This overlaps {n} other jobs. It can't be saved — change the driver, vehicle, or time.",
  "ops.assignNeeds": "Needs:",
  "common.createdAt": "Created {when}",
  "ops.income": "Income",
  "ops.expense": "Expense",
  "ops.saveTemplate": "Save Template",
  "ops.reoptimize": "Re-optimize",
  "ops.optimizing": "Optimizing…",
  "ops.avoidTollsDesc": "Route will not use toll roads",
  "ops.avoidHighwaysDesc": "Route will not use motorways or expressways",
  "ops.noRunsWithStatus": "No runs with status \"{status}\"",
  "ops.noRunsYet": "No optimization runs yet — add stops and click Optimize to begin",
  "ops.noStopsYet": "No stops added yet.",
  "ops.searchLocationHint": "Search for a location above to begin.",
  "ops.noShowCount": "No-Show ({n})",
  "ops.cancellationsCount": "Cancellations ({n})",
  "ops.viewRoutes": "View routes",
  "ops.contractTypeEmployee": "Employee shuttle",
  "ops.contractTypeStaff": "Staff shuttle",
  "ops.contractTypeStudent": "Student shuttle",
  "ops.contractTypeOther": "Other",
  "ops.limitedSeats": "Limited · {n} seats",
  "ops.unlimitedSeats": "Unlimited seats",
  "ops.markingNoShow": "Marking…",
  "ops.loadingStops": "Loading stops…",
  "ops.selectPickupStop": "Select pickup stop…",
  "ops.noStopsAvailable": "No stops available",
  "ops.selectTicketFirst": "Select a job ticket first",
  "ops.createBooking": "Create Booking",
  "ops.contractNotFound": "Contract not found",
  "ops.noContractsYet": "No contracts yet — click New Contract to get started",
  "ops.noContractsMatch": "No contracts match the current filter",
  "ops.bookingLimited": "Booking · Limited seats",
  "ops.bookingUnlimited": "Booking · Unlimited seats",
  "ops.rejectNewBookings": "Reject new bookings",
  "ops.allowAlertTeam": "Allow, alert the team",
  "ops.autoOverflow": "Auto-create overflow trip",
  "ops.gpsGeofence": "GPS Geofence",
  "ops.qrScan": "QR Scan",
  "ops.qrScanned": "QR Scanned",
  "ops.manualBoard": "Manual Board",
  "ops.capacitySeats": "Capacity (seats)",
  "ops.actualBoarded": "Actual Boarded",
  "ops.boardingRate": "Boarding Rate",
  "ops.boardingReport": "Boarding Report",
  "ops.viaRoutingOnly": "Via (routing only)",
  "ops.allDates": "All Dates",
  // ADR 0314 — หน้ารายละเอียดใบงานในพอร์ทัลดูได้อย่างเดียว
  "ops.backToJobTickets": "Back to Job Tickets",
  "ops.passengersTotal": "{n} total",
  "ops.passengersCheckedIn": "{n} checked in",
  "ops.noPassengersBooked": "No passengers booked",
  "ops.noPassengersMatch": "No passengers match your filters",
  "ops.seatsN": "{n} seats",
  "ops.seatUnassigned": "Unassigned",
  "ops.seatAssigned": "Assigned",
  "ops.viewOnlyNotice": "This job ticket is view-only.",
  "ops.acceptBy": "Accept by",
  "ops.routeStops": "Route & Stops",
  "ops.currentStop": "Current stop",
  "warroom.nowLabel": "now",
  "warroom.jobsLabel": "jobs",
  "warroom.next": "next out",
  "warroom.queued": "queued",
  "warroom.noJobsQueued": "No jobs queued for today",
  "warroom.upNextToday": "Job queue",
  "warroom.filters": "Filters",
  "warroom.clearFilters": "Clear all",
  "warroom.finishedLast24h": "Finished in the last 24h",
  "warroom.filterTime": "Time",
  "warroom.applyFilters": "Apply",
  "warroom.timeFieldConfirm": "Confirm time",
  "warroom.timeFieldStart": "Start time",
  "warroom.filterBoard": "Schedule plan",
  "warroom.filterBoardShort": "Plan",
  "warroom.filterVehicle": "Vehicle",
  "warroom.boardNone": "Not from a plan",
  "warroom.noJobsFiltered": "No jobs match the filters",
  "warroom.lateCount": "late",
  // ── ADR 0347 — the vehicle pin card ─────────────────────────────────
  "warroom.noDriver": "Driver unknown",
  "warroom.boundDriver": "Usual driver",
  "warroom.atDepot": "depot",
  "warroom.nextJob": "Next job",
  "warroom.nextStopAt": "next stop {time}",
  "warroom.openJobDetail": "Open job detail",
  "warroom.fitAll": "Show all",
  "warroom.fitAllHint": "Zoom out to show every vehicle and route",
  // ADR 0343 — more than a service day past departure and never left. Not "late".
  "warroom.staleCount": "stale",
  // ADR 0343 — the number only; the header already says the word.
  "warroom.dayGroupStale": "+{n}d",
  "warroom.dayGroupToday": "Today",
  "warroom.dayGroupJobs": "{n} jobs",
  "warroom.confirmLate": "Not confirmed · {overrun} late",
  "warroom.startLate": "Not departed · {overrun} late",
  "hr.setInterview": "Schedule",
  "ops.sameResourceAsCell": "Same {what} as another cell",
  "ops.clashesWith": "Clashes with",
  "common.resource": "resource",
  "ops.noRoutesInWindow": "No routes in the selected window — showing all dimmed",
  "ops.scrollToLoadMap": "Scroll to load map…",
  "ops.idleCount": "Idle ({n})",
  "ops.conflictsCount": "Conflicts ({n})",
  "ops.unfilledCount": "Unfilled ({n})",
  "ops.noDatesPickFirst": "No dates in this table — pick dates first",
  "ops.backhaulRadius": "Backhaul radius",
  "ops.driverMinRest": "Driver min rest",
  "ops.maxDutyPerDay": "Max duty / driver / day",
  "ops.maxDutyHint": "Caps one driver's on-duty window within a single day (default 13).",
  "ops.allOrNothing": "All or nothing — if any job clashes, none are created.",
  "ops.routesDefineColumns": "Routes define the columns — manage them on the table grid.",
  "ops.timeLockedToSlot": "Time window is locked to the slot — edit slot to change.",
  "ops.unsavedTripWarning": "You have unsaved changes to this trip. Save before leaving?",
  "ops.deleteTripFor": "Delete trip for",
  // ADR 0186 — remove from a published cell = cancel the job ticket
  "ops.removeFromSlot": "Remove from slot",
  "ops.removeFromSlotNamed": "Remove {name} from slot",
  "ops.cancelTicketFailed": "Failed to cancel the job",
  "ops.jobCancelledAndRemoved": "Job cancelled and removed from the board",
  "ops.removeAndCancelJob": "Remove and cancel this job?",
  "ops.removeAndCancelJobConfirm": "This vehicle already has a job for this slot. Removing it will cancel that job.",
  "ops.cancelWarningAccepted": "The driver has already accepted or started this job — cancelling will need to reach them.",
  "ops.cancelWarningDone": "This job is already finished. Cancelling won't erase what already happened (GPS, boarding records) — it only marks the job cancelled.",
  "ops.cancelReasonOptional": "Reason (optional)",
  "ops.removeAndCancelJobButton": "Cancel job & remove",
  "ops.boardName": "Board Name",
  "ops.problemsOnly": "Problems only",
  "ops.allDaysMinimap": "All days",
  "ops.cellsLeftAlone": "{n} cells already matched and were left alone.",
  "ops.publishFromBoard": "From {board}",
  "ops.jobsWillBePublished": "job(s) will be published",
  "ops.nothingToPublish": "Nothing to publish — no jobs to create, edit, cancel, or delete.",
  "ops.nDays": "{n} days",
  "ops.nDrivers": "{n} drivers",
  "ops.nVehicles": "{n} vehicles",
  "ops.unassignedSlotsNote": "{n} vehicle slot(s) on the board have no vehicle assigned — no job is created for them.",
  "ops.nJobsCancelled": "{n} job(s) cancelled",
  "ops.nJobsDeleted": "{n} job(s) deleted",
  "ops.createTicketsNow": "Create job tickets for the driver now",
  "ops.alreadyUpToDate": "Already up to date",
  "ops.couldNotReadBoard": "Could not read the board",
  "ops.publishFailed": "Publish failed",
  "ops.problemGone": "That problem is gone.",
  "ops.driversCanSeeNow": "— drivers can see them now",
  "ops.jobsPublished": "{n} jobs published",
  "ops.jobsReplaced": "{n} jobs replaced",
  "ops.jobsRemoved": "{n} jobs removed",
  "ops.addRouteTitle": "Add route",
  "ops.editRouteTitle": "Edit route",
  "ops.addRouteBtn": "Add Route",
  "ops.saveRouteBtn": "Save Route",
  "ops.route": "Route",
  "ops.jobTypeHint": "Defaults to the route's job type; change it for this route only.",
  "ops.acceptDeadlineHint": "Deadline for drivers to accept, used when this board is published into jobs.",
  "ops.crossMidnight": "Cross-midnight — ends next day",
  "ops.vehiclesEditHint": "Applies to dates added later (existing cells unchanged).",
  "ops.vehiclesNewHint": "Pre-fills every date in this table with this count.",
  "ops.slotLabelExample": "e.g. \"Group A\", \"Run 1\"",
  "ops.slotLabelHint": "Helps distinguish duplicate routes; otherwise auto-numbered #1, #2…",
  "ops.searchRouteTemplateEllipsis": "Search routes…",
  // ADR 0187 Phase 1 — two-column route pairing (Add Route only)
  "ops.pairRoute": "Pair route",
  "ops.pairRouteHint": "Off = add a single route, like before. Remembered on this device.",
  "ops.routeColumnLabelIn": "In",
  "ops.routeColumnLabelOut": "Out",
  "ops.routeColumnLabelField": "Label",
  "ops.ghostNoPairColumn": "No paired leg yet",
  "ops.ghostNoPairCell": "— no run —",
  "ops.ghostNoPairTitle": "This route has no paired leg yet — click to add one",
  "ops.partnerSuggestCompact": "Paired route found: {partnerCode}",
  "ops.removeColumn": "Remove this column",
  "ops.routeLegHeader": "Leg {n}",
  "fleet.ownershipCol": "Ownership",
  "common.fileCol": "File",
  "safety.caseNo": "Case #",
  "fuel.efficiency": "Efficiency",
  "fuel.entries": "Entries",
  "fuel.std": "Std",
  "hr.ticketNo": "Ticket #",
  "fuel.totalCost": "Total Cost",
  "fuel.totalLiters": "Total L",
  "ops.createCustomer": "Create Customer",
  "common.pageOf": "Page {page} / {total}",
  "ops.pageOfRuns": "Page {page} of {total} · {n} run(s)",
  "ops.showingCustomers": "Showing {from}–{to} of {total} customers",
  // ADR 0183 — /jobs/customers i18n gap closure
  "ops.noCustomersMatchSearch": "No customers match the filters",
  "ops.noCustomersYet": "No customers yet — click New Customer to get started",
  "common.editItem": "Edit {name}",
  "ops.customerUpdated": "Customer updated",
  "ops.customerCreated": "Customer created",
  "ops.customerCreatedNo": "Customer {no} created",
  "ops.editCustomer": "Edit Customer",
  "ops.editCustomerName": "Edit Customer · {name}",
  "ops.customerNotFound": "Customer not found",
  "ops.customerDeleted": "Customer deleted",
  "ops.customerDeleteFailed": "Failed to delete customer",
  "common.deleting": "Deleting...",
  "ops.resetRouteMessage": "Reset all stops back to the original route template? Any per-job edits will be lost.",
  "hr.deleteChargeMessage": "Delete {type}: ฿{amount}?",
  "hr.deleteChargeReceiptNote": " Its receipt file will also be deleted.",
  "ops.cancelJobMessage": "Are you sure you want to cancel {id}? This action cannot be undone. Booked passengers will be notified.",
  "ops.jobCancelled": "Job {id} cancelled",
  "ops.changeStatusMessage": "Change {id} from {from} to {to}?",
  "ops.changeStatusConfirm": "Change to {to}",
  "ops.stopsColShort": "Stops",
  "ops.timeInputHint": "Type a 24-hour time HH:MM, e.g. 08:00, 13:00, 23.00, or click the clock icon in a field to pick from the list",
  "ops.availFree": "Free",
  "ops.availBusy": "Busy",
  "ops.availOnLeave": "On leave",
  "ops.availOnLeaveHalf": "Half-day leave",
  "ops.availOverlap": "Overlap",
  "ops.availOtherBoard": "Other board",
  "ops.stopN": "Stop {n}",
  "ops.nStops": "{n} stops",
  "ops.stopsOverlapWarn": "Check-in zones overlap: {stops} are {m} m apart — the driver must leave both zones before the second one can check in (set the radius to {max} m or less to avoid this)",
  "ops.stopsProgress": "{done} / {total} stops",
  "common.selectEllipsis": "Select...",
  "ops.optionalLabel": "Optional label",
  "common.nTotal": "{n} total",
  "ops.nEvents": "{n} events",
  "ops.appBackground": "background:",
  "ops.appKilled": "kill app:",
  "ops.idColon": "ID:",
  "hr.driverPayReadOnly": "Read-only — driver pay is managed in",
  "ops.acceptByTime": "Accept by {time}",
  "ops.driverPlusVehicle": "Driver + Vehicle",
  "ops.driverChangedComp": "Driver changed — compensation",
  "hr.routeBase": "Route base: ",
  "ops.bookingAndSeats": "BOOKING & SEATS",
  "ops.saveStopsFailed": "Failed to save stops",
  "ops.routeStopsSavedShort": "Route stops saved",
  "ops.financialsSaved": "Financials saved",
  "ops.availabilityCheckFailed": "Couldn't check availability",
  "ops.jobSettingsSaved": "Job settings saved",
  "ops.saveSettingsFailed": "Failed to save settings",
  "ops.autosaveIdle": "Autosave · Saved {time}",
  "ops.autosaveNoChanges": "Autosave on",
  "ops.autosaveSaving": "Saving…",
  "ops.autosaveError": "Couldn't save",
  "ops.autosaveReadOnly": "Read only — you don't have permission to edit",
  "ops.autosaveBlocksStatus": "Settings didn't save — fix that before changing status",
  "ops.autosaveChangedElsewhere": "Changed elsewhere — your value is kept until it saves",
  "ops.statusChangeFailed": "Status change failed",
  // ADR 0465 D4 — "Complete anyway" (a cargo job not finished)
  "ops.openCargo.title": "Complete {id}?",
  "ops.openCargo.open": "{n} cargo items are not recorded yet.",
  "ops.openCargo.returns": "{n} put-back photos are missing.",
  "ops.openCargo.confirm": "Complete anyway",
  "ops.exportingPdf": "Exporting job report as PDF...",
  "hr.compensationOnly": "Compensation only",
  "hr.replaceReceipt": "Replace receipt",
  "common.cancelling": "Cancelling…",
  "ops.liveUpdateFailed": "Live update failed — retrying…",
  "hr.reimburseHint": "Reimburse — adds to driver pay",
  "hr.deductHint": "Deduct — subtracts from driver pay",
  "hr.openReceiptFailed": "Failed to open receipt",
  "hr.selectChargeType": "Please select a charge type",
  "common.enterValidAmount": "Please enter a valid amount",
  "hr.saveChargeFailed": "Failed to save charge",
  "hr.chargeUpdated": "Charge updated",
  "hr.chargeApprovalRevoked": "Charge approval revoked",
  "hr.chargeApproved": "Charge approved",
  "hr.updateApprovalFailed": "Failed to update approval",
  "hr.deleteChargeTitle": "Delete Charge",
  "hr.chargeDeleted": "Charge deleted",
  "hr.deleteChargeFailed": "Failed to delete charge",
  "hr.noDriverAssignedYet": "No driver assigned yet",
  "hr.attachReceipt": "Attach receipt",
  "hr.chargeId": "Charge ID",
  "fuel.receipt": "Receipt",
  "ops.liveTracking": "LIVE TRACKING",
  "ops.completedRoute": "COMPLETED ROUTE",
  "ops.plannedRouteCaps": "PLANNED ROUTE",
  "ops.switchedSelectDriver": "Switched to Settings — select new driver",
  "ops.switchedSelectVehicle": "Switched to Settings — select new vehicle",
  "ops.operatingDate": "Operating date",
  "ops.acceptDeadline": "Accept deadline",
  "ops.routeEndTimeLabel": "End time",
  "ops.columnLocked": "Column locked",
  "ops.columnLockedExplain": "This column has a published job ticket — route, accept deadline, start/end time, and default vehicle count can't be changed, and it can't be deleted (single or paired).",
  "ops.dateLockedExplain": "This date has a published job ticket and can't be removed.",
  "ops.startTimeLabel": "Start time",
  "ops.expectedEndLabel": "Expected end",
  "ops.endUseEstimate": "Use the worked-out time ({time})",
  "ops.endAuto": "Worked out from the pins / route",
  "ops.assignNeedEnd": "End time (type it, or pin the stops so it is worked out)",
  "ops.pickDriverHint": "Pick a driver to see details",
  "ops.pickVehicleHint": "Pick a vehicle to see details",
  "ops.noBookingDesc": "Board freely, no reservation.",
  "ops.bookingLimitedDesc": "Must book. Seats are capped.",
  "ops.bookingUnlimitedDesc": "Must book. Extra seats allowed.",
  "ops.saveSettings": "Save Settings",
  "ops.selectEmployee": "Please select an employee",
  "ops.selectPassenger": "Please select a passenger",
  "ops.addPassengerFailed": "Failed to add passenger",
  "common.updateFailed": "Update failed",
  "ops.passengerUpdated": "Passenger updated",
  "ops.markedNoShow": "Marked as no-show",
  "ops.bookingCancelled": "Booking cancelled",
  "common.cancelFailed": "Cancel failed",
  "ops.allMethods": "All Methods",
  "ops.telRef": "Tel/Ref",
  "ops.noPassengersOnTicket": "No passengers yet",
  "ops.routeEditCancelled": "Route editing cancelled",
  "ops.routeStopsSaved": "Route stops saved successfully",
  "ops.resetRouteTitle": "Reset Route",
  "ops.routeResetDone": "Route reset to template",
  "ops.timeNotSet": "Time not set",
  "ops.overlapsAnotherJob": "This overlaps another job",
  "ops.driverVehicleUnavailable": "Driver and vehicle unavailable",
  "ops.vehicleUnavailable": "Vehicle is not available",
  "ops.driverUnavailable": "Driver is not available",
  "common.adding": "Adding…",
  "common.showingOf": "Showing {from}–{to} of {total}",
  "common.showingNofM": "Showing {n} of {total}",
  "fleet.ofNVehicles": "of {n} vehicles",
  "fuel.noEntriesMatch": "No fuel entries match your filters",
  "ops.ticketsScopeToday": "Today's Tickets",
  "ops.ticketsScopeYesterday": "Yesterday's Tickets",
  "ops.ticketsScopeTotal": "Total Tickets",
  "ops.noTemplate": "No template",
  "ops.noRouteSet": "No route set",
  "ops.bookingLimitedShort": "Limited",
  "ops.bookingUnlimitedShort": "Unlimited",
  "common.showingRange": "Showing {from}–{to} of {total}",
  "common.entriesCount": "{n} entries",
  "fleet.vehiclesTrackedCount": "{n} vehicles tracked",
  "fuel.allEfficiency": "All Efficiency",
  "common.addAttribute": "Add Attribute",
  "hr.addCharge": "Add Charge",
  "fuel.addEntry": "Add Entry",
  "masterData.addField": "Add field",
  "common.addFiles": "Add Files",
  "fleet.addFinanceRecord": "Add Finance Record",
  "fleet.addGroup": "Add Group",
  "hr.addIdCard": "Add ID Card",
  "common.addItem": "Add Item",
  "hr.addLicense": "Add Licence",
  "settings.addMember": "Add Member",
  "hr.addOpCharge": "Add OP Charge",
  "common.addRecord": "Add Record",
  "fleet.addTaxRecord": "Add Tax Record",
  "ops.applyBestSuggestion": "Apply Best Suggestion",
  "ops.cancelJob": "Cancel Job",
  "ops.changeDriver": "Change Driver",
  "ops.changeVehicle": "Change Vehicle",
  "common.copy": "Copy",
  "warroom.createRoom": "Create Room",
  "ops.editRouteLower": "Edit route",
  "ops.editRoute": "Edit Route",
  "fleet.fail": "Fail",
  "ops.fillRouteEllipsis": "Fill route…",
  "common.importCsv": "Import CSV",
  "common.loadingMore": "Loading more…",
  "fleet.loadingTemplate": "Loading template...",
  "hr.lockPeriod": "Lock Period",
  "status.locked": "Locked",
  "common.markResolved": "Mark Resolved",
  "hr.monthlyCompensation": "Monthly Compensation",
  "ops.newBoard": "New Board",
  "ops.newRoute": "New Route",
  "hr.newTeam": "New Team",
  "settings.notificationsToSend": "Notifications to Send",
  "warroom.online": "Online",
  "common.openOriginal": "Open original",
  "common.preview": "Preview",
  "common.protected": "Protected",
  "warroom.readReceipts": "Read Receipts",
  "settings.resendInvite": "Resend Invite",
  "safety.signed": "Signed",
  "common.uploadPhoto": "Upload Photo",
  "hr.documentUploaded": "Document uploaded successfully",
  "ops.anyDirection": "Any direction",
  "ops.outbound": "Outbound",
  "ops.return": "Return",
  "hr.applicants": "Applicants",
  "common.approvedBy": "Approved By",
  "ops.bookingId": "Booking ID",
  "safety.cleanliness": "Cleanliness",
  "hr.companyDriverLower": "Internal driver",
  "analytics.completionRate": "Completion Rate",
  "ops.contractPeriod": "Contract Period",
  "safety.damages": "Damages",
  "common.daysLeft": "Days Left",
  "fleet.device": "Device",
  "ops.driverVehicle": "Driver / Vehicle",
  "hr.employeeTypeCol": "Employee Type",
  "common.excel": "Excel",
  "settings.formulaSet": "Formula Set",
  "settings.formulas": "Formulas",
  "fuel.id": "Fuel ID",
  "common.items": "Items",
  "hr.joined": "Joined",
  "fleet.lastDone": "Last Done",
  "fleet.nextDue": "Next Due",
  "hr.officeStaffLower": "Office staff",
  "analytics.onTime": "On-Time",
  "fleet.premium": "Premium",
  "fleet.provider": "Provider",
  "analytics.punctuality": "Punctuality",
  "common.rate": "Rate",
  "ops.routeContract": "Route / Contract",
  "fleet.serviceType": "Service Type",
  "safety.signatures": "Signatures",
  "settings.states": "States",
  "ops.tables": "Tables",
  "analytics.totalTrips": "Total Trips",
  "settings.transitions": "Transitions",
  "ops.trip": "Trip",
  "common.version": "Version",
  "settings.workflowName": "Workflow Name",
  "status.resolvedLower": "resolved",
  "common.action": "Action",
  "hr.activeDrivers": "Active Drivers",
  "hr.addEmployee": "Add Employee",
  "fleet.addPmSchedule": "Add PM schedule",
  "hr.addRate": "Add Rate",
  "fleet.addVehicle": "Add Vehicle",
  "ops.bookingManagement": "Booking Management",
  "fleet.bus": "Bus",
  "hr.candidate": "Candidate",
  "settings.wfCaseTicket": "Case Ticket Workflow",
  "fleet.company": "Company",
  "hr.wfCompanyDriverPayroll": "Company Driver Payroll",
  "hr.wfContractorSettlement": "Contractor Settlement",
  "hr.contractors": "External drivers",
  "common.document": "Document",
  "roles.driverPortal": "Driver (Portal)",
  "analytics.driverPerformance": "Driver Performance",
  "analytics.driverPerformanceDesc": "Driver evaluation - on-time, fuel efficiency, completion rate",
  "ops.dropoff": "Dropoff",
  "settings.wfEmployeeLifecycle": "Employee Lifecycle",
  "roles.mechanicDesc": "Fleet maintenance access",
  "analytics.fuelScoring": "Fuel Efficiency Scoring",
  "roles.hrManagerDesc": "Full HR, partial payroll and safety",
  "roles.superAdminDesc": "Full access to all modules",
  "common.groups": "Groups",
  "roles.hrManager": "HR Manager",
  "settings.wfJobTicket": "Job Ticket Lifecycle",
  "fleet.leased": "Leased",
  "settings.wfLeaveApproval": "Leave Request Approval",
  "roles.driverPortalDesc": "Limited portal access for drivers",
  "roles.opsManagerDesc": "Manage fleet, jobs, dispatch, tracking",
  "hr.wfMechanicPayroll": "Mechanic Payroll",
  "ops.newContract": "New Contract",
  "ops.newCustomer": "New Customer",
  "hr.newPosting": "New Posting",
  "warroom.newRequest": "New Request",
  "fleet.newTemplate": "New Template",
  "fleet.newWorkOrder": "New Work Order",
  "warroom.noGps": "No GPS",
  "hr.noConditionsHint": "No conditions — add one to filter by check-in evidence.",
  "ops.noDriver": "No driver",
  "common.noEntries": "No entries",
  "warroom.noVehiclesRunning": "No vehicles running",
  "hr.wfOfficeStaffPayroll": "Office Staff Payroll",
  "fleet.outsource": "Outsource",
  "settings.wfPayTicket": "Pay Ticket Processing",
  "roles.financeDesc": "Payroll, billing, contracts",
  "ops.pickup": "Pickup",
  "fleet.preventive": "Preventive",
  "fleet.repair": "Repair",
  "analytics.routePerformance": "Route Performance",
  "analytics.routePerformanceDesc": "Route analysis - time, distance, booking utilization",
  "hr.runCalculation": "Run Calculation",
  "settings.wfSlaKpi": "SLA/KPI Calculation",
  "roles.safetyOfficer": "Safety Officer",
  "roles.safetyOfficerDesc": "Safety cases, inspections, handover",
  "roles.superAdmin": "Super Admin",
  "analytics.tripSummary": "Trip Summary",
  "analytics.tripSummaryDesc": "Trip summary - total trips, on-time rate, passengers",
  "fleet.vehicleStatus": "Vehicle Status",
  "settings.wfWorkOrderApproval": "Work Order Approval",
  "common.tue": "Tue",
  "common.wed": "Wed",
  "common.thu": "Thu",
  "common.fri": "Fri",
  "common.sat": "Sat",
  "common.allTime": "all time",
  "fleet.allVehicles": "all vehicles",
  "fuel.below10Std": "below 10% of std",
  "fuel.fleetAverage": "fleet average",
  "safety.needsInvestigation": "needs investigation",
  "warroom.noActive": "no active",
  "warroom.onJob": "on job",
  "common.todayLower": "today",
  "fuel.withFuelData": "with fuel data",
  "common.recorded": "recorded",
  "common.allPriority": "All Priority",
  "fleet.allProviders": "All Providers",
  "safety.allSeverity": "All Severity",
  "common.allStatus": "All Status",
  "common.allTypes": "All Types",
  "ops.cancelledBy": "Cancelled by",
  "hr.checkinIncomplete": "Check-in incomplete",
  "hr.compensation": "Compensation",
  "common.createdVia": "Created via",
  "common.currency": "Currency",
  "common.daily": "Daily",
  "hr.dailyWindowFrom": "Daily window from",
  "common.days": "Days",
  "hr.decision": "Decision",
  "hr.employmentType": "Employment Type",
  "hr.emptyRuns": "Empty runs",
  "common.expiry": "Expiry",
  "common.fromDateTime": "From (date & time)",
  "ops.gpsTrace": "GPS trace",
  "hr.goldStandard": "Gold standard",
  "hr.groupByDriver": "Group by driver",
  "hr.hasCharge": "Has charge",
  "hr.hasChargesPending": "Has charges pending approval",
  "hr.hasNoShow": "Has no-show",
  "hr.hasWalkIn": "Has walk-in",
  "hr.includeNoTime": "Include tickets without a time",
  "hr.license": "Driver's License",
  "hr.licenseExpiry": "License Expiry",
  "fuel.liters": "Liters",
  "common.locale": "Locale",
  "common.location": "Location",
  "status.notStarted": "Not started",
  "hr.otPerHour": "OT/hr",
  "fuel.odometerShort": "Odometer",
  "status.onTime": "On-time",
  "hr.paySource": "Pay source",
  "hr.perTrip": "Per Trip",
  "hr.periodState": "Period state",
  "hr.readyToPay": "Ready to pay",
  "fuel.receipts": "Receipts",
  "warroom.sos": "SOS",
  "hr.salaryRange": "Salary Range",
  "hr.skippedPickups": "Skipped pickups",
  "hr.stopCheckin": "Stop check-in",
  "common.toDateTime": "To (date & time)",
  "ops.tripDirection": "Trip direction",
  "ops.trips": "Trips",
  "status.unaccepted": "Unaccepted",
  "common.updated": "Updated",
  "hr.usernameOrId": "Username / ID",
  "hr.weakEvidence": "Weak evidence",
  "fleet.workOrder": "Work Order",
  "hr.zeroRateTrips": "Zero-rate trips (no rate set)",
  "hr.acceptedLate": "accepted, late",
  "nav.contractsLower": "contracts",
  "fuel.kmPerLiter": "km/L",
  "hr.pastAccept": "past accept",
  "hr.toCrossMidnight": "to (cross-midnight OK)",
  "common.somethingWentWrong": "Something went wrong",
  "fleet.markInactiveHint": "This marks {name} as inactive. You can reactivate it later.",
  "fleet.markActiveHint": "This marks {name} as active again.",
  "register.otpSentToName": "OTP sent to {email}",
  "settings.webhookSigHeader": "X-DeepTrack-Signature",
  "common.menu": "Menu",
  "settings.searchOrgsShort": "Search organizations",
  "analytics.summaryToday": "Summary Today",
  "analytics.freeDrivers": "Free Drivers",
  "analytics.expiringDocs": "Expiring Docs",
  "analytics.fuelToday": "Fuel Today",
  "analytics.scheduleTomorrow": "Schedule Tomorrow",
  "analytics.customReports": "Custom Reports",
  "analytics.source": "Source",
  "analytics.chartType": "Chart Type",
  "common.filters": "Filters",
  "analytics.groupBy": "Group by",
  "analytics.aggregate": "Aggregate",
  "analytics.aggregateField": "Aggregate field",
  "analytics.srcPayTickets": "Pay Tickets",
  "analytics.srcSafetyCases": "Safety Cases",
  "analytics.aggCount": "Count",
  "analytics.aggSum": "Sum",
  "analytics.aggAvg": "Average",
  "analytics.aggMin": "Min",
  "analytics.aggMax": "Max",
  "analytics.chartTable": "Table",
  "analytics.chartBar": "Bar",
  "analytics.chartLine": "Line",
  "analytics.chartPie": "Pie",
  "analytics.chartStat": "Stat",
  "analytics.totalTrips30d": "Total Trips (30d)",
  "analytics.avgOnTimeRate": "Avg On-time Rate",
  "analytics.fleetUtilization": "Fleet Utilization",
  "common.productName": "DeepTrack",
  "createOrg.title": "Create Organization / Project",
  "createOrg.inviteLater": "You can invite team members after setup",
  "common.productTagline": "Transport Management System",
  "invite.notFound": "Invite Not Found",
  "invite.goToLogin": "Go to Login",
  "invite.redirecting": "Redirecting to dashboard...",
  "invite.createAccount": "Create account to join",
  "invite.haveAccount": "Already have an account? Sign in",
  "onboarding.langThai": "Thai",
  "onboarding.langEnglish": "English",
  "onboarding.vehVan": "Van",
  "onboarding.vehBus40": "Bus (40 seats)",
  "onboarding.vehBus50": "Bus (50 seats)",
  "onboarding.vehTruck": "Truck",
  "roles.fleetManager": "Fleet Manager",
  "roles.opsManager": "Operations Manager",
  "roles.viewerReadOnly": "Viewer (read-only)",
  "onboarding.setupWizard": "Setup Wizard",
  "onboarding.orgInfo": "Organization Info",
  "onboarding.orgInfoHint": "Basic information about your transport company.",
  "onboarding.logoOptional": "Logo (Optional)",
  "onboarding.uploadLogo": "Upload logo (PNG, JPG, SVG)",
  "onboarding.timezone": "Timezone",
  "onboarding.language": "Language",
  "onboarding.depotLocation": "Depot Location",
  "onboarding.depotName": "Depot Name",
  "onboarding.mapPlaceholder": "Google Map will appear here",
  "onboarding.pinDepotHint": "Click to pin your depot location",
  "onboarding.addFirstVehicle": "Add First Vehicle",
  "onboarding.capacitySeats": "Capacity (seats)",
  "onboarding.inviteTeam": "Invite Your Team",
  "common.emailAddress": "Email Address",
  "common.skip": "Skip",
  "common.invite": "Invite",
  "register.otpSentTo": "OTP sent to",
  "selectOrg.createNew": "Create New Organization / Project",
  "createOrg.subtitle": "Create a workspace for your organization or project to get started with DeepTrack",
  "createOrg.nameLabel": "Name *",
  "createOrg.descLabel": "Description",
  "common.optional": "(optional)",
  "createOrg.submit": "Create & Continue Setup",
  "createOrg.failed": "Failed to create organization",
  "createOrg.slugRequired": "The name needs at least one Latin letter or digit — it is used to build the organization's slug",
  "selectOrg.subtitle": "Choose an organization or project to continue",
  "selectOrg.superAdminSubtitle": "Super admin access to every organization{total}",
  "selectOrg.noMatch": "No organizations match that search",
  "selectOrg.noneYet": "No organizations yet",
  "selectOrg.tryDifferent": "Try a different name.",
  "selectOrg.createBelow": "Create one below to get started.",
  "selectOrg.showMore": "Show more ({shown} of {total})",
  "onboarding.stepOf": "Step {step} of 4",
  "onboarding.vehicleHint": "Add your first vehicle. You can add more later in the Fleet section.",
  "onboarding.inviteHint": "Invite your first team member. You can add more later in Settings.",
  "onboarding.addAnotherMember": "+ Add another member",
  "onboarding.finishSetup": "Finish Setup",
  "onboarding.saveFailed": "Unable to save onboarding",
  "invite.acceptJoin": "Accept & Join",
  "invite.noToken": "Invalid invite link — no token provided.",
  "invite.invalid": "This invite is invalid or has expired.",
  "invite.loadFailed": "Could not load invite. Please try again.",
  "invite.acceptFailed": "Failed to accept invite.",
  "warroom.announcementsLoadFailed": "Failed to load announcements",
  "warroom.noChatRooms": "No chat rooms yet",
  "warroom.noMessages": "No messages yet",
  "warroom.readReceipt": "Read receipt",
  "common.remaining": "Remaining",
  "warroom.searchChats": "Search chats...",
  "warroom.typeMessage": "Type a message...",
  "warroom.sendMessage": "Send message",
  "warroom.announcements": "Announcements",
  "warroom.requests": "Requests",
  "warroom.upNext": "Up next",
  "common.mon": "Mon",
  "common.sun": "Sun",
  "warroom.rewind5m": "Rewind 5 min",
  "warroom.forward5m": "Skip forward 5 min",
  "warroom.statDone": "Done",
  "warroom.statRun": "Run",
  "warroom.statQueue": "Queue",
  "warroom.statUnassigned": "Unasgn",
  // ADR 0247 D7 — the FLEET header owns "Free" (free_now); this is "no work today".
  "warroom.statIdle": "No work",
  /* ADR 0251 — the SAME word the driver app shows for the same fact
     (`timeline.skipped` in apps/driver). A stop the bus drove past. */
  "warroom.stopSkipped": "Skipped",
  "warroom.statNoGps": "No-GPS",
  "safety.handoverLoadFailed": "Failed to load handover records",
  "safety.newInspection": "New Inspection",
  "safety.inspector": "Inspector",
  "status.investigating": "Investigating",
  "status.pendingAssessment": "Pending Assessment",
  "status.pendingDeduction": "Pending Deduction",
  "safety.damagedItems": "Damaged Items",
  "safety.missingItems": "Missing Items",
  "safety.totalDamageCost": "Total Damage Cost",
  "safety.evidencePhotos": "Evidence Photos",
  "safety.pendingInProgress": "Pending / In Progress",
  "safety.damagedReturns": "Damaged Returns",
  "safety.totalRecords": "Total Records",
  "safety.casesLoadFailed": "Failed to load safety cases",
  "status.hrReview": "HR Review",
  "status.warningIssued": "Warning Issued",
  "status.pendingESign": "Pending E-Sign",
  "status.pendingPayment": "Pending Payment",
  "safety.accident": "Accident",
  "safety.violation": "Violation",
  "safety.complaint": "Complaint",
  "safety.inspection": "Inspection",
  "safety.trafficViolation": "Traffic Violation",
  "safety.timeliness": "Timeliness",
  "safety.customerComplaint": "Customer Complaint",
  "safety.gpsTampering": "GPS Tampering",
  "safety.policyNonCompliance": "Policy Non-Compliance",
  "safety.nonSopCompliance": "Non-SOP Compliance",
  "safety.substanceAbuse": "Substance Abuse",
  "safety.offenseType": "Offense Type",
  "safety.incidentDate": "Incident Date",
  "safety.punishment": "Punishment",
  "safety.openCases": "Open Cases",
  "safety.criticalOpen": "Critical (Open)",
  "safety.accidentFreeDays": "Accident-Free Days",
  "safety.pendingAction": "Pending Action",
  "portal.cancelRate": "Cancel Rate",
  "portal.totalBookings": "Total Bookings",
  "portal.cancelledBookings": "Cancelled Bookings",
  "portal.noCancellations": "No cancellations",
  "common.reasonRequired": "Reason (required)",
  "ops.markAsNoShow": "Mark as No-Show",
  "ops.editBooking": "Edit Booking",
  "portal.financialsEditableHint": "Financials are editable only while the trip is in",
  "hr.driverCompensation": "Driver Compensation",
  "common.attached": "Attached",
  "ops.guest": "Guest",
  "ops.schedule": "Schedule",
  // ADR 0263 — tickets list plan→actual times + table status change
  "ops.noTicketsMatchFilters": "No job tickets match your filters",
  "ops.noTicketsFound": "No job tickets found",
  "ops.actualRunning": "Running",
  "ops.lateByMin": "+{m} min",
  "ops.markedBy": "by {name}",
  "ops.statusStaleRefresh": "This job's status already changed elsewhere — the row now shows the real status",
  "ops.cancelReasonLabel": "Cancel reason",
  "ops.noStopsShort": "No stops",
  "ops.newJobTicket": "New Job Ticket",
  "ops.searchTicketsAll": "Search: job#, job name, route, driver, plate, contract...",
  "ops.searchRouteTemplate": "Search routes…",
  "ops.searchVehicleShort": "Search vehicle...",
  "ops.searchDriverShort": "Search driver...",
  "common.demoData": "Demo data",
  "ops.activeTickets": "Active Job Tickets",
  "portal.adminOnly": "This portal is available to administrators only.",
  "portal.sessionExpired": "Your session has expired — please sign in again.",
  "portal.orgRequired": "This email has accounts in more than one organization — please sign in through your organization's portal link.",
  "portal.customerPortal": "Customer Portal",
  "settings.planFree": "Free",
  "settings.planStarter": "Starter",
  "settings.planPro": "Pro",
  "settings.planEnterprise": "Enterprise",
  // ADR 0394 — ฟีเจอร์ระดับองค์กร
  "settings.orgNameExample": "Acme Transport",
  "common.briefDescription": "Brief description (optional)",
  "settings.orgName": "Organization Name",
  "settings.yearEra": "Year shown",
  "settings.yearEra.ce": "CE",
  "settings.yearEra.be": "Buddhist era",
  "settings.yearEraHint": "Every Thai-language date of this organisation — web, driver app, customer portal, alerts and bill notes — switches at once. English screens stay CE. Not changed: document numbers (JOB-2026…, MT-2026…), dates in exported files, and Revenue Department forms (always Buddhist era).",
  "settings.dispatchPhone": "Main line for drivers",
  "settings.dispatchPhoneHint": "Shown in the driver app\u2019s \u201ccancel this job\u201d sheet, beside the creator\u2019s number",
  "settings.dispatchPhoneEmpty": "Not set — drivers will only see the creator\u2019s number",
  "settings.slugChangeHint": "Changing the slug will also update it in OAuth.",
  "settings.tenantId": "Tenant ID",
  "settings.logoUrl": "Logo URL",
  "settings.initials": "Initials (2 chars)",
  "settings.initialsColor": "Initials Color",
  "settings.primaryColor": "Primary Color",
  "settings.noRoleAssignments": "No TMS role assignments found",
  "common.savedSuccessfully": "Saved successfully",
  "settings.backToOrgs": "Back to Organizations",
  "settings.branding": "Branding",
  "nav.orgs": "Organizations",
  "settings.allPlans": "All Plans",
  "settings.noOrgsFound": "No organizations found",
  "settings.searchOrgs": "Search by name, slug, or ID…",
  "common.showDeleted": "Show deleted",
  "masterData.displayName": "Display name",
  "masterData.noCategories": "No categories found",
  "masterData.noDropdownYet": "No form dropdown uses this category yet",
  "masterData.manageOptions": "Manage this field's options in Master Data",
  "masterData.searchCategories": "Search categories...",
  "masterData.customFields": "Custom Fields",
  "common.icon": "Icon",
  "masterData.fillRequiredFields": "Please fill in all required custom fields.",
  "masterData.selectCategory": "Select a category",
  "masterData.chooseCategoryHint": "Choose a category on the left to view and manage its items.",
  "masterData.effective": "Effective",
  "common.default": "Default",
  "masterData.noItems": "No items yet",
  "common.reorder": "Reorder",
  "masterData.labelEn": "Label (EN)",
  "masterData.labelTh": "Label (TH)",
  "masterData.removeField": "Remove field",
  "masterData.customItemFields": "Custom Item Fields",
  "masterData.loadFailed": "Failed to load master data",
  "masterData.subtitle": "Controlled vocabularies used across forms throughout the app.",
  "settings.versionHistory": "Version History",
  "settings.testCalculate": "Test Calculate",
  "settings.variables": "Variables",
  "settings.formulaEngine": "Formula Engine",
  "settings.newFormulaSet": "New Formula Set",
  "settings.totalFormulaSets": "Total Formula Sets",
  "settings.totalFormulas": "Total Formulas",
  "settings.employeeTypes": "Employee Types",
  "settings.generateKey": "Generate Key",
  "settings.addWebhook": "Add Webhook",
  "status.revoked": "Revoked",
  "settings.apiKeys": "API Keys",
  "settings.webhooks": "Webhooks",
  "settings.notificationTemplates": "Notification Templates",
  "roles.dispatcher": "Dispatcher",
  "roles.viewer": "Viewer",
  "settings.closeInviteForm": "Close invite form",
  "settings.noMembers": "No members yet",
  "settings.noPendingInvites": "No pending invites",
  "settings.pendingInvites": "Pending Invites",
  "settings.team": "Team",
  "settings.idPatterns": "ID Patterns",
  "nav.integrations": "Integrations",
  "settings.features": "Features",
  "settings.editPermissions": "Edit Permissions",
  "settings.newRole": "New Role",
  "status.driverAccepted": "Driver Accepted",
  "settings.actAssign": "Assign",
  "settings.actAccept": "Accept",
  "settings.actUnassign": "Unassign",
  "settings.actStartTrip": "Start Trip",
  "settings.actComplete": "Complete",
  "settings.actReopen": "Reopen",
  "settings.stateId": "State ID",
  "settings.sla": "SLA",
  "settings.autoActions": "Auto-Actions",
  "settings.activeWorkflows": "Active Workflows",
  "settings.modulesCovered": "Modules Covered",
  "hr.quickPresets": "Quick presets",
  "hr.daysOfWeek": "Days of week",
  "hr.employeeType": "Employee type",
  "hr.rateSource": "Rate source",
  "hr.chargeType": "Charge type",
  "common.removeFilter": "Remove filter",
  "hr.payDecision": "Pay decision",
  "hr.lateOverMin": "Late > min",
  "hr.peopleVehicleRoute": "People · Vehicle · Route",
  "hr.tripTruth": "Trip truth",
  "hr.rateAndNet": "Rate & Net",
  "hr.decisionsPeriod": "Decisions & Period",
  "hr.checkinEvidence": "Check-in evidence",
  "hr.driverPayAdminOnly": "Driver Pay finance is visible to admins only.",
  "hr.noPayLines": "No pay lines match this filter",
  "hr.decide": "Decide",
  "hr.srcRoute": "Personal route rate",
  "hr.srcContract": "Personal contract rate",
  "hr.srcRouteBonus": "ROUTE BONUS",
  "hr.srcDefault": "Personal default rate",
  "hr.srcStandard": "Route's standard pay",
  "hr.srcManual": "Set on the ticket",
  "hr.totalPayable": "Total payable",
  "hr.tripsPayable": "Trips payable",
  "hr.excludedNotRun": "Excluded / not run",
  "hr.needsDecision": "Needs decision",
  "hr.deductionsApproved": "Deductions (approved)",
  "hr.partial": "Partial…",
  "hr.unknownProvince": "Unknown province",
  "hr.deleteProvinceRate": "Delete Province Rate",
  "hr.searchProvince": "Search province, label...",
  "hr.editRate": "Edit rate",
  "hr.deleteRate": "Delete rate",
  "hr.provinceRatesAdminOnly": "Province Rates is visible to admins only.",
  "hr.totalRates": "Total Rates",
  "hr.avgCompensation": "Avg Compensation",
  "common.highest": "Highest",
  "common.lowest": "Lowest",
  "hr.username": "Username",
  "hr.permissionRole": "Permission Role",
  "hr.roleSystemLevelOnly": "(system-level role — cannot be changed from an organization)",
  "hr.salaryBaseMonth": "Salary (base/month)",
  "common.thb": "THB",
  "status.trial": "Trial",
  "status.onLeave": "On Leave",
  "status.resigned": "Resigned",
  "status.terminated": "Terminated",
  "hr.licenses": "Driver's Licenses",
  "hr.payee": "Payee",
  "hr.behavior": "Behavior",
  "hr.moneyManagement": "Money Management",
  "hr.prefix": "Prefix…",
  "hr.male": "Male",
  "hr.female": "Female",
  "common.na": "N/A",
  "status.verified": "Verified",
  "hr.perTripPay": "Per-Trip Pay",
  "hr.compensationHistory": "Compensation History",
  "status.currentCaps": "CURRENT",
  "hr.noLeaveBalance": "No leave balance data",
  "hr.requestHistory": "Request History",
  "hr.noLeaveRequests": "No leave requests yet",
  "hr.disciplinaryScorecard": "Disciplinary Scorecard",
  "common.overall": "Overall",
  "hr.noBehaviorRecords": "No behavior records yet",
  "hr.employeeNotFound": "Employee not found",
  "hr.manageChargesHint": "Manage operational charges (fuel, tolls, deductions…) for this ticket",
  "hr.idCardAdminOnly": "ID card details are visible to HR admins only.",
  "hr.lifetime": "Lifetime",
  "common.override": "Override",
  "common.manage": "Manage",
  "hr.behaviorAdminOnly": "Behavior records are visible to HR admins only.",
  "hr.backToEmployees": "Back to employees",
  "hr.hireDate": "Hire Date",
  "hr.trialEnd": "Trial End",
  "hr.vehicleAssigned": "Vehicle Assigned",
  "hr.srcBonusLegacy": "BONUS (LEGACY)",
  "hr.payCutoffDay": "Pay Cutoff Day",
  "hr.payday": "Payday",
  "hr.otRate": "OT Rate",
  "hr.withholdingTax": "Withholding Tax",
  "hr.effectiveFrom": "Effective From",
  "hr.effectiveTo": "Effective To",
  "hr.lastChangeReason": "Last Change Reason",
  "hr.totalAllowances": "Total Allowances",
  "hr.totalDeductions": "Total Deductions",
  "hr.netEffectOnPay": "Net Effect on Pay",
  "hr.estMonthlyPay": "Est. Monthly Pay",
  "hr.bank": "Bank",
  "hr.accountName": "Account Name",
  "hr.accountNumber": "Account Number",
  "hr.taxId": "Tax ID",
  "hr.ssoNo": "Social Security No.",
  "status.warning": "Warning",
  "status.suspension": "Suspension",
  "status.commendation": "Commendation",
  "hr.documentsAcrossEmployees": "{n} documents across all employees",
  "hr.documentsLoadFailed": "Failed to load documents",
  "hr.noDocumentsMatch": "No documents match your filters",
  "hr.searchDocuments": "Search name, ID, document type, licence #...",
  "hr.verify": "Verify",
  "hr.noExpiry": "No Expiry",
  "status.valid": "Valid",
  "hr.totalDocuments": "Total Documents",
  "status.unverified": "Unverified",
  "hr.expiringSoon90d": "Expiring Soon (90d)",
  "hr.allDocuments": "All Documents",
  "hr.licensesOnly": "Licences Only",
  "hr.backToHr": "Back to HR",
  "hr.importFromCsv": "Import Employees from CSV",
  "hr.leaveLoadFailed": "Failed to load leave data",
  "common.prevMonth": "Previous month",
  "common.nextMonth": "Next month",
  "hr.rejectLeave": "Reject leave request",
  "hr.searchLeave": "Search name, ID, reason...",
  "hr.leaveSearchAll": "Searching every status (approved too)",
  "hr.noLeaveMatch": "No leave requests match your filters.",
  "status.pendingApproval": "Pending Approval",
  "hr.onLeaveToday": "On Leave Today",
  "hr.approvedThisMonth": "Approved (This Month)",
  "hr.totalEmployees": "Total Employees",
  "hr.leaveRequests": "Leave Requests",
  "hr.teamCalendar": "Team Calendar",
  "hr.drivers": "Drivers",
  "hr.trialPeriod": "Trial Period",
  "hr.licenseExpiring": "License Expiring",
  "status.applied": "Applied",
  "status.screening": "Screening",
  "status.onboarding": "Onboarding",
  "status.paused": "Paused",
  "status.filled": "Filled",
  "hr.partTime": "Part-time",
  "hr.positionType": "Position Type",
  "hr.resume": "Resume",
  "hr.interviewer": "Interviewer",
  "hr.score": "Score",
  "hr.salary": "Salary",
  "hr.openPostings": "Open Postings",
  "hr.totalPostings": "Total Postings",
  "hr.totalApplicants": "Total Applicants",
  "hr.payBasis": "Pay Basis",
  "hr.dailyWage": "Daily Wage",
  "hr.perTripRate": "Per Trip Rate",
  "hr.avgBaseSalary": "Avg Base Salary",
  "hr.totalMonthlyBase": "Total Monthly Base",
  "common.onThisPage": "On This Page",
  "hr.payrollLoadFailed": "Failed to load payroll data",
  "hr.income": "Income",
  "hr.gross": "Gross",
  "hr.netPay": "Net Pay",
  "hr.tripCountAnomaly": "Trip count unusually high compared to historical average",
  "hr.excelExport": "Excel Export",
  "hr.bankExport": "Bank Export",
  "hr.anomaliesOnly": "Anomalies Only",
  "hr.flags": "Flags",
  "hr.noPayTickets": "No pay tickets match your filters",
  "hr.searchPayTickets": "Search ticket#, employee...",
  "hr.submitForReview": "Submit for Review",
  "hr.submitForPayment": "Submit for Payment",
  "hr.markAsPaid": "Mark as Paid",
  "status.calculated": "Calculated",
  "status.paid": "Paid",
  "hr.phoneAllowance": "Phone Allowance",
  "hr.attendanceBonus": "Attendance Bonus",
  "hr.insuranceDeduction": "Insurance Deduction",
  "hr.gpsDeduction": "GPS Deduction",
  "hr.transferFee": "Transfer Fee",
  "hr.workDaysTripsOt": "Work Days / Trips / OT",
  "hr.totalNetPay": "Total Net Pay",
  "hr.totalGross": "Total Gross",
  "hr.tickets": "Tickets",
  "hr.anomalies": "Anomalies",
  "common.endAfterStart": "End must be after start.",
  "hr.noProvincesConfigured": "No provinces are configured. Add them in Master Data first.",
  "hr.noChargesForTicket": "No operational charges for this ticket",
  "hr.driverDefaultAnyTrip": "Driver default (any trip)",
  "hr.specificContract": "Specific contract",
  "fleet.tax": "Tax",
  "fleet.groupsItems": "Groups & Items",
  "fleet.runInspection": "Run Inspection",
  "fleet.template": "Template",
  "fleet.vehicle": "Vehicle",
  "fleet.templateNoItems": "This template has no items.",
  "common.requiredCaps": "REQUIRED",
  "fleet.answerRequired": "Answer required",
  "fleet.noAnswers": "No answers recorded.",
  "fleet.inspectionChecklist": "Vehicle Inspection Checklist",
  "fleet.inspectionSubtitle": "Build inspection templates and review submitted inspections",
  "fleet.checklistPurpose": "Purpose of this checklist...",
  "fleet.groupName": "Group name",
  "fleet.itemLabel": "Item label",
  "fleet.selectTemplate": "Select template...",
  "fleet.closeSignOff": "Close & sign off inspection?",
  "fleet.editTemplate": "Edit template",
  "fleet.deactivateTemplate": "Deactivate template",
  "common.deactivate": "Deactivate",
  "fleet.deactivateTemplateQ": "Deactivate template?",
  "fleet.noGroupsYet": "No groups yet. Add a group to start building the checklist.",
  "fleet.passFail": "Pass / Fail",
  "common.text": "Text",
  "common.number": "Number",
  "fleet.photoUrl": "Photo (URL)",
  "status.submitted": "Submitted",
  "fleet.closedBy": "Closed by",
  "fleet.templates": "Templates",
  "fleet.submissions": "Submissions",
  "common.pngJpg": "PNG / JPG",
  "fleet.financeType": "Finance Type",
  "fleet.lenderRequired": "Lender is required",
  "fleet.principal": "Principal (THB)",
  "fleet.monthlyPayment": "Monthly payment (THB)",
  "fleet.termMonths": "Term (months)",
  "fleet.paidInstallments": "Paid Installments",
  "fleet.installmentProgress": "Installment Progress",
  "fleet.installmentsExceedTerm": "Paid installments exceed the term",
  "common.startDateRequired": "Start date is required",
  "fleet.noFinanceRecords": "No finance records for this vehicle.",
  "fleet.maintenanceHistory": "Maintenance History",
  "fleet.noWorkOrdersVehicle": "No work orders for this vehicle.",
  "fleet.editTaxRecord": "Edit tax record",
  "fleet.deleteTaxRecord": "Delete tax record",
  "fleet.deleteTaxRecordQ": "Delete Tax Record?",
  "fleet.editFinanceRecord": "Edit finance record",
  "fleet.deleteFinanceRecord": "Delete finance record",
  "fleet.deleteFinanceRecordQ": "Delete Finance Record?",
  "fleet.firstRegistration": "First Registration",
  "fleet.front": "Front",
  "fleet.rear": "Rear",
  "fleet.left": "Left",
  "fleet.right": "Right",
  "fleet.interior": "Interior",
  "fleet.registrationBook": "Registration Book",
  "common.other": "Other",
  "status.open": "Open",
  "fleet.financeLoadFailed": "Failed to load finance data",
  "fleet.fleetFinance": "Fleet Finance",
  "fleet.fuelCostSummary": "Fuel cost summary per vehicle",
  "fleet.noVehiclesFound": "No vehicles found",
  "fleet.topFuelCosts": "Top Fuel Costs",
  "fleet.noFuelData": "No fuel data available",
  "fleet.fleetEfficiency": "Fleet Efficiency",
  "fleet.totalVehicles": "Total Vehicles",
  "fleet.avgEfficiency": "Avg Efficiency",
  "fleet.vehiclesTracked": "Vehicles Tracked",
  "fleet.totalContractCost": "Total Contract Cost (฿)",
  "fleet.expiring90d": "Expiring ≤90d",
  "fleet.importFromCsv": "Import Vehicles from CSV",
  "fleet.vehicleRequired": "Vehicle is required",
  "fleet.policyNumber": "Policy Number",
  "fleet.insurer": "Insurer / Company",
  "fleet.expiryDate": "Expiry Date",
  "fleet.insuranceLoadFailed": "Failed to load insurance data",
  "fleet.addPolicy": "Add Policy",
  "fleet.expiring90dShort": "Expiring (90d)",
  "fleet.noPolicies": "No insurance policies found",
  "fleet.searchInsurance": "Search vehicle, company, policy#...",
  "status.pendingRenewal": "Pending Renewal",
  "status.ok": "OK",
  "fleet.activePolicies": "Active Policies",
  "fleet.totalPremiumYr": "Total Premium/yr",
  "fleet.vehiclesInsured": "Vehicles Insured",
  "fleet.workflowTimeline": "Workflow Timeline",
  "fleet.woLoadFailed": "Failed to load work orders",
  "fleet.workOrders": "Work Orders",
  "fleet.noWorkOrdersMatch": "No work orders match your filters",
  "fleet.issueDetails": "Issue details...",
  "fleet.searchWorkOrders": "Search WO#, vehicle, description, shop...",
  "fleet.deleteWorkOrder": "Delete work order",
  "status.critical": "Critical",
  "status.high": "High",
  "status.medium": "Medium",
  "status.low": "Low",
  "fleet.totalWos": "Total WOs",
  "fleet.openWos": "Open WOs",
  "common.id": "ID",
  "fleet.plate": "Plate",
  "fleet.province": "Province",
  "fleet.age": "Age",
  "fleet.pmSchedule": "PM Schedule",
  "status.overdue": "Overdue",
  "status.dueSoon": "Due Soon",
  "fleet.totalScheduled": "Total Scheduled",
  "fleet.drvShort": "DRV",
  "fleet.doorShort": "DOOR",
  "fleet.seat": "Seat",
  "fleet.aisle": "Aisle",
  "fleet.door": "Door",
  "fleet.empty": "Empty",
  "fleet.wheelchair": "Wheelchair",
  "fleet.luggage": "Luggage",
  "ops.upcomingTrips": "Upcoming Trips",
  "ops.noUpcomingTrips": "No upcoming trips",
  "ops.recentAlerts": "Recent Alerts",
  "ops.noRecentAlerts": "No recent alerts",
  "fleet.status": "Fleet Status",
  "ops.bookingByRoute": "Booking by Route",
  "ops.noBookingData": "No booking data",
  "ops.tripsToday": "Trips Today",
  "fleet.vehiclesRunning": "Vehicles Running",
  "ops.onTimeRate": "On-time Rate",
  "ops.passengersBooked": "Passengers Booked",
  "ops.seatUtilization": "Seat Utilization",
  "status.running": "Running",
  "feedback.loadFailed": "Failed to load feedback",
  "feedback.rateTrip": "Rate Your Trip",
  "feedback.rating": "Rating",
  "feedback.comment": "Comment",
  "feedback.submitted": "Your feedback has been submitted.",
  "feedback.mine": "My Feedback",
  "common.resolve": "Resolve",
  "nav.feedback": "Feedback",
  "feedback.submitAnother": "Submit Another",
  "status.acknowledged": "Acknowledged",
  "status.resolved": "Resolved",
  "fuel.loadFailed": "Failed to load fuel data",
  "fuel.avgCostPerVehicle": "Avg Cost / Vehicle",
  "fuel.vehiclePerformance": "Vehicle Performance",
  "fleet.noVehiclesMatch": "No vehicles match your filters",
  "common.noRecordsMatch": "No records match your filters",
  "fuel.searchIdPlateDriver": "Fuel ID / plate / driver",
  "fleet.searchIdPlateType": "Search ID, plate, type...",
  "common.backToPage1": "Back to page 1",
  "fuel.pass": "Pass",
  "fuel.watch": "Watch",
  "fuel.belowStdShort": "Below Std",
  "fuel.fromNMeasured": "from {n} measured",
  "fuel.kmplSample": "{segments} segs · {km} km · {liters} L",
  "fuel.kmplNoEntries": "No fuel entries yet",
  "fuel.kmplTooFew": "Not enough data",
  "fuel.kmplNoOdometer": "No odometer yet",
  "fuel.kmplOutOfRange": "Out of range",
  "fuel.kmplRowsFlagged": "Liters look wrong",
  "fuel.kmplOdometerBackwards": "Odometer went backwards",
  "fuel.avgKmPerLiter": "Avg km/L (Fleet)",
  "fuel.belowStandard": "Below Standard",
  "fuel.efficiencyDashboard": "Efficiency Dashboard",
  "fuel.vehicleRecords": "Vehicle Records",
  "ops.loadingHistory": "Loading history...",
  "ops.noCompletedTrips": "No completed trips yet",
  "ops.departure": "Departure",
  "ops.routeHistory": "Route History",
  "legal.terms": "Terms",
  "legal.privacy": "Privacy",
  "legal.pdpa": "PDPA",
  "legal.rightsReserved": "All rights reserved.",
  "legal.privacyPolicy": "Privacy Policy",
  "legal.purposeEmails": "To send transactional emails (invoices, security alerts).",
  "legal.purposeReliability": "To improve service reliability and detect abuse.",
  "legal.purposeLegal": "To comply with legal obligations.",
  "legal.rightAccess": "Access the data we hold about you.",
  "legal.rightCorrect": "Request correction of inaccurate data.",
  "legal.rightExport": "Request data export in a portable format.",
  "legal.rightObject": "Object to or restrict processing.",
  "legal.rightWithdraw": "Withdraw consent at any time.",
  "legal.pdpaNotice": "PDPA Notice",
  "legal.termsOfService": "Terms of Service",
  "legal.prohibitUpload": "Upload illegal, harmful, or infringing content.",
  "legal.prohibitAccess": "Attempt to access data belonging to another Tenant.",
  "legal.prohibitProbe": "Interfere with or probe the Service's security mechanisms.",
  "legal.prohibitSpam": "Use the Service to send unsolicited communications.",
  "ops.startTime": "Start Time",
  "ops.expectedEnd": "Expected End",
  "common.totalRows": "Total rows",
  "common.row": "Row",
  "common.field": "Field",
  "common.message": "Message",
  "nav.notifications": "Notifications",
  "common.clearSelection": "Clear selection",
  "common.dismissNotification": "Dismiss notification",
  "common.noImage": "No image",
  "fuel.entryDetails": "Fuel Entry Details",
  "fuel.proofPhotos": "Proof Photos",
  "fuel.reportedLiters": "Reported litres",
  "fuel.reportedCost": "Reported amount",
  "fuel.approxPerLiter": "Per litre (approx.)",
  "fuel.reportedOdometer": "Reported odometer",
  /* ADR 0275 — event rail + map in the right pane of the fuel detail dialog */
  "fuel.timeline": "Timeline",
  "fuel.tlTotal": "Total {d}",
  "fuel.tlCreated": "Entry started",
  "fuel.tlSubmitted": "Submitted",
  "fuel.tlNoCoords": "No location",
  "fuel.tlNoCoordsAll": "No location on any step",
  "fuel.tlOpenMaps": "Open in Google Maps",
  "fuel.tlAccuracy": "Accuracy ±{m} m",
  "fuel.unitHour": "h",
  "fuel.unitMinute": "m",
  "fuel.unitSecond": "s",
  "fuel.sourceWebTest": "Web test",
  "fuel.sourceWeb": "Logged on web",
  "fuel.sourceDemoSeed": "Demo data",
  "fuel.reviewedBy": "Reviewed by",
  "fuel.rejectReason": "Reject reason",
  "fuel.vehicleStatusAtEntry": "Vehicle status at entry",
  "fuel.flagClockSkew": "Device clock off",
  "fuel.flagNoLocation": "No location",
  "fuel.flagOdometerBackwards": "Odometer went backwards",
  "fuel.flagVehicleNotActive": "Vehicle not in service",
  "fuel.locPermissionDenied": "No location — permission denied",
  "fuel.locUnavailable": "No location — unavailable",
  "fuel.locTimeout": "No location — timed out",
  "fuel.locNotRequested": "Location not requested",
  "fuel.confirmApproveTitle": "Approve this fuel entry?",
  "fuel.confirmApproveBody": "Approve the entry for {vehicle}.",
  "fuel.confirmSuspiciousTitle": "Mark this entry suspicious?",
  "fuel.confirmSuspiciousBody": "Hold the entry for {vehicle} as suspicious. It stays out of every total until someone approves it.",
  "fuel.confirmRejectTitle": "Reject this fuel entry?",
  "fuel.confirmRejectBody": "Reject the entry for {vehicle}.\nNote: {note}",
  "fuel.markSuspicious": "Mark suspicious",
  "fuel.reviewFailed": "Review failed",
  "fuel.noteFirst": "Write the note first — a rejection has to say why",
  "fuel.noteHint": "Note — required when rejecting, optional otherwise",
  "fuel.adjustedOnReview": "Adjusted on review",
  "fuel.approvedLiters": "Approved liters",
  "fuel.odometer": "Odometer (km)",
  "status.rejected": "Rejected",
  "status.suspicious": "Suspicious",
  "status.discarded": "Discarded",
  "fuel.logTest": "Log Fuel (Test)",
  "fuel.logFromWeb": "Log Fuel",
  "fuel.logFromWebHint": "Record a refuel from the office, with proof photos if you have them",
  "fuel.type": "Fuel Type",
  "fuel.payment": "Payment",
  "fuel.station": "Station",
  "fuel.driverWhoFilled": "Driver who filled",
  "fuel.driverDefaultsToBound": "Leave empty to use this vehicle's assigned driver",
  "fuel.proofGaugeBefore": "Gauge (before)",
  "fuel.proofGaugeAfter": "Gauge (after)",
  "fuel.proofPumpMeter": "Pump meter",
  "fuel.proofSlip": "Receipt / slip",
  "fuel.proofPhotosOptional": "Proof photos (optional)",
  "fleet.searchVehicleIdPlate": "Search vehicle (ID / plate)...",
  "fuel.cash": "Cash",
  "fuel.card": "Fuel Card",
  "fuel.bankTransfer": "Bank Transfer",
  "nav.portal": "Portal",
  "ops.activeContracts": "Active Contracts",
  "ops.totalContracts": "Total Contracts",
  "ops.totalCustomers": "Total Customers",
  "ops.deleteCustomer": "Delete Customer",
  "common.confirmDeleteShort": "Confirm delete",
  "common.lastUpdated": "Last updated",
  "ops.ovr": "OVR",
  "ops.bookingDashboard": "Booking Dashboard",
  "ops.totalBookings30d": "Total Bookings (30d)",
  "ops.loadingTicket": "Loading job ticket...",
  "hr.driverCompReadOnly": "Driver compensation (read-only)",
  "common.quickActions": "QUICK ACTIONS",
  "ops.plannedRoute": "Planned route",
  "ops.actualPathLive": "Actual path (live)",
  "ops.actualPathDone": "Actual path (finished)",
  "ops.arrivedStop": "Arrived stop",
  /* ADR 0253 — the fourth state, in the map legend. Same fact as
     `warroom.stopSkipped`; this one names a STOP in a key, that one labels a row. */
  "ops.skippedStop": "Skipped stop",
  "ops.pendingStop": "Pending stop",
  "ops.promise": "Promise",
  "ops.resetToTemplate": "Reset to Template",
  "ops.noActivityYet": "No activity recorded yet",
  "ops.refreshTimeline": "Refresh timeline",
  "status.invited": "Invited",
  "ops.searchNameSlug": "Search name or slug…",
  "ops.noPortalsYet": "No portals yet. Create the first one to get started.",
  "ops.startEndMustDiffer": "Start time must differ from end time",
  "ops.routeLockedHasTrips": "Route is locked once it has trips. Delete trips to change.",
  "ops.defaultVehiclesPerDay": "Default vehicles / day",
  "ops.noVehicleAssignedYet": "No vehicle assigned yet",
  "ops.newScheduleBoard": "New Schedule Board",
  "common.stats": "Stats",
  "ops.toggleDepots": "Toggle vehicle depots",
  "ops.editTrip": "Edit trip",
  "ops.clearAssignments": "Clear assignments",
  "ops.pickDates": "Pick dates",
  "common.jumpToToday": "Jump to today",
  "ops.quickAddThisMonth": "Quick add (this month)",
  "common.monFri": "Mon–Fri",
  "common.allMondays": "All Mondays",
  "common.tueThu": "Tue/Thu",
  "ops.clearMonth": "Clear month",
  "ops.fillRoute": "Fill route",
  "ops.optimizeSettings": "Optimize settings",
  "ops.optimizationMode": "Optimization mode",
  "ops.backhaulTuning": "Backhaul tuning",
  "ops.balanced": "Balanced",
  "ops.backhaul": "Backhaul",
  "ops.board": "Board",
  "ops.rechecking": "Rechecking",
  "ops.publishJobs": "Publish jobs",
  "ops.checkingBoard": "Checking the board…",
  "ops.readyPublishAgain": "Ready to publish again",
  "ops.publishSettings": "Publish settings",
  "ops.nothingCreated": "Nothing was created",
  "ops.publish": "Publish",
  "ops.renameTable": "Rename table",
  "ops.removeThisDate": "Remove this date",
  "ops.dateRoute": "Date / Route",
  // ADR 0202 follow-up — แถบหัวตารางของบอร์ด (TableSection)
  // หน่วยนับแยก เอกพจน์/พหูพจน์ เพราะอังกฤษเติม s ไทยไม่เติม — ตัวเลขยังเป็น <b> แยกต่างหาก
  "ops.saveAndContinue": "Save & continue",
  "ops.deleteTripBlockedByJobs": "This cell already has {n} job(s) — remove each vehicle first, which cancels its job",
  "ops.deleteTripForOn": "Delete trip for {route} on {date}?",
  "ops.unitDate": "date",
  "ops.unitDates": "dates",
  "ops.unitRoute": "route",
  "ops.unitRoutes": "routes",
  "ops.unitRun": "run",
  "ops.unitRuns": "runs",
  "ops.sortByTimeTip": "Sort columns by time",
  "ops.sortByRouteTip": "Group columns by route",
  "ops.sortByPairTip": "Group outbound/return route pairs together",
  "ops.clickToEdit": "click to edit",
  "ops.runFallback": "Run",
  "ops.emptyAddRouteFirst": "Add a route from the header, then pick dates",
  "ops.emptyNoDatesYet": "No dates yet — click + Add Date below to start",
  "ops.plusOneDay": "+1 day",
  "ops.searchAvailablePlateDriver": "Search available — plate / driver…",
  "ops.searchPlateOrDriver": "Search plate or driver…",
  "ops.timeConflictOverlap": "Time conflict — assigned to overlapping trips",
  "ops.removeFromPool": "Remove from pool",
  "ops.vehiclePoolTitle": "Vehicle Pool",
  "ops.addVehicleToPool": "Add vehicle to pool",
  "ops.addDriverTeamToPool": "Add driver team to pool",
  "ops.someDriversNoVehicle": "Some drivers have no vehicle",
  "status.expiringSoon": "Expiring Soon",
  "status.received": "Received",
  "status.returned": "Returned",
  "common.emailAddr": "Email",
  "ops.jobTimeline": "Job Timeline",
  "common.contact": "Contact",
  "common.noDocuments": "No documents uploaded",
  "status.voided": "VOIDED",
  "status.ended": "ENDED",
  "status.activeCaps": "ACTIVE",
  "hr.driverRates": "Driver Rates",
  "common.newestFirst": "Newest first",
  "common.oldestFirst": "Oldest first",
  "common.idAsc": "ID (low → high)",
  "common.idDesc": "ID (high → low)",
  "common.nameAsc": "Name (A → Z)",
  "common.nameDesc": "Name (Z → A)",
  "ops.noPassengersYet": "No passengers yet. Create the first one to get started.",
  "hr.initialPassword": "Initial password (optional)",
  "hr.initialPasswordHint": "Min 8 chars. Leave blank to send an invite email instead.",
  "status.ready": "Ready",
  "status.decommissioned": "Decommissioned",
  "ops.maxSeats": "Max seats",
  "ops.maxSeatsHint": "Total seats allowed.",
  "ops.seatModeHint": "Numbered or just a count.",
  "ops.capActionHint": "Action when the cap is hit.",
  "ops.allowDriverBoarding": "Allow driver to board passengers",
  "ops.allowQrCheckin": "Allow QR Check-in",
  "ops.gpsInterval": "GPS interval",
  // ADR 0515 D6 — shown under the locked GPS interval of a Locus ticket
  "ops.gpsIntervalLocusLocked": "Locus tickets use the GPS interval from the organization standby settings, so it is not set per ticket",
  "ops.deleteRouteWarning": "This route has {n} trip(s). Deleting will remove the column and all its trips.",
  "ops.deletePairWarning": "This route pair has {n} trip(s) combined. Deleting will remove both columns and all their trips.",
  "ops.removeDateWarning": "This date has {n} trip(s). Removing will also delete them.",
  "ops.clearAllWarning": "Remove all assigned vehicles from every cell on this board? Routes, dates, and required counts stay — only the vehicle assignments are cleared.",
  "ops.clearAll": "Clear all",
  "ops.removeAndDeleteTrips": "Remove & delete trips",
  "ops.boardNotFound": "Board not found",
  "ops.clearAllOnBoard": "Clear all vehicle assignments on this board",
  "ops.withdrawLastPublish": "Testing tool — withdraw the jobs the last publish created",
  "ops.undoLastAiApply": "Undo the last AI apply",
  "ops.undoAi": "Undo AI",
  "ops.clearAllAssignments": "Clear all assignments",
  "ops.removeDate": "Remove date",
  "ops.undoneNotEverything": "Undone, but not everything",
  "ops.undoAiPlan": "Undo AI plan",
  "ops.aiOptimizePlan": "AI optimize plan",
  "status.published": "Published",
  "common.rename": "Rename",
  "ops.duplicateBoard": "Duplicate board",
  "ops.newBoardName": "New board name",
  "ops.datesAndRouteCols": "Dates and route columns",
  "ops.vehiclePool": "Vehicle pool",
  "ops.minimapRunning": "Running {n}",
  "ops.minimapFree": "Free {n}",
  "ops.minimapWarned": "Warnings {n}",
  "ops.minimapPending": "Add {added} · Remove {removed}",
  "ops.minimapNoChange": "No changes yet",
  "ops.minimapTooManyOps": "{n} cells changed — {max} per save is the limit",
  "ops.minimapDragWillAdd": "Add to {n} cells",
  "ops.minimapDragWillRemove": "Remove from {n} cells",
  "ops.minimapClearPending": "Clear selection",
  "ops.minimapSelectAll": "Select all",
  "ops.minimapSelectAllTip": "Skips cells with a warning — add those by hand.",
  "ops.minimapSelectVacant": "Select vacant only",
  "ops.viewByTime": "Time",
  "ops.viewByRoute": "Route",
  "ops.viewByPair": "Pair",
  "ops.boardTabTable": "Table",
  "ops.boardTabStats": "Stats",
  "ops.boardTabs": "Board view",
  "common.checking": "Checking…",
  "ops.noMatchingVehicles": "No matching vehicles",
  "ops.noVehiclesAvailable": "No vehicles available",
  "ops.searchTeamName": "Search team name…",
  "ops.poolTeams": "Driver teams",
  "ops.poolTeamsPicked": "{n} selected",
  "ops.teamHasNoVehicles": "This team has no vehicles yet",
  "ops.poolTeamRemoveTitle": "Remove {name} from the pool",
  "ops.poolTeamWillDrop": "Removes {n} vehicles",
  "ops.poolTeamAssigned": "{v} are assigned to {c} cells — they will be unassigned too",
  "ops.poolTeamPublished": "{n} of those cells already sent jobs — tickets still draft or assigned will be cancelled automatically, others are left alone",
  "ops.poolRemoveCancelled": "cancelled {n} ticket(s)",
  "ops.poolRemoveLocked": "{n} ticket(s) left untouched — already in progress",
  "ops.poolSaved": "Vehicle pool saved",
  "ops.addingTeam": "Adding team…",
  "ops.vehiclePoolRuns": "{n} run(s)",
  "ops.statusFree": "Free",
  "ops.statusPartial": "Partial",
  "ops.statusBusy": "Busy",
  "ops.statusRepair": "Repair",
  "ops.statusConflict": "Conflict",
  "ops.cellSent": "Published",
  "ops.cellResend": "Edited",
  "ops.cellSentTitle": "Drivers have these jobs",
  "ops.cellResendTitle": "Changed since it was published — publish again",
  // ADR 0238 — dispatch board legend
  "ops.legendGroupCell": "In a cell",
  "ops.legendGroupColumn": "Column header (route)",
  "ops.legendGroupDate": "Row header (date)",
  "ops.legendCellSent": "Published to the driver",
  "ops.legendCellSentSub": "A real job ticket exists for this cell",
  "ops.legendCellResend": "Edited after publish",
  "ops.legendCellResendSub": "Needs a re-publish — the driver still has the old data",
  "ops.legendPlateGreen": "Green plate chip",
  "ops.legendPlateGreenSub": "This vehicle's ticket has been published",
  "ops.legendPlateAmber": "Amber plate chip",
  "ops.legendPlateAmberSub": "This vehicle hasn't been sent, even if others in the same cell have",
  "ops.legendPlateConflict": "Red-bordered plate chip",
  "ops.legendPlateConflictSub": "This vehicle is double-booked into an overlapping trip elsewhere",
  "ops.legendCountPending": "Gray count",
  "ops.legendCountPendingSub": "No vehicle selected yet",
  "ops.legendCountFilled": "Green count",
  "ops.legendCountFilledSub": "Matches the number needed — nothing wrong",
  "ops.legendCountBad": "Red count",
  "ops.legendCountBadSub": "Over the number needed, or a vehicle is time-conflicted",
  "ops.legendConflictBg": "Faint red cell background",
  "ops.legendConflictBgSub": "This trip overlaps another trip on the same vehicle",
  "ops.legendEmptyDash": "Em dash",
  "ops.legendEmptyDashSub": "No vehicle needed in this cell",
  "ops.legendColCheck": "Green checkmark",
  "ops.legendColCheckSub": "Every date in this column is published and matches current data",
  "ops.legendColAlert": "Amber alert circle",
  "ops.legendColAlertSub": "Some dates in this column are unpublished or edited since publish",
  "ops.legendColLock": "Lock",
  "ops.legendColLockSub": "This column has a published job ticket — route/times/vehicle-count and delete are frozen",
  "ops.legendPlusOneDay": "\"+1 day\" badge",
  "ops.legendPlusOneDaySub": "The trip crosses midnight, so the end time reads before the start time",
  "ops.legendGhostColumn": "Diagonal-hatch column",
  "ops.legendGhostColumnSub": "Flagged to expect a paired route, but the partner leg hasn't been added yet",
  "ops.legendDateLock": "Lock",
  "ops.legendDateLockSub": "This date has a published job ticket and can't be removed",
  "ops.tripTimeConflictTitle": "Time conflict — assigned to overlapping trips",
  "ops.optimizeRunning": "Optimizing…",
  "ops.optimizeRun": "Run Optimize",
  "ops.balancedDesc": "Spread work evenly across vehicles (default).",
  "ops.backhaulDesc": "Chain nearby trips to cut empty driving.",
  "ops.hideDepots": "Hide vehicle depots ({n})",
  "ops.showDepots": "Show vehicle depots ({n})",
  "ops.timelineBrushHint": "Drag the box to move it · drag an edge to resize · click outside to clear",
  "ops.timelineEndHint": "Click again to set the end of the range",
  "ops.timelineStartHint": "Click to set a start, then click again for the end",
  "ops.minimapDragOverCap": "Over the {max}-cell limit",
  "ops.minimapNeedsDatesAndRoutes": "This board has no dates or routes yet",
  "ops.minimapDateColumn": "Date",
  "ops.minimapOpenClashing": "Open the clashing job in a new tab",
  "ops.minimapChangedElsewhere": "Someone changed this board at {at} — refresh to see it",
  "ops.minimapLoadFailed": "Could not load the driver's schedule",
  "ops.minimapSaveFailed": "Save failed — no cell was changed",
  "ops.driverSchedule": "Driver schedule",
  "ops.confirmSaveTitle": "Confirm before saving",
  "ops.previewGroupUnassign": "Driver will be removed from the trip",
  "ops.previewGroupCancel": "Driver will be removed from the trip and the job cancelled",
  "ops.previewGroupLocked": "Driver's job cannot be cancelled",
  "ops.confirmWillCreate": "Will create {n} new job(s)",
  "ops.confirmWillReplace": "Will re-issue {n} job(s)",
  "ops.confirmWillCancel": "Will cancel {n} existing job(s)",
  "ops.confirmUntouchable": "{n} job(s) cannot be touched (driver accepted or already driving) — left alone",
  "ops.confirmBlockedConflict": "Something you are saving overlaps in time. Fix it first.",
  "ops.confirmBlockedError": "Too much to save at once — change fewer cells.",
  "ops.confirmAndSave": "Confirm",
  "ops.minimapConfirmFailed": "Save failed — nothing was changed",
  "ops.minimapBoardMoved": "The board changed while you were looking. Press save again to see the new result.",
  "ops.removeOrphanJobs": "Remove {n} job(s) whose column or date was deleted",
  "ops.backdatedJobsTitle": "{n} job(s) start in the past",
  "ops.backdatedJobsHelp": "Their scheduled start is already more than 30 minutes ago. Confirm to save them as already completed — a driver will not see them as an upcoming job.",
  "ops.backdatedJobsConfirm": "Confirm creating these {n} job(s) as backdated",
  "ops.nNewJobs": "{n} new job(s)",
  "ops.nBackdatedJobs": "{n} backdated",
  "ops.addToSlot": "Add to slot",
  // ADR 0248 — a vehicle just added to the pool but not yet saved can't be assigned to a cell yet
  "ops.assignNeedsPoolSave": "Save the vehicle pool first before assigning this vehicle.",
  // ADR 0278 D1 — a pool row is a person; these two states are theirs.
  "ops.memberNoVehicle": "No vehicle",
  "ops.memberHasNoVehicle": "This person has no vehicle bound right now, so they cannot be put on a cell.",
  "ops.memberInactive": "Inactive",
  "ops.cellBlockedPoolDirty": "Save the vehicle pool first before opening this cell.",
  "ops.poolEmpty": "No vehicles in this board's pool yet",
  "ops.poolEmptyHint": "No cell can be assigned until you add some — use Add vehicle above.",
  // ADR 0528
  "ops.poolOutsideDrivers": "{n} people on this board's cells are not in the vehicle pool",
  "ops.poolAddOutsideDrivers": "Add all {n} to the pool",
  "ops.scheduleNeedsPoolSave": "Save the vehicle pool first to open a driver's schedule",
  "ops.vehiclesPerCell": "Vehicles needed per cell",
  "ops.deleteBoard": "Delete board",
  "ops.totalBoards": "Total boards",
  "ops.totalTables": "Total tables",
  "ops.scheduleBoards": "Schedule Boards",
  "ops.searchBoards": "Search boards...",
  "ops.createFirstBoard": "Create your first board",
  "ops.editBoard": "Edit board",
  "ops.boardDescriptionMore": "Additional details",
  "ops.routesScopedToContract": "Only routes on this board's contract are listed — unlink the contract to see the rest.",
  "ops.contractLockedRefreshed": "Someone added a route to this board while you were editing — the contract can no longer be changed.",
  "ops.seedVehiclePool": "Add every driver-bound vehicle to the pool",
  "ops.seedVehiclePoolEmpty": "No driver-bound vehicles to add — this board's pool is empty, add them yourself.",
  "ops.boardCreatedAt": "Created",
  "ops.boardCreatedBy": "Created by",
  "ops.boardUpdatedBy": "Edited by",
  "ops.people": "People",
  "common.tools": "Tools",
  "ops.time": "Time",
  "ops.boardSaved": "Board saved",
  "ops.boardUpdatedAt": "Edited",
  "ops.boardUpdatedAtHint": "When the board's settings were last changed — assigning vehicles and publishing do not count.",
  "common.charsLeft": "{n} characters left",
  "ops.noBoardsMatchFilters": "No boards match the current filters",
  "ops.noBoardsYet": "No boards yet",
  "ops.copiesFrom": "Copies from {name}:",
  "ops.cellsStartUnassigned": "Cells start unassigned — vehicles and drivers are not copied.",
  "ops.deleteBoardConfirm": "Delete {name} and all its tables and trips? This cannot be undone.",
  "ops.boardCreateFailed": "Failed to create board",
  "ops.boardRenamed": "Renamed",
  "ops.boardRenameFailed": "Failed to rename",
  "ops.boardDuplicated": "Board duplicated",
  "ops.boardDuplicateFailed": "Failed to duplicate board",
  "ops.boardDeleted": "Board deleted",
  "ops.boardDeleteFailed": "Failed to delete board",
  "common.duplicating": "Duplicating…",
  "ops.routeNameExampleLong": "Bangkok → Ladkrabang (Morning)",
  "ops.activeRoutes": "Active Routes",
  "ops.totalDistance": "Total Distance",
  "ops.totalRoutes": "Total Routes",
  "ops.depot": "Depot",
  "ops.depotMarker": "D",
  "ops.routesApi": "Routes API",
  "ops.fleetVrp": "Fleet VRP",
  "ops.mockOffline": "Mock (offline)",
  "status.failed": "Failed",
  "status.processing": "Processing",
  "ops.searchPlateVehicleId": "Search plate / vehicle id...",
  "ops.avoidTolls": "Avoid tolls",
  "ops.avoidHighways": "Avoid highways",
  "ops.optimizerRuns": "Optimizer Runs",
  "common.when": "When",
  "ops.assignedDemand": "Assigned / Demand",
  "ops.fillRate": "Fill rate",
  "ops.poolTrimmed": "More vehicles in the pool than the optimizer takes — planned from {considered}, {trimmed} left out",
  "ops.expandMap": "Expand map",
  "ops.baselineDistanceAndCost": "Baseline distance and cost",
  "ops.baselineDistance": "Baseline distance",
  "ops.estimatedCost": "Estimated cost",
  "ops.plannedDistance": "Distance from plan",
  "ops.plannedDistanceHint": "Typed in to record it",
  "ops.googleDistance": "Distance from Google Map",
  "ops.googleDistanceHint": "Computed from the route drawn on the map",
  "ops.ticketDistance": "Distance from jobs",
  "ops.ticketDistanceHint": "Average of each job's planned distance; differs from above only for jobs whose stops were edited",
  "ops.plannedCost": "Estimated cost from plan",
  "ops.plannedCostHint": "Typed in to record it",
  "ops.actualCost": "Actual cost",
  "ops.actualCostHint": "Average of approved charges on finished jobs (fuel not included yet)",
  "ops.costVariance": "Variance",
  "ops.costVarianceHint": "Actual − estimated · positive means over plan",
  "ops.fromNJobs": "Average of {n} jobs",
  "ops.statsLoadFailed": "Could not load stats",
  "ops.showWholeRoute": "Show whole route",
  "ops.showWholeRouteHint": "Zoom out to show every stop on this route",
  "ops.showAllPins": "Show all pins", // ADR 0449 D6 — the shop dialog: branches, not a route
  "ops.showAllPinsHint": "Zoom out to see every pin on this map",
  "ops.pickStopLocation": "Pick the stop location",
  "ops.pickViaLocation": "Pick the via-point location",
  "ops.searchPlaceOrAddress": "Search a place or address...",
  "ops.unfilled": "Unfilled",
  "ops.optimize": "Optimize",
  "ops.pendingDatesTitle": "{n} date(s) not published yet",
  "ops.lockedByPrefix": "Locked by",
  "ops.demandMet": "Demand met",
  "ops.cells": "Cells",
  "ops.optimizeNeedsSetup": "Add a route, pick dates, and add vehicles to the pool first",
  "ops.generateAiPlan": "Generate an AI assignment plan",
  "ops.addRouteColumnFirst": "Add a route column first",
  "ops.addDateFirst": "Add a date first",
  "ops.createTicketsFromBoard": "Create job tickets from this board",
  "ops.undoStillLive": "Still live (a driver has them):",
  "ops.undoGone": "Gone for good (deleted by that publish):",
  "common.applying": "Applying…",
  "ops.applyPlan": "Apply plan",
  "ops.inProgressLower": "in progress",
  "ops.blockLockedByStatusTitle": "The cells you changed belong to jobs a driver already accepted",
  "ops.blockNoCellsTitle": "This board has no cells to publish",
  "ops.blockStaleTitle": "The board on screen is out of date",
  "ops.blockAllPublishedTitle": "Everything is already published",
  "ops.creatingJobs": "Creating {n} job(s)…",
  "ops.nothingOn": "Nothing on",
  "ops.needsPublishing": "needed publishing",
  "ops.theBoard": "the board",
  "ops.outcomeCreated": "Created",
  "ops.outcomeReplaced": "Replaced",
  "ops.outcomeRemoved": "Removed",
  "ops.conflicts": "Conflicts",
  "ops.vehiclesUsedIdle": "Vehicles used / idle",
  "ops.coverageDateRoute": "Coverage · date × route",
  "ops.noRoutesOrDates": "No routes or dates yet",
  "ops.demandVsAssigned": "Demand vs Assigned · by day",
  "ops.fillRateByRoute": "Fill rate · by route",
  "ops.vehicleUtilization": "Vehicle utilization",
  "ops.noTimeConflicts": "No time conflicts",
  "ops.allTripsStaffed": "All trips fully staffed",
  "ops.allDays": "All days",
  "ops.noVehiclesAssigned": "No vehicles assigned",
  "ops.qr": "QR",
  "ops.searchByNameIdEmail": "Search by name, ID, email...",
  "ops.passengerEmployee": "Passenger (employee)",
  "common.method": "Method",
  "roles.admin": "Admin",
  "ops.loadingPassenger": "Loading passenger...",
  "ops.passengerNotFound": "Passenger not found",
  "common.account": "Account",
  "ops.removePassengerQ": "Remove Passenger?",
  "hr.invitedAt": "Invited At",
  "hr.joinedAt": "Joined At",
  "hr.removeFromOrg": "Remove from organization",
  "status.upcoming": "Upcoming",
  "ops.searchBookingId": "Search booking ID…",
  "hr.profileName": "Profile Name",
  "hr.accountEmail": "Account Email",
  "hr.personalPhone": "Personal Phone",
  "hr.lastLogin": "Last Login",
  "hr.registered": "Registered",
  "hr.loginAccount": "Login Account",
  "hr.setTempPassword": "Set Temporary Password",
  "hr.tempPassword": "Temporary password",
  "common.removeAttribute": "Remove attribute",
  "portal.member": "Member",
  "portal.loading": "Loading portal...",
  "portal.notFound": "Portal not found",
  "portal.searchIdCustomer": "Search ID or customer…",
  "portal.unlinkContract": "Unlink contract",
  "portal.addContract": "Add contract",
  "portal.searchContracts": "Search contracts by ID or customer...",
  "portal.searchNameId": "Search name or ID…",
  "portal.addMember": "Add member",
  "portal.removeMemberConfirm": "Remove {name} from this portal? They lose access to it but stay a passenger of the organization.",
  /* ADR 0310 — create a new passenger from the add-member dialog */
  "portal.addHow": "How",
  "portal.addModeNew": "Create new",
  "portal.addModePick": "Pick existing",
  "portal.initialPassword": "Initial password",
  "portal.createAndAdd": "Create and add",
  "portal.dupUseExisting": "Use this one",
  "portal.addHalfDone": "{id} was created, but adding to the portal failed — press “{add}” again.",
  "portal.searchPassengers": "Search active passengers...",
  "portal.deletePortal": "Delete this portal",
  "portal.editPortal": "Edit portal",
  "portal.accessUrl": "Access URL",
  "portal.slugLabel": "Link name",
  "portal.openLink": "Open",
  "portal.openPortalNewTab": "Open portal in a new tab",
  "portal.slugHint": "Unique within your organization. Lowercase letters, numbers and hyphens.",
  "portal.scope": "Contracts & routes",
  "common.print": "Print",
  "portal.passengerQr": "Passenger QR",
  "portal.qrMint": "Mint QR",
  "portal.qrStatusScheduled": "Not started",
  "portal.qrWindowRangeBoth": "Usable from {from} 00:00 until {until} 23:59.",
  "portal.qrWindowRangeFrom": "Usable from {from} 00:00 onwards, with no expiry.",
  "portal.qrWindowRangeUntil": "Usable immediately, until {until} 23:59.",
  "portal.qrWindowHint": "Leave blank to start now, with no expiry.",
  "portal.qrWindow": "Valid",
  "portal.qrNoExpiry": "Never expires",
  "portal.qrWindowFrom": "From {from}",
  "portal.qrWindowUntil": "Until {until}",
  "portal.qrWindowBoth": "{from} – {until}",
  "portal.qrValidFrom": "Valid from",
  "portal.qrValidUntil": "Expires",
  "portal.qrExpiryInPast": "The expiry date cannot be in the past.",
  "portal.qrWindowOrderError": "The start date cannot be after the expiry date",
  "portal.qrRevealFailed": "This QR cannot be reopened — it may predate the stored image",
  "portal.qrDeadWarning": "This QR no longer works. You can open it to check which one it is, but do not print it.",
  "portal.qrScheduledNote": "This QR has not started yet — print it and put it up now; it will scan from the start date.",
  "portal.qrShortCodeGone": "The backup code cannot be recovered — mint a new QR if you need one.",
  "portal.qrView": "View QR",
  "portal.qrCannotReopen": "Cannot reopen",
  "portal.qrCannotReopenNote": "Only a fingerprint of the secret is stored, so the same QR cannot be drawn again — if it is lost, mint a new one and revoke this.",
  "portal.qrSearchName": "Search QR name…",
  "portal.qrNoneMatch": "No QR matches this search or filter",
  "portal.qrNoun": "QRs",
  "portal.qrNone": "No QR has been minted for this portal yet",
  "portal.qrLabel": "QR name",
  "portal.qrScans": "Scans",
  "portal.qrExpires": "Expires",
  "portal.qrCreated": "Created",
  "portal.qrUnlabelled": "(no label)",
  "portal.qrRevoke": "Revoke",
  "portal.qrRevokedByUnknown": "unknown user",
  "portal.qrRevokeTitle": "Revoke QR",
  "portal.qrRevokeConfirm": "Revoke “{label}”? Devices that already scanned it stop working on their next request, and this cannot be undone.",
  "portal.qrRevoked": "QR revoked",
  "portal.qrRevokeFailed": "Could not revoke the QR",
  "portal.qrMintFailed": "Could not mint the QR",
  "portal.qrStatusLive": "Live",
  "portal.qrStatusRevoked": "Revoked",
  "portal.qrStatusExpired": "Expired",
  "portal.qrMintedTitle": "Your new QR is ready",
  "portal.qrShownOnce": "Shown once only — the database keeps just a fingerprint of the secret and cannot read it back. Close this and you have to mint a new one.",
  "portal.qrShortCodeNote": "Backup code — print it under the QR for riders who cannot scan",
  "portal.qrDownloadSvg": "Download SVG",
  "portal.qrDownloadPng": "Download image",
  "portal.qrDownloadFailed": "Could not create the image file",
  "portal.qrDownloaded": "QR downloaded",
  "portal.qrPrintBlocked": "The browser blocked the print window — allow pop-ups and try again",
  "portal.qrSaved": "I've saved it",
  "ops.loadingOverflow": "Loading overflow data from bookings API…",
  "ops.overflowCommandCenter": "Overflow Command Center",
  "ops.totalOverflow": "Total Overflow",
  "ops.tripsAffected": "Trips Affected",
  "ops.stopNotFound": "Stop not found",
  "ops.vehicleNotFound": "Vehicle not found",
  "common.zoomIn": "Zoom in",
  "common.zoomOut": "Zoom out",
  "common.rotate": "Rotate",
  "ops.toggleMapLayers": "Toggle map layers",
  "common.legend": "Legend",
  "ops.overflowStop": "Overflow Stop",
  "ops.availableVehicle": "Available Vehicle",
  "ops.canDetour": "Can Detour",
  "ops.currentRoute": "Current Route",
  "ops.proposedRoute": "Proposed Route",
  "ops.aiSuggestions": "AI Suggestions",
  "ops.selectOverflowTrip": "Select an overflow trip to see suggestions",
  "common.best": "BEST",
  "ops.nearbyVehicles": "Nearby Vehicles",
  "ops.recurringPatterns": "Recurring Patterns & Capacity Planning",
  "ops.overflowFrequency": "Overflow Frequency (30 days)",
  "ops.capacityRecommendations": "Capacity Planning Recommendations",
  "ops.aiInsight": "AI Insight",
  "common.confirmAction": "Confirm Action",
  "ops.strategy": "Strategy",
  "ops.passengersAbsorbed": "Passengers Absorbed",
  "ops.additionalTime": "Additional Time",
  "ops.etaImpact": "ETA Impact (existing passengers)",
  "common.cost": "Cost",
  "ops.confirmAndDispatch": "Confirm & Dispatch",
  "ops.autoResolveConfig": "Auto-Resolve Configuration",
  "ops.enableAutoResolve": "Enable Auto-Resolve",
  "ops.autoResolveHint": "System resolves overflow automatically",
  "ops.maxAutoDispatch": "Max Auto-Dispatch Vehicles/Day",
  "ops.maxDetourMin": "Max Detour (minutes)",
  "ops.strategyPreference": "Strategy Preference",
  "ops.notifyAdmin": "Notify Admin",
  "ops.notifyPassengers": "Notify Passengers",
  "ops.saveConfiguration": "Save Configuration",
  "ops.statusChangedTo": "Status changed to",
  "ops.changeStatusFrom": "Change status from",
  "ops.changeStatusTo": "to",
  "ops.loadingRoute": "Loading route…",
  "ops.routeNotFound": "Route not found",
  "ops.manageStops": "Manage Stops",
  "ops.noStopsInTemplate": "No stops configured for this route template",
  "ops.latShort": "Lat",
  "ops.lngShort": "Lng",
  "ops.copyCoordinates": "Copy coordinates",
  "ops.limitShort": "Limit",
  "ops.speedKmhVal": "{n} km/h",
  "ops.radiusMVal": "{n} m",
  "ops.dwellMinVal": "{n} min",
  "ops.radiusShort": "Radius",
  "ops.dwellShort": "Dwell",
  "ops.dwellMin": "stop {n} min", // ADR 0476 D1
  "ops.copyCoords": "Copy coordinates", // ADR 0476 D2
  "ops.coordsCopied": "Coordinates copied",
  "common.copyFailed": "Copy failed",
  "ops.addViaAfterStop": "Add via point after this stop",
  "ops.editStop": "Edit stop",
  "ops.routeAssignments": "Route Assignments",
  "ops.estOperatingCost": "Est. Operating Cost",
  "ops.selectRoutePlaceholder": "Select a route…",
  "ops.pairedRouteFor": "Paired route ({type})",
  "ops.pairedRouteHint": "Set to the opposite type automatically · optional for a single-leg route",
  "ops.unpairOnSave": "Saving unpairs this route from {code}. The other route keeps its own job type.",
  "ops.searchDriver": "Search driver…",
  "ops.searchVehicle": "Search vehicle…",
  "ops.editRouteTemplate": "Edit Route",
  "ops.routeCodeExample": "A1-IN",
  "ops.routeNameExample": "Bangkok → Ladkrabang",
  "common.distance": "Distance",
  "common.duration": "Duration",
  "common.editing": "Editing",
  "common.new": "New",
  "common.modified": "Modified",
  "common.amounts": "Amounts",
  "hr.payAmountThb": "Amount to pay (THB)",
  "hr.payReason": "Reason",
  "hr.payReasonHint": "always shown in reports — auditable",
  "hr.payToDriver": "Pay to driver",
  "hr.deductFromDriver": "Deduct from driver",
  "hr.baseRatePerTrip": "Base Rate (per trip)",
  "hr.employee": "Employee",
  // ADR 0266 — internal/external wording, aligned with route + ticket rates
  "hr.companyDriver": "Internal Driver",
  "hr.contractor": "External Driver",
  "hr.officeStaff": "Office Staff",
  "hr.mechanic": "Mechanic",
  "hr.baseSalary": "Base Salary",
  "hr.socialSecurity": "Social Security",
  "hr.payType": "Pay Type",
  "hr.hasFile": "File Attached",
  "fleet.completedAt": "Completed",
  "fleet.costParts": "Parts Cost",
  "fleet.filterByVehicle": "Any vehicle",
  "common.km": "km",
  "fleet.deletePolicyFailed": "Could not delete the policy",
  "toast.policyDeleted": "Policy deleted",
  "toast.policySaved": "Policy saved",
  "fleet.uninsuredVehicles": "Uninsured vehicles",
  "fleet.noCmiVehicles": "Vehicles without CMI",
  "fleet.expiringSoon": "Expiring soon",
  "fleet.renewPolicy": "Renew (issue next term)",
  "fleet.deleteContract": "Delete GPS contract",
  "fleet.nContracts": "{n} contracts",
  "fleet.allVehiclesInsured": "Every active vehicle has cover in force",
  "fleet.allVehiclesHaveCmi": "Every active vehicle has CMI in force",
  "fleet.untrackedVehicles": "Vehicles without GPS",
  "fleet.allVehiclesTracked": "Every active vehicle has a GPS contract in force",
  "fleet.gpsContractsTitle": "GPS Contracts",
  "toast.gpsContractDeleted": "GPS contract deleted",
  "fleet.deleteGpsFailed": "Could not delete the GPS contract",
  "fleet.filterByShop": "Any garage",
  "fleet.overdueByDays": "{n} days late",
  "fleet.dueToday": "Due today",
  "fleet.dueInDays": "in {n} days",
  "fleet.noWorkOrdersYet": "No work orders yet — create one to get started",
  "fleet.overdueOnThisPage": "{n} overdue on this page",
  "fleet.noPoliciesYet": "No insurance policies yet",
  "fleet.noGpsYet": "No GPS contracts yet",
  "fleet.uninsuredCount": "Uninsured ({n})",
  "fleet.noCmiCount": "No CMI ({n})",
  "fleet.untrackedCount": "No GPS ({n})",
  "fleet.filterByProvider": "Any provider",
  "fleet.expiringWindow": "Expiring within",
  "fleet.daysN": "{n} days",
  "fleet.insCompulsory": "Compulsory (พ.ร.บ.)",
  "fleet.insVoluntary": "Voluntary",
  "fleet.insCargo": "Cargo",
  "fleet.billingQuarterly": "Quarterly",
  "fleet.billingYearly": "Yearly",
  "common.model": "Model",
  "fleet.costLabour": "Labour Cost",
  "fleet.costTotal": "Total Cost",
  "fleet.insuranceCoverage": "Coverage",
  "fleet.billingType": "Billing",
  "hr.docCategory": "Category",
  "hr.docKind": "Document Type",
  "hr.payTypeMonthly": "Monthly",
  "hr.payTypeDaily": "Daily",
  "hr.payTypePerTrip": "Per Trip",
  "hr.salaryConfigured": "Rules Set",
  "hr.salaryNotConfigured": "No rules yet",
  "hr.salaryNotSetShort": "not set",
  "hr.viewSalaryHistory": "Salary history",
  "hr.otMultiplier": "OT Multiplier",
  "common.effectiveFrom": "Effective From",
  "warroom.run": "Run",
  "warroom.queue": "Queue",
  "warroom.opMode": "OP Mode",
  "warroom.executive": "Executive",
  // ADR 0325 — the day picker.
  "warroom.dayLive": "Live",
  "warroom.dayLiveHint": "Live data, refreshing every 10 seconds",
  "warroom.dayFrozenHint": "Viewing a past day — auto-refresh is off and editing is disabled",
  "warroom.dayHistory": "Past days",
  "warroom.dayHistoryLimit": "{days} days of history",
  "warroom.dayJobCount": "{n} jobs",
  "warroom.dayNoJobs": "No jobs",
  "warroom.dayReadOnly": "Past day — read only",
  "warroom.playback": "Playback",

  "common.to": "To",

  // ── nav ────────────────────────────────────────────────────────────────
  "nav.group.overview": "Overview",
  "nav.group.operations": "Operations",
  "nav.group.monitoring": "Monitoring",
  "nav.group.fleet": "Fleet",
  "nav.group.people": "People",
  "nav.group.finance": "Finance",
  "nav.group.analytics": "Analytics",
  "nav.group.system": "System",
  "nav.group.apps": "Apps",
  "nav.group.admin": "Admin",

  "nav.dashboard": "Dashboard",
  "nav.customers": "Customers",
  "nav.contracts": "Contracts",
  "nav.routes": "Routes",
  "nav.routeOptimizer": "Route Optimizer",
  "nav.tickets": "Job Tickets",
  "nav.dispatch": "Schedule",
  "nav.booking": "Booking",
  "nav.passengers": "Passengers",
  "nav.portals": "Portals",
  "nav.overflow": "Overflow",
  "nav.tracking": "War Room",
  "nav.comms": "Communications",
  "nav.locusSync": "Locus pull", // ADR 0490 D1 · D10
  "nav.fleet": "Vehicles",
  "nav.maintenance": "Maintenance",
  "nav.checklist": "Inspections",
  "nav.insurance": "Insurance",
  "nav.gps": "GPS",
  "nav.fuel": "Fuel & Energy",
  "nav.finance": "Finance",
  "nav.seatMap": "Seat Map",
  "nav.fleetImport": "Import Vehicles",
  "nav.pm": "Preventive Maint.",
  "nav.hr": "Employees",
  "nav.driverGroups": "Driver Teams",
  "nav.recruitment": "Recruitment",
  "nav.leave": "Leave",
  "nav.salary": "Salary Rules",
  "nav.provinceRates": "Province Rates",
  /* ADR 0359 — per-org fuel station registry */
  "nav.fuelStations": "Fuel Stations",
  "fuelStation.title": "Fuel Stations",
  "fuelStation.subtitle": "{n} stations",
  "fuelStation.add": "Add station",
  "fuelStation.edit": "Edit station",
  "fuelStation.searchPlaceholder": "Search station name",
  "fuelStation.name": "Station name",
  "fuelStation.note": "Details",
  "fuelStation.lat": "Latitude",
  "fuelStation.lng": "Longitude",
  "fuelStation.coordHint": "Paste coordinates from Google Maps",
  "fuelStation.pickOnMap": "Pick on map",
  "fuelStation.emptyTitle": "No stations yet",
  "fuelStation.emptyCta": "Add the first station",
  "fuelStation.overlapWarning": "Overlaps {names}",
  "fuelStation.saved": "Saved",
  "fuelStation.deleted": "Deleted",
  "fuelStation.openInMaps": "Open in Google Maps",
  /* ADR 0360 — a person's home fuel stations */
  "hr.fuelStationsTab": "Fuel Stations",
  "hr.fuelStationsEmptyMeans": "No stations assigned",
  "hr.fuelStationsAdd": "Add station",
  "hr.fuelStationsAddN": "Add {n}",
  "hr.fuelStationsAddHint": "Pick several, then confirm once.",
  "hr.fuelStationsSelected": "{n} stations",
  "hr.fuelStationsAdded": "Added",
  "hr.fuelStationsRemoveTitle": "Remove from home stations",
  "hr.fuelStationsRemoveBody": "Remove {name} from this employee. The station stays in the register.",
  "hr.fuelStationsRemoveConfirm": "Remove",
  "hr.fuelStationsRemoved": "Removed",
  "hr.fuelStationsNoneRegistered": "No stations in the system yet",
  "fuel.flagOffStation": "Off home station",
  "fuel.stationMatched": "Home station",
  "fuel.stationNoHome": "No home station set",
  "fuel.stationNoLocation": "No location to check",
  "fuel.stationNearby": "Nearest: {name}",
  "fuel.stationNotSet": "Not set",
  "fuelStation.deleteTitle": "Delete this station",
  "fuelStation.deleteBody": "Remove {name} from the list",
  "nav.driverPay": "Driver Pay",
  "nav.documents": "Documents",
  "nav.hrImport": "Bulk Import",
  "nav.payroll": "Payroll",
  "nav.safety": "Safety & Cases",
  "nav.reports": "Reports",
  "nav.copilot": "AI Copilot",
  "nav.masterData": "Master Data",
  "nav.audit": "Audit Log",
  "nav.settings": "Settings",
  "nav.members": "Members",
  "nav.organizations": "Organizations",
  "nav.driverApp": "Driver App",
  "nav.passengerApp": "Passenger App",
  "nav.portalView": "Portal View",
  "nav.driverAppDesc": "WebView for drivers",
  "nav.passengerAppDesc": "WebView for passengers",
  "nav.portalViewDesc": "Customer / Factory portal",

  "nav.collapseSidebar": "Collapse sidebar",
  "nav.expandSidebar": "Expand sidebar",
  "nav.openMenu": "Open navigation menu",
  "nav.switchOrg": "Switch organization",
  "nav.createOrg": "Create Organization",
  "nav.logout": "Logout",
  // ADR 0266 — driver rate split by driver kind (internal = staff, external = partner)
  "ops.driverRateInternal": "Internal Driver Rate per Job (THB)",
  "ops.driverRateExternal": "External Driver Rate per Job (THB)",
  "ops.driverRateExternalSeeded": "Auto-filled from the internal rate — not reviewed yet",
  "ops.payCycleSection": "Pay cycle",
  "ops.payCycleCutoff": "Cutoff day",
  "ops.payCyclePayday": "Payday",
  "ops.payCycleOffset": "Paid in",
  "ops.payCycleNotSet": "No cycle",
  "ops.payCycleMonthEnd": "Month end",
  "ops.payCycleDay": "Day {day}",
  "ops.payCycleOffset0": "Same month as cutoff",
  "ops.payCycleOffset1": "Next month",
  "ops.payCycleOffset2": "Two months later",
  "ops.payCycleNext": "Next 3 cycles",
  "ops.payCycleWindow": "{start} – {end}",
  "ops.payCyclePaid": "paid {payout}",
  "ops.payCycleHint": "Trip pay and other money for people driving this contract is split into bills by this cycle.",
  "ops.payCycleUnsetHint": "No cycle yet — this contract's money waits in each person's “Accumulated” box until someone pays it out.",
  "ops.payCyclePaydayBeforeCutoff": "When paying in the same month, payday cannot come before the cutoff day.",
  "ops.payCycleSummary": "Cut {cutoff} · paid {payday} {offset}",
  "ops.routeSection": "Routes",
  "ops.routeCodePrefix": "Route Code Prefix",
  "ops.routeCodePrefixHint": "Seeds the code when a new route is created under this contract — CCA gives CCA001, CCA002 · A-Z, 0-9 and - only, up to 10 characters.",
  "ops.routeCodePrefixInvalid": "Use A-Z, 0-9 or - only, up to 10 characters.",
  "ops.routeCodeFromPrefix": "Filled from the contract prefix",
  "ops.editContract": "Edit Contract",
  "ops.createContract": "Create Contract",
  "ops.contractCreated": "Contract created",
  "ops.contractUpdated": "Contract updated",
  "ops.contractSaveFailed": "Failed to save contract",
  "files.uploadFailed": "Upload failed",
  "files.savedButSomeFailed": "Saved, but some files failed to upload",
  "files.unsupportedType": "Unsupported file type: {name}",
  "files.tooLarge": "File too large (max {mb}MB): {name}",
  "common.documentsCount": "Documents ({n})",
  "files.fileName": "File name",
  "files.uploadedAt": "Uploaded",
  "common.maxChars": "Max {n} characters",
  "common.creating": "Creating…",
  "ops.routeCodeInUse": "Route code \"{code}\" is already in use",
  "ops.routeCreated": "Route created",
  "ops.loadRoutesFailed": "Failed to load routes: {error}",
  "ops.pairedLegOnDetail": "Pair with the opposite leg on the detail page (optional).",
  "ops.noRoutesMatch": "No routes match the current filters.",
  // ADR 0256 — distance range filter on the routes table
  "ops.distanceRange": "Distance (km)",
  "ops.distanceFrom": "From",
  "ops.distanceTo": "To",
  "ops.distanceMinGtMax": "\"From\" must not be greater than \"To\"",
  "ops.distanceExcludesUnset": "Routes without a distance are excluded",
  "ops.noRouteTemplates": "No routes yet — click \"New Route\" to get started",
  "ops.createRouteTemplate": "Create Route",
  "ops.endFromRoute": "From the route — {drive} min driving + {service} min at stops",
  "ops.endRecalculated": "The route works out to {time}",
  "ops.useCalculatedEnd": "Use it",
  "ops.routeHasNoEstimate": "This route has no estimated duration yet — set the end time yourself.",
  "ops.useRouteEnd": "Use {time}",
  "ops.overnightTitle": "Overnight (+1d)",
  "ops.overnightDetail": "— ends {date} · ≈ {dur}",
  "ops.nextDay": "next day",
  "ops.durationHm": "{h}h {m}m",
  // ADR 0268 — list filters
  "common.tomorrow": "Tomorrow",
  "common.thisMonth": "This month",
  "common.clearFiltersCount": "Clear filters ({n})",
  "common.pageN": "Page {n}",
  "ops.customerContractActive": "Has active contract",
  "ops.customerContractInactive": "No active contract",
  "ops.customerContractExpiring": "Expiring soon",
  "ops.allJobTypes": "All types",
  "ops.noRoutesYet": "No routes yet — click New Route to get started",
  "ops.dateRangeExcludesHint": "Excludes tickets with no service date",
  "ops.datePickerTitle": "Pick a date range",
  // ADR 0270 — tier-2 filter popover / ADR 0271 — sorting
  "common.filtersCount": "Filters ({n})",
  "common.sortByCol": "Sort by {col}",
  "ops.customerFactory": "Company / plant",
  "ops.allVehicleTypes": "All vehicle types",
  "ops.completedVia": "Completed by",
  "ops.completedViaDriver": "Driver",
  "ops.completedViaAdmin": "Admin",
  "ops.cancelledViaLocus": "Cancelled in Locus", // ADR 0494 — cancel_source locus
  "ops.jobTypeOldTicketsHint": "Excludes older tickets with no job type",
  "ops.completedViaHint": "Counts jobs completed after this was introduced",
  // ADR 0272 — CSV export flow
  "common.exportConfirmTitle": "Export {n} rows?",
  "common.exportConfirmBody": "Everything matching the current filters, not just this page · Language: {lang}",
  "common.exportTooManyTitle": "{n} rows exceed the {max}-row limit",
  "common.exportTooManyBody": "Narrow the filters and try again",
  "common.exportEmpty": "No rows match the current filters",
  "common.exportCounting": "Counting…",
  "common.exporting": "Exporting {n} rows…",
  "common.exported": "Exported {n} rows",
  "common.exportFailed": "Export failed — please try again",
  "common.exportForbidden": "You are not allowed to export this data",
  "common.exportCsv": "Export CSV",
  "common.languageThai": "Thai",
  "common.languageEnglish": "English",
  // ADR 0273 — route / province / date-time filters
  "ops.dateTimeFilter": "Date / time",
  "ops.dateTimeTitle": "Date and time",
  "ops.dateFieldLabel": "Filter by",
  "ops.dateFieldService": "Service date",
  "ops.dateFieldAccept": "Accept by",
  "ops.dateFieldStart": "Start",
  "ops.dateFieldEnd": "End",
  "ops.dateFieldPlannedGroup": "Planned",
  "ops.dateFieldActualGroup": "Actual",
  "ops.hourNeedsTimeField": "The service date has no time",
  "ops.customerProvince": "Province (customer)",
  "ops.customerProvinceHint": "Province of the customer on the contract",
  "ops.filterGroupContractCustomer": "Contract / customer",
  "ops.filterGroupDriverVehicle": "Driver / vehicle",
  "ops.filterGroupType": "Type",
  "common.keyboardDialogHint": "Esc closes · Enter applies",
  "common.summary": "Summary",
  "common.selectRoute": "Select route",
  "common.selectCustomer": "Select customer",
  "common.allProvinces": "All provinces",
  "common.hourAbbr": "h",
  // ADR 0269 — duplicate route
  "ops.duplicateRoute": "Duplicate Route",
  "ops.newRouteName": "New route name",
  "ops.dupCopiesStops": "{n} stops with the map geometry",
  "ops.dupCopiesStopsVia": "{n} stops + {via} via points with the map geometry",
  "ops.dupCopiesMoney": "Driver rates, revenue and planned distance",
  "ops.dupCopiesCrew": "Default drivers and vehicles",
  "ops.dupCopiesPresets": "{n} time presets",
  "ops.dupFromVersion": "Plan taken from version {n}",
  "ops.dupCopiesHeader": "Copied",
  "ops.dupSkipsHeader": "Not copied",
  "ops.dupSkipDocs": "Documents{docs}",
  "ops.dupSkipPairing": "Outbound/return pairing",
  "ops.dupStartsDraft": "The copy starts as a draft",
  "ops.dupSkipsDocsCount": " ({n} files)",
  "ops.routeDuplicated": "Route duplicated",
  "ops.routeDuplicateFailed": "Could not duplicate the route",
  // ADR 0272 — export column headers
  "ops.activeContractsCount": "Active Contracts (count)",
  "ops.expiringContractsCount": "Expiring Soon (count)",
  "ops.endedContractsCount": "Ended (count)",
  "ops.contractTitle": "Contract Title",
  "ops.contractNo": "Contract No.",
  "ops.remainDaysColumn": "Days Remaining",
  "ops.driverCompensationThb": "Driver Compensation (THB)",
  "ops.compSource": "Compensation Source",
  "ops.compFromContract": "set on contract",
  "ops.compFromProvince": "from customer's province rate",
  "ops.routeCount": "Routes (count)",
  "ops.openReminders": "Open Reminders",
  "ops.durationMin": "Duration (min)",
  "ops.stopCount": "Stops (count)",
  "ops.firstStop": "First Stop",
  "ops.lastStop": "Last Stop",
  "ops.pairedRoute": "Paired Route",
  "ops.revenuePerJobThb": "Revenue per Job (THB)",
  "ops.estimatedCostThb": "Estimated Cost (THB)",
  // ADR 0272 D3 — tickets export (34 columns)
  "ops.jobName": "Job Name",
  "ops.driverName": "Driver Name",
  "ops.plannedStart": "Start (planned)",
  "ops.plannedAccept": "Accept by (planned)",
  "ops.plannedEnd": "End (expected)",
  "ops.actualAccept": "Accepted (actual)",
  "ops.actualStart": "Started (actual)",
  "ops.actualEnd": "Completed (actual)",
  "ops.arrivedStopCount": "Arrived (stops)",
  "ops.driverPayTotalThb": "Total Driver Pay (THB)",
  "ops.createdViaAdmin": "Created manually",
  "ops.createdViaPublish": "From schedule board",
  "ops.driverKind": "Driver Kind",

  // ── ADR 0274 — bulk edit of job tickets ──────────────────────────────────
  "bulk.openButton": "Bulk Edit",
  "bulk.openButtonCount": "Bulk Edit ({n})",
  "bulk.openDisabledTitle": "Select tickets first",
  "bulk.selectAllMatching": "Select all {n} matching the filter",
  "bulk.filterChangedCleared": "Filter changed — selection cleared",
  "bulk.rowCheckLabel": "Select ticket {id}",
  "bulk.titleCount": "Bulk Edit · {n} tickets",
  "bulk.step1": "Fill in",
  "bulk.step2": "Check",
  "bulk.step3": "Result",
  "bulk.groupStatus": "Status",
  "bulk.groupSchedule": "Schedule",
  "bulk.groupAssignment": "Assignment",
  "bulk.groupPayNotes": "Pay and notes",
  "bulk.groupJob": "Job",
  "bulk.statusChangeTo": "Change status to",
  "bulk.statusHint1": "Assigning needs driver, vehicle and start/end times",
  "bulk.statusHint2": "Choosing Cancel disables the other fields",
  "bulk.cancelReasonHint": "One reason for every ticket",
  "bulk.enableField": "Edit this field",
  "bulk.serviceDate": "Service date",
  "bulk.times": "Confirm / start / end time",
  "bulk.timesHint": "An empty box is left alone",
  "bulk.tripRate": "Trip rate (THB)",
  "bulk.tripRateHint": "Manual — a later driver change will not recompute it",
  "bulk.notesReplace": "Replace",
  "bulk.notesAppend": "Append",
  "bulk.check": "Check",
  "bulk.checking": "Checking…",
  "bulk.summary": "OK {ok} · Warn {warn} · Blocked {error} · Skipped {skipped}",
  "bulk.summaryNote": "Blocked / skipped tickets are not changed",
  "bulk.pillAll": "All",
  "bulk.pillOk": "OK",
  "bulk.pillWarn": "Warn",
  "bulk.pillError": "Blocked",
  "bulk.pillSkipped": "Skipped",
  "bulk.colContinue": "Proceed",
  "bulk.colId": "ID",
  "bulk.colResult": "Result",
  "bulk.colMessage": "Detail",
  "bulk.tagDone": "Updated",
  "bulk.tagFailed": "Failed",
  "bulk.willApply": "Will change: {fields}",
  "bulk.back": "‹ Back to edit",
  "bulk.confirm": "Apply to {n} tickets",
  "bulk.confirming": "Updating {n} tickets…",
  "bulk.atomicNote": "All passing tickets are updated together — if any one fails, none is changed",
  "bulk.noneDoable": "No ticket can be changed",
  "bulk.doneCount": "Updated {n} tickets",
  "bulk.rolledBack": "Nothing was changed — stopped at {id}: {message} · rolled back",
  "bulk.retryWithout": "Drop this ticket and retry",
  "bulk.tooMany": "At most {max} tickets per run — narrow the filter first",
  "bulk.forbidden": "Not allowed — org admin only",
  "bulk.checkFailed": "Check failed",
  "bulk.applyFailed": "Update failed",
  "bulk.err.reasonRequired": "Enter a cancel reason",
  "bulk.err.tooLong": "Longer than {max} characters",
  "bulk.err.nothingSelected": "No field selected to change",
  "bulk.err.dateRequired": "Pick a service date",
  "bulk.err.timeFormat": "Time must be HH:mm",
  "bulk.err.timesEmpty": "No time box is set",
  "bulk.err.driverRequired": "Pick a driver",
  "bulk.err.vehicleRequired": "Pick a vehicle",
  "bulk.err.tripRateInvalid": "Trip rate must be a number, 0 or more",
  "bulk.err.notesRequired": "Enter the note text",
  "bulk.field.driver_id": "Driver",
  "bulk.field.vehicle_id": "Vehicle",
  "bulk.field.date": "Service date",
  "bulk.field.settings": "Times",
  "bulk.field.accept_deadline": "Confirm time",
  "bulk.field.start_time": "Start time",
  "bulk.field.expected_end": "End time",
  "bulk.field.notes": "Notes",
  "bulk.field.compensation": "Pay",
  "bulk.field.status": "Status",
  "bulk.row.EDIT_WINDOW": "Driver / vehicle / date can only change before the driver accepts",
  "bulk.row.SETTINGS_WINDOW": "Times can only change before the driver accepts",
  "bulk.row.PERIOD_LOCKED": "The pay period for this month is closed — reopen it first",
  "bulk.row.NOT_AVAILABLE": "Driver / vehicle not available — clashes with another ticket",
  "bulk.row.NOT_AVAILABLE_IN_BATCH": "Driver / vehicle clashes with a ticket in this batch",
  "bulk.row.ASSIGN_INCOMPLETE": "Cannot assign — required fields missing",
  "bulk.row.CARGO_NOT_FINISHED": "Cargo not recorded yet — finish this job on its own page", // ADR 0465 D5 — left out of a bulk complete
  "bulk.row.TRANSITION": "Cannot change from {from} to {to}",
  "bulk.row.TRANSITION_GENERIC": "This status change is not allowed",
  "bulk.row.STALE": "Status changed while checking — reload",
  "bulk.row.INTERNAL": "System error — this ticket was not changed",
  "bulk.row.NOOP": "Nothing to change (already the same value / status)",
  "bulk.row.NOT_FOUND": "Ticket not found (deleted or not in this organisation)",
  "bulk.row.ABORTED": "Not reached — the batch stopped before this ticket",
  "bulk.row.ROLLED_BACK": "Changed then rolled back — another ticket in the batch failed",
  "bulk.warn.W1_TERMINAL_CANCEL": "A running / finished ticket will be cancelled (approved charges are kept)",
  "bulk.warn.W2_PAY_RESEED": "Driver change — pay is recomputed at the new driver's rate",
  "bulk.warn.W3_END_MISSING": "Start time set without an end time — cannot assign until it is filled",
  "bulk.warn.W4_FROM_BOARD": "Ticket comes from a board — the next publish will cancel / delete it and recreate it from the board; edit the board instead",
  "bulk.warn.W5_NOTIFY": "One summary email of the cancellation goes to the admins",
  "bulk.warn.W6_PAY_AFTER_DONE": "Ticket already finished / cancelled — computed pay will change; re-check the pay period",
  "bulk.warn.W7_PAY_MANUAL": "Pay is locked to the manual value (a later driver change will not recompute it)",
  "bulk.warn.W8_REVERT_DRAFT": "Removing driver / vehicle → the ticket goes back to draft",
  "bulk.warn.W9_ZERO_WINDOW": "Start time equals end time — a 0-minute window",  "bulk.contractOfSelection": "Contract of the selected tickets",
  "bulk.contractMixedHint": "Selected tickets must share one contract — tickets on another contract are reported in the check step",
  "bulk.err.gateRange": "Must be a whole number 0–{max}",
  "bulk.err.jobTypeRequired": "Pick a job type",
  "bulk.err.routeRequired": "Pick a route",
  "bulk.field.job_type": "Job type",
  "bulk.field.time_gates": "Time gates",
  "bulk.field.late_threshold_min": "Late threshold",
  "bulk.field.accept_open_before_min": "Accept opens before",
  "bulk.field.cancel_cutoff_before_min": "Cancel cutoff before",
  "bulk.field.province_rate": "Province rate",
  "bulk.field.province_rate_id": "Province rate",
  "bulk.field.route_template_id": "Route",
  "bulk.row.PROVINCE_RATE_NOT_FOUND": "The chosen province rate was not found",
  "bulk.tripRateMode": "How to change trip pay",
  "bulk.tripRateMode.set": "Set to",
  "bulk.tripRateMode.add": "Add",
  "bulk.tripRateMode.sub": "Take off",
  "bulk.row.PAY_BELOW_ZERO": "Trip pay would go below 0 — this ticket was not changed",
  "bulk.row.CONTRACT_MISMATCH": "This route does not belong to the ticket's contract",
  // ADR 0330 E1 — ทั้งสามรหัสนี้ server ส่งมานานแล้วแต่ไม่เคยมีคำแปล
  // `t()` echo คีย์กลับมาเมื่อไม่พบ และค่านั้น truthy ตัว fallback จึงไม่ทำงาน
  // ผลคือจอโชว์สตริงดิบ "bulk.row.ROUTE_NOT_USABLE" ให้ผู้ใช้เห็น
  "bulk.row.ROUTE_TEMPLATE_NOT_FOUND": "Route not found — it may have been deleted",
  "bulk.row.ROUTE_NOT_USABLE": "Route is not active — activate it on the route page first",
  "bulk.row.DRIVER_ON_LEAVE": "Driver is on approved leave on this date",
  "bulk.row.DRIVER_NOT_EMPLOYED": "The driver has left, or has not started yet, at the time of this job",
  "bulk.warn.W10_ROUTE_RESNAPSHOT": "Stops edited on this ticket alone are replaced by the new route's stops",  "bulk.err.statusRequired": "Pick a status",
  "bulk.overCap": "At most {max} tickets per run — {n} selected now; narrow the filter or deselect some first",
  "bulk.transitionReason.accepted": "the driver has accepted",
  "bulk.transitionReason.started": "the job has started",
  "bulk.transitionReason.done": "the job is finished",
  "bulk.detail.driverAndVehicle": "Driver and vehicle",
  "bulk.detail.clash": "{who} clashes with {id}",
  "bulk.detail.clashWith": "clashes with {id}",
  "bulk.detail.period": "period {period}",
  "bulk.detail.missing": "missing {fields}",  "bulk.selectedShort": "{n} selected",
  "bulk.allShort": "All {n}",
  "bulk.overCapShort": "Max {max} tickets",  "bulk.tagExcluded": "Dropped",
  "bulk.row.EXCLUDED": "Dropped from this batch — the ticket that failed the previous run",

  /* ── Identity documents: national ID card + driver's licence ── */
  "hr.expiry.daysLeft": "{n} days left",
  "hr.expiry.today": "Expires today",
  "hr.expiry.expiredDays": "Expired {n} days ago",
  "hr.expiry.expiredToday": "Expired today",
  "hr.expiry.none": "No data",
  "hr.expiry.lifetime": "Lifetime",
  "hr.expiry.ok": "Valid",
  "hr.idCardColumn": "ID Card",
  "hr.filterLicenseStatus": "Licence",
  "hr.filterIdCardStatus": "ID Card",
  "hr.filterAll": "All",
  "hr.filterExpired": "Expired",
  "hr.filterDue30": "Due in \u2264 30 days",
  "hr.filterDue90": "Due in \u2264 90 days",
  "hr.filterNormal": "Valid",
  "hr.filterNoLicense": "No licence on file",
  "hr.filterNoIdCard": "No ID card on file",
  "hr.filterLifetime": "Lifetime card",
  "hr.docsIncomplete": "Documents incomplete",
  "hr.docsIncompleteHint": "Missing an ID card image, or a driver missing a licence image",
  "hr.kpiLicenseExpired": "Licence expired",
  "hr.kpiDocsIncomplete": "Docs incomplete",
  "hr.watermarkFor": "For {org} use only",
  "hr.noImageYet": "No image yet",
  "hr.attachImage": "Attach image",
  "hr.replaceImage": "Choose a new file to replace",
  "hr.idCardImage": "ID card image",
  "hr.licenseImage": "Licence image",
  "hr.imageRequired": "Image required",
  "hr.imageOnlyHint": "Images only (JPG, PNG, WebP, HEIC) \u00b7 max {mb} MB",
  "hr.viewFullSize": "Click to enlarge",
  "hr.closeViewer": "Close",
  "hr.showIdNumber": "Show ID number",
  "hr.hideIdNumber": "Hide ID number",
  "hr.licenseType": "Licence type",
  "hr.addLicenseTitle": "Add licence",
  "hr.editLicenseTitle": "Edit licence",
  "hr.addIdCardTitle": "Add ID card",
  "hr.editIdCardTitle": "Edit ID card",
  "hr.idNumberLabel": "ID number (13 digits)",
  "hr.idNumberInvalid": "ID number must be exactly 13 digits",
  "hr.expiryBeforeIssue": "Expiry date must be on or after the issue date",
  "hr.blankMeansLifetime": "Leave blank = lifetime",
  "hr.saving": "Saving\u2026",
  "hr.fileTooLarge": "File too large (max {mb} MB)",
  "hr.fileTypeUnsupported": "Unsupported file type \u2014 accepted: {types}",
  "hr.licenseDeleteBody": "Licence \"{name}\" will be permanently removed.",
  "hr.docsMissingBanner": "This employee still has incomplete documents",
  "hr.docsMissingIdCard": "No ID card image yet",
  "hr.docsMissingLicense": "No licence image yet",
  "hr.goToTab": "Go to tab",
  "hr.idCardRequiredHint": "Required for every position",
  "hr.licenseRequiredHint": "Required only for driver positions",
  "hr.deleteImage": "Delete image",
  "hr.deleteImageQ": "Delete this document image?",
  "hr.deleteImageBody": "The image is permanently removed and cannot be restored. The rest of this document's details stay as they are.",
  "hr.deleteImageIncomplete": "After deleting, this employee counts as \"documents incomplete\" until a new image is attached.",
  "toast.imageDeleted": "Image deleted",
  // ADR 0279 — version of a route
  "ops.versionShowing": "Showing route",
  "ops.versionN": "Version {n}",
  "ops.versionOriginal": "Version {n} (original)",
  "ops.versionDraft": "Draft",
  "ops.versionActive": "In use",
  "ops.versionStopped": "Stopped",
  "ops.versionExpired": "Window ended",
  "ops.versionInactive": "No longer in use",
  "ops.versionArchived": "Archived",
  "ops.versionBaseline": "Original",
  "ops.versionEffectiveNow": "In effect",
  "ops.versionNoWindow": "No effective window yet",
  "ops.versionNoEnd": "no end date",
  "ops.versionBaselineWindow": "Used when no version is in effect",
  "ops.versionMenuHint": "Draft first, then newest to oldest",
  "ops.versionCreate": "New version",
  "ops.versionDiscard": "Discard draft",
  "ops.versionActivate": "Put this version in use",
  "ops.versionStop": "Stop using this version",
  "ops.versionActivateTitle": "Put version {n} in use",
  "ops.versionStopTitle": "Stop using version {n}",
  "ops.versionStopConfirm": "Stop version {n}",
  "ops.versionFrom": "Effective from",
  "ops.versionTo": "Effective until",
  "ops.versionStopAt": "Effective until",
  "ops.versionNoEndHint": "Leave empty = stays in use until stopped, or until a newer version covers it",
  "ops.versionApplyWhat": "What to change on the selected jobs",
  "ops.versionApplyCeilingHint": "Each job changes only as far as its status allows; jobs already under way can change money only",
  "ops.versionFlagPlan": "Route / stops",
  "ops.versionFlagMoney": "Money",
  "ops.versionFlagUnavailable": "No job in this list can change that",
  "ops.versionAffectedCount": "Jobs starting in this window: {n}",
  "ops.versionSelectedCount": "{n} selected",
  "ops.versionSelectAll": "Select all editable",
  "ops.versionNoAffected": "No jobs start in this window",
  "ops.versionRowMoneyOnly": "Money only",
  "ops.duplicateThisVersion": "Duplicate this version",
  "ops.dupCopyFrom": "Copy content from",
  "ops.dupFromVersionLine": "The copy gets **the stops and assignment of version {n}** — not the original's",
  "ops.dupFromBaselineLine": "The copy gets the original's stops and assignment",
  "ops.versionPickJobsTitle": "Pick the jobs to change",
  "ops.versionPickJobsStopTitle": "Pick the jobs to roll back",
  "ops.versionNeedStart": "Pick a start date first",
  "ops.versionEndBeforeStart": "The end must be after the start — with no time set, it counts as 00:00 that day",
  "ops.versionCreateDraftFailed": "Could not create the draft version",
  "ops.versionDiscardFailed": "Could not discard the draft",
  "ops.versionScanFailed": "Could not load the job list",
  "ops.versionSaveFailed": "Could not save",
  "ops.compFromDraft": "Draft version {n} · pay edited here takes effect only once the version is activated",
  "ops.compFromBaseline": "Pay from the original (version {n}) · applies to dates outside every version's period",
  "ops.compFromVersion": "Pay from version {n} · effective {from}",
  "ops.compDraftHint": "When you activate this version you set the effective date and choose whether to update jobs already created.",
  "ops.compLockedHint": "Pay that is already set can't be edited here. Every change needs an effective date and a history, so change it through a new version.",
  "ops.compEffectiveNow": "The system is currently using the pay of version {n}",
  "ops.compChangeViaVersion": "Create a new version to change the pay",
  "ops.compGoDraft": "Go to draft version {n}",
  "err.route.compViaVersion": "Pay that is already set can't be changed directly. Create a new version and set the date it starts.",
  "err.route.compPairRequired": "Fill in both internal and external driver pay first",
  /* ADR 0285 — routes that are not active yet */
  "err.route.notUsable": "This route is not active yet — activate it on the route page before creating work",
  /* ADR 0286 — cannot create a version yet */
  "err.route.incompleteForVersion": "This route is not complete — it needs stops and driver pay before a version can be created",
  "ops.incompleteTitle": "Cannot create a version yet",
  "ops.incompleteIntroBaseline": "Complete the original route before creating a version.",
  "ops.incompleteIntroSource": "Version {n}, the one being copied, is incomplete",
  "ops.incompleteNeedStops": "Stops",
  "ops.incompleteNeedCompInternal": "Internal driver pay",
  "ops.incompleteNeedCompExternal": "External driver pay",
  "ops.incompleteGoFixStops": "Add stops",
  "ops.incompleteGoFixComp": "Fill in driver pay",
  "ops.blockRouteNotUsableTitle": "{n} cells point at a route that is not active",
  "ops.blockRouteNotUsableNote": "A draft or archived route cannot become work. Activate the route on the route page, or remove that column from the board.",
  "err.version.locked": "This version is already active and cannot be edited — create the next draft instead",
  "err.version.draftExists": "This route already has draft version {n}. Edit that draft instead of creating another",
  "err.version.notDraft": "Only a draft can be activated — this one is no longer a draft. Reload the page",
  "err.version.notActive": "Only an active version can do this",
  "err.version.empty": "This version has no stops yet — add stops before activating",
  "err.version.stillEffective": "This version is currently in effect — stop it before archiving",
  "err.version.identityField": "That field belongs to the route, not to a version — edit it on the original",
  "err.version.tooManyTickets": "At most {n} jobs per request — deselect some or do it in batches",
  "ops.versionFlagPlanHint": "Rewrites the job's stops, order and distance, and clears recorded stop arrivals",
  "ops.versionFlagMoneyHint": "Driver pay (internal/external), revenue per trip, and estimated operating cost",
  "ops.versionSearchPlaceholder": "Search job number",
  "ops.versionFilterFull": "Fully editable",
  "ops.versionFilterMoneyOnly": "Money only",
  "ops.versionFilterBlocked": "Not editable",
  "ops.versionFilterAria": "Filter by what can change",
  "ops.versionColJob": "Job number",
  "ops.versionColStart": "Starts",
  "ops.versionColStatus": "Status",
  "ops.versionColResult": "Will change",
  "ops.versionSelectAllAria": "Select every matching job (all pages)",
  "ops.versionSelectPageAria": "Select every editable job on this page",
  "ops.versionSelectAllMatching": "Select all matching ({n})",
  "ops.versionSelectAllTooMany": "Select all matching ({n}) — over the {max} limit",
  /* ADR 0291 — applying a version to many jobs, one chunk at a time */

  /* ADR 0292 — bulk edit, one chunk at a time */
  "bulk.runNotice": "Applied in chunks of {chunk} (~{min} min) — chunks already applied are not rolled back if a later one fails",
  "bulk.runHalted": "Paused · {done} done · {n} left (what was applied stays applied)",
  "bulk.runSkipAndContinue": "Skip this job and continue",
  "bulk.runUnfinished": "An unfinished bulk edit — {done} of {total} jobs done",
  "ops.applyRunProgress": "{done} of {total} jobs done",
  "ops.applyRunRemaining": "about {min} min left",
  "ops.applyRunStop": "Stop",
  "ops.applyRunBreakdown": "plan+money {full} · money only {money} · blocked {blocked}",
  "ops.applyRunKeepOpen": "Keep this page open until it finishes — you can close it, the run pauses here and can be continued.",
  "ops.applyRunHalted": "Paused · {n} jobs left",
  "ops.applyRunResume": "Continue",
  "ops.applyRunCloseJob": "Close this run",
  "ops.applyRunEta": "(~{min} min)",
  "ops.applyRunUnfinished": "An unfinished run for version {v} — {done} of {total} jobs done",
  "ops.applyRunCode.ROUTE_VERSION_NOT_ACTIVE": "This version was stopped while the run was going",
  "ops.applyRunCode.ROUTE_NOT_USABLE": "This route can no longer be used for work",
  "ops.applyRunCode.APPLY_RUN_NOT_RUNNING": "This run was already closed",
  "ops.applyRunCode.RETRIES_EXHAUSTED": "The network or server failed repeatedly",
  "ops.applyRunCode.STOPPED_BY_USER": "You stopped it",

  "ops.versionOverCap": "{n} selected, over the limit of {max} per request — deselect some or do it in batches",
  "ops.versionTruncated": "Too many jobs in this window to list them all (over {n}) — narrow the window or filter",
  "ops.versionAffectedMoreThan": "Jobs starting in this window · more than {n}",
  "ops.versionNoMatch": "No jobs match this search",
  "ops.versionResultTitle": "Apply result",
  "ops.versionResultOnlyFailed": "Only the jobs that did not go through are listed",
  "ops.versionRolledBack": "Nothing was changed — the whole batch runs in one transaction, so one failure rolls back all of it",
  "ops.versionCode.MONEY_ONLY": "Money only (job already under way)",
  "ops.versionCode.OUT_OF_WINDOW": "Job falls outside this version's effective window",
  "ops.versionCode.UNKNOWN_STATUS": "The system does not recognise this job status",
  "ops.versionCode.NOT_FOUND": "Job not found (it may have been deleted meanwhile)",
  "ops.versionCode.NOOP": "Nothing to change",
  "ops.versionBookingWarning": "{n} seat bookings will lose their pickup/dropoff point — those stops are replaced by this version.",
  "ops.versionRowBookings": "· {n} bookings",
  "ops.versionRowLocked": "Cannot change, settlement period closed",
  "ops.versionRowBlocked": "Cannot change",
  "ops.versionRowNothing": "Nothing changes",
  "ops.versionActivateOnly": "Put in use only",
  "ops.versionStopOnly": "Stop only",
  "ops.versionSubmitApply": "Save + update selected jobs ({n})",
  "ops.versionLockedTitle": "Version {n} is in use",
  "ops.versionLockedWhy": "A version that is in use cannot be edited: jobs already created reference it.",
  "ops.versionLockedAsk": "Create draft version {n} by copying the route from version {from} and edit that instead? A draft affects no job until you put it in use.",
  "ops.versionLockedCreate": "Create draft version {n}",
  "ops.versionDraftBanner": "Editing draft version {n}",
  "ops.versionDraftBannerCopiedBaseline": "Copied from the original. Stops and assignments were copied; frequently used times and documents are shared with the original.",
  "ops.versionDraftBannerCopied": "Copied from version {n}. Stops and assignments were copied; frequently used times and documents are shared with the original.",
  "ops.versionDraftBannerNote": "Affects no job until you put it in use",
  "ops.versionSharedAcrossVersions": "Shared across all versions",
  "ops.versionIdentityHint": "Job type and paired route belong to the route, not to a version",
  "toast.versionDraftCreated": "Draft version {n} created",
  "toast.versionDraftExists": "Draft version {n} already exists, opened it instead",
  "toast.versionDiscarded": "Draft discarded",
  "toast.versionActivated": "Version {n} is now in use",
  "toast.versionStopped": "Version {n} stopped",

  /* ── ADR 0289 — แท็บรถประจำผู้ขับ + ตัวช่วยเปลี่ยนรถ ── */
  "vb.tab": "Vehicle & Depot",
  "depot.tab": "Depot",
  "depot.section": "Depot",
  "vb.section": "Assigned Vehicle",
  "depot.openInMaps": "Open in Google Maps",
  "depot.pickHint": "Search for a place above, then drag the pin to the exact depot",
  "depot.clearTitle": "Clear this depot?",
  "depot.clearBody": "The assigned vehicle will have no position on the map until a new depot is set. The history is kept.",
  "depot.set": "Set depot",
  "depot.change": "Change depot",
  "depot.clear": "Clear depot",
  "depot.search": "Search for a place",
  "depot.saved": "Depot saved",
  "depot.cleared": "Depot cleared",
  "depot.saveFailed": "Could not save the depot",
  "depot.never": "No depot has been set yet",
  "depot.releasedRow": "Depot cleared",
  "depot.colDate": "Effective",
  "depot.colState": "State",
  "depot.colPlace": "Place",
  "depot.colCoords": "Coordinates",
  "depot.fromDriver": "{name}'s depot",
  "depot.noDriver": "No assigned driver — no depot",
  "vb.manageVehicle": "Assign vehicle",
  "vb.never": "No vehicle has ever been assigned",
  "vb.current": "Current",
  "vb.colDate": "Date",
  "vb.colState": "State",
  "vb.colPlate": "Plate",
  "vb.colFrom": "From driver",
  "vb.colBy": "By",
  "vb.colNote": "Note",
  "vb.released": "Released",
  "vb.typeChanged": "No longer a driver",
  "vb.step1": "Pick vehicle",
  "vb.step2": "Check jobs",
  "vb.step3": "Confirm",
  "vb.sideThis": "This driver",
  "vb.sideDisplaced": "Current driver of that vehicle",
  "vb.pickVehicle": "Pick the new vehicle",
  "vb.pickVehiclePlaceholder": "Select a vehicle…",
  "vb.optionRelease": "Release the vehicle (no vehicle)",
  "vb.notPickedYet": "No vehicle picked yet",
  "vb.notPickedBody": "Pick a vehicle on the left",
  "vb.freeHead": "Vehicle is free",
  "vb.freeBody": "No other driver is affected",
  "vb.noVehicle": "No vehicle",
  "vb.free": "Free",
  "vb.freedByChange": "Freed by this change",
  "vb.inactive": "Inactive",
  "vb.searchTickets": "Search job or route…",
  "vb.colStartAt": "Start date & time",
  "vb.sumMove": "Jobs from {from} to {to}",
  "vb.sumClear": "Jobs from {from} to no vehicle",
  "vb.sumLeft": "Left as they are",
  "vb.sumKeeps": "still on {plate}",
  "vb.sumUnit": "{n} jobs",
  "vb.colJob": "Job",
  "vb.colStatus": "Status",
  "vb.fromBoard": "Board",
  "vb.selectedOf": "{n} of {total} selected",
  "vb.selectedOutside": "{n} selected outside the search",
  "vb.overdue": "Overdue — not ticked for you",
  "vb.noTickets": "No open jobs on this vehicle",
  "vb.noMatch": "No job matches the search",
  "vb.clearSearch": "Clear search",
  "vb.pageOf": "{from}–{to} of {total}",
  "vb.overCap": "Select at most {max} jobs — {n} right now",
  "vb.overlapWarn": "This driver already has {n} unfinished jobs whose times overlap ({ids}) — one person drives one vehicle at a time. Changing the vehicle does not cause it and does not fix it; the schedule needs sorting out.",
  "vb.warnTitle": "Before you continue",
  "vb.runningWarn": "A trip is running — the plate in the driver app changes at once",
  "vb.revertWarn": "Jobs in \"assigned\" go back to draft — the driver stops seeing them in the app",
  "vb.next": "Next",
  "vb.back": "Back",
  "vb.confirm": "Confirm",
  "vb.reason": "Note (optional)",
  "vb.jobsLine": "Jobs   {n} changed",
  "vb.done": "Vehicle changed · {n} jobs updated",
  "vb.rolledBack": "Nothing was changed — everything rolled back",
  "vb.stoppedAt": "Stopped at {id}",
  "vb.dropRetry": "Drop that job and try again",
  "vb.holderChanged": "The data changed while you were checking",
  "vb.recheck": "Check again",
  "vb.manageAtTab": "Manage on the driver's page",
  "vb.bindFromDriver": "Bind from the driver's page",
  "vb.noDriver": "— none —",
  "fleet.expiry.notSet": "Not set",
  "fleet.expiry.overdueDays": "{n}d overdue",
  "fleet.expiry.daysLeft": "{n}d",
  "fleet.expiry.window": "Expiry window",
  "fleet.expiry.all": "All",
  "fleet.expiry.expiring": "Expiring",
  "fleet.expiry.expired": "Expired",
  "fleet.expiry.withinDays": "within {n} days",
  "fleet.kpi.total": "Total",
  "fleet.kpi.expiring90": "Expiring in 90d",
  "fleet.kpi.expired": "Expired",
  "fleet.kpi.missing": "Vehicles with none",
  "fleet.kpi.sumAmount": "Filtered total",
  "fleet.lender": "Lender",
  "fleet.progress": "Progress",
  "fleet.regPlate": "Plate",
  "fleet.regProvince": "Province",
  "fleet.regTo": "To",
  "fleet.regCurrent": "current",
  "fleet.noDepotSet": "No depot set — edit to add a location.",
  "fleet.deleteFinanceConfirm": "Remove this finance record ({lender})? This cannot be undone.",
  "fleet.colPrincipal": "Principal",
  "fleet.colMonthly": "Monthly",
  "fleet.taxRoadTax": "Road Tax",
  "fleet.taxOther": "Other Tax",
  "fleet.nTaxRecordsAcrossFleet": "{n} tax records across the fleet",
  "fleet.taxLoadFailed": "Could not load tax records",
  "fleet.searchTax": "Search receipt no., vehicle, plate…",
  "fleet.addTaxRecord2": "Add tax record",
  "fleet.untaxedVehicles": "Vehicles with no valid tax",
  "fleet.untaxedCount": "No valid tax ({n})",
  "fleet.allVehiclesTaxed": "Every vehicle has tax in force",
  "fleet.noTaxRecords2": "No tax records match these filters",
  "fleet.noTaxRecordsYet": "No tax records yet",
  "fleet.taxTotalFiltered": "Filtered tax total",
  "fleet.taxFeeItems": "Fee breakdown",
  "fleet.addFeeItem": "Add fee line",
  "fleet.feeType": "Fee type",
  "fleet.feeAmount": "Amount (THB)",
  "fleet.feeItemsTotal": "Total",
  "fleet.feeItemsHint": "Add lines and the total is computed for you — leave it empty to type a total instead.",
  "fleet.paidDate": "Paid on",
  "fleet.taxStatus": "Payment status",
  "fleet.documentUrl": "Document (URL)",
  "fleet.viewDocument": "Open document",
  "fleet.deleteTaxRecordQ2": "Delete this tax record?",
  "fleet.editTaxRecord2": "Edit tax record",
  "fleet.renewTax": "Renew tax",
  "fleet.taxExpiringSoon": "Tax expiring soon",
  "nav.tax": "Tax",
  "toast.taxSaved": "Tax record saved",
  "toast.taxDeleted": "Tax record deleted",
  "fleet.taxDeleteFailed": "Could not delete the tax record",
  "fleet.taxPageTitle": "Tax",
  "fleet.colAmount": "Amount",
  "fleet.colIssue": "Issued",
  "fleet.deleteTaxConfirm": "Delete this tax record? {ref} This cannot be undone.",
  "fleet.nFeeLines": "{n} lines",
  "fleet.deviceType": "Device type",
  "fleet.installCost": "Install cost",
  "fleet.warrantyEnd": "Warranty until",
  "fleet.certificateNo": "Certificate no.",
  "fleet.certificateExpiry": "Certificate expiry",
  "fleet.positionNote": "Fitted at",
  "fleet.removedAt": "Removed on",
  "fleet.devices": "Devices",
  "nav.devices": "Devices",
  "fleet.nDevicesAcrossFleet": "{n} devices across the fleet",
  "fleet.devicesLoadFailed": "Could not load devices",
  "fleet.searchDevice": "Search provider, device no., SIM, vehicle…",
  "fleet.addDevice": "Add device",
  "fleet.editDevice": "Edit device",
  "fleet.removeDevice": "Remove device",
  "fleet.removeDeviceQ": "Remove this device from the vehicle? The removal date is recorded.",
  "fleet.noDevices": "No devices match these filters",
  "fleet.noDevicesYet": "No devices yet",
  "fleet.missingDevice": "Vehicles without this device",
  "fleet.missingDeviceCount": "Missing ({n})",
  "fleet.allVehiclesHaveDevice": "Every vehicle has this device",
  "fleet.deviceCostPerMonth": "Filtered device cost",
  "fleet.expiryKind": "Expiry to watch",
  "fleet.expiryKindContract": "Service contract",
  "fleet.expiryKindWarranty": "Warranty",
  "fleet.expiryKindCertificate": "Certificate",
  "fleet.signal": "Last signal",
  "fleet.signalLive": "Live",
  "fleet.signalQuiet": "Quiet",
  "fleet.signalDead": "Dead",
  "fleet.signalNever": "Never",
  "fleet.signalHint": "Signal status comes from real vehicle data — separate from contract status",
  "toast.deviceSaved": "Device saved",
  "toast.deviceRemoved": "Device removed",
  "fleet.deviceRemoveFailed": "Could not remove the device",
  "fleet.deviceTypeGps": "GPS Tracker",

  /* ── GPS devices (ADR 0362) ── separate from fitted devices: a tracker is an
     asset that moves between vehicles. */
  "fleet.gps": "GPS",
  "fleet.gpsDevices": "GPS devices",
  "fleet.gpsTitle": "GPS devices",
  "fleet.gpsSerial": "Serial (S/N)",
  "fleet.gpsStatus": "Device status",
  "fleet.gpsInStock": "In stock",
  "fleet.gpsInstalled": "Installed",
  "fleet.gpsRepair": "In repair",
  "fleet.gpsRetired": "Retired",
  "fleet.gpsAdd": "Add GPS device",
  "fleet.gpsEdit": "Edit GPS device",
  "fleet.gpsTabAll": "All devices",
  "fleet.gpsTabStock": "In stock",
  "fleet.gpsTabMissing": "Vehicles without GPS",
  "fleet.gpsStockCount": "In stock ({n})",
  "fleet.gpsMissingCount": "Missing ({n})",
  "fleet.nGpsAcrossFleet": "{n} devices across the fleet",
  "fleet.gpsNone": "No GPS device matches these filters",
  "fleet.gpsNoneYet": "No GPS devices yet",
  "fleet.gpsStockEmpty": "No spare devices in stock",
  "fleet.gpsAllVehiclesHave": "Every vehicle has a GPS device",
  "fleet.gpsSearch": "Search serial, provider, SIM…",
  "fleet.gpsCostTotal": "Recurring cost of the filtered set",
  "fleet.gpsBind": "Fit to a vehicle",
  "fleet.gpsBindTitle": "Fit GPS device to a vehicle",
  "fleet.gpsUnbind": "Remove to stock",
  "fleet.gpsMove": "Move to another vehicle",
  "fleet.gpsMoveTitle": "Move GPS device to another vehicle",
  "fleet.gpsMoveTo": "Receiving vehicle",
  "fleet.gpsMoveDate": "Move date",
  "fleet.gpsBindDate": "Fitted on",
  "fleet.gpsUnbindDate": "Removed on",
  "fleet.gpsInstallCost": "Fitting cost this time",
  "fleet.gpsReason": "Reason",
  "fleet.gpsReasonHint": "e.g. moved to a new truck · unit failed · returned to vendor",
  "fleet.gpsCurrent": "Fitted right now",
  "fleet.gpsNotFitted": "No GPS device fitted to this vehicle",
  "fleet.gpsHistory": "Installation history",
  "fleet.gpsVehicleHistory": "Devices this vehicle has carried",
  "fleet.gpsNoHistory": "No history yet",
  "fleet.gpsStillFitted": "Still fitted",
  "fleet.gpsUntilNow": "now",
  "fleet.gpsMovedIn": "Moved in from the previous vehicle",
  "fleet.gpsMovedOut": "Removed by moving to another vehicle",
  "fleet.gpsRecordedOn": "Recorded on {date}",
  "fleet.gpsFittedDays": "Fitted for {n} days",
  "fleet.gpsMovedBy": "by {name}",
  "fleet.gpsTabIssues": "Needs checking ({n})",
  "fleet.gpsIssueGroupMismatch": "Plate does not match",
  "fleet.gpsIssueGroupUnbound": "Not bound to a vehicle",
  "fleet.gpsIssueGroupDuplicate": "Duplicate plate",
  "fleet.gpsIssueGroupSilent": "Not reporting",
  "fleet.gpsIssueGroupAcked": "Acknowledged",
  "fleet.gpsIssueFilters": "Filter the issues",
  "fleet.gpsIssueColDtcPlate": "Plate in DTC",
  "fleet.gpsIssueColVehicle": "Vehicle bound in TMS",
  "fleet.gpsIssueColIssues": "What to check",
  "fleet.gpsIssueColFound": "Found",
  "fleet.gpsIssueColLastData": "Last data",
  "fleet.gpsIssueNoPlate": "Not given",
  "fleet.gpsIssueNotBound": "Not bound",
  "fleet.gpsIssueNoData": "None",
  "fleet.gpsIssueEmpty": "No GPS boxes need checking",
  "fleet.gpsIssueEmptyFiltered": "No GPS boxes match this filter",
  "fleet.gpsIssueLoadFailed": "Could not load the boxes that need checking",
  "fleet.gpsIssueActMove": "Move box",
  "fleet.gpsIssueActBind": "Bind to a vehicle",
  "fleet.gpsIssueActAck": "Acknowledge",
  "fleet.gpsIssueAckedLabel": "Acknowledged",
  "fleet.gpsIssueActRegister": "Add to the register",
  "fleet.gpsIssueAckedNote": "Acknowledged: {reason}",
  "fleet.gpsAckTitle": "Acknowledge what needs checking",
  "fleet.gpsAckWhich": "Issues to acknowledge",
  "fleet.gpsAckReason": "Reason for acknowledging",
  "fleet.gpsAckReasonHint": "For example, a demo box that is not on a real vehicle",
  "fleet.gpsAckHelp": "The issue stays quiet until the value changes again. A reason is always required.",
  "fleet.gpsAckSubmit": "Acknowledge",
  "fleet.gpsAckFailed": "Could not acknowledge",
  "toast.gpsIssueAcked": "Acknowledged",
  "fleet.gpsMoveHintTitle": "DTC showed data that does not match the bound vehicle at {time}",
  "fleet.gpsMoveHintBody": "The system suggests moving it to vehicle {plate}, effective from that time. Points from {time} on will go back to the correct vehicle.",
  "fleet.gpsMoveHintNoVehicle": "No vehicle in the system has the plate {plate}. Choose the destination vehicle yourself.",
  "fleet.gpsMoveHintBind": "DTC saw this box at {time} and it is not bound to any vehicle. Choose the vehicle it is really fitted to.",
  "fleet.gpsMoveTime": "Time of the move",
  "fleet.gpsBindTime": "Time of the binding",
  "fleet.gpsMoveBeforeBind": "The time you chose is before the box was bound to its current vehicle ({bound}). The system refuses it.",
  "fleet.gpsMoveUseBound": "Use the original binding time instead",
  "fleet.gpsIssueNotInRegister": "This box is not in the GPS register yet. Add it to the register first, then bind it to a vehicle.",
  "fleet.gpsTimelineDtc": "From DTC",
  "fleet.gpsEvFromTo": "From {from} to {to}",
  "fleet.gpsEvPlate": "Plate {plate}",
  "fleet.gpsEvKm": "{km} km from where it was",
  "fleet.gpsEvBindingMove": "The box was moved",
  "fleet.gpsEvBindingBind": "The box was bound to a vehicle",
  "fleet.gpsEvBindingUnbind": "The box was removed from the vehicle",
  "fleet.gpsIssue.plate_mismatch": "Plate does not match the bound vehicle",
  "fleet.gpsIssue.box_unbound": "Box not bound to a vehicle",
  "fleet.gpsIssue.plate_duplicate": "Same plate on several boxes",
  "fleet.gpsIssue.plate_missing": "DTC gives no plate",
  "fleet.gpsIssue.plate_changed": "DTC changed the plate",
  "fleet.gpsIssue.plate_ambiguous": "Plate matches several vehicles",
  "fleet.gpsIssue.master_left": "Box left the DTC list",
  "fleet.gpsIssue.master_plate_differs": "Plate in the DTC list differs",
  "fleet.gpsIssue.master_missing": "Reports but is not in the DTC list",
  "fleet.gpsIssue.never_reported": "Has never reported",
  "fleet.gpsIssue.silent_started": "Box is silent",
  "fleet.gpsIssue.mileage_back": "Mileage goes backwards repeatedly",
  "fleet.gpsIssue.mileage_jump": "Mileage jumps repeatedly",
  "fleet.gpsIssue.replaced_suspected": "Box may have been replaced",
  "fleet.gpsIssue.clock_ahead_started": "Box clock is ahead",
  "fleet.gpsIssue.mass_change": "Many boxes changed at once",
  "fleet.gpsEv.baseline": "This box was first seen in DTC",
  "fleet.gpsEv.box_new": "A new box appeared in the DTC account",
  "fleet.gpsEv.box_unbound": "The box reports but is not bound to a vehicle",
  "fleet.gpsEv.plate_changed": "DTC changed this box's plate",
  "fleet.gpsEv.plate_mismatch": "The plate in DTC does not match the bound vehicle",
  "fleet.gpsEv.plate_missing": "DTC has no plate for this box",
  "fleet.gpsEv.plate_duplicate": "The same plate is on several boxes",
  "fleet.gpsEv.plate_ambiguous": "This plate matches several vehicles in TMS",
  "fleet.gpsEv.name_changed": "DTC changed the truck name of this box",
  "fleet.gpsEv.type_changed": "DTC changed the truck type of this box",
  "fleet.gpsEv.master_joined": "The box joined the DTC list",
  "fleet.gpsEv.master_left": "The box left the DTC list",
  "fleet.gpsEv.master_plate_differs": "The plate in the DTC list differs from what the box sends",
  "fleet.gpsEv.master_missing": "The box reports but is not in the DTC list",
  "fleet.gpsEv.never_reported": "The box is listed and bound but has never reported",
  "fleet.gpsEv.silent_started": "The box went silent past the limit",
  "fleet.gpsEv.silent_ended": "The box is reporting again",
  "fleet.gpsEv.moved_while_silent": "The box woke up in a different place",
  "fleet.gpsEv.mileage_back": "The mileage went backwards",
  "fleet.gpsEv.mileage_jump": "The mileage jumped abnormally",
  "fleet.gpsEv.replaced_suspected": "The box may have been replaced",
  "fleet.gpsEv.clock_ahead_started": "The box clock is ahead past the limit",
  "fleet.gpsEv.clock_ahead_ended": "The box clock is back to normal",
  "fleet.gpsEv.binding_changed": "The binding in TMS changed",
  "fleet.gpsEv.vehicle_plate_changed": "The plate of the bound vehicle changed in TMS",
  "fleet.gpsEv.mass_change": "Many boxes changed or vanished at once",
  "fleet.gpsWarnHasGps": "This vehicle already carries a GPS device — you can fit another, but check you picked the right truck",
  "fleet.gpsUnbindQ": "Remove this device from the vehicle? The removal date is recorded in its history.",
  "fleet.gpsRetire": "Retire",
  "fleet.gpsRetireQ": "Retire this device? Its history stays, but it cannot be fitted again.",
  "fleet.gpsRetireBlocked": "Remove it from its vehicle before retiring it",
  "fleet.gpsSaveFailed": "Could not save the GPS device",
  "fleet.gpsActionFailed": "Could not complete the GPS action",
  "fleet.gpsNote": "Note",
  "fleet.gpsInStockNoVehicle": "—",
  "fleet.costGps": "GPS cost",
  "fleet.colGpsContract": "GPS contract",
  "fleet.gpsErrAlreadyFitted": "This device is already on another vehicle — use “Move to another vehicle” instead",
  "fleet.gpsErrSerialTaken": "That serial already exists. Check the number, or the device may already be recorded",
  "fleet.gpsErrSameVehicle": "The device is already on this vehicle",
  "fleet.gpsErrRetireWhileFitted": "Remove the device from its vehicle before retiring it",
  "fleet.gpsErrRetiredCannotFit": "A retired device cannot be fitted to a vehicle",
  "fleet.gpsErrUnbindBeforeBind": "The removal date is before the date it was fitted — check the dates",
  "fleet.gpsErrRaced": "Someone else fitted this device a moment ago. Refresh and try again",
  "fleet.gpsErrStatusInstalled": "“Installed” cannot be set by hand — it follows from fitting the device",
  "fleet.gpsErrVehicleGone": "That vehicle no longer exists. Refresh and pick another",
  "toast.gpsSaved": "GPS device saved",
  "toast.gpsBound": "GPS device fitted to the vehicle",
  "toast.gpsUnbound": "GPS device removed to stock",
  "toast.gpsMoved": "GPS device moved to the new vehicle",
  "toast.gpsRetired": "GPS device retired",
  "fleet.deviceTypeOther": "Other device",
  "fleet.none": "None",
  "nav.installments": "Installments",
  "fleet.installments": "Installments",
  "fleet.installment": "Installment",
  "fleet.installmentNo": "Installment {n}",
  "fleet.nInstallmentsAcrossFleet": "{n} installments across the fleet",
  "fleet.installmentsLoadFailed": "Could not load installments",
  "fleet.searchInstallment": "Search vehicle, plate, financer, contract no.…",
  "fleet.financeContract": "Finance contract",
  "fleet.contractNumber": "Contract no.",
  "fleet.financer": "Financer",
  "fleet.vehiclePrice": "Vehicle price (THB)",
  "fleet.discount": "Discount (THB)",
  "fleet.netPrice": "Net price",
  "fleet.downPayment": "Down payment (THB)",
  "fleet.financeAmount": "Financed amount",
  "fleet.interestRate": "Interest rate (%)",
  "fleet.totalInterest": "Total interest",
  "fleet.totalAmount": "Total payable",
  "fleet.installmentsTotal": "Installments",
  "fleet.installmentAmount": "Monthly installment (THB)",
  "fleet.firstPaymentDate": "First payment due",
  "fleet.deliveryDate": "Delivery date",
  "fleet.saleDate": "Sold on",
  "fleet.ownershipDate": "Owned since",
  "fleet.addFinanceContract": "Add finance contract",
  "fleet.editFinanceContract": "Edit finance contract",
  "fleet.deleteFinanceContract": "Delete finance contract",
  "fleet.deleteFinanceContractQ": "Delete this contract? Only contracts with no recorded payments can be deleted.",
  "fleet.recordPayment": "Record payment",
  "fleet.clearPayment": "Undo payment",
  "fleet.amountPaid": "Amount paid (THB)",
  "fleet.amountDue": "Amount due",
  "fleet.receiptUrl": "Receipt (URL)",
  "fleet.paidOfTotal": "{paid} of {total} paid",
  "fleet.amountRemaining": "Remaining",
  "fleet.overdueInstallments": "Overdue installments",
  "fleet.dueNext30": "Due in 30 days",
  "fleet.monthlyTotal": "Monthly total",
  "fleet.vehiclesFinanced": "Vehicles financed",
  "fleet.paidOffContracts": "Paid off",
  "fleet.importedData": "Imported",
  "fleet.importedHint": "Carried over from the old system — the actual payment date is unknown",
  "fleet.noFinanceContracts": "No finance contracts yet",
  "fleet.noInstallments": "No installments match these filters",
  "fleet.noInstallmentsYet": "No installments yet",
  "fleet.filterOverdue": "Overdue",
  "fleet.filterDueSoon": "Due soon",
  "fleet.installmentTimeline": "Payment schedule",
  "toast.financeContractSaved": "Finance contract saved",
  "toast.paymentRecorded": "Payment recorded",
  "fleet.paymentFailed": "Could not record the payment",
  "fleet.contractDeleteFailed": "Could not delete the contract",
  "status.partial": "Partial",
  "fleet.scheduleResyncHint": "Changing the term or amount re-syncs the unpaid installments. Installments already paid are never rewritten.",
  "fleet.partialHint": "Short by ฿{n} — the shortfall keeps counting as owed",
  "common.paymentMethod": "Payment method",
  "fleet.showAllInstallments": "Show all {n}",
  "fleet.nInstallmentsHidden": "{n} more hidden",
  "common.showLess": "Show less",
  "nav.costs": "Cost per vehicle",
  "fleet.costs": "Cost per vehicle",
  "fleet.costsSubtitle": "Fuel, maintenance, insurance, tax, devices, GPS and installments combined",
  "fleet.costsLoadFailed": "Could not load costs",
  "fleet.costFuel": "Fuel",
  "fleet.costMaintenance": "Maintenance",
  "fleet.costInsurance": "Insurance",
  "fleet.costTax": "Tax",
  "fleet.costDevice": "Devices",
  "fleet.costInstallment": "Installments",
  "fleet.costTotal2": "Total",
  "fleet.costPerKm": "Cost / km",
  "fleet.kmInPeriod": "Distance in period",
  "fleet.costPeriod": "Period",
  "fleet.periodThisYear": "This year",
  "fleet.periodThisMonth": "This month",
  "fleet.periodThisQuarter": "This quarter",
  "fleet.periodLastYear": "Last year",
  "fleet.periodCustom": "Custom",
  "fleet.costRecognitionHint": "A cost lands in the window it falls due or is incurred — it is not spread across the period it covers",
  "fleet.noCostData": "No vehicles match these filters",
  "fleet.costPerKmHint": "Worked out from the odometer on this period's fuel entries — no entries, no figure",
  "fleet.aisleHint": "Aisle = non-bookable gap",
  "fleet.seatCountMismatch": "Grid {grid} ≠ vehicle {declared}",
  "fleet.seatCountMismatchHint": "The grid and the vehicle record disagree on seat count — check before selling seats",
  "fleet.noVehiclesHint": "No vehicles yet — add or import vehicles first",
  "fleet.seatMap": "Seat map",
  // ADR 0539 — the vehicle's "นอกเวลางาน" tab
  "fleet.offJobTab": "Off-job",
  "fleet.offJob.noTicket": "No ticket",
  "fleet.offJob.tickets": "{n} tickets",
  "fleet.offJob.drove": "Out on {n} trips, {time} in all",
  "fleet.offJob.offTrips": "{n} off-job trips, {time} in all",
  "fleet.offJob.noOffTrips": "No off-job trips",
  "fleet.offJob.span": "From {from} to {to}",
  "fleet.offJob.box": "GPS box",
  "fleet.offJob.day": "Day to view",
  "fleet.offJob.upTo": "Data up to {time}",
  "fleet.offJob.colDriver": "Driver (card)",
  "fleet.offJob.range": "{from} to {to}",
  "fleet.offJob.and": " and ",
  "fleet.offJob.gapWarn": "The box sent nothing while the truck was moving, {time} in all, so that part has no line on the map",
  "fleet.offJob.notRun": "The truck did not run on this day",
  "fleet.offJob.noData": "No data from the box for this day yet",
  "fleet.offJob.jump": "Go to {day}",
  "fleet.offJob.noBox": "This vehicle has no GPS box yet",
  "fleet.offJob.goGps": "Go to the GPS tab",
  "fleet.offJob.loadFail": "Could not load this day",
  "fleet.inspections": "Inspections",
  "fleet.noInspections": "No inspections recorded for this vehicle",
  "fleet.inspectionTemplate": "Checklist",
  "fleet.inspector": "Inspector",
  "fleet.inspectedAt": "Inspected",
  "fleet.viewAllInspections": "Open the inspections screen",
  "fleet.pmSchedules": "Preventive maintenance",
  "fleet.noPmForVehicle": "No preventive maintenance planned for this vehicle",
  "fleet.viewAllPm": "Open the PM screen",
  "fleet.costsForVehicle": "Cost of this vehicle",
  "fleet.viewAllCosts": "See the whole fleet",
  "fleet.make": "Make",
  "fleet.model": "Model",
  "fleet.year": "Year",
  "fleet.vin": "VIN",
  "fleet.depotId": "Depot",
  "fleet.kmPerLiterLabel": "km/L",
  "fleet.attention": "Needs attention",
  "fleet.attentionHint": "Paperwork expiring or expired, no insurance, or an overdue work order",
  "fleet.colInsurance": "Insurance",
  "fleet.colTax": "Tax",
  "fleet.colDevices": "Devices",
  "fleet.colWorkOrders": "Work orders",
  "fleet.colInstallments": "Installments",
  "fleet.nOverdue": "{n} late",
  "fleet.fuelSection": "Fuel & energy",
  "fleet.vehicleFront": "Front",
  "fleet.nFailedItems": "{n} failed",
  "fleet.nOpenOfTotal": "{open} open of {total}",
  "fleet.pmSchedule2": "PM schedules",
  "fleet.pmIntervalType": "Interval",
  "fleet.pmByTime": "By time",
  "fleet.pmByOdometer": "By distance",
  "fleet.pmByBoth": "Whichever first",
  "fleet.pmEveryMonths": "Every {n} months",
  "fleet.pmEveryKm": "Every {n} km",
  "fleet.pmTimeInterval": "Time interval (months)",
  "fleet.pmOdoInterval": "Distance interval (km)",
  "fleet.pmLastPerformed": "Last performed",
  "fleet.pmLastOdometer": "Odometer then",
  "fleet.pmNextDue": "Next due",
  "fleet.pmNextOdometer": "Due at odometer",
  "fleet.pmOdometerRemaining": "{n} km to go",
  "fleet.pmOverdueKm": "{n} km past due",
  "fleet.pmAutoCreate": "Raise a work order automatically when due",
  "fleet.pmAutoOn": "Auto",
  "fleet.pmRecordPerformed": "Mark performed",
  "fleet.pmRecordPerformedQ": "Record this service as done? The next cycle is scheduled automatically.",
  "fleet.deletePmScheduleQ": "Delete this PM schedule? Work orders it already raised are kept.",
  "fleet.noPmSchedules": "No PM schedules yet",
  "fleet.noPmSchedulesFiltered": "No PM schedules match these filters",
  "fleet.searchPm": "Search vehicle, plate, service, shop…",
  "fleet.pmDueOverdue": "Overdue",
  "fleet.pmDueSoon": "Due soon",
  "toast.pmSaved": "PM schedule saved",
  "toast.pmPerformed": "Recorded — the next cycle is scheduled",
  "fleet.pmSaveFailed": "Could not save the PM schedule",
  "fleet.costItems": "Cost breakdown",
  "fleet.addCostItem": "Add line",
  "fleet.costCategory": "Category",
  "fleet.costDescription": "Description",
  "fleet.costQuantity": "Qty",
  "fleet.costUnitPrice": "Unit price",
  "fleet.costLineTotal": "Total",
  "fleet.costItemsHint": "Add lines and parts / labour / total are computed for you — leave empty to type the totals instead",
  "fleet.woTimeline": "Status timeline",
  "fleet.interestRateRange": "The rate must be between 0 and 100%",
  "fleet.includeInactive": "Include inactive",
  "fleet.openFleetWide": "See the whole fleet",

  // ── ADR 0366 — ตั๋วซ่อมบำรุง: กระดาน · ผู้เกี่ยวข้อง · ใบเสร็จ ──
  "maint.statusRequested": "Awaiting triage",
  "maint.statusOpen": "Open",
  "maint.statusInProgress": "In progress",
  "maint.statusCompleted": "Closed",
  "maint.statusCancelled": "Cancelled",
  "maint.statusRejected": "Rejected",
  "maint.roleOwner": "Owner",
  "maint.roleAssignee": "Assignee",
  "maint.roleDriver": "Driver",
  "maint.roleWatcher": "Watcher",
  "maint.sourceDriverApp": "From the driver app",
  "maint.sourceWeb": "Raised in the back office",
  "maint.sourceAuto": "Scheduled maintenance",
  "maint.tabBoard": "Board",
  "maint.tabInfo": "General",
  "maint.tabReceipts": "Receipts",
  "maint.openFor": "Open for",
  "maint.notStarted": "Not started",
  "maint.days": "{n} days",
  "maint.lateBy": "{n} days late",
  "maint.dueDate": "Due",
  "maint.odometer": "Odometer",
  "maint.totalCost": "Total cost",
  "maint.startedAt": "Started",
  "maint.closedAt": "Closed",
  "maint.ticketCode": "Ticket number",
  "maint.ticketNotFound": "That maintenance ticket was not found",
  "maint.backToList": "Back to the list",
  "maint.cost": "Cost",
  "maint.receiptCount": "{n} receipts",
  "maint.ticketAttachments": "Ticket attachments",
  "maint.legacyPhoto": "Older photo (pre-migration)",
  "maint.attachment": "Attachment",
  "maint.openFile": "Open file",
  "maint.actionAccept": "Accept",
  "maint.actionReject": "Reject",
  "maint.actionStart": "Start work",
  "maint.actionFinish": "Close ticket",
  "maint.actionReopen": "Reopen",
  "maint.actionCancel": "Cancel ticket",
  "maint.statusChanged": "Status changed",
  "maint.statusChangeFailed": "Could not change the status",
  "maint.reason": "Reason",
  "maint.reasonHint": "Say why — it goes on the board for everybody involved",
  "maint.finishTitle": "Close this maintenance ticket",
  "maint.finishMessage": "Receipts are locked once it is closed, but the board stays open",
  "maint.finishNoReceipts": "No receipts recorded yet — you can still finish, but the cost will be zero",
  "maint.editTicket": "Edit ticket",
  "maint.systemAuthor": "System",
  "maint.edited": "(edited)",
  "maint.postDeleted": "Message deleted",
  "maint.reply": "Reply",
  "maint.replyingTo": "Replying to a post",
  "maint.filterAll": "Everything",
  "maint.filterPeople": "Conversation",
  "maint.filterSystem": "Activity",
  "maint.boardEmpty": "Nobody has said anything yet — go first",
  "maint.composerPlaceholder": "Write something to the team about this…",
  "maint.attachFile": "Attach",
  "maint.post": "Post",
  "maint.fileTooBig": "That file is over 20 MB",
  "maint.uploadFailed": "Upload failed",
  "maint.postFailed": "Could not post",
  "maint.deleteFailed": "Could not delete",
  "maint.uploading": "Uploading…",
  "maint.eventCreated": "opened the ticket",
  "maint.eventStatus": "changed the status",
  "maint.eventJoined": "added",
  "maint.eventLeft": "removed",
  "maint.eventReceiptAdded": "recorded receipt",
  "maint.eventReceiptUpdated": "edited receipt",
  "maint.eventReceiptRemoved": "removed receipt",
  "maint.eventVehicleChanged": "moved the ticket to another vehicle",
  "maint.participants": "People",
  "maint.noParticipants": "Nobody is on this ticket yet",
  "maint.addParticipant": "Add somebody",
  "maint.removeParticipant": "Remove from ticket",
  "maint.searchPerson": "Search by name, ID, department",
  "maint.participantAdded": "Added",
  "maint.participantAddFailed": "Could not add that person",
  "maint.participantRemoveFailed": "Could not remove that person",
  "maint.receipts": "Receipts",
  "maint.addReceipt": "Add receipt",
  "maint.editReceipt": "Edit receipt",
  "maint.noReceipts": "No receipts on this ticket yet",
  "maint.noReceiptNo": "No number",
  "maint.noLines": "No line items",
  "maint.totalMismatch": "The printed total differs from the lines",
  "maint.linesSum": "Lines add up to",
  "maint.receiptPhoto": "Receipt photo",
  "maint.receiptNo": "Receipt number",
  "maint.receiptNoHint": "Leave empty if it has none",
  "maint.receiptDate": "Receipt date",
  "maint.receiptTotal": "Printed total",
  "maint.receiptFiles": "Receipt photos / files",
  "maint.vat": "VAT",
  "maint.receiptSaved": "Receipt saved",
  "maint.receiptSaveFailed": "Could not save the receipt",
  "maint.receiptRemoved": "Receipt removed",
  "maint.receiptRemoveFailed": "Could not remove the receipt",
  "maint.removeReceiptTitle": "Remove this receipt",
  "maint.removeReceiptMessage": "The ticket total will be recalculated",
  "maint.receiptsLocked": "This ticket is closed — reopen it to change a receipt",
  "maint.age": "Age",
  "maint.newActivity": "New posts",
  "common.unknown": "Unknown",

  // ── ADR 0366 รอบสอง — ภาพรวมซ่อมบำรุง · อาการ/ตำแหน่ง ──
  "maint.dashboardTitle": "Maintenance overview",
  "maint.dashboardSub": "What it costs, what keeps breaking, and how long trucks sit",
  "maint.lastDays": "Last {n} days",
  "maint.statOpen": "Open tickets",
  "maint.statOverdue": "Overdue",
  "maint.statAwaiting": "Awaiting triage",
  "maint.statAwaitingNote": "Driver reports nobody has picked up",
  "maint.statClosed": "Tickets closed in range",
  "maint.statSpend": "Spend in range",
  "maint.statDaysOffRoad": "Avg days off road",
  "maint.statDaysOffRoadNote": "longest {n} days",
  "maint.spendByMonth": "Spend by month",
  "maint.topSymptoms": "Most common faults",
  "maint.byCategory": "By job type",
  "maint.topParts": "Costliest parts",
  "maint.topShops": "Costliest garages",
  "maint.topVehicles": "Costliest vehicles",
  "maint.noDataInRange": "Nothing in this window",
  "maint.noSymptomsYet": "No faults recorded on receipt lines yet — fill them in and this chart fills up",
  "maint.timesUnit": "jobs",
  "maint.ticketsUnit": "tickets",
  "maint.chartTableHint": "Every row prints its value as text beside the bar",
  "maint.symptom": "Fault",
  "maint.faultToggle": "Add fault / damaged position",
  "maint.faultOptional": "optional",
  "maint.faultHint": "Optional — feeds the maintenance overview: which faults recur, and where vehicles keep failing",
  "maint.symptomsLabel": "Faults found",
  "maint.pickMany": "pick any",
  "maint.pickOne": "pick one",
  "maint.otherChip": "Other",
  "maint.otherSymptomPlaceholder": "Type a fault, press Enter",
  "maint.otherPositionPlaceholder": "Type a position, press Enter",
  "maint.position": "Damaged position",
  "maint.openDashboard": "Overview",
  "maint.statusModification": "Modification",

  // ── ADR 0366 — แท็บข้อมูลที่แก้ไขได้ในที่ ──
  "maint.titleRequired": "A title is required",
  "maint.readOnlyNotice": "You can read this ticket — editing needs an admin or dispatcher",
  "maint.source": "Raised via",

  // ── ADR 0366 — จอใบเสร็จที่อ่านออก ──
  "fleet.addFirstCostItem": "Add the first line",
  "maint.mismatchExplain": "Printed total {total} differs from the lines and VAT {lines} (by {diff}) — fine to save if it is a discount or a call-out fee",
  "maint.receiptSheetSubtitle": "One receipt = one bill the shop issued · a ticket can hold several",
  "maint.receiptHeadSection": "Receipt header",
  "maint.receiptTotalsSection": "Totals",
  "maint.shopPlaceholder": "Garage or shop that issued it",
  "maint.receiptTotalHint": "Leave empty to use the lines and VAT",
  "maint.vatMode": "Value added tax (VAT)",
  "maint.vatNone": "No VAT",
  "maint.vatExclusive": "Prices exclude VAT",
  "maint.vatInclusive": "Prices include VAT",
  "maint.vatNoneHint": "Shop is not VAT-registered or issued a cash bill — the total is the lines sum",
  "maint.vatExclusiveHint": "Line prices are before tax — VAT is added underneath",
  "maint.vatInclusiveHint": "Line prices already contain tax — VAT is backed out and shown separately",
  "maint.vatRate": "VAT rate",
  "maint.taxId": "Shop tax ID",
  "maint.taxIdHint": "13 digits, from the tax invoice header — needed to claim input VAT",
  "maint.baseBeforeVat": "Before VAT",
  "maint.vatWithRate": "VAT {rate}%",
  "maint.vatIncludedWithRate": "VAT {rate}% (included in prices)",
  "maint.vatAutoHint": "Leave empty to compute it ({amount}) · type over it if the bill rounds differently",
  "maint.vatUseComputed": "Use the computed amount",
  "maint.vatChipExclusive": "+VAT {rate}%",
  "maint.vatChipInclusive": "VAT {rate}% incl.",
  "maint.statTotal": "Total of {n} receipts",
  "maint.statVatCount": "from {n} VAT receipts",
  "maint.statNoVat": "No receipt carries VAT",
  "maint.attachReceiptPhoto": "Attach a photo or file of the receipt",
  "maint.receiptNotePlaceholder": "e.g. negotiated 200 baht off at the counter",
  "maint.ticketInfoSection": "Ticket details",
  "maint.changeVehicleHint": "Moving the ticket takes its board, receipts and number with it",
  "maint.createCostHint": "Costs are not entered here — record receipts on the ticket when the bill arrives",
  "maint.titlePlaceholder": "e.g. Replace rear brake pads",
  "fleet.woCreateFailed": "Could not open the work order",
  "maint.ticketHistorySection": "Origin and timing",
  "maint.dropHint": "Drop files here, or paste an image",
  "maint.today": "Today",
  "maint.yesterday": "Yesterday",
  "maint.tabActivity": "Activity",
  "maint.comment": "Comment",
  "maint.nComments": "{n} comments",
  "maint.commentPlaceholder": "Write a comment…",
  "maint.sendComment": "Send comment",
  "maint.noActivity": "Nothing has been recorded on this ticket yet",

  // ── ADR 0333 — กล่องอนุมัติการลา + ปลดคนขับ ─────────────────────────
  "common.month.1": "Jan",
  "common.month.2": "Feb",
  "common.month.3": "Mar",
  "common.month.4": "Apr",
  "common.month.5": "May",
  "common.month.6": "Jun",
  "common.month.7": "Jul",
  "common.month.8": "Aug",
  "common.month.9": "Sep",
  "common.month.10": "Oct",
  "common.month.11": "Nov",
  "common.month.12": "Dec",
  "common.route": "Route",
  "jobs.ticket": "Job ticket",
  "hr.approveLeaveTitle": "Approve leave request",
  "hr.leaveRangeLabel": "Leave dates",
  "hr.leaveDaysN": "{n} days",
  "hr.leaveBalanceShort": "Balance",
  "hr.leaveUncapped": "Uncapped",
  "hr.leaveAttachments": "Attachments",
  "hr.leaveJobsInWindow": "Job tickets in this window",
  "hr.leaveSelectedOf": "{n} of {total} selected",
  "hr.leaveBlockedCount": "{n} cannot be released",
  "hr.leaveNoJobsInWindow": "No job tickets are planned in this window",
  "hr.leaveNoJobsToAct": "No job tickets to act on",
  "hr.leaveSelectAllJobs": "Select every releasable ticket",
  "hr.leaveJobStart": "Starts",
  "hr.leaveReleaseDay": "Release day",
  "hr.leaveKeepDay": "Keep day",
  "hr.leaveMoreJobs": "+{n} more",
  "hr.leavePlate": "Plate",
  "hr.leaveVehiclesReleasedNote": "{tickets} ticket(s) selected but {vehicles} vehicle(s) will be freed — some tickets have no vehicle, or share one",
  "hr.leaveLegendLeaveDay": "Leave day",
  "hr.leaveLegendRelease": "Release",
  "hr.leaveLegendKeep": "Keep driver",
  "hr.leaveLegendLocked": "Locked",
  "hr.leaveLegendOutside": "Outside",
  "hr.leaveLegendMore": "In the table",
  "hr.leaveApproveRelease": "Approve · release {n} (driver + vehicle)",
  "hr.leaveApproveNoRelease": "Approve (release nothing)",
  "hr.leaveApproveFailed": "Could not approve",
  "hr.leaveBookingsWarn": "The selected tickets carry {n} booking(s) — once released, passengers stop seeing the trip until a new driver is assigned",
  "hr.leaveHalfDayWarn": "Some tickets fall on a half day. The system does not store what time \"half\" means, so the whole day is shown for you to judge",
  "hr.leaveHalfDayTag": "Half day — check",
  "hr.leaveBookingsOnTicket": "{n} booked",
  "hr.leaveTooManyJobs": "This window holds {n} tickets — more than one request can carry. Use bulk edit instead",
  "hr.leaveOverdraftAck": "Approve {n} day(s) past the entitlement",
  "hr.leaveOverdraftNote": "Recorded in the decision notes and in the audit trail",
  "hr.leaveConflictsLoadFailed": "Could not load the clashing job tickets",
  "hr.leaveApproveWithoutRelease": "You can still approve, but no ticket will be touched",
  "hr.leaveBlock.ACCEPTED": "Driver already accepted",
  "hr.leaveBlock.RUNNING": "Trip has started — cancel the job instead",
  "hr.leaveBlock.FINISHED": "Trip is finished",
  "hr.leaveBlock.PERIOD_LOCKED": "Settlement period is closed",
  "hr.leaveBlock.UNKNOWN": "Cannot be released",
  "toast.leaveApproved": "Leave approved",
  "toast.leaveApprovedReleased": "Leave approved · driver released from {n} ticket(s)",
  "hr.leaveFilteredBy": "Filtered: {list}",
  "hr.leaveDaysCount": "Days",
  "hr.leaveBalanceLeft": "Balance left",
  "hr.leaveSelectAllPending": "Select every pending request on this page",
  "hr.leaveBalanceTooltip": "Was {before} d · this request {days} d · leaves {after} d",
  "hr.leaveDayUnit": "d",
  "time.today": "today",
  "time.yesterday": "yesterday",
  "time.daysAgo": "{n} days ago",
  "time.monthsAgo": "{n} months ago",
  "time.yearsAgo": "{n} years ago",
  "hr.leaveCheckingJobs": "Checking job tickets…",
  "hr.leaveClashCount": "{n} clashing",
  "hr.leaveClashTooltip": "See which tickets clash",
  "hr.leaveClashDrawer": "{n} ticket(s) are already assigned in this window — approving means finding someone else",
  "hr.leaveOutsideWindow": "Outside the leave window — shown for context only",
  // ── ADR 0346/0348 — เอกสารในใบลา ─────────────────────────────────────
  "hr.tabRequest": "Request",
  "hr.tabTimeline": "Timeline",
  "hr.timelineEmpty": "Nothing recorded yet",
  "hr.docsColumn": "Extra documents",
  "hr.tabExtraDocs": "Extra documents",
  "hr.docsAtFiling": "Attachments",
  "hr.docsNone": "Nothing has been sent after filing",
  "hr.docsFile": "File",
  "hr.docsSentAt": "Sent at",
  "hr.docsReviewState": "Review",
  "hr.docsBatch": "Batch {n}",
  "hr.docStatusPending": "Awaiting review",
  "hr.docStatusApproved": "Approved",
  "hr.docStatusRejected": "Rejected",
  "hr.docsApprove": "Approve",
  "hr.docsReject": "Reject",
  "hr.docsRejectReasonHint": "Tell the driver what to fix — e.g. too blurry to read",
  "hr.docsNoteLabel": "Comment:",
  "toast.docsApproved": "Marked approved",
  "toast.docsRejected": "Marked rejected",
  "hr.docsNew": "{n} to review",
  "hr.moreFiles": "{n} more files",
  "hr.docsApprovedN": "{n} approved",
  "hr.docsRejectedN": "{n} rejected",
  "hr.docsBadgeTooltip": "Open the extra-documents tab",
  "hr.docsFilterAll": "All documents",
  "hr.docsPendingReview": "Awaiting review",
  "hr.kpiDocsUnreviewed": "Extra docs awaiting review",
  "hr.docsReviewFailed": "Could not mark it reviewed",
  "hr.tlDocsReviewed": "Document reviewed",
  "hr.tlDocsApproved": "Documents approved",
  "hr.tlDocsRejected": "Documents rejected",
  "hr.tlSubmitted": "Request filed",
  "hr.tlApproved": "Approved",
  "hr.tlRejected": "Rejected",
  "hr.tlCancelled": "Cancelled",
  "hr.tlAttachmentAdded": "File attached",
  "hr.tlAttachmentRemoved": "File removed",

  // ── ADR 0357 — central finance ledger ──
  "nav.financeBills": "Bills",
  "bills.title": "Bills",
  "bills.intro": "Each person's pay, split by the contract's pay cycle. Amounts keep moving as entries arrive, until a bill is picked into a pay batch.",
  "bills.kpiDue": "Cycle closed, to pay",
  "bills.kpiAccumulating": "Accumulating",
  "bills.kpiBox": "Accumulated (no cycle yet)",
  "bills.kpiBills": "{n} bills",
  "bills.kpiPeople": "{n} people",
  "bills.status.accumulating": "Accumulating",
  "bills.status.due": "Cycle closed, to pay",
  "bills.status.in_batch": "In a pay batch",
  "bills.status.paid": "Paid",
  "bills.filterStatus": "Bill status",
  "bills.filterBox": "Accumulated",
  "bills.allContracts": "All contracts",
  "bills.searchPlaceholder": "Search employee name",
  "bills.openTotal": "Unpaid total",
  "bills.window": "{start} – {end}",
  "bills.payout": "paid {date}",
  "bills.billTotal": "This bill",
  "bills.boxTitle": "Accumulated",
  "bills.boxSubtitle": "No pay cycle yet · oldest {date}",
  "bills.boxHint": "Entries with no contract, or whose contract has no pay cycle, wait here. Once picked into a payment they count as used.",
  "bills.boxTotal": "Accumulated total",
  "bills.unitTrip": "{n} trips",
  "bills.unitItem": "{n} items",
  "bills.showLines": "Show entries ({n})",
  "bills.carriedFrom": "Carried from the {date} cycle",
  "bills.unassigned": "{n} money entries name no employee, so they are in no one's bill — check them on the Transactions page",
  "bills.emptyNone": "No employee money entries yet",
  "bills.emptyNoneHint": "Trip pay arrives on its own from finished job tickets.",
  "bills.emptyNoMatch": "No bills match these filters",
  "bills.colPerson": "Employee",
  "bills.colDue": "Cycle closed, to pay",
  "bills.colAccumulating": "Accumulating",
  "bills.colInBatch": "In a pay batch",
  "bills.colBox": "Accumulated",
  "bills.colOpen": "Unpaid total",
  // NIGHT-1023 N2 — the bills table: next step and what else a person has
  "bills.colNext": "Next",
  "bills.next.held_all": "Held as a whole — no round",
  "bills.next.flagged": "{n} bill(s) with a problem — clear it",
  "bills.next.stale": "{n} bill(s) changed — look again",
  "bills.next.unchecked": "Check {n} bill(s)",
  "bills.next.ready": "Ready for a pay round",
  "bills.next.box": "Put accumulated items in a round",
  "bills.next.in_batch": "In a pay round",
  "bills.next.none": "—",
  "bills.extra.checked": "Checked {done}/{n} bills",
  "bills.extra.held": "Held {amount} ({n} rows)",
  "bills.extra.inBatch": "In a pay round {amount}",
  "bills.extra.debt": "Debt left {amount}",
  "bills.billsCount": "{n} bills",
  "bills.billsCountWithBox": "{n} bills + {m} accumulated items",
  "bills.itemsSince": "{n} items · since {date}",
  "bills.personNotFound": "No bills for this employee",
  "bills.billNotFound": "Bill not found",
  "bills.byDay": "By day",
  "bills.dayItems": "{n} items",
  "bills.searchDoc": "Search a job or money entry number",
  "bills.searchResults": "{n} results",
  "bills.loadMore": "Load more ({shown}/{total})",
  "bills.kindAll": "All kinds",
  "bills.allDaysHint": "Tap a day to see its entries",
  "bills.hintDue": "Past the cutoff, waiting for a pay batch",
  "bills.hintAccumulating": "Before the cutoff, still growing",
  "bills.hintBox": "No pay cycle yet, paid when someone picks it",
  "bills.searchNoMatch": "Not in this bill. Try a job number (JOB-…) or a money entry number (FTX-…)",
  "bills.backToBills": "Back to bills",
  "bills.payoutInRound": "Paid {date} · round {round}",
  "personTl.searchPlaceholder": "Search this person's bills — contract, code, amount, kind",
  "personTl.statusLabel": "Bill status",
  "personTl.status.all": "All",
  "personTl.status.over": "Overdue",
  "personTl.status.due": "Due",
  "personTl.status.accumulating": "Accumulating",
  "personTl.status.in_batch": "In a round",
  "personTl.status.paid": "Paid",
  "personTl.allContracts": "All contracts ({n})",
  "personTl.monthly": "Salary",
  "personTl.from": "Worked from",
  "personTl.to": "to",
  "personTl.found": "{n} of {m} bills",
  "personTl.clear": "Clear filters",
  "personTl.noMatch": "No bill matches the filters",
  "personTl.stripTitle": "This person's pay days — tap to jump to the bills",
  "personTl.payOn": "Paid {date}",
  "personTl.inDays": "in {n} days",
  "personTl.payToday": "paid today",
  "personTl.overDays": "{n} days past the pay day",
  "personTl.dayTotal": "Total",
  "personTl.nBills": "{n} bills",
  "personTl.nItems": "{n} items",
  "personTl.unfold": "Show",
  "personTl.fold": "Fold",
  "personTl.todayLine": "Today {date}",
  "personTl.todayShort": "Today {date}",
  "personTl.nodate": "No pay day yet · accumulated items",
  "personTl.paidTitle": "Paid — month by month",
  "personTl.older": "Older ({n} months)",
  "personTl.checkBill": "Check this bill",
  "personTl.checkDay": "Check this pay day",
  "personTl.checkPerson": "Check this person's bills",
  "bills.openTicket": "Open job ticket",
  "bills.paidBills": "Paid: {n} bills",
  "bills.move.title": "Move into a contract bill",
  "bills.move.from": "From the accumulated items of",
  "bills.move.to": "Move into contract",
  "bills.move.cutoffDay": "Cuts off on day {d}",
  "bills.move.cutoffEnd": "Cuts off at month end",
  "bills.move.landHint": "Each item joins the bill of the cycle that covers its own date. If that bill is already in a pay batch, it goes to the next cycle's bill.",
  "bills.move.note": "Note",
  "bills.move.notePlaceholder": "e.g. contract was not linked when the job was created",
  "bills.move.confirm": "Move {n} items",
  "bills.move.failed": "Could not complete — please try again",
  "bills.move.done": "Moved {n} items into {contract}",
  "bills.move.panel": "These items are not in any bill yet. Tick days or items to move them into one of {name}'s contract bills, or move them all at once.",
  "bills.move.noTargets": "Cannot move into a bill yet — {name}'s contracts have no pay cycle",
  "bills.move.goContracts": "Set a pay cycle on the contract",
  "bills.move.all": "Move all ({n} items)",
  "bills.move.pickHint": "Tick days or items to move · tap a day to see its items",
  "bills.move.picked": "{n} items selected",
  "bills.move.clear": "Clear",
  "bills.move.action": "Move into a contract bill",
  "bills.move.pickRow": "Select {doc}",
  "bills.move.pickDay": "Select all of {date}",
  "bills.move.movedBy": "Moved here from accumulated items by {name} · {date}",
  "bills.return.action": "Return to accumulated items",
  "bills.return.ask": "Return this item?",
  "bills.return.yes": "Return",
  "bills.return.done": "Returned to accumulated items",
  "nav.financePayBatches": "Pay batches",
  "payBatches.title": "Pay batches",
  "payBatches.intro": "Gather bills that are due into one batch, check each person, then freeze it before approval and transfer. Each person gets one transfer per batch, however many bills they have.",
  "payBatches.create": "New pay batch",
  "payBatches.createHint": "A new batch starts as a draft — bills can go in and out until it is frozen.",
  "payBatches.payoutDate": "Payout date",
  "payBatches.name": "Batch name",
  "payBatches.defaultName": "Pay batch {date}",
  "payBatches.createConfirm": "Create batch",
  "payBatches.empty": "No pay batches yet",
  "payBatches.emptyHint": "Create the first batch, then pick the bills that are due",
  "payBatches.payOn": "Pay on {date}",
  "payBatches.peopleBills": "{people} people · {bills} bills",
  // NIGHT-1023 N2 — the rounds table
  "payBatches.col.round": "Pay round",
  "payBatches.col.payOn": "Pay on",
  "payBatches.col.people": "People · bills",
  "payBatches.col.check": "Bill check",
  "payBatches.col.approval": "Approval",
  "payBatches.col.total": "Total",
  "payBatches.col.next": "Next",
  "payBatches.createdBy": "Created by {name}",
  "payBatches.check.checked": "Checked {done}/{n} people",
  "payBatches.check.flagged": "{n} with a problem",
  "payBatches.check.unchecked": "{n} not checked",
  "payBatches.check.negative": "{n} below zero",
  "payBatches.approval.step": "Step {done}/{of}",
  "payBatches.approval.done": "Approved",
  "payBatches.file.taken": "Bank file taken {date}",
  "payBatches.file.notYet": "Bank file not taken yet",
  "payBatches.next.pick": "Pick bills",
  "payBatches.next.freeze": "Lock and send for approval",
  "payBatches.next.fix_negative": "{n} below zero — fix first",
  "payBatches.next.approve": "Waiting for approval — review",
  "payBatches.next.export": "Download the bank file",
  "payBatches.next.confirm_paid": "Confirm it was sent",
  "payBatches.next.done": "Open",
  "payBatches.status.draft": "Draft",
  "finSettings.section.approval": "Approval steps",
  "finSettings.section.fee": "Operating fee",
  "approval.title": "Pay round approval steps",
  "approval.lead": "How many steps a frozen round goes through before its files can go out. Each step has a name and the people who press it; they go in order. Sent back, it starts from the first step again.",
  "approval.oneStep": "No steps set — a round is approved in one step by anyone who approves rounds (as before)",
  "approval.name": "Step name",
  "approval.namePh": "e.g. Head of finance",
  "approval.up": "Move up",
  "approval.down": "Move down",
  "approval.remove": "Remove this step",
  "approval.removePerson": "Remove {name}",
  "approval.addPerson": "Add someone to this step",
  "approval.addStep": "Add a step",
  "approval.save": "Save",
  "approval.saved": "Approval steps saved — used for rounds frozen from now on",
  "approval.saveFailed": "Could not save the approval steps",
  "approval.note": "A round already waiting keeps the steps it was frozen with; changes here apply from the next freeze.",
  "approval.stepOf": "Step {n}/{of}",
  "approval.doneBy": "approved by {name} · {date}",
  "approval.waitingFor": "waiting for {names}",
  "approval.waitingAnyone": "waiting for anyone who approves rounds",
  "approval.anyone": "anyone who approves rounds",
  "approval.stepDone": "Step {n}/{of} approved — the next step waits",
  "review.approveStep": "Approve step {n}/{of} · {total}",
  "payBatches.accounting.button": "Accounting file",
  "payBatches.accounting.file": "accounting",
  "payBatches.accounting.sheet": "Accounting",
  "payBatches.accounting.failed": "Could not make the accounting file",
  "payBatches.accounting.afterApprove": "The accounting file is available once the round is approved",
  "payBatches.status.frozen": "Frozen",
  "payBatches.status.approved": "Approved",
  "payBatches.status.paid": "Paid",
  "payBatches.stepsLabel": "Pay batch steps",
  "payBatches.step1": "Pick & check",
  "payBatches.step2": "Freeze",
  "payBatches.step3": "Approve",
  "payBatches.step4": "File & pay",
  "payBatches.boxAll": "Everything waiting",
  "payBatches.negativeOne": "Below zero — when frozen this round pays 0 and the shortfall is taken from the next bill",
  "payBatches.removeBill": "Remove this bill from the batch",
  "payBatches.pickTitle": "Pick bills",
  "payBatches.pickSummary": "{n} bills are due and not in any batch",
  "payBatches.boxWaiting": "{n} people have accumulated items totalling {total} — they can go into this batch",
  "payBatches.nothingToPick": "Nothing to pick right now — a contract bill can be picked once its cutoff day has passed",
  "payBatches.pickLoadFailed": "Could not load the bills — it is not known yet whether any can be picked. Try again.",
  "payBatches.reloadFailed": "Saved, but the round's figures could not be reloaded — refresh the page to see them",
  "payBatches.pickedN": "{n} bills selected",
  "payBatches.addPicked": "Add to batch",
  "payBatches.added": "Added {n} bills",
  "payBatches.removePicked": "Take out of batch",
  "payBatches.removed": "Took {n} bills out of the batch",
  "payBatches.edit.tabs": "Switch between bills to pick and bills in the batch",
  "payBatches.edit.tabPick": "To pick · {n} bills",
  "payBatches.edit.tabRound": "In batch · {n} people",
  "payBatches.edit.behind": "The lists may not be up to date",
  "payBatches.edit.retry": "Try again",
  "payBatches.edit.rereadFailed": "Saved, but the lists could not be read again — press “Try again” at the top of the list",
  "payBatches.edit.elsewhere": "incl. {n} on other pages",
  "payBatches.edit.recent": "Just taken out of the batch · {n} bills",
  "payBatches.edit.recentAll": "Select every bill just taken out of the batch",
  "payBatches.edit.recentHide": "Hide",
  "payBatches.edit.refusedOne": "“{what}” cannot be picked: {why} · the list was read again, pick again",
  "payBatches.edit.refusedSome": "{why} · the lists were read again, try again",
  "payBatches.edit.selectAll": "Select all",
  "payBatches.edit.selectAllMatch": "Select all matches",
  "payBatches.edit.countBills": "{n} bills",
  "payBatches.edit.masterPick": "Select every bill that can be picked (all pages)",
  "payBatches.edit.masterRound": "Select every bill in the batch (all pages)",
  "payBatches.edit.person": "Select all bills of {name}",
  "payBatches.edit.bill": "Select bill {what}",
  "payBatches.edit.clear": "Clear selection",
  "payBatches.edit.refreshing": "Updating the lists…",
  "payBatches.notFound": "Pay batch not found",
  "payBatches.totalLabel": "Total to transfer",
  "payBatches.frozenNote": "Frozen {when} — the figures are fixed; items arriving later go to the next cycle's bill",
  "payBatches.negativeWarn": "{n} people are below zero — when frozen this round pays them 0 and the shortfall is taken from their next bill",
  "payBatches.missingWarn": "{n} bills have no items any more (they may have been moved) — remove them before freezing",
  "payBatches.missingFix": "Remove",
  "payBatches.freeze": "Freeze batch",
  "payBatches.freezeHint": "Check each person first — after freezing the figures stop moving",
  "payBatches.freezeEmpty": "Pick bills first",
  "payBatches.delete": "Delete draft",
  "payBatches.unfreeze": "Send back to draft",
  "payBatches.nextApprove": "Next step: approval",
  "payBatches.inBatch": "In this batch: {n} people",
  "payBatches.oneTransfer": "One person = one transfer",
  "payBatches.noneYet": "No bills in this batch yet — tick bills in “Pick bills” and press Add to batch",
  "payBatches.history": "History",
  "payBatches.event.create": "Created",
  "payBatches.event.add": "Added {n} bills",
  "payBatches.event.remove": "Removed {n} bills",
  "payBatches.event.freeze": "Frozen",
  "payBatches.event.unfreeze": "Sent back to draft",
  "payBatches.event.rename": "Renamed",
  "payBatches.freezeTitle": "Freeze this batch?",
  "payBatches.freezeBody1": "{people} people, {total} in total, will be locked as they are now.",
  "payBatches.freezeBody2": "Items arriving after this go to the next cycle's bill. To change anything, send the batch back — every picked bill stays.",
  "payBatches.frozenToast": "Batch frozen",
  "payBatches.unfreezeTitle": "Send back to draft?",
  "payBatches.unfreezeBody": "Figures are computed live again. Every picked bill stays, and late items return to their own cycle's bill.",
  "payBatches.reason": "Reason",
  "payBatches.reasonPlaceholder": "e.g. two drivers' totals do not match their tickets",
  "payBatches.unfrozenToast": "Sent back to draft",
  "payBatches.deleteTitle": "Delete this draft?",
  "payBatches.deleteBody": "Its picked bills become free to go into another batch. No money item is touched.",
  "payBatches.methodLabel": "Paid this round by",
  "payBatches.methodOpt.bank_transfer": "Bank transfer",
  "payBatches.methodOpt.cheque": "Cheque",
  "payBatches.methodOpt.cash": "Cash",
  "hr.promptpay": "PromptPay",
  "hr.promptpayPh": "10-digit mobile number or 13-digit ID card number",
  "hr.promptpayBad": "PromptPay is a 10-digit mobile number or a 13-digit ID card number",
  "hr.promptpayNeeded": "Paid by PromptPay — enter the PromptPay number",
  "payBatches.methodOpt.promptpay": "PromptPay",
  "payBatches.method.promptpay": "PromptPay",
  "payBatches.noPromptPay": "No PromptPay number yet — add it on the employee page",
  "payBatches.methodChanged": "Not this person's usual way this round",
  "payBatches.methodAll": "Everyone in this round paid by",
  "payBatches.methodPick": "Choose…",
  "payBatches.methodSaved": "{n} people now paid by {method}",
  "payBatches.event.method": "Changed how {n} people are paid",
  "payBatches.event.hold_person": "Held a person out of the round",
  // night 2026-10-14 — the round's safety catches (W1 W2 W3 W4 W12 W17)
  "payBatches.needFile.one": "Download the {file} file first, then confirm it is paid",
  "payBatches.needFile.both": "Download the {a} and {b} files first, then confirm it is paid",
  "payBatches.needFile.any": "Download the payout file first, then confirm it is paid",
  "payBatches.sendBackSentWarn": "This round's bank file was downloaded after the approval. If money was sent by that file, do not send the round back — confirm it is paid instead",
  "payBatches.sendBackSentAck": "The bank file went out — I confirm no money was sent by it",
  "payBatches.payeeAfterApproval": "Bank details of {names} changed after the approval — send the round back and approve it again before the file",
  "payBatches.cancelledUnpaid": "{n} job ticket(s) cancelled after the freeze — send the round back and the system takes them out",
  "payBatches.cancelledPaid": "{n} cancelled after the freeze — the system took them back in the next bill",
  "payBatches.failed.action": "Transfer failed",
  "payBatches.failed.title": "The transfer to {name} failed",
  "payBatches.failed.body": "{amount} from this round moves to {name}'s next bill to be paid in the next round. The system records it",
  "payBatches.failed.reason": "Reason",
  "payBatches.failed.reasonPlaceholder": "e.g. returned by the bank, account closed",
  "payBatches.failed.confirm": "Record the failed transfer",
  "payBatches.failed.line": "Transfer failed — moved to the next round",
  "payBatches.failed.toast": "Recorded — moved to the next bill",
  "payBatches.event.approve_step": "Approved one step",
  "payBatches.event.transfer_failed": "Transfer failed, moved to the next bill",
  "payBatches.event.approval_reset": "Round total changed — approval starts again at step 1",
  "payBatches.event.sentAck": "Confirmed no money was sent by the file that went out",
  "payBatches.err.carryElsewhere": "The carried shortfall is in round {name} now — send round {name} back first",
  "payBatches.err.carryElsewherePaid": "The carried shortfall was taken in round {name}, which is paid — this round cannot go back",
  "slip.title": "Pay slip",
  "slip.to": "Paid to",
  "slip.round": "Pay round",
  "slip.paidOn": "Paid on",
  "slip.notPaid": "This round is not paid yet — the figures are as frozen",
  "slip.paidBy": "Paid by",
  "slip.trips": "Trip pay",
  "slip.noContract": "Work outside a contract",
  "slip.tripLine": "{n} trips × {amount}",
  "slip.income": "Other income",
  "slip.deductions": "Deductions",
  "slip.items": "{n} items",
  // plan/distance-pay audit — a ticket's own +/− lines, out of the trip fare
  "slip.ticketLines": "On the tickets",
  "slip.ticketLine.distance": "Pay by distance",
  "slip.ticketLine.extra": "Other money added on tickets",
  "slip.ticketLine.deduct": "Taken on tickets",
  "slip.ticketLineKm": "{n} tickets · {km} km",
  "ticketParts.fare": "Trip fare",
  "ticketParts.distance": "Distance {km} km × {rate}",
  "ticketParts.extra": "Added on the ticket",
  "ticketParts.deduct": "Taken on the ticket",
  "slip.gross": "Total income",
  "slip.deducted": "Total deductions",
  "slip.net": "Net pay",
  "slip.print": "Save as PDF",
  "slip.notFound": "Slip not found",
  "slip.open": "Slip",
  "pending.title": "Starting later",
  "pending.lead": "Every value set to start on a later day, soonest first — company settings, a pay basis and an employment status change can be taken back here before their day; a salary, a trip rate and a leaving day are changed where they are owned",
  "pending.empty": "Nothing is set to start later",
  "pending.from": "From {day}",
  "pending.kind.pay_basis": "Pay basis",
  "pending.kind.home_site": "Main contract",
  "pending.kind.salary": "Base salary",
  "pending.kind.trip_rate": "Trip rate",
  "pending.kind.leaving": "Leaving day",
  "pending.change": "{from} → {to}",
  "pending.becomes": "Becomes {value}",
  "pending.leavingNote": "Settle before the last pay round",
  "pending.kind.employment": "Employment status",
  "pending.kind.relation": "Employee or contractor",
  "pending.kind.daily_wage": "Daily wage",
  "pending.kind.pay_schedule": "Pay schedule for one person",
  "pending.kind.sso_status": "Social security",
  "pending.kind.payee": "Payee account",
  "pending.kind.role": "System role",
  "pending.kind.employee_type": "Employee type",
  "pending.kind.operating_fee": "Operating fee",
  "pending.sso.in": "In social security",
  "pending.sso.out": "Out of social security",
  "pending.fee.notSet": "Not set (the next level applies)",
  "pending.payScheduleNote": "A pay schedule set for this person only. See the person page for the details.",
  "pending.atTime": "at {time}",
  "pending.by": "set by {name}",
  "pending.sched.to": "Changes to {value}",
  "pending.sched.from": "Was {value}",
  "pending.open": "Open",
  "pending.editThere": "Change it there",
  "pending.cancel": "Take back",
  "pending.cancelTitle": "Take back a value set for later",
  "pending.cancelBody": "Take back {what} becoming {value} from {day} — the current value stays",
  "pending.cancelled": "Taken back",
  "pending.cancelFailed": "Could not take it back",
  "finHome.pendingTitle": "Starting later",
  "finHome.pendingSub": "{n} values set to start on a later day",
  "finHome.pendingAll": "See all",
  "breakdown.label": "View this round by",
  "breakdown.tab.people": "Person",
  "breakdown.tab.contract": "Contract › route",
  "breakdown.tab.day": "Day",
  "breakdown.tab.item": "Money item",
  "breakdown.tab.debt": "Debts",
  "breakdown.noDebts": "No debt instalments in this round",
  "breakdown.debt": "Debt",
  "breakdown.seqs": "instalment {seqs}",
  "breakdown.seqsOf": "instalment {seqs} of {of}",
  "breakdown.openDebt": "Open this debt",
  "breakdown.draftNote": "Draft: figures still move with the picked bills. The operating fee, withholding tax and transfer fee are added at freeze.",
  "breakdown.counts": "{rows} rows · {people} people",
  "breakdown.noContract": "Outside a contract",
  "breakdown.noRoute": "No route",
  "breakdown.more": "and {n} more",
  "overview.title": "Finance overview",
  "overview.lead": "What the company paid out and took in, month by month for the last six, and the chosen month by money item · employee type · contract (voided rows left out)",
  "overview.trend": "Month by month",
  "overview.out": "Paid out",
  "overview.in": "Taken in",
  "overview.byItem": "By money item",
  "overview.byType": "By employee type",
  "overview.byContract": "By contract",
  "overview.what": "What",
  "overview.rows": "{n} rows",
  "overview.none": "Nothing this month",
  "overview.noContract": "Outside a contract",
  "overview.type.none": "No person (customer revenue)",
  "overview.type.other": "Other type",
  "finHome.overview": "Overview",
  "finHome.todo.requests": "{n} requests waiting to be taken",
  "finHome.todo.requestsDetail": "Requests from the app nobody has taken yet — the sender sees the status in the app",
  "finHome.act.requests": "Open requests",
  "finHome.bulkTripPay": "Change trip pay on many tickets",
  "finHome.bulkTripPayHint": "Select tickets on the ticket list, then Edit many › trip pay: set to / add / take off — written on each ticket",
  "payBatches.find.placeholder": "Find a name or driver id",
  "payBatches.find.show": "Show",
  "payBatches.find.all": "All {n}",
  "payBatches.find.attention": "Needs a look {n}",
  "payBatches.find.none": "Nobody matches",
  "payBatches.find.range": "{from}–{to} of {n} people",
  "payBatches.method.cash": "Paid in cash",
  "payBatches.method.cheque": "Paid by cheque",
  "payBatches.noAccount": "No bank account — add it in the employee profile",
  "payBatches.exported": "Payout file exported ({n} people)",
  "payBatches.paidNote": "Confirmed paid {when} — this batch is closed and can no longer change",
  "payBatches.missingBankWarn": "{n} people have no bank account — the payout file cannot be exported until it is added in their employee profile",
  "payBatches.approve": "Approve",
  "payBatches.download": "Download payout file",
  "payBatches.downloadAgain": "Download file again",
  "payBatches.markPaid": "Confirm paid",
  "payBatches.approveBlocked": "Someone is below zero — send the batch back and remove their bills before approving",
  "payBatches.approveHint": "Checked each person? Approve to move on to the payout file",
  "payBatches.paidHint": "Transferred according to the file? Confirm to close the batch",
  "payBatches.downloadHint": "Download the files above and make the transfers first — every download is recorded in the history",
  "payBatches.files.title": "Bank files (K-Cash Connect Plus)",
  "payBatches.files.all": "Everyone in one file",
  "payBatches.files.kbank": "KBank accounts — KBank Payroll",
  "payBatches.files.smart": "Other banks — SMART Payroll",
  "payBatches.files.manual": "Pay by hand",
  "payBatches.files.manualWhat": "Cash, cheque, or people the bank files cannot carry (the list says why) — not uploaded",
  "payBatches.files.people": "{n} people",
  "payBatches.files.fee": "about {fee} in fees",
  "payBatches.files.kbankWhen": "Upload by the pay date, before {time}",
  "payBatches.files.smartWhen": "Upload and approve in the bank by {date}, before {time}",
  "payBatches.files.smartLate": "Past the deadline ({date} {time}) — move the pay date or pay this group another way",
  "payBatches.files.smartWeekend": "The pay date is a Saturday or Sunday; this inter-bank service pays on business days only — move the pay date",
  "payBatches.files.smartCheck": "{n} people need their branch code or account checked — see the «ต้องตรวจ» column",
  "payBatches.files.download": "Download",
  "payBatches.files.downloadList": "Download list",
  "payBatches.files.paidCopy": "This batch is paid — download again only to keep or check the files",
  "finance.err.bad_debit_account": "A KBank account number has 10 digits",
  "payBatches.auditLink": "See the full history in the audit log (who · when · from where)",
  "payBatches.exportEvent": "{part} · money lands {date}",
  "payBatches.files.done": "File out · money lands {date} · by {who}",
  "payBatches.files.smartLateOpen": "Too late for the batch's pay date (due {date} {time}) — pick a new day when exporting",
  "payBatches.payeeChanged": "Bank details changed {n} times after this batch was frozen — check before approving and paying",
  "payBatches.payeeChangedRow": "{when} by {who}: {what}",
  "payBatches.x.title": "Export payout files",
  "payBatches.x.open": "Export payout files",
  "payBatches.x.openFix": "Export payout files (needs fixing)",
  "payBatches.x.openCopy": "Download file copies",
  "payBatches.x.day": "Day the money lands",
  "payBatches.x.kbankOn": "Lands {date} · upload by that day, before {time}",
  "payBatches.x.manualOn": "Pay on {date}",
  "payBatches.x.past": "That day has passed",
  "payBatches.x.weekend": "A weekend — other-bank transfers run on business days only",
  "payBatches.x.tooLate": "Too late — had to be uploaded by {date} before {time}",
  "payBatches.x.smartLater": "Use {date} for the other-bank file only",
  "payBatches.x.smartSame": "Back to the same day as the other files",
  "payBatches.x.company": "Paying company",
  "payBatches.x.companyFirst": "First time only — remembered for the next rounds",
  "payBatches.x.edit": "Edit",
  "payBatches.x.payer": "Payer name as the bank knows it",
  "payBatches.x.debit": "Debit account",
  "payBatches.x.debitNew": "Company KBank account money leaves from",
  "payBatches.x.debitAdd": "Add a debit account (optional)",
  "payBatches.x.debitLabel": "Account nickname (optional)",
  "payBatches.x.services": "Services the company has with the bank",
  "payBatches.x.svcKbank": "KBank Payroll (to KBank accounts)",
  "payBatches.x.svcSmart": "SMART Payroll (to other banks)",
  "payBatches.x.servicesOff": "The company does not have {which} — those people are on the pay-by-hand list",
  "payBatches.x.saveCompany": "Save company details",
  "payBatches.x.fixTitle": "Fix first: {n} people",
  "payBatches.x.fixNote": "Saved to the employee record too — not asked again, and the change is logged with who made it",
  "payBatches.x.pickBank": "Pick a bank",
  "payBatches.x.accountNo": "Account number",
  "payBatches.x.fixSave": "Save",
  "payBatches.x.fixSaved": "Saved {name}'s account",
  "payBatches.x.template": "In the template: payer {payer} · date {date} · debit account {account}",
  "payBatches.x.lastExport": "Last downloaded {when} by {who} · lands {date}",
  "payBatches.part.kbankService": "KBank Payroll",
  "payBatches.part.smartService": "SMART Payroll",
  "payBatches.x.ownDay": "this file lands {date}",
  "payBatches.files.afterApprove": "Approve the batch to download the files",
  "payBatches.files.howTo": "How to use: open the file in Excel → copy the first 3 columns (account, name, amount) → paste into the KBank Excel Template → press «Generate» → upload at K-Cash Connect Plus, choosing the matching service",
  "payBatches.files.feeNote": "Fees at the bank's posted rates (KBank Payroll {kbank} · SMART Payroll {smart} a transfer); the company's contract may differ",
  "payBatches.files.holidayNote": "The deadline skips weekends only — bring it forward for any public holiday in between",
  "payBatches.part.kbank": "KBank Payroll",
  "payBatches.part.smart": "Other bank (SMART)",
  "payBatches.reason.no_smart": "SMART does not reach this bank — pay by hand",
  "payBatches.unknownBank": "Unknown bank name «{name}» — correct it in the employee profile",
  "payBatches.badAccount": "A KBank account needs 10 digits ({account}) — correct it in the employee profile",
  "payBatches.unknownBankWarn": "{n} people have a bank name the system cannot place — no files until it is corrected in their employee profile",
  "payBatches.badAccountWarn": "{n} people have a KBank account that is not 10 digits — the bank would refuse the whole file; correct it in their employee profile first",
  "finance.err.bad_account": "A KBank account number needs 10 digits — correct it in the employee profile first",
  "payBatches.approveTitle": "Approve this batch?",
  "payBatches.approveBody": "{people} people, {total} in total, will be ready for the payout file",
  "payBatches.approvedToast": "Batch approved",
  "payBatches.paidTitle": "Confirm paid?",
  "payBatches.paidBody1": "{people} people, {total} in total, will be recorded as paid",
  "payBatches.paidBody2": "After confirming, this batch can no longer be changed or sent back",
  "payBatches.paidPlaceholder": "e.g. transferred through the bank on the 20th",
  "payBatches.paidToast": "Recorded as paid — batch closed",
  "payBatches.event.approve": "Approved",
  "payBatches.event.export": "Exported payout file ({n} people)",
  "payBatches.event.paid": "Confirmed paid",
  "money.confirm.approveChargeTitle": "Approve this charge?",
  "money.confirm.approveChargeBody": "{type} ฿{amount} will count in this ticket's driver pay",
  "money.confirm.revokeChargeTitle": "Revoke approval?",
  "money.confirm.revokeChargeBody": "{type} ฿{amount} will no longer count in this ticket's driver pay",
  "money.confirm.approve": "Approve",
  "money.confirm.revoke": "Revoke approval",
  "money.confirm.backdatedTitle": "Save with a past start date?",
  "money.confirm.backdatedSalary": "The new salary takes effect from {date}, {n} day(s) before today. Check the date before saving.",
  "money.confirm.backdatedItems": "{names} take effect from {date}, {n} day(s) before today. Check the date before saving.",
  "money.confirm.saveBackdated": "Save back-dated",
  "nav.financeSettings": "Finance settings",
  "finSettings.title": "Finance settings",
  "finSettings.lead": "Change any value yourself, no code change needed · rows already saved keep what they had",
  "finSettings.sections": "Sections",
  "finSettings.section.items": "Money items",
  "finSettings.items.lead": "The names used in the add form, the accounting file and slips · ordered as the accounting file's columns",
  "finSettings.items.add": "Add item",
  "finSettings.items.showHidden": "Show hidden ({n})",
  "finSettings.group.income": "Income",
  "finSettings.group.deduction": "Deductions",
  "finSettings.group.contractor_after_tax": "External driver deductions after 1% withholding",
  "finSettings.tag.computed": "Worked out by the system",
  "finSettings.tag.perBill": "Every bill",
  "finSettings.tag.monthlyHomeSite": "Once a month",
  "finSettings.tag.perPayout": "Every payout",
  "finSettings.tag.perDay": "Per day",
  "finSettings.tag.percentOfIncome": "% of income",
  "finSettings.tag.installments": "Installments",
  "finSettings.tag.stagePerRow": "Before/after tax per row",
  "finSettings.tag.postTax": "After tax",
  "finSettings.tag.bucket": "Other",
  "finSettings.tag.company": "Company's own",
  "finSettings.tag.onlyFor": "{types} only",
  "finSettings.hidden": "Hidden",
  "finSettings.code": "Code {code}",
  "finSettings.action.up": "Move \"{name}\" up",
  "finSettings.action.down": "Move \"{name}\" down",
  "finSettings.action.edit": "Edit",
  "finSettings.action.hide": "Hide",
  "finSettings.action.show": "Show",
  "finSettings.sheet.addTitle": "Add money item",
  "finSettings.sheet.editTitle": "Edit money item",
  "finSettings.f.name": "Item name",
  "finSettings.f.namePh": "e.g. Uniform",
  "finSettings.f.side": "What kind of money",
  "finSettings.f.earn": "Earn (paid to them)",
  "finSettings.f.deduct": "Deduct (taken back)",
  "finSettings.f.group": "Which group",
  "finSettings.f.stage": "Deduct before or after tax",
  "finSettings.f.preTax": "Before tax",
  "finSettings.f.postTax": "After tax",
  "finSettings.f.frequency": "How often",
  "finSettings.f.each": "Every time it is entered",
  "finSettings.f.monthlyHomeSite": "Once a month — employees: from the salary bill; contractors: from the bill of the contract they ran most that month",
  "finSettings.f.basis": "The amount comes from",
  "finSettings.f.fixed": "An amount typed in",
  "finSettings.f.perDay": "Days × rate",
  "finSettings.f.installments": "Can be paid in installments",
  "finSettings.f.appliesTo": "Applies to",
  "finSettings.f.everyone": "Everyone",
  "finSettings.f.systemNote": "A built-in item: only its name, order and visibility can change",
  "finSettings.f.computedNote": "The system works this amount out from other rows, so it cannot be hidden",
  "finSettings.f.orderAndShow": "Order and visibility",
  "finSettings.f.moveUp": "Move up",
  "finSettings.f.moveDown": "Move down",
  "finSettings.action.delete": "Delete item",
  "finSettings.confirmDelete.title": "Delete \"{name}\"?",
  "finSettings.confirmDelete.body": "Only an item no money row uses yet can be deleted; once used it can only be hidden",
  "finSettings.deleted": "Deleted \"{name}\"",
  "finSettings.section.legal": "Legal rates",
  "finSettings.section.payout": "Payer and payment",
  "finCfg.lead.legal": "Used for social security, withholding tax and installments · a new value applies from its date; frozen or paid rounds do not change",
  "finCfg.lead.payout": "The transfer fee taken from external drivers, once per transfer · who is told about pay rounds, and how",
  "finCfg.title.payout": "Transfer fee and notifications",
  "finCfg.lead.company": "What every bank file uses — the same details the first export asks for",
  "finCfg.col.name": "Setting",
  "finCfg.col.now": "Now",
  "finCfg.col.from": "Since",
  "finCfg.col.pending": "Coming",
  "finCfg.setNew": "Set a new value…",
  "finCfg.historyN": "History {n}",
  "finCfg.default": "Default",
  "finCfg.since": "Since {date}",
  "finCfg.pendingFrom": "{value} from {date}",
  "finCfg.key.sso_rate": "Social security",
  "finCfg.key.sso_base": "Social security base, min–max (THB)",
  "finCfg.key.sso_base_items": "Items counted in the social security base",
  "finCfg.key.wht_no_sso": "Withholding, no social security (before bills by kind start)",
  "finCfg.key.wht_contractor": "Withholding tax, external drivers",
  "finCfg.key.days_per_month": "Days per month (pro rata · absence)",
  "finCfg.key.installment_rounding": "Installment rounding",
  "finCfg.key.transfer_fee": "Transfer fee",
  "finCfg.key.notify_in_app": "Tell people in the app (round waiting · sent back · approved · hold due · instalment left out · bank details changed)",
  "finCfg.key.notify_email": "Also send these by email",
  "finCfg.v.baht": "THB {amount}",
  "finCfg.key.min_wage_day": "Minimum wage a day (a warning only)",
  "finCfg.key.pay_by_kind": "Bills by kind of money + employees' income tax by the brackets (start it on the 1st of a month)",
  "finCfg.key.pay_schedule": "Company salary pay schedule",
  "finCfg.v.notSet": "Not set",
  "finCfg.v.days": "{n} days",
  "finCfg.v.roundLast": "Remainder on the last installment",
  "finCfg.v.roundFirst": "Remainder on the first installment",
  "finCfg.v.feePer": "THB {amount} per transfer",
  "finCfg.v.feeOff": "Not charged",
  "finCfg.v.capAmount": "THB {amount}",
  "finCfg.v.yes": "Yes",
  "finCfg.v.no": "No",
  "finCfg.sheet.title": "Set a new value",
  "finCfg.f.which": "Which setting",
  "finCfg.f.value": "New value",
  "finCfg.f.from": "Takes effect from",
  "finCfg.f.nowIs": "Now: {value}",
  "finCfg.f.today": "Today",
  "finCfg.f.nextMonth": "1st of next month",
  "finCfg.f.nextYear": "1 Jan next year",
  "finCfg.f.min": "Minimum (THB)",
  "finCfg.f.max": "Maximum (THB)",
  "finCfg.f.feeOn": "Charge a transfer fee",
  "finCfg.f.bahtUnit": "THB",
  "finCfg.f.daysUnit": "days",
  "finCfg.sum": "{label}: {before} → {after} from {date}",
  "finCfg.sum.pending": "the old value stays until then",
  "finCfg.sum.back": "{n} day(s) back — bills in that period that are not frozen will use the new value",
  "finCfg.sum.untouched": "frozen or paid rounds do not change",
  "finCfg.sum.same": "Still the same value — type the new one and pick the day it starts",
  "finCfg.sum.incomplete": "Fill in the new value first",
  "finCfg.saved": "Saved — {label} from {date}",
  "finCfg.cancelled": "Cancelled the value coming on {date}",
  "finCfg.history.title": "History: {label}",
  "finCfg.history.default": "System default (before anything was set)",
  "finCfg.history.by": "set by {name}",
  "finCfg.history.cancel": "Cancel this value",
  "finCfg.state.current": "In use",
  "finCfg.state.pending": "Coming",
  "finCfg.state.past": "Used before",
  "finCfg.cancelConfirm.title": "Cancel the coming value?",
  "finCfg.cancelConfirm.body": "{label} = {value} from {date} will not be used",
  "finCfg.company.saved": "Company details saved",
  "finCfg.company.noDebit": "No debit account yet",
  "finCfg.company.default": "Default",
  "finCfg.company.makeDefault": "Make default",
  "finCfg.company.add": "Add account",
  "finCfg.company.remove": "Remove account {account}",
  "finCfg.company.removeTitle": "Remove this debit account?",
  "finCfg.company.removeBody": "Account {account} will not be used for files any more; files already made do not change",
  "finCfg.company.removeConfirm": "Remove account",
  "financeTxn.status.hold": "On hold",
  "financeTxn.status.voided": "Voided",
  "financeTxn.voidedView": "Show voided rows",
  "financeTxn.voidedNote": "Only voided rows — not counted in any total · the reason is in each row's \"history\"",
  "financeTxn.status.paidOutside": "Paid outside a round",
  "financeTxn.status.settled": "Settled",
  "addMoney.title": "Add money",
  "addMoney.who": "Whose",
  "addMoney.way": "Which way",
  "addMoney.earn": "Company pays them",
  "addMoney.deduct": "Taken from them",
  "addMoney.what": "What for",
  "addMoney.whatPh": "Search items",
  "addMoney.tripHint": "Trip pay is set on the job ticket, not here",
  "addMoney.howMuch": "How much",
  "addMoney.split": "Split into installments",
  "addMoney.installments": "Installments",
  "addMoney.installmentsUnit": "times",
  "addMoney.on": "On {day}",
  "addMoney.startOn": "From {day}",
  "addMoney.planLine": "Worked out: THB {per} each · last on {end}",
  "addMoney.source": "Source (required)",
  "addMoney.department": "Department",
  "addMoney.reporter": "Reported by (required)",
  "addMoney.missing": "Still needed: {list}",
  "addMoney.need.person": "person",
  "addMoney.need.item": "item",
  "addMoney.need.amount": "amount",
  "addMoney.need.department": "department",
  "addMoney.need.reporter": "reported by",
  "addMoney.need.settled": "reason and evidence (settled outside)",
  "addMoney.settled": "Record as settled (outside the system)",
  "addMoney.settledHint": "Money already settled outside the system, e.g. paid in cash or recorded after the fact — this row never goes into a bill or a pay round",
  "addMoney.settledNeed": "Pick how it was settled, write a note and attach at least one evidence file",
  "addMoney.attachRequired": "Attach evidence (required)",
  "addMoney.notePhRequired": "Note, e.g. paid in cash on site on 12 Oct (required)",
  "addMoney.sum.settledTail": " · recorded as settled, not in any bill or pay round",
  "addMoney.attach": "Attach evidence (optional)",
  "addMoney.uploading": "Attaching…",
  "addMoney.uploadFailed": "Could not attach the file",
  "addMoney.removeFile": "Remove {name}",
  "addMoney.note": "Reason / note",
  "addMoney.notePh": "Reason / note (optional)",
  "addMoney.specific": "For this item (optional)",
  "addMoney.contract": "Contract",
  "addMoney.vehicle": "Vehicle plate",
  "addMoney.taxStage": "Before or after the 1% withholding",
  "addMoney.sum.incomplete": "Answer 1–4 and the summary appears here before you save",
  "addMoney.sum.earn": "Pays {who} THB {amount} as {item} on {day}",
  "addMoney.sum.deduct": "Takes THB {amount} from {who} as {item} on {day}",
  "addMoney.sum.planEarn": "Pays {who} THB {per} a month for {n} months, from {from} to {to}{last}",
  "addMoney.sum.planDeduct": "Takes THB {per} a month from {who} for {n} months, from {from} to {to}{last}",
  "addMoney.sum.lastDiffers": " (the installment with the remainder: THB {last})",
  "addMoney.save": "Save",
  "addMoney.saved": "Saved {name}",
  "addMoney.savedPlan": "Saved {name} in {n} installments",
  "addMoney.failed": "Could not save",
  "addMoney.companyCost": "Company money (not a person's)",
  "personFin.title": "Finance details",
  "personFin.notSet": "Not set",
  "personFin.payBasis": "Fixed pay",
  "personFin.basis.per_trip": "None (trip pay from tickets)",
  "personFin.basis.daily": "Daily wage",
  "personFin.basis.monthly": "Salary",
  "personFin.change": "Change…",
  "personFin.salaryValue": "฿{amount} a month",
  "personFin.salarySince": "In effect from {date}",
  "personFin.lead": "How this person is paid — every value has an effective date and a history",
  "personFin.homeSite": "Main contract",
  "personFin.sso": "Social security",
  "personFin.ssoDatedHint": "Social security starts or stops by the month, from the 1st of the month picked; earlier months stay as they were. For someone who leaves, the system stops it from the month after their last working day.",
  "personFin.ssoFromMonth": "From month",
  "personFin.taxYear": "Tax year",
  "personFin.ssoIn": "Enrolled from {month}",
  "personFin.ssoOut": "Not enrolled",
  "personFin.ssoYes": "Enrolled",
  "personFin.ssoMonth": "Month enrolled",
  "personFin.central": "Central group",
  "personFin.centralHint": "People in this group get no trip-pay multipliers",
  "personFin.operatingFee": "Operating fee (% of bill income)",
  "personFin.commission": "Personal commission",
  "personFin.resigned": "Last working day",
  "personFin.resignedHint": "Set from the employment status at the top right of the employee page",
  "personFin.gaps": "Missing: {list}",
  "personFin.gapsWhat": "This person's money settings are incomplete: {list} — trip pay from tickets is paid as usual, but items that need these settings cannot be worked out yet",
  "personFin.gapsSet": "Set them",
  "personFin.gap.pay_basis": "fixed pay",
  "personFin.gap.sso": "social security",
  "personFin.gap.home_site": "main contract",
  "personFin.gap.operating_fee": "operating fee",
  "personFin.saved": "Finance details saved",
  "personFin.payBasisHint": "A salary is paid every month from the salary page · a daily wage only for days a ticket started or the office added · trip pay from tickets comes with any of them",
  "personFin.homeSiteHint": "The main contract's bill takes the monthly GPS and operating charge",
  "workDays.title": "Work days",
  "workDays.thisMonth": "This month",
  "workDays.lastMonth": "Last month",
  "workDays.add": "Add a work day",
  "workDays.summary": "THB {wage} a day · {n} days · THB {total} in all",
  "workDays.noWage": "No wage a day yet — set it in the finance details on the employee page",
  "workDays.empty": "No work days this month yet",
  "workDays.fromTickets": "Tickets {list}",
  "workDays.added": "Added by the office: {reason}",
  "workDays.removed": "Not counted: {reason}",
  "workDays.adjusted": "Adjusted after it was paid",
  "workDays.notCount": "Don't count this day",
  "workDays.undo": "Let the tickets decide",
  "workDays.addTitle": "Add a work day",
  "workDays.removeTitle": "Don't count a work day",
  "workDays.day": "Day",
  "workDays.reason": "Reason (required)",
  "workDays.reasonPhAdd": "e.g. waited at the yard, no trip",
  "workDays.reasonPhRemove": "e.g. ticket closed for them, did not come in",
  "workDays.addHint": "The day is paid at that day's wage, even with no ticket",
  "workDays.removeHint": "The day is not paid; if it is already in a frozen round, it is taken back in the next bill",
  "workDays.saved": "Work days saved",
  "workDays.state.in_round": "In a round",
  "workDays.state.paid": "Paid",
  "workDays.hint": "Counted from tickets started that day; several tickets in a day count once; paid at that day's wage",
  "financeTxn.kind.daily_wage": "Daily wage",
  "personFin.taxPriorWithheld": "Tax that employer withheld",
  "personFin.taxPriorIncome": "Income from a previous employer this year",
  "personFin.taxAllowanceHint": "Spouse, children, insurance, funds … from the person's own allowance form",
  "personFin.taxAllowance": "More allowances a year (beyond the personal 60,000)",
  "personFin.taxPrior": "From a previous employer this year THB {income}, tax withheld THB {withheld}",
  "personFin.taxValue": "THB {amount} more allowances",
  "personFin.tax": "Tax allowances a year",
  "personFin.salaryWhere": "The salary amount is set on the salary page",
  "personFin.belowMinWage": "Below the minimum set, THB {min} a day",
  "payBatches.paydayOnly": "Paid on {date} ({n} bills)",
  "paySched.keep": "Keep it",
  "paySched.periodOf": "Period {n} of {month}",
  "paySched.waiting": "From {date}: {what}",
  "paySched.title": "Salary pay schedule",
  "paySched.followsCompanyText": "the company's schedule",
  "paySched.sum.one": "1 period a month",
  "paySched.sum.many": "{n} periods a month",
  "paySched.sum.text": "{count}, cut {cuts}, paid {pays}",
  "paySched.sum.day": "on day {day}",
  "paySched.sum.end": "at month end",
  "paySched.sum.nextMonth": "{when} of the next month",
  "paySched.sum.laterMonths": "{when}, {n} months later",
  "paySched.sum.and": "{a} and {b}",
  "paySched.sum.andNum": "{a} and {b}",
  "paySched.sum.withSplit": "{text}, split {shares}",
  "paySched.lead": "People on a salary or a daily wage: salary, trips, allowances and deductions go into the period their day falls in",
  "paySched.period": "Period {n}",
  "paySched.work": "Work from",
  "paySched.dayPrev": "the {day}th of the month before",
  "paySched.day": "the {day}th",
  "paySched.to": "to",
  "paySched.payOn": "Paid on",
  "paySched.share": "Share of the salary (%)",
  "paySched.end": "month end",
  "paySched.month.0": "same month",
  "paySched.month.1": "next month",
  "paySched.month.2": "two months on",
  "paySched.add": "Add a period",
  "paySched.remove": "Remove period {n}",
  "paySched.covered": "Every day of the month is covered — each period starts the day after the one before ends",
  "paySched.p.no_periods": "At least one period",
  "paySched.p.too_many": "At most {max} periods a month",
  "paySched.p.bad_cut": "Period {n}: the cut is the 1st–28th or month end",
  "paySched.p.cuts_out_of_order": "Period {n}: its cut must come after period {prev}'s",
  "paySched.p.bad_pay_day": "Period {n}: the pay day is not valid",
  "paySched.p.paid_before_cut": "Period {n}: paid before its cut — pick a later pay day or the next month",
  "paySched.p.bad_share": "Period {n}: a share above 0 and at most 100",
  "paySched.p.shares_not_100": "The shares must add up to 100 (now {total})",
  "paySched.preview": "The first 3 periods from the start day",
  "paySched.col.period": "Period",
  "paySched.col.work": "Work",
  "paySched.col.pay": "Paid",
  "paySched.col.share": "%",
  "paySched.short": "The first period is shorter ({days} days) — its salary is by the day (salary ÷ 30 × days)",
  "paySched.text": "Period {n}: work {start} to {cut} · paid {pay} {month} · {share}%",
  "paySched.legacy": "The old way: the whole month, paid at month end (trips in the next month's bill)",
  "paySched.company": "The company's",
  "paySched.own": "Own for this person",
  "paySched.followsCompany": "The company's",
  "paySched.set": "Set a new schedule",
  "paySched.saved": "Pay schedule saved, from {date}",
  "paySched.sum": "From {date} the salary is paid like this · bills in a round or paid stay as they are",
  "paySched.sum.company": "From {date} this person follows the company · bills in a round or paid stay as they are",
  "paySched.since": "since {date}",
  "paySched.pending": "from {date}",
  "paySched.history": "{n} versions",
  "paySched.history.title": "Pay schedule history",
  "paySched.cancel": "Cancel",
  "paySched.cancelConfirm": "Cancel the schedule starting {date}?",
  "paySched.cancelled": "The schedule starting {date} was cancelled",
  "paySched.personHint": "Pick \"Own for this person\" when this person is paid on other days than the rest of the company",
  "personFin.dailyWageHint": "Paid only for days really worked: a ticket started, or the office added the day (see this person's finance page)",
  "personFin.dailyWageValue": "THB {amount} a day",
  "personFin.dailyWage": "Wage a day",
  "personFin.law.contractor": "1 % withheld (PND 3) + operating fee",
  "personFin.law.employee": "Social security + income tax by the brackets (PND 1)",
  "personFin.relationHint": "Employee: social security and income tax by the brackets (PND 1) · Contractor: 1 % withheld (PND 3) and the operating fee",
  "personFin.relationFromType": "from the HR type",
  "personFin.relation.contractor": "Contractor",
  "personFin.relation.employee": "Employee",
  "personFin.relation": "Relation",
  "personFin.gap.daily_wage": "wage a day",
  "addMoney.recurringHas": "The profile already recorded {item} this month ({doc} · THB {amount}) — it cannot be entered twice",
  "addMoney.recurringWill": "The profile pays {item} every month; the system records it on the 1st — change the amount on the profile",
  "addMoney.adjustThisMonth": "Adjust this month",
  "addMoney.viewRow": "See that row",
  "addMoney.openProfile": "Open the profile",
  "addMoney.adjustTo": "This month should be",
  "addMoney.adjustHint": "Recorded THB {amount} ({doc}) — only the difference is saved",
  "addMoney.adjustHintAgain": "Already adjusted {n}× this month, now THB {amount} ({doc}) — only the difference from this is saved",
  "addMoney.recurringHasAdjusted": "The profile already recorded {item} this month and it was adjusted {n}×, now THB {amount} ({doc}) — it cannot be entered twice",
  "addMoney.adjustSame": "Same amount — nothing to adjust",
  "addMoney.sum.adjust": "Changes {who}'s {item} this month from {from} to THB {to} (saves the difference {delta})",
  "payComp.itemPh": "Link to a money item",
  "payComp.linked": "The system records this every month from the month it was linked",
  "payComp.notLinked": "Not linked to a money item — the system will not pay it",
  "financeTxn.kind.recurring": "Monthly profile item",
  "nav.badge": "{n} waiting",
  "finSteps.aria": "Paying out, step by step",
  "finSteps.waits.rows": "{n} rows not in a round",
  "finSteps.waits.due": "{n} bills due",
  "finSteps.waits.dueBox": "{n} bills due · {box} people accumulating",
  "finSteps.waits.rounds": "{n} rounds not paid",
  "nav.finSection.flow": "Paying out",
  "nav.finSection.source": "Where money comes from",
  "nav.finSection.more": "Reports & setup",
  "bills.inRound": "In {name} ({status})",
  "bills.notInRound": "Due, not in a pay round yet",
  "bills.goRounds": "Go to pay rounds",
  "bills.openTxn": "Open this row in the money list",
  "financeTxn.where.round": "{name} · {status}",
  "financeTxn.where.none": "Not in a round yet",
  "financeTxn.where.bills": "See bills",
  "finFlow.title": "The money's way out",
  "finFlow.lead": "Rows make bills, due bills go into a pay round, the round is paid",
  "finFlow.rows": "Not in a round yet",
  "finFlow.rowsSub": "{n} rows",
  "finFlow.due": "Bills to put in a round",
  "finFlow.dueSub": "{n} bills",
  "finFlow.dueSubBox": "{n} bills due · {box} people accumulating",
  "finFlow.rounds": "Rounds to pay",
  "finFlow.roundsSub": "{n} rounds",
  "finFlow.paid": "Paid this month",
  "finFlow.paidSub": "{n} rounds",
  "finFlow.rowsUnit": "rows",
  "nav.financeHome": "Finance home",
  "finHome.title": "Finance",
  "finHome.today": "Today {date}",
  "finHome.nextPay": "Next payout {date}",
  "finHome.inDays": "in {n} days",
  "finHome.dueToday": "today",
  "finHome.overdue": "{n} days ago",
  "finHome.noBatch": "No payout set up yet",
  "finHome.findPerson": "Find a person",
  "finHome.addMoney": "Add money",
  "finHome.loadFailed": "Could not load the finance home — no figures are shown yet. Try again.",
  "finHome.card.next": "To pay · {name}",
  "finHome.card.nextSub": "{people} people · {bills} bills · pays {date}",
  "finHome.card.ready": "Ready, not in a payout",
  "finHome.card.readySub": "{bills} bills due · {box} in the box",
  "finHome.card.decide": "Waiting for you",
  "finHome.card.decideUnit": "items",
  "finHome.card.decideSub": "{u} urgent · {r} not urgent",
  "finHome.card.instalments": "Owed to the company",
  "finHome.card.instalmentsSub": "{plans} debts · {people} people",
  "finHome.card.holds": "On hold",
  "finHome.card.holdsSub": "{n} rows · {w} due this week",
  "finHome.todoTitle": "To do",
  "finHome.todoSub": "Most urgent first · rows disappear once done",
  "finHome.urgent": "Urgent",
  "finHome.empty": "Nothing waiting — finance is clear today",
  "finHome.more": "and {n} more",
  "finHome.batchesTitle": "Open payouts",
  "finHome.batchesEmpty": "No open payouts",
  "finHome.allBatches": "All payouts",
  "finHome.batchOn": "pays {date}",
  "finHome.todo.batchPaid": "{name} has reached its pay day — confirm it was paid",
  "finHome.todo.batchPaidDetail": "File taken · pays {date} · {people} people · THB {total}",
  "finHome.todo.batchFile": "{name} is approved but its bank file is not taken yet",
  "finHome.todo.batchFileDetail": "Lands {date} · {people} people · THB {total}",
  "finHome.todo.batchApprove": "{name} is waiting for approval",
  "finHome.todo.batchApproveDetail": "Frozen · pays {date} · {people} people · THB {total}",
  "finHome.todo.batchDraft": "{name} is still a draft and its pay day is close",
  "finHome.todo.batchDraftDetail": "Pays {date} · {bills} bills · THB {total} — check it, then freeze it for approval",
  "finHome.todo.holdDue": "{n} held rows have reached their date",
  "finHome.todo.holdSoon": "{n} held rows come due within 3 days",
  "finHome.todo.holdLine": "{item} · {who} THB {amount} · due {date} · held because \"{reason}\"",
  "finHome.todo.holdLineNoReason": "{item} · {who} THB {amount} · due {date}",
  "finHome.todo.unbatched": "THB {total} is due and in no payout yet",
  "finHome.todo.unbatchedDetail": "{bills} bills due · {box} people in the box",
  "finHome.todo.unassigned": "{n} money rows name nobody",
  "finHome.todo.unassignedDetail": "They cannot be paid until someone is named",
  "finHome.todo.gaps": "{n} people have incomplete finance details",
  "finHome.todo.gapLine": "{who} — missing {list}",
  "finHome.act.confirmPaid": "Confirm paid",
  "finHome.act.exportFile": "Export file",
  "finHome.act.openBatch": "Open payout",
  "finHome.act.openRow": "View row",
  "finHome.act.toBatch": "Add to payout",
  "finHome.act.openBills": "Open bills",
  "finHome.act.fillIn": "Fill in",
  "finHome.corner.title": "Finance · {n} urgent",
  "finHome.corner.open": "Open finance home",
  "finHome.corner.close": "Dismiss",
  "finPerson.back": "Back to finance home",
  "finPerson.crumbs": "Breadcrumbs",
  "finPerson.people": "People",
  "finPerson.homeSite": "Main contract {name}",
  "finPerson.payee": "Paid to {bank} {account}",
  "finPerson.noPayee": "No bank account yet",
  "finPerson.profile": "Pay settings",
  "finPerson.card.open": "To receive · next payout {date}",
  "finPerson.card.openNegative": "Below zero — more deducted than earned",
  "finPerson.card.openSubDate": "{n} unpaid bills · next payout {date}",
  "finPerson.card.openNoDate": "Not paid yet",
  "finPerson.card.openSub": "{n} unpaid bills",
  "finPerson.card.plansSub": "{n} open debts",
  "finPerson.card.holdsSub": "{n} rows · next date {date}",
  "finPerson.card.none": "None",
  "finPerson.plans": "Instalments",
  "finPerson.plansEmpty": "No instalment plans",
  "finPerson.planTotal": "THB {total}",
  "finPerson.planDone": "{done}/{count} back",
  "finPerson.planPer": "{per} each",
  "finPerson.planLeft": "Left",
  "finPerson.planFinished": "Finished",
  "finPerson.holdTrip": "Trip pay {job}",
  "finPerson.holds": "On hold",
  "finPerson.holdUntil": "Due {date}",
  "finPerson.holdNoDate": "No date set",
  "finPerson.holdWhy": "held because \"{reason}\"",
  "finPerson.bills": "Bills",
  "finPerson.billCards": "Bill details",
  "finPerson.billsEmpty": "No unpaid bills",
  "finPerson.window": "{start}–{end}",
  "finPerson.payOn": "pays {date}",
  "finPerson.inBatch": "in {name}",
  "finPerson.paidIn": "paid {date} · {name}",
  "finPerson.paidBills": "{n} paid bills",
  "nav.financeDebts": "Debts",
  "nav.financeRequests": "Requests",
  "requests.title": "Requests",
  "requests.tab.open": "Open",
  "requests.tab.submitted": "Waiting",
  "requests.tab.received": "Taken",
  "requests.tab.in_progress": "In progress",
  "requests.tab.closed": "Closed",
  "requests.tab.rejected": "Not approved",
  "requests.find": "Search request no., subject, words, name or employee no.",
  "requests.empty": "No requests here",
  "requests.emptyHint": "Staff send requests from the driver app, menu Requests",
  "requests.filedOn": "filed {date}",
  "requests.ownedBy": "taken by {name}",
  "requests.status.submitted": "Waiting",
  "requests.status.received": "Taken",
  "requests.status.in_progress": "In progress",
  "requests.status.closed": "Closed",
  "requests.status.rejected": "Not approved",
  "requests.file": "Attachment {n}",
  "requests.step.received": "Take it",
  "requests.step.in_progress": "Start working",
  "requests.step.closed": "Close",
  "requests.step.rejected": "Not approved",
  "requests.done.received": "Taken — the sender is told in the app",
  "requests.done.in_progress": "Started",
  "requests.done.closed": "Closed — the sender is told in the app",
  "requests.done.rejected": "Marked not approved — the sender is told with the reason",
  "requests.stepFailed": "Could not change the request",
  "requests.money.add": "Add money",
  "requests.money.lend": "Lend / advance",
  "requests.moneyHow": "When this request is about money, make the row with the buttons above — you set the amount and how it is paid; it is tied to this request",
  "requests.reasonNo": "Why not (the sender sees this)",
  "requests.closeNote": "Note on closing (optional, the sender sees it)",
  "requests.moneyTitle": "Money tied to this request",
  "requests.moneyNone": "No money from this request yet",
  "requests.paid": "paid",
  "requests.settled": "settled outside",
  "requests.inRound": "in a pay round",
  "requests.reject.goes": "The {n} money rows tied to it are cancelled with it: {docs}",
  "requests.reject.taken": "Cannot say no yet — {docs} is in a pay round or paid; reverse it on the money list first",
  "requests.history": "Steps",
  "requests.event.submitted": "Filed",
  "docLink.label": "Linked to (optional)",
  "docLink.role.none": "Nothing",
  "docLink.role.work_order": "Repair job",
  "docLink.role.safety": "Safety case",
  "docLink.role.request": "Request",
  "docLink.pick.request": "Pick a request",
  "docLink.pick.work_order": "Pick a repair job",
  "docLink.pick.safety": "Pick a safety case",
  "docLink.loading": "Loading…",
  "docLink.none": "No documents yet",
  "financeTxn.role.work_order": "Repair job",
  "financeTxn.role.safety": "Safety case",
  "ticketMoney.title": "This ticket's money in finance",
  "ticketMoney.paidOf": "{paid} paid of {total}",
  "ticketMoney.empty": "No money rows yet — trip pay enters finance the moment the ticket is closed",
  "ticketMoney.kind.trip": "Trip pay",
  "ticketMoney.kind.adjust": "Trip pay adjusted after payment",
  "ticketMoney.inBill": "In a bill, waiting for a pay round (pay day {date})",
  "ticketMoney.inRound": "In pay round {round} (pays {date})",
  "ticketMoney.paid": "Paid in round {round} on {date}",
  "ticketMoney.hold": "Held until {date}",
  "ticketMoney.settled": "Settled outside the payroll",
  "ticketMoney.none": "In no bill yet",
  "ticketMoney.openBill": "Bill: {bill}",
  "ticketMoney.openRound": "Open pay round",
  "nav.financeReports": "Reports",
  // plan/finance-full J1/J2 — compare with the company's sheet
  "finCompare.title": "Compare with the company's sheet",
  "finCompare.lead": "Lay the amounts from the company's own sheet beside the system, person by person. Used when starting: check the carried-over debts against the accountant's list, and run in parallel with the old payroll sheet. This page only reads — nothing in the system changes",
  "finCompare.step1": "1 · Compare with",
  "finCompare.step2": "2 · The company's sheet",
  "finCompare.kind.debts": "What each person still owes",
  "finCompare.kind.debtsLead": "Opening balances — each person's debts on the chosen day, against the accountant's list",
  "finCompare.kind.round": "What each person is paid in a round",
  "finCompare.kind.roundLead": "Parallel run — each person's amount in the round, against the net pay in the old payroll sheet",
  "finCompare.asOf": "Balance on",
  "finCompare.asOfDay": "Balance on {day}",
  "finCompare.pickRound": "Pick a pay round",
  "finCompare.fileLead": "An .xlsx or .csv with an employee column (code or name) and an amount column — the old system's export works as it is. Several lines for one person are added up",
  "finCompare.pickFile": "Choose file",
  "finCompare.template": "Download an empty template",
  "finCompare.templateFile": "Compare template",
  "finCompare.read": "{name} · {n} lines read",
  "finCompare.noColumns": "No employee and amount columns in this file — the header needs employee / employee id / name and amount / net pay / balance",
  "finCompare.tooMany": "One file compares at most {n} lines",
  "finCompare.unreadable": "This file could not be read — save it again as .xlsx or .csv",
  "finCompare.run": "Compare",
  "finCompare.failed": "Could not compare",
  "finCompare.file": "Compare result",
  "finCompare.export": "Save result to Excel",
  "finCompare.allMatch": "Everyone agrees",
  "finCompare.someOff": "{n} people differ",
  "finCompare.againstLine": "File {file} · against {against}",
  "finCompare.against.round": "round {name} ({status})",
  "finCompare.against.debts": "debts owed on {day}",
  "finCompare.stat.match": "Agree (people)",
  "finCompare.stat.differ": "Differ (people)",
  "finCompare.stat.onlyFile": "Only in the file (people)",
  "finCompare.stat.onlySystem": "Only in the system (people)",
  "finCompare.totals": "File total {file} · system {system} · difference {diff}",
  "finCompare.bad": "{n} lines in the file match no employee",
  "finCompare.badLine": "Line {row} · {who} · {amount} — {why}",
  "finCompare.badMore": "and {n} more lines (in the saved Excel file)",
  "finCompare.filter": "Show",
  "finCompare.show.off": "Differences only",
  "finCompare.show.all": "Everyone",
  "finCompare.show.match": "Agreeing",
  "finCompare.nothingHere": "Nothing in this view",
  "finCompare.diffShort": "off by {diff}",
  "finCompare.both": "file {file} · system {system}",
  "finCompare.col.person": "Employee",
  "finCompare.col.code": "Code",
  "finCompare.col.file": "In the file",
  "finCompare.col.system": "In the system",
  "finCompare.col.diff": "Difference (file − system)",
  "finCompare.col.status": "Result",
  "finCompare.col.lines": "File lines",
  "finCompare.col.sheet": "Compare result",
  "finCompare.col.badSheet": "Unmatched lines",
  "finCompare.col.line": "Line",
  "finCompare.col.problem": "Reason",
  "finCompare.col.tplPerson": "Employee",
  "finCompare.col.tplAmount": "Amount (THB)",
  "finCompare.status.match": "Agrees",
  "finCompare.status.differ": "Differs",
  "finCompare.status.only_file": "Only in the file",
  "finCompare.status.only_system": "Only in the system",
  "finCompare.problem.person_blank": "No employee name or code",
  "finCompare.problem.person_unknown": "No such employee in the system",
  "finCompare.problem.person_ambiguous": "Several people have this name — use the employee code",
  "finCompare.problem.amount_bad": "The amount cannot be read",
  "finHome.compare": "Compare with a sheet",
  "opening.compareLink": "Check carried-over debts against the accountant's list",
  "finReports.title": "Finance reports",
  "finReports.lead": "What staff were paid — by round, by person, and instalments still due. The same figures as the pay round and bill pages; every tab exports to Excel.",
  "finReports.export": "Export Excel",
  "finReports.year": "Year",
  "finReports.bills": "{n} bills",
  "finReports.peopleN": "{n} people",
  "finReports.tab.rounds": "Pay rounds",
  "finReports.tab.people": "By person",
  "finReports.tab.tax": "Withholding tax",
  "finReports.file.tax": "withholding-tax-report",
  "finReports.col.tax": "Withholding tax",
  "finReports.col.form": "Form",
  "finReports.col.pnd1": "PND 1",
  "finReports.col.pnd3": "PND 3",
  "finReports.col.taxBase": "Income taxed",
  "finReports.col.withheld": "Tax withheld",
  "finReports.tax.lead": "Tax the system withheld in paid rounds — for the 50 Tawi certificates and the PND 1 / PND 3 filings; the Excel file splits every month · Income counts in the month its bill ends (the month it is paid), as tax counts money when received",
  "cert.title": "Withholding tax certificate",
  "cert.law": "Section 50 bis of the Revenue Code",
  "cert.payer": "Withholding agent",
  "cert.payee": "Person withheld from",
  "cert.taxId": "Tax ID",
  "cert.idNumber": "ID card number",
  "cert.address": "Address",
  "cert.formOrder": "Form",
  "cert.pnd1a": "PND 1A",
  "cert.pnd3": "PND 3",
  "cert.col.type": "Kind of income paid",
  "cert.col.year": "Tax year",
  "cert.col.paid": "Paid",
  "cert.col.tax": "Tax withheld",
  "cert.type.salary": "1. Salary, wages, allowances, bonuses … under section 40 (1)",
  "cert.type.transport": "5. Income withheld under Revenue Department orders by section 3 tres (transport)",
  "cert.total": "Total paid and tax withheld",
  "cert.sso": "Social security contributions THB {amount}",
  "cert.payerKind": "Payer",
  "cert.withheldAtSource": "(1) withheld at source",
  "cert.statement": "I certify that the above is true and correct.",
  "cert.sign": "Signed, payer",
  "cert.date": "Date issued",
  "cert.none": "No tax withheld from this person this year",
  "cert.back": "Back to the tax report",
  "cert.missing": "Missing on the certificate: {list}",
  "cert.missing.payerTax": "the company's tax ID (settings › payer)",
  "cert.missing.payerAddress": "the company's address",
  "cert.missing.idNumber": "the person's ID card number (HR › ID card)",
  "cert.missing.address": "the person's address",
  "finReports.tax.cert": "50 bis",
  "finReports.tax.pnd1a": "PND 1A (Excel)",
  "finReports.file.pnd1a": "pnd1a",
  "finReports.col.pnd1a": "PND 1A",
  "finReports.col.seq": "No.",
  "finReports.col.idNumber": "Tax ID",
  "finReports.col.address": "Address",
  "finReports.col.income401": "40(1) income, year",
  "finReports.col.taxYear": "Tax withheld, year",
  "finReports.col.condition": "Condition",
  "finCfg.company.taxId": "Company tax ID",
  "finCfg.company.taxIdHint": "13 digits — on the 50 bis certificates and PND 1A",
  "finCfg.company.address": "Company address (as registered)",
  "finReports.tax.withheld": "Tax withheld in {year}",
  "finReports.tax.base": "on income of {amount}",
  "finReports.tax.empty": "No paid round withheld tax in {year} yet",
  "finReports.tab.overdue": "Overdue instalments",
  "finReports.file.rounds": "pay-rounds",
  "finReports.file.people": "paid-by-person",
  "finReports.file.overdue": "overdue-instalments",
  "finReports.col.round": "Pay round",
  "finReports.col.payDay": "Pay day",
  "finReports.col.status": "Status",
  "finReports.col.people": "People",
  "finReports.col.bills": "Bills",
  "finReports.col.total": "Total",
  "finReports.col.approvedBy": "Approved by",
  "finReports.col.approvedAt": "Approved on",
  "finReports.col.paidAt": "Marked paid on",
  "finReports.col.held": "Held while approving",
  "finReports.col.sentBack": "Sent back (times)",
  "finReports.col.createdBy": "Created by",
  "finReports.col.notes": "Notes",
  "finReports.col.person": "Employee",
  "finReports.col.employeeNo": "Employee no.",
  "finReports.col.yearTotal": "Year total",
  "finReports.col.rounds": "Rounds",
  "finReports.col.sum": "Total",
  "finReports.col.overdue": "Overdue",
  "finReports.col.debt": "Debt no.",
  "finReports.col.item": "Item",
  "finReports.col.instalment": "Instalment",
  "finReports.col.dueDay": "Due",
  "finReports.col.amount": "Amount",
  "finReports.col.skipped": "Skipped (times)",
  "finReports.rounds.paid": "Paid this year",
  "finReports.rounds.waiting": "Not paid yet",
  "finReports.rounds.waitingSub": "Draft · awaiting approval · approved",
  "finReports.rounds.count": "Rounds",
  "finReports.rounds.empty": "No pay rounds in {year} yet",
  "finReports.rounds.held": "{n} held",
  "finReports.rounds.sentBack": "Sent back {n} times",
  "finReports.rounds.approvedBy": "Approved by {name}",
  "finReports.people.total": "Paid in {year}",
  "finReports.people.totalSub": "Rounds marked paid only, by the round's pay day",
  "finReports.people.count": "People paid",
  "finReports.people.empty": "No round marked paid in {year} yet",
  "finReports.people.find": "Search name or employee no.",
  "finReports.people.noMatch": "No one matches",
  "finReports.overdue.lead": "Instalments already due that no pay round has taken. Add them to a round on the pay round page, or skip one on the debt page. Held rows are on",
  "finReports.overdue.holds": "the holds page",
  "finReports.overdue.amount": "Overdue amount",
  "finReports.overdue.people": "People",
  "finReports.overdue.empty": "Nothing overdue — every instalment that is due sits in a round",
  "finReports.overdue.instalment": "instalment {n}",
  "finReports.overdue.due": "due {date}",
  "finReports.overdue.skipped": "skipped {n} times",
  "finPerson.earned": "Paid so far",
  "finPerson.earned.month": "This month",
  "finPerson.earned.year": "This year",
  "finPerson.earned.sub": "Rounds marked paid only",
  "finPerson.earned.report": "Everyone",
  "audit.fin.finance_debt_lend": "Lent / advanced",
  "audit.fin.finance_debt_repay_outside": "Paid back outside the payroll",
  "audit.fin.finance_debt_reschedule": "Changed the instalments",
  "audit.fin.finance_debt_close": "Closed a debt",
  "audit.fin.finance_debt_reprice": "Interest reworked",
  "debts.title": "Debts and instalments",
  "debts.lend": "Lend / advance",
  "debts.totals": "THB {total} owed · {n} debts · {people} people",
  "debts.filter": "Debt status",
  "debts.filter.open": "Open",
  "debts.filter.closed": "Closed",
  "debts.filter.all": "All",
  "debts.empty": "No open debts",
  "debts.emptyClosed": "No closed debts yet",
  "debts.progress": "{done}/{count} back",
  "debts.next": "next {date} · {per}",
  "debts.finished": "Closed",
  "debts.left": "left",
  "debts.kept": "kept",
  "debt.notFound": "Debt not found",
  "debt.left": "Left",
  "debt.kept": "Kept",
  "debt.of": "of {total} · {back} back",
  "debt.leftCount": "{n} instalments to go",
  "debt.sourceLoan": "Money lent out",
  "debt.sourcePlan": "Instalment plan",
  "debt.loanLine": "Company paid · {item} THB {amount}",
  "debt.planLine": "{item} THB {amount}",
  "debt.paidOutside": "lent out already (outside the payroll)",
  "debt.paidInBatch": "lent out already (sent in a pay round)",
  "debt.paysWithBatch": "will be sent with the next pay round",
  "debt.interest": "interest {interest}",
  "debt.terms": "{n} instalments of {per}",
  "debt.rowsLoan": "Repayments ({n})",
  "debt.rowsPlan": "Instalments ({n})",
  "debt.row.seq": "Instalment {seq}/{count}",
  "debt.row.outside": "Paid back outside",
  "debt.row.deferred": "moved from an earlier bill",
  "debt.state.paid": "Back",
  "debt.state.outside": "Back outside",
  "debt.state.in_batch": "In a payout",
  "debt.state.held": "On hold",
  "debt.state.due": "To deduct",
  "debt.state.ahead": "Ahead",
  "debt.repay.open": "+ Paid back outside",
  "debt.re.open": "Change instalments",
  "debt.close.open": "Close the debt",
  "debt.repay.explainLoan": "Paid back outside = a repayment row that points at the source, with evidence. What is left drops at once and the open instalments are worked out again.",
  "debt.repay.explainPlan": "Paid back outside = pick the instalments they paid in cash, with evidence. Those stay out of every bill.",
  "debt.repay.title": "Paid back outside the payroll",
  "debt.repay.save": "Save",
  "debt.repay.howMuch": "How much came back",
  "debt.repay.left": "THB {left} left",
  "debt.repay.all": "All of it ({left})",
  "debt.repay.which": "Instalments paid in cash",
  "debt.repay.when": "When, and the evidence",
  "debt.repay.notePh": "e.g. transferred back / paid cash at the branch",
  "debt.repay.note": "Details",
  "debt.repay.sumEmpty": "Enter what came back",
  "debt.repay.sumClosed": "THB {got} back — the debt is closed and the open instalments go",
  "debt.repay.sumSpread": "THB {got} back · THB {left} left in {n} instalments of {per}",
  "debt.repay.sumLeft": "THB {got} back · THB {left} left",
  "debt.re.title": "Change the number of instalments",
  "debt.re.save": "Save",
  "debt.re.only": "Only instalments still open change — ones already back, in a payout or on hold stay as they are",
  "debt.re.count": "Instalments left",
  "debt.re.from": "First instalment",
  "debt.re.reason": "Reason (required)",
  "debt.re.sum": "What is left in {n} instalments of THB {per}, {from} to {end}",
  "debt.re.sumNothing": "Nothing left to spread",
  "debt.re.fixed": "(not counting THB {fixed} in a payout or on hold)",
  "debt.close.title": "Close the debt",
  "debt.close.save": "Close",
  "debt.close.on": "Take it all on",
  "debt.close.sum": "Take everything left, THB {total}, in one instalment on {day}",
  "debt.repaid": "Saved the repayment of {code}",
  "debt.rescheduled": "Changed the instalments of {code}",
  "debt.closed": "Closed {code}",
  "debt.failed": "Could not save",
  "debt.attachRequired": "Attach evidence (required)",
  // NIGHT-1023 N1 — where an instalment is taken
  "lend.home.monthly": "Taken from {who}'s salary bill — the first one in the bill that closes {end}",
  "lend.home.contract": "Taken from {who}'s {contract} contract bill (the contract run most that month) — the first one in the bill that closes {end}",
  "lend.home.box": "{who} has no salary bill and ran no trips that month — instalments wait in \"accumulated items\", which never come due on their own; someone has to put them in a pay round each time",
  "lend.home.checking": "Checking which bill the instalments go to…",
  "debt.where.batch": "In pay round {name} · {status}",
  "debt.where.monthly": "Salary bill closing {end}",
  "debt.where.monthlyLater": "Will be taken in the salary bill closing {end}",
  "debt.where.contract": "{contract} bill closing {end}",
  "debt.where.contractLater": "Will be taken in the {contract} bill closing {end}",
  "debt.where.box": "Accumulated items — put in a pay round by hand",
  "debt.where.boxNote": "Some instalments of this debt wait in {who}'s accumulated items. Those never come due on their own: nothing is taken until someone puts them in a pay round.",
  "debt.where.boxOpen": "Open {who}'s bills",
  "debt.where.status.accumulating": "accumulating",
  "debt.where.status.due": "due, to pay",
  "debt.where.status.in_batch": "in a pay round",
  "debt.where.status.paid": "paid",
  "debt.where.batchStatus.draft": "draft",
  "debt.where.batchStatus.frozen": "locked, waiting for approval",
  "debt.where.batchStatus.approved": "approved",
  "debt.where.batchStatus.paid": "paid",
  // ADVANCE — pay advance + postpone
  "advance.open": "Pay advance",
  "advance.title": "Pay advance",
  "advance.save": "Save advance",
  "advance.who": "For whom",
  "advance.q1": "How much",
  "advance.amount": "Advance amount",
  "advance.amountDecimals": "At most 2 decimal places",
  "advance.amountOverUnpaid": "More than {who}'s unpaid total ({total}) — double-check before saving",
  "advance.q3": "Which bill takes it back",
  "advance.pickPersonFirst": "Pick the person first to see their bills",
  "advance.monthly": "Salary bill closing {end}",
  "advance.contract": "{contract} bill closing {end}",
  "advance.box": "Accumulated items (no main bill)",
  "advance.thisOne": "(this one)",
  "advance.takenOn": "Taken back once in this bill · dated {day}",
  "advance.inRound": "This bill is in a pay round — pick the next one",
  "advance.notSameBill": "Sent with the next round and taken back in the same bill would net to nothing",
  "advance.what": "What it is for (required)",
  "advance.whatPh": "e.g. school fees / medical bill",
  "advance.noItem": "This company has no \"pay advance\" item yet — add it in the money items first",
  "advance.sum": "{who} takes {amount} baht {out} · taken back once from the {bill}",
  "advance.sumIncomplete": "Answer the three questions and the summary appears here",
  "advance.onceHint": "An advance is taken back once, from the bill picked — to pay it back over months, use \"company loan\"",
  "advance.toLoan": "Make it a company loan",
  "advance.need.bill": "the bill to take it back from",
  "advance.need.note": "what it is for",
  "advance.need.evidence": "proof of payment",
  "advance.saved": "Saved {who}'s advance of {amount} · taken back from the {bill}",
  "advance.chip": "Advance",
  "debt.postpone.open": "Postpone / start later",
  "debt.postpone.title": "Postpone — start taking it later",
  "debt.postpone.from": "First instalment on",
  "debt.postpone.quick": "Quick picks",
  "debt.postpone.months": "+{n} months",
  "debt.postpone.save": "Save postponement",
  "lend.title": "Lend / advance",
  "lend.save": "Save",
  "lend.saved": "Saved {code}",
  "lend.failed": "Could not save",
  "lend.who": "Who · under which item",
  "lend.item": "Pick the item (advance / loan)",
  "lend.q1": "How much",
  "lend.principal": "Money lent",
  "lend.interest": "Interest (an amount, optional)",
  "lend.interestHint": "Interest is a fixed amount, not a rate",
  "lend.totalBack": "THB {total} to pay back",
  "lend.q2": "How the money reaches the person",
  "lend.out.outside": "Sent already (cash / own transfer) — attach the slip",
  "lend.out.withPayout": "Send with the next pay round",
  "lend.paidOn": "Sent on {day}",
  "lend.q3": "How it comes back",
  "lend.back.once": "All of it in the next bill",
  "lend.back.split": "In instalments",
  "lend.instalments": "inst.",
  "lend.count": "Instalments",
  "lend.startOn": "From {day}",
  "lend.what": "What is it for (required)",
  "lend.whatPh": "e.g. car repair / school fees",
  "lend.sum.incomplete": "Answer the three questions and the summary appears before saving",
  "lend.sum": "Lends {who} THB {amount}{interest}, {out} · {back}",
  "lend.sum.interest": " + THB {interest} interest",
  "lend.sum.outside": "sent on {day} (outside the payroll)",
  "lend.sum.withPayout": "sent with the next pay round",
  "lend.sum.once": "THB {total} back in the next bill",
  "lend.sum.split": "{n} instalments of THB {per}, ending {end}",
  "lend.rate.fixed": "Interest as an amount (the old way)",
  "lend.rate.onPay": "Interest starts the day the round really pays it out (set when the round is marked paid)",
  "lend.rate.pickDay": "Set the day interest starts",
  "lend.rate.quoteOnPay": "Interest about THB {interest} · about THB {total} back in all (worked out again on the day it is paid)",
  "debt.formula.onPay": "interest starts when it is paid out",
  "opening.debt.formulaHint": "THB {left} of principal still owed earns interest from the carry day (today)",
  "opening.debt.formulaTotal": "Lent (principal)",
  "lend.rate.label": "Interest",
  "lend.rate.none": "No interest",
  "lend.rate.flat": "Flat — worked out once on the full amount",
  "lend.rate.reducing": "Reducing balance — daily on what is still owed",
  "lend.rate.pct": "Rate a year",
  "lend.rate.unit": "% a year",
  "lend.rate.max": "At most 15 % a year by law",
  "lend.rate.style": "Instalments",
  "lend.rate.equal": "The same every month",
  "lend.rate.principal": "Equal principal — highest first, then falling",
  "lend.rate.prepay": "If paid off early",
  "lend.rate.prepayNone": "Interest stays",
  "lend.rate.prepayProRata": "Drop the interest of instalments not yet due",
  "lend.rate.from": "Interest from {day}",
  "lend.rate.fromHint": "The day the money reaches the person — interest counts by the day from here",
  "lend.rate.quote": "Interest THB {interest} · THB {total} back in all",
  "lend.rate.quoting": "Working out the interest…",
  "lend.planFalling": "Worked out: first THB {first}, falling to THB {last} · last on {end}",
  "lend.sum.splitFalling": "{n} instalments, first THB {first} falling to THB {last}, last on {end}",
  "debt.formula.flat": "Flat {rate}% a year",
  "debt.formula.reducing_equal": "Reducing {rate}% a year, equal instalments",
  "debt.formula.reducing_principal": "Reducing {rate}% a year, equal principal",
  "debt.formula.from": "interest from {day}",
  "debt.formula.prepay": "paid off early drops the interest not yet due",
  "debt.row.parts": "principal {principal} · interest {interest}",
  "debt.repay.payoff": "Clears it on {day}: THB {payoff}",
  "debt.repay.payoffDiscount": "THB {discount} of interest not yet due is taken off",
  "debt.repay.reprice": "After saving, the interest and the instalments left are worked out again from that day",
  "finance.err.interest_from": "Interest must start on or before the first instalment",
  "finance.err.debt_too_much": "More than is left on the debt",
  "finHome.createDebt": "Lend / advance",
  "finPerson.planKept": "Kept",
  "finPerson.lend": "Lend / advance",
  "bills.instalment": "Instalment",
  "finHome.todo.overdue": "{n} due instalments are in no payout",
  "finHome.todo.overdueDetail": "THB {total} — the bills holding them are tagged \"instalments taken\" in the pay round; add the bill or leave it for the next round",
  "finHome.todo.overdueLine": "{who} · {item} {code} instalment {seq}/{count} · THB {amount}",
  "finHome.act.decide": "Decide",
  "payBatches.overdue.skippedTimes": "skipped {n} times already",
  "payBatches.overdue.skip": "Skip to the next round",
  "payBatches.billInstalments": "{n} instalments taken {amount}",
  "payBatches.overdue.showThem": "Show them",
  "payBatches.overdue.checkFailed": "Could not check for due instalments; the round can be frozen once the check passes",
  "payBatches.overdue.takeBill": "Add this bill to the round",
  "payBatches.overdue.billEnd": "bill ended {date}",
  "payBatches.overdue.billLine": "{title} · whole bill {total}",
  "payBatches.overdue.inPerson": "Another bill of theirs has an instalment due, and that bill is not in this round",
  "payBatches.overdue.reasonPh": "Why it is skipped (required)",
  "payBatches.overdue.confirmSkip": "Skip",
  "payBatches.overdue.skippedToast": "Moved to the next bill",
  "payBatches.freezeBlocked": "Cannot freeze until the due instalments of {n} people in the round ({names}) are decided, on each person's card",
  "bills.deferredFrom": "moved on from the {date} bill",
  "bills.deferredFromBox": "moved on from the box",
  "payBatches.overdue.checking": "Checking for due instalments…",
  "finPerson.card.advances": "Advanced, not taken back yet",
  "finPerson.card.advancesSub": "advanced {lent} · back {back}",
  "payBatches.lines.open": "Lines / untick",
  "payBatches.lines.title": "Lines in this round",
  "payBatches.lines.keep": "Keep {doc} in this round",
  "payBatches.lines.cameFrom": "moved on from an earlier bill · \"{reason}\"",
  "payBatches.lines.reasonPh": "Why it waits (required)",
  "payBatches.lines.untick": "Not this round",
  "payBatches.lines.more": "and {n} more — see them all on the bill page",
  "payBatches.lines.outTitle": "Not in this round (never lost — moved to the next bill)",
  "payBatches.lines.putBack": "Put {doc} back in this round",
  "payBatches.lines.outLine": "\"{reason}\" · moved to the next bill (from {date})",
  "payBatches.lines.total": "Ticked total",
  "payBatches.lines.explain": "Taken out is not lost — it stays on the person page and comes up in the next bill by itself · a reason is required · a due instalment counts the skip · until the round is frozen",
  "payBatches.lines.unticked": "Moved to the next bill",
  "payBatches.lines.reticked": "Back in this round",
  "audit.fin.bill_defer": "Moved rows to the next bill",
  "audit.fin.bill_undefer": "Put rows back in a round",
  "finImport.title": "Import money rows from a file",
  "finImport.open": "Import from file",
  "finImport.listTitle": "Imports",
  "finImport.listHint": "Every batch imported from a file, newest first — open one to see the rows it made, or undo it as a whole",
  "finImport.listEmpty": "Nothing imported from a file yet",
  "finImport.x.sheetLines": "Lines",
  "finImport.x.sheetItems": "Money items",
  "finImport.x.sheetPeople": "People",
  "finImport.x.sheetHelp": "How to fill in",
  "finImport.col.employee": "Employee (code or name)",
  "finImport.col.item": "Money item",
  "finImport.col.amount": "Amount (baht)",
  "finImport.col.date": "Date (dd/mm/yyyy)",
  "finImport.col.count": "Instalments (blank = once)",
  "finImport.col.start_on": "First instalment (dd/mm/yyyy)",
  "finImport.col.tax_stage": "Before/after tax",
  "finImport.col.note": "Note",
  "finImport.col.department": "Reporting department",
  "finImport.col.reported_by": "Reported by",
  "finImport.field.employee": "Employee",
  "finImport.field.item": "Money item",
  "finImport.field.amount": "Amount",
  "finImport.field.date": "Date",
  "finImport.field.count": "Instalments",
  "finImport.field.start_on": "First instalment",
  "finImport.field.tax_stage": "Before/after tax",
  "finImport.x.itemName": "Name (pick it in the money item column)",
  "finImport.x.itemSide": "Pay/deduct",
  "finImport.x.itemPlan": "Instalments",
  "finImport.x.itemStage": "Asks before/after tax",
  "finImport.x.itemFor": "For (blank = everyone)",
  "finImport.x.itemCode": "Code",
  "finImport.x.personCode": "Code",
  "finImport.x.personName": "Name",
  "finImport.x.personType": "Type",
  "finImport.x.earn": "Pay",
  "finImport.x.deduct": "Deduct",
  "finImport.x.yes": "Yes",
  "finImport.x.preTax": "Before tax",
  "finImport.x.postTax": "After tax",
  "finImport.x.pickFromList": "Pick from the list in this cell",
  "finImport.x.fileName": "money-import-template",
  "finImport.x.help1": "How to fill in the money import form",
  "finImport.x.help2": "1) Fill in the \"Lines\" sheet, one line per row of money, from line 2 — keep the header row as it is",
  "finImport.x.help3": "2) Employee: their code (e.g. DRV-0001) or name as on the \"People\" sheet — use the code when two people share a name",
  "finImport.x.help4": "3) Money item: pick from the list in the cell (from the \"Money items\" sheet) — the item says whether it is paid or deducted, so no minus sign",
  "finImport.x.help5": "4) Amount: in baht, e.g. 1,500 or 1500.50",
  "finImport.x.help6": "5) Date: dd/mm/yyyy, e.g. 9/10/2026 (a Buddhist year is read too)",
  "finImport.x.help7": "6) Instalments: the number and the first instalment's date, only for items paid that way · Before/after tax: only for items that ask, else blank",
  "finImport.x.help8": "7) Upload it on Money rows → Import from file — every line is checked first, nothing is saved until you import, and the whole batch can be undone later",
  "finImport.templateFailed": "Could not make the template",
  "finImport.missingCols": "This file has no {cols} column — use the template above",
  "finImport.empty": "The file has no lines — fill in from line 2, under the header",
  "finImport.tooMany": "The file has {n} lines, more than {max} — split it",
  "finImport.readFailed": "Could not read the file — use .xlsx or .csv",
  "finImport.done": "Imported as {code}",
  "finImport.failed": "Import failed",
  "finImport.doneTitle": "Imported — batch {code}",
  "finImport.doneLine": "{rows} rows · {people} people · deduct {in} · pay {out}",
  "finImport.doneUndo": "Wrong as a whole? Open the batch and choose \"Undo the batch\" — rows not yet in a payout round are voided",
  "finImport.openJob": "Open {code}",
  "finImport.commit": "Import {n} lines",
  "finImport.commitNone": "Import",
  "finImport.step1": "Download the template",
  "finImport.step1Hint": "Made from your money items and people — the money item column has a drop-down",
  "finImport.template": "Download template (.xlsx)",
  "finImport.templateBusy": "Making…",
  "finImport.pastJobs": "Past imports",
  "finImport.step2": "Choose the filled-in file",
  "finImport.pick": "Choose .xlsx or .csv",
  "finImport.pickAgain": "Choose another file",
  "finImport.reading": "Reading and checking…",
  "finImport.step3": "Checked line by line",
  "finImport.showing": "Showing the first {n} of {total} lines — pick the problems or worth-a-look filter to see only those lines",
  "finImport.summary": "{lines} lines · the good ones: {people} people, deduct {in}, pay {out}",
  "finImport.hasErrors": "{n} lines have problems — fix the file and choose it again. Nothing is saved yet.",
  "finImport.allOk": "All {n} lines pass — nothing is saved until you import",
  "finImport.filter": "Show",
  "finImport.f.all": "All {n}",
  "finImport.f.bad": "Problems {n}",
  "finImport.f.warn": "Worth a look {n}",
  "finImport.noneHere": "No lines here",
  "finImport.plan": "{n} instalments from {start}",
  "finImport.lineNo": "Line {n}",
  "finImport.step4": "What is this batch",
  "finImport.reasonPh": "e.g. September traffic fines from the police tickets",
  "finImport.reasonHint": "Lines without their own note carry this · attach the batch's evidence if you have it (optional)",
  "finImport.err.employee_missing": "missing",
  "finImport.err.employee_unknown": "no employee with this code or name",
  "finImport.err.employee_ambiguous": "more than one employee has this name — use the code",
  "finImport.err.item_missing": "missing",
  "finImport.err.item_unknown": "no money item by this name — pick from the template's list",
  "finImport.err.item_ambiguous": "more than one item has this name — use the code",
  "finImport.err.item_trip": "trip pay comes from job tickets, not a file",
  "finImport.err.item_hidden": "this item is hidden",
  "finImport.err.item_computed": "the system works this item out; it cannot be imported",
  "finImport.err.item_not_for_person": "this item does not apply to this kind of employee",
  "finImport.err.item_direction": "the item and the money's direction do not match",
  "finImport.err.item_recurring": "their profile already pays or deducts this every month — change the profile, or adjust that month on Money rows",
  "finImport.err.amount_missing": "missing",
  "finImport.err.amount_invalid": "not an amount — a number like 1,500 or 1500.50",
  "finImport.err.amount_zero": "must be more than 0",
  "finImport.err.count_invalid": "a number 1–60, or blank for once",
  "finImport.err.item_no_installments": "this item is not paid in instalments",
  "finImport.err.count_too_many": "too many instalments for this amount",
  "finImport.err.date_missing": "missing",
  "finImport.err.date_invalid": "not a date — use dd/mm/yyyy, e.g. 9/10/2026",
  "finImport.err.start_invalid": "not a date — use dd/mm/yyyy",
  "finImport.err.tax_stage_invalid": "\"Before tax\", \"After tax\" or blank",
  "finImport.err.month_closed": "that month is closed",
  "finImport.err.refused": "refused: {detail}",
  "finImport.err.other": "refused ({code})",
  "finImport.warn.same_in_file": "same as line {detail} of this file — on purpose?",
  "finImport.warn.same_in_ledger": "the same row is already in the ledger ({detail}) — imported twice?",
  "finImport.warn.future_day": "the date is still to come",
  "finImport.warn.other": "worth a look ({code})",
  "finImport.status.running": "In progress",
  "finImport.status.done": "In use",
  "finImport.status.undone": "Undone",
  "finImport.status.failed": "Failed",
  "finImport.rowsPeople": "{rows} rows · {people} people",
  "finImport.undoneLine": "Undone {date} by {who} · {n} rows voided · {kept} kept",
  "finImport.takeBack": "deduct {amount}",
  "finImport.payOut": "pay {amount}",
  "finImport.notFound": "No such import",
  "finImport.jobTitle": "Import",
  "finImport.evidence": "{n} evidence files",
  "finImport.k.rows": "Rows",
  "finImport.k.people": "People",
  "finImport.k.in": "Deduct",
  "finImport.k.out": "Pay",
  "finImport.failedLine": "The import failed and everything it had written was voided: {why}",
  "finImport.keptWhy": "Kept rows are in a frozen or paid payout round, or their month is closed — fix them with an adjustment in the next round",
  "finImport.rowsTitle": "Rows this batch made ({n})",
  "finImport.row.live": "In use",
  "finImport.row.frozen": "In a payout round",
  "finImport.row.paid": "Paid",
  "finImport.row.locked": "Month closed",
  "finImport.row.voided": "Voided",
  "finImport.undo.open": "Undo the batch",
  "finImport.undo.title": "Undo {code}",
  "finImport.undo.confirm": "Undo {n} rows",
  "finImport.undo.what": "This voids the {n} rows the batch made that are not in a payout round yet.",
  "finImport.undo.keeps": "{n} more are in a payout round — a round not paid yet is sent back first; for paid ones the system writes a reversal into the next bill.",
  "finImport.undo.reason": "Why (required)",
  "finImport.undo.doneReversed": "Import {code} undone · {n} rows cancelled · {reversed} already paid are taken back in the next bill",
  "finImport.undo.inRound": "Cannot undo — some rows are in round {rounds}, not paid yet. Send that round back first",
  "finImport.undo.done": "{code} undone · {n} voided · {kept} kept",
  "finImport.undo.failed": "Could not undo",
  "finImport.undo.nothing": "Every row of this batch is in a payout round or voided already — nothing to undo",
  "finImport.undo.explain": "Undone rows are voided, not deleted, with their history · money already paid gets a reversal in each person's next bill",
  "audit.fin.bulk_job_import": "Imported money rows from a file",
  "audit.fin.bulk_job_undo": "Undid an import",
  "finImport.undoneLineAll": "Undone {date} by {who} · all {n} rows voided",
  "leave.title": "Settle everything — {name}",
  "leave.subtitle": "Leaving on {date} (from HR)",
  "leave.open": "Settle everything (leaving)",
  "leave.banner": "Leaving on {date} — not settled yet. A payout round with this person cannot be frozen until it is.",
  "leave.intro": "Every open balance is added up for you — decide only the damages",
  "leave.line.debt": "{item} {code} · {n} instalments left",
  "leave.line.deposit": "Deposit held back {code}",
  "leave.line.depositDropped": "the {n} instalments left are dropped",
  "leave.line.bills": "Money in bills not paid yet ({n} bills)",
  "leave.nothingOpen": "Nothing open",
  "leave.damage": "Damages (if any)",
  "leave.damageHint": "Taken from the deposit / the final total · link a safety case",
  "leave.case": "Related safety case",
  "leave.noCase": "No case",
  "leave.tooEarly": "Settling opens on the last working day ({date}) — for now this is a preview only",
  "leave.info.lastBill": "Last bill: {title}, {from}–{to}, paid {payout}",
  "leave.info.lastBillNoWindow": "Last bill: {title}",
  "leave.info.moneyDay": "The settled money is dated {date}",
  "leave.total": "Final total",
  "leave.negative": "Below zero = they owe the company — choose below",
  "leave.positive": "The company pays this in the next payout round",
  "leave.negativeQ": "Below zero: what to do",
  "leave.neg.carry": "Carry it as a debt",
  "leave.neg.carryHint": "The last bill comes to zero; this becomes a debt collected outside the payroll (on the debts page)",
  "leave.neg.write_off": "Write it off",
  "leave.neg.write_offHint": "The company does not collect it; a write-off row with the reason",
  "leave.negWhy": "Reason (required)",
  "leave.note": "Note (required)",
  "leave.notePh": "e.g. resignation letter no. …, vehicle and equipment returned",
  "leave.info.inBatch": "{n} bills ({total}) are in a frozen round already — paid with it, not counted here",
  "leave.info.holds": "{n} held rows ({total}) — decided separately, not counted here",
  "leave.info.later": "{n} more rows are dated later — they come in their own bill",
  "leave.info.recurring": "{n} profile rows after the leaving date will be voided",
  "leave.info.stops": "On settling, every debt becomes one instalment on the settled day, and no new instalments are made",
  "leave.submit": "Settle",
  "leave.done": "Settled",
  "leave.failed": "Could not settle",
  "leave.noDate": "No leaving date yet — set it in the person's finance details first",
  "finHome.todo.leaving": "{n} people reached their last working day, not settled yet",
  "finHome.todo.leavingDetail": "Settle before their last payout — a round with them cannot be frozen until then",
  "finHome.todo.leavingLine": "{who} · last working day {date}",
  "finHome.act.settle": "Settle",
  "finHome.todo.leavingSoon": "{n} people leaving soon — last working day is near",
  "finHome.todo.leavingSoonDetail": "The first one works their last day on {date}. Open the settle sheet to see the money ahead of time.",
  "finHome.todo.afterLeaving": "{total} baht still to collect after leaving, from {n} people",
  "finHome.todo.afterLeavingDetail": "Debts carried past leaving are collected outside the payroll. See who owes what and what came back on the leavers page.",
  "finHome.act.leavers": "See leavers",
  "nav.financeLeavers": "Leavers",
  "leavers.title": "Leavers",
  "leavers.lead": "People about to leave, people who have left, and people who still owe the company after leaving. Settle and collect from here.",
  "leavers.total.unsettled": "Not settled",
  "leavers.total.owing": "Still owing after leaving",
  "leavers.total.owed": "Total to collect",
  "leavers.total.people": "{n} people",
  "leavers.total.baht": "{total} baht",
  "leavers.col.person": "Name",
  "leavers.col.status": "Status",
  "leavers.col.lastDay": "Last working day",
  "leavers.col.stage": "Settlement",
  "leavers.col.owed": "Owed after leaving",
  "leavers.stage.coming": "Leaving",
  "leavers.stage.unsettled": "Day came, not settled",
  "leavers.stage.settled": "Settled",
  "leavers.out.carry": "Carried as a debt, collected outside the payroll",
  "leavers.out.write_off": "Written off",
  "leavers.out.pay": "Company pays the rest, {total} baht",
  "leavers.out.zero": "Nothing owed either way",
  "leavers.settledOn": "Settled {day}",
  "leavers.inDays": "in {n} days",
  "leavers.today": "today",
  "leavers.owed": "Owes {amount} baht",
  "leavers.collected": "{amount} baht came back",
  "leavers.collectedAll": "{amount} baht came back in full",
  "leavers.settle": "Settle",
  "leavers.openDebt": "See debt / collect",
  "leavers.empty": "Nobody has left or is about to leave",
  "audit.fin.person_finance_settle": "Settled on leaving",
  "leave.undo.open": "Undo the settlement",
  "leave.undo.title": "Undo the settlement — {name}",
  "leave.undo.intro": "Everything the settlement changed goes back as it was, and the leaving of this person can be edited or cancelled again.",
  "leave.undo.backHead": "What comes back",
  "leave.undo.backNone": "No instalments to put back",
  "leave.undo.debt": "{item} {code} goes back to its old plan, {n} instalments left",
  "leave.undo.deposit": "Deposit {code} is taken again by its old plan",
  "leave.undo.recurring": "{n} profile rows after the leaving come back",
  "leave.undo.cancelHead": "What is cancelled",
  "leave.undo.cancelRows": "{n} rows the settlement wrote (the final instalment, the deposit refund, damages, write-off or the carried debt)",
  "leave.undo.cancelDamage": "Damages {amount}",
  "leave.undo.cancelCarry": "Carried debt {amount} ({code})",
  "leave.undo.cancelWriteOff": "Write-off {amount}",
  "leave.undo.after": "After this the person has to be settled again; their bills are as they were before the settlement.",
  "leave.undo.reason": "Why (required)",
  "leave.undo.reasonPh": "e.g. wrong damages, or the leaving date changed",
  "leave.undo.submit": "Undo the settlement",
  "leave.undo.done": "Settlement undone",
  "leave.undo.failed": "Could not undo the settlement",
  "leave.undo.blocked.leaving_not_settled": "This person is not settled; there is nothing to undo.",
  "leave.undo.blocked.leaving_undo_no_record": "This settlement was made before the old plans were kept, so it cannot be undone automatically. To fix it, edit the instalments and rows one by one on the debts page.",
  "leave.undo.blocked.leaving_undo_in_round": "The final bill or an instalment is in a round that is frozen, approved or paid ({docs}), so it cannot be undone now. Send that round back first and then undo again. A round that is paid cannot be undone.",
  "leave.undo.blocked.leaving_undo_changed": "Rows {docs} were changed after the settlement, so the old plan is not put over them. Look at their history first.",
  "audit.fin.person_finance_settle_undo": "Settlement undone",
  "leave.settledHead": "Settled on {date} by {who}",
  "leave.outcomeCarry": "After the last payout they still owe {amount} — debt {code}, collected outside the payroll",
  "leave.outcomeWriteOff": "{amount} written off ({reason}) — nothing owed after the last payout",
  "leave.outcomePay": "The company pays the final {total} in the next payout round — nothing owed after that",
  "leave.outcomeZero": "Nothing owed either way after the last payout",
  "leave.card.lastBill": "Taken in the last bill",
  "leave.card.lastBillSub": "{n} debts · gone once the last round is paid",
  "leave.card.owedAfter": "Owed after leaving",
  "leave.card.owedAfterSub": "collected outside the payroll · {codes}",
  "leave.chip.carried": "Owed after leaving · collected outside",
  "leave.chip.lastBill": "In the last bill",
  "finSettings.sum.earn": "Adds \"{name}\" as money paid out",
  "finSettings.sum.deduct": "Adds \"{name}\" as money taken back",
  "finSettings.sum.contractorAfterTax": "taken from external drivers after the 1% withholding",
  "finSettings.sum.postTax": "deducted after tax",
  "finSettings.sum.monthlyHomeSite": "once a month, from the salary bill or the bill of the contract run most that month (contractors)",
  "finSettings.sum.perDay": "worked out per day",
  "finSettings.sum.installments": "can be paid in installments",
  "finSettings.sum.everyone": "applies to everyone",
  "finSettings.sum.onlyFor": "applies to {types} only",
  "finSettings.added": "Added \"{name}\"",
  "finSettings.savedLater": "Saved — the new rule for \"{name}\" applies from {day}",
  "finSettings.f.when": "New rule applies from",
  "finSettings.f.whenToday": "Today",
  "finSettings.f.whenNote": "Rows already recorded stay as they are; rows dated from the chosen day use the new rule",
  "finSettings.f.whenLater": "On {day} the system switches over by itself; until then the old rule applies. Take it back from the history below or Pending",
  "finSettings.f.history": "Rule history",
  "finSettings.tag.waiting": "Rule changes {day}",
  "pending.kind.finance_item": "Money item rule",
  "finSettings.saved": "Saved \"{name}\"",
  "finSettings.hiddenToast": "Hid \"{name}\" — rows that already use it stay",
  "finSettings.shownToast": "Showing \"{name}\" again",
  "finSettings.save": "Save",
  "finSettings.failed": "Could not save",
  "closed.payroll.heading": "The old payroll is closed",
  "closed.payroll.body": "Money now moves one way: transactions → bills → payouts → bank file. Salary and trip pay sit in each person's bill and are paid through a payout.",
  "closed.formulas.heading": "The formulas page was removed",
  "closed.formulas.body": "It was a sample that never connected to the system. Trip pay is set on the job ticket (prefilled from the contract or province rates); salary is set on each employee's money tab.",
  "closed.toBills": "Go to bills",
  "closed.toPayBatches": "Go to payouts",
  "closed.toSalaryRules": "Go to the employee list",
  "closed.toProvinceRates": "Go to province rates",
  "driverPay.notPayment": "This page decides trip pay one trip at a time (how much, or nothing). It pays no one — payment happens under Finance › Payouts.",
  "driverPay.toPayBatches": "Go to payouts",
  "driverPay.exportReport": "Download report .xlsx",
  "driverPay.lockHint": "Lock {period}: this month's trip pay stops following ticket edits (this is not a payment)",
  "driverPay.lockPending": "{n} trip(s) in this period still need a decision",
  "driverPay.lockTitle": "Lock {period}?",
  "driverPay.lockBody": "The whole period is frozen at {total} ({payable} payable trips, {excluded} excluded). After locking, rates and decisions cannot change retroactively — corrections become adjustments in the next period. Locking pays no one; payment happens in Payouts.",
  "driverPay.lockConfirm": "Lock",
  "nav.financeTxn": "Finance Transactions",
  "financeTxn.role.ticket": "Job ticket",
  "financeTxn.role.vehicle": "Vehicle",
  "financeTxn.role.employee": "Employee",
  "financeTxn.role.contract": "Contract",
  "financeTxn.role.route": "Route",
  "financeTxn.vendorName": "Or type a vendor name",
  "financeTxn.syncedAt": "Last synced {at}",
  "financeTxn.neverSynced": "Nothing has been synced into this table yet",
  "financeTxn.searchTicket": "Search job ticket",
  "financeTxn.searchVehicle": "Search by plate or vehicle ID",
  "financeTxn.searchEmployee": "Search by name or employee ID",
  "financeTxn.searchContract": "Search contract",
  "financeTxn.searchRoute": "Search route code or name",
  "financeTxn.title": "Finance transactions",
  "financeTxn.subtitle": "Every money event recorded in the system",
  "financeTxn.docNo": "Doc no.",
  "financeTxn.type": "Type",
  "financeTxn.party": "Paid to / received from",
  "financeTxn.amount": "Amount",
  "financeTxn.occurredOn": "Transaction date",
  "financeTxn.occurredTime": "Time",
  "financeTxn.noTime": "The source recorded no time",
  "financeTxn.linkedDoc": "Reference document",
  "financeTxn.kind.recurring_adjust": "Back-dated salary/leave adjustment",
  "financeTxn.kind.trip_pay_adjust": "Trip pay adjusted after payout",
  "financeTxn.kind.carry": "Carried over",
  "financeTxn.kind.trip_pay": "Driver trip pay",
  "financeTxn.kind.job_revenue": "Route revenue",
  "financeTxn.kind.manual": "Manually recorded",
  "financeTxn.origin.manual": "Recorded manually",
  "financeTxn.edited": "Edited",
  "financeTxn.estimatedTime": "Estimated time",
  "financeTxn.revenue": "Revenue",
  "financeTxn.expense": "Expense",
  "financeTxn.direction": "Revenue / expense",
  "financeTxn.sourceRollup": "Totals from rollup table",
  "financeTxn.sourceGroup": "Totals computed live",
  "financeTxn.add": "Add entry",
  "financeTxn.addTitle": "Record a finance entry",
  "financeTxn.linkFirst": "Which document does this belong to",
  "financeTxn.reason": "Reason",
  "financeTxn.save": "Save",
  "financeTxn.cancel": "Cancel",
  "financeTxn.noMatch": "No transactions match these filters",
  "financeTxn.noneYet": "No finance transactions in the selected range",
  "financeTxn.empty": "No finance transactions in the selected range",
  "financeTxn.lastSynced": "Last synced",
  "financeTxn.notRealtime": "Every money row of the company, one line each — trip pay arrives the moment a ticket is closed",
  "financeTxn.history": "Change history",
  "financeTxn.derivedValue": "Value from source",
  "financeTxn.effectiveValue": "Value in use",
  "financeTxn.clearOverride": "Remove edit record",
  "financeTxn.reverse": "Create reversing entry",
  "financeTxn.void": "Void entry",
  "financeTxn.lockedNotice": "This period is locked — edits are not allowed, create a reversing entry",
  // hunt r1-money item 1/4/5/6 (2026-10-25) — the company "Add entry" box (AddTxnDialog)
  "financeTxn.missingPrefix": "Still missing: {fields}",
  "financeTxn.missingLink": "the linked document",
  "financeTxn.missingAmount": "the amount",
  "financeTxn.amountDecimals": "Enter at most 2 decimal places",
  "financeTxn.amountSignHint": "Choose revenue / expense with the buttons above — this field only takes digits, no minus sign needed",
  "financeTxn.amountLargeWarn": "This amount is unusually large (over {max}) — press \"Save\" again to confirm it is intended",
  "financeTxn.amountLargeConfirmed": "This amount is confirmed — press \"Save\" again to save it",
  "financeTxn.editAction": "Edit amount",
  "financeTxn.editTitle": "Edit finance entry",
  "financeTxn.editReason": "Reason for the edit",
  "financeTxn.editReasonRequired": "Say why before saving",
  "financeTxn.editLinkedReadonly": "The linked document can't be moved here — the original link stays",
  "financeTxn.editNoChange": "Nothing changed yet",
  "financeTxn.staleWrite": "This row has already changed — reload it first",

  /* ── ADR 0363 · หน้าแดชบอร์ด ─────────────────────────── */
  "dashboard.tab.today": "Today",
  "dashboard.tab.ontime": "On-time & jobs",
  "dashboard.tab.fleet": "Fleet & fuel",
  "dashboard.tab.contract": "Contracts",
  "dashboard.tab.people": "People",
  "dashboard.tile.today.timeline": "Today's work",
  "dashboard.tile.today.overdueConfirm": "Jobs past accept time",
  "dashboard.tile.today.overdueStart": "Jobs past start time",
  "dashboard.tile.jobs.ontimeSplit": "On time · Late · Unmeasurable",
  "dashboard.tile.jobs.lateByRoute": "Routes that run late most",
  "dashboard.tile.jobs.completedVia": "Jobs closed by an admin",
  "dashboard.tile.jobs.cancelled": "Cancelled this period",
  "dashboard.tile.fleet.statusSplit": "Fleet status",
  "dashboard.tile.fuel.kmplFleet": "Average fuel economy",
  "dashboard.tile.fuel.worstVehicles": "Vehicles burning more than spec",
  "dashboard.tile.fuel.cost": "Fuel cost this period",
  "dashboard.tile.contracts.expiring": "Contracts expiring",
  "dashboard.tile.contracts.remindersDue": "Memos due",
  "dashboard.tile.finance.roundsWaiting": "Pay rounds awaiting approval",
  "dashboard.unit.round": "rounds",
  "dashboard.unit.roundWaiting": "awaiting approval",
  "dashboard.rounds.waiting": "awaiting approval",
  "dashboard.rounds.approved": "approved, not marked paid",
  "dashboard.rounds.approvedN": "{n} approved, not paid yet",
  "dashboard.rounds.none": "No pay round awaiting approval",
  "dashboard.rounds.open": "All pay rounds",
  "dashboard.note.roundsWaiting": "The number is frozen rounds awaiting approval — approved rounds not marked paid yet are listed too",
  "dashboard.tile.people.leavePending": "Leave awaiting approval",
  "dashboard.tile.people.leaveToday": "On leave today",
  "dashboard.tile.people.docsExpiring": "Documents expiring",
  "dashboard.tile.people.headcount": "Headcount",
  "dashboard.cannotCompute.noTicketsToday": "No jobs today yet",
  "dashboard.cannotCompute.noTicketsInPeriod": "No jobs in this period",
  "dashboard.cannotCompute.noLateTicket": "No late jobs this period",
  "dashboard.cannotCompute.noCompletedTicket": "No completed jobs this period",
  "dashboard.cannotCompute.noApprovedFuelEntries": "No approved fuel entries in this period",
  "dashboard.cannotCompute.noMeasuredVehicle": "No vehicle has a measurable fuel rate yet",
  "dashboard.period.7d": "7 days",
  "dashboard.period.30d": "30 days",
  "dashboard.period.90d": "90 days",
  "dashboard.empty": "No data in this period",
  "dashboard.unit.ticket": "jobs",
  "dashboard.unit.vehicle": "vehicles",
  "dashboard.unit.person": "people",
  "dashboard.unit.contract": "contracts",
  "dashboard.unit.reminder": "items",
  "dashboard.unit.reminderOverdue": "items overdue",
  "dashboard.unit.thb": "THB",
  "dashboard.unit.km_per_l": "km/L",
  "dashboard.split.onTime": "On time",
  "dashboard.split.late": "Late",
  "dashboard.split.unmeasurable": "Unmeasurable",
  "dashboard.split.byDriver": "Driver closed",
  "dashboard.split.byAdmin": "Admin closed",
  "dashboard.split.unknown": "Not recorded",
  "dashboard.fleet.active": "In service",
  "dashboard.fleet.repair": "In repair",
  "dashboard.fleet.stored": "Stored",
  "dashboard.fleet.decommissioned": "Decommissioned",
  "dashboard.total": "Total",
  "dashboard.daysLeft": "days",
  "dashboard.daysLeftN": "{n} days left",
  "dashboard.reminder.overdue": "Overdue",
  "dashboard.reminder.dueSoon": "Due in 7 days",
  "dashboard.reminder.open": "Open in total",
  "dashboard.today": "Today",
  "dashboard.noRoute": "Jobs with no route",
  "dashboard.noExpiring": "No contract expires within 30 days",
  "dashboard.asOfNow": "As of now",
  "dashboard.band.tickets": "Jobs",
  "dashboard.band.fuel": "Fuel",
  "dashboard.band.vehicles": "Vehicles",
  "dashboard.tile.today.cancelled": "Cancelled jobs",
  "dashboard.tile.fuel.logsToday": "Fuel entries",
  "dashboard.tile.fuel.pending": "Not reviewed",
  "dashboard.tile.fuel.reviewed": "Reviewed",
  "dashboard.tile.today.vehiclesInUse": "Vehicles in use",
  "dashboard.tile.vehicle.docsExpiring": "Insurance/tax expiring",
  "dashboard.cannotCompute.notWiredYet": "—",
  "dashboard.shape.draft": "Draft",
  "dashboard.shape.assigned": "Assigned",
  "dashboard.shape.driverAccepted": "Accepted",
  "dashboard.shape.started": "Started",
  "dashboard.shape.inProgress": "In progress",
  "dashboard.shape.completed": "Completed",
  "dashboard.shape.closed": "Closed",
  "dashboard.shape.other": "Other status",
  "dashboard.shape.now": "Now",
  "dashboard.shape.noTime": "No departure time",
  "dashboard.shape.axis": "Scheduled departure hour · full day 00–23 · number on the bar = tickets",
  "dashboard.shape.yUnit": "tickets",
  "dashboard.shape.totalOf": "{n} tickets in total",
  "dashboard.compare.yesterdayNow": "Yesterday at this hour",
  "dashboard.compare.yesterdayFull": "Yesterday, full day",
  "dashboard.compare.prevPeriod": "Previous period",
  "dashboard.compare.sameDayLastMonth": "Last month",
  "dashboard.compare.sameDayLastMonthLong": "Last month, same day",
  "dashboard.compare.none": "—",
  "dashboard.period.thisMonth": "This month",
  "dashboard.period.label": "Period",
  "dashboard.period.todayAt": "{date} · data as of {time}",
  "dashboard.period.asOfVs": "As of {date} · same snapshot on {prev}",
  "dashboard.end.oldestPending": "Oldest pending {n} days",
  "dashboard.end.approved": "Approved {n}",
  "dashboard.end.rejected": "Rejected {n}",
  "dashboard.end.refusedByDriver": "Refused by driver {n}",
  "dashboard.end.measured": "Measured {n}/{total}",
  "dashboard.end.fromApproved": "From {n} approved entries",
  "dashboard.end.clear": "Clear →",
  "dashboard.end.ofDrivers": "Drivers {n}",
  "dashboard.end.expiredAlready": "Expired {n}",
  "dashboard.end.withinDays": "Within {n} days",
  "dashboard.end.ofActive": "of {n} active contracts",
  "dashboard.end.overdueItems": "{n} items overdue",
  "dashboard.end.notRanked": "{n} vehicles not ranked — cannot compute",
  "dashboard.end.ofMeasured": "Late {pct}% of {n} measurable tickets",
  "dashboard.end.fromPct": "from {pct}%",
  "dashboard.end.outOf": "of {n} tickets",
  "dashboard.hint.unmeasurable": "“Cannot measure” is the third block — it is not counted as on time",
  "dashboard.hint.worstFuel": "Against the model's spec — the shorter the bar, the worse the burn",
  "dashboard.note.ontimeSplit": "“Cannot measure” = no expected finish, or the ticket never started — not counted as on time",
  "dashboard.note.lateByRoute": "Only the top 3 are dark, the rest grey · bar = share of late tickets on that route",
  "dashboard.note.fleetStatus": "Every status must be listed, decommissioned included, or the rows do not add up to the total",
  "dashboard.note.worstFuel": "Red = below 70% of spec · the rest grey",
  "dashboard.note.expiring": "Expiring within 30 days · dark only under 14 days left",
  "dashboard.note.reminders": "Only “overdue” is coloured — the other two rows are context, not an alarm",
  /* ── ADR 0377 / 0378 — War Room executive tab (one block, keep together) ── */
  "warroom.exec.range": "Range",
  "warroom.exec.preset.today": "Today",
  "warroom.exec.preset.yesterday": "Yesterday",
  "warroom.exec.preset.7d": "7 days",
  "warroom.exec.preset.30d": "30 days",
  "warroom.exec.preset.month": "This month",
  "warroom.exec.custom": "Custom",
  "warroom.exec.from": "From",
  "warroom.exec.to": "To",
  "warroom.exec.apply": "Apply",
  "warroom.exec.err.too_long": "At most {max} days — longer ranges are on the dashboard",
  "warroom.exec.err.order": "Start must not be after end",
  "warroom.exec.err.future": "Future dates cannot be picked",
  "warroom.exec.err.format": "Invalid date",
  "warroom.exec.asOf": "Data as of {time}",
  "warroom.exec.refresh": "Refresh",
  "warroom.exec.noJobs": "No tickets in this range",
  "warroom.exec.progress.title": "Jobs",
  "warroom.exec.progress.doneOf": "of {total} tickets done",
  "warroom.exec.progress.why": "Tickets in the selected range, cancelled excluded ({c})",
  "warroom.exec.seg.onTime": "On time",
  "warroom.exec.seg.late": "Late",
  "warroom.exec.seg.used": "Had work",
  "warroom.exec.seg.unused": "No work",
  "warroom.exec.ontime.title": "On time",
  "warroom.exec.ontime.cannot": "Not measurable yet — no ticket has left",
  "warroom.exec.ontime.late": "{n} late",
  "warroom.exec.cmp.up": "▲ Better",
  "warroom.exec.cmp.down": "▼ Worse",
  "warroom.exec.cmp.from": "than {p}% in {w}",
  "warroom.exec.cmp.same": "Same as {p}% in {w}",
  "warroom.exec.cmp.na": "Not comparable — {w} has nothing measurable",
  "warroom.exec.cmp.winSingle": "the {n} days before",
  "warroom.exec.cmp.winRange": "the {n} days before",
  "warroom.exec.vehicles.title": "Vehicles used",
  "warroom.exec.vehicles.of": "of {fleet}",
  "warroom.exec.vehicles.pct": "{pct}% of the fleet used",
  "warroom.exec.contracts.title": "Contracts to look at",
  "warroom.exec.contracts.count": "{n} contracts",
  "warroom.exec.contracts.colName": "Contract",
  "warroom.exec.contracts.colBehind": "Past end, not done",
  "warroom.exec.contracts.none": "No contract",
  "warroom.exec.contracts.other": "Others",
  "warroom.exec.contracts.pick": "Filter the page to this contract",
  "warroom.exec.contracts.more": "{n} more not going to plan · show all ▾",
  "warroom.exec.contracts.fold": "Back to the top 5 ▴",
  "warroom.exec.contracts.onPlan": "{n} going to plan",
  "warroom.exec.contracts.allOk": "Every contract is going to plan ✓",
  "warroom.exec.must.title": "To handle now",
  "warroom.exec.must.sos": "SOS from drivers",
  "warroom.exec.must.overdue_start": "Due to leave, not left",
  "warroom.exec.must.overdue_confirm": "Driver has not accepted",
  "warroom.exec.must.stale_from_yesterday": "Earlier tickets still open",
  "warroom.exec.must.docs_expired_on_job": "Working today with expired documents",
  "warroom.exec.must.tomorrow_unready": "Tomorrow without driver/vehicle",
  "warroom.exec.must.sosClear": "No SOS · everything else normal",
  "warroom.exec.must.clear": "Nothing to handle ✓",
  "warroom.exec.must.notLive": "This box is the state right now — the range does not include today and no history is kept",
  "warroom.exec.must.goToday": "Switch to today →",
  "warroom.exec.see": "View →",
  "warroom.exec.trend.last7": "On time, last 7 days",
  "warroom.exec.trend.daily": "On time per day",
  "warroom.exec.trend.noWork": "No work",
  "warroom.exec.trend.tip": "{day} · from {n} tickets",
  "warroom.exec.contracts.colDoneLate": "Finished late",
  "warroom.exec.ontime.lateSrc": "= finished late {d} · still open past the end {o}",
  "warroom.exec.progress.whyStale": " · of which {s} are from earlier days nobody closed",
  "warroom.exec.progress.whyOver": "Past end, not done: {n} = not started {ns} · running {r}",
  "warroom.exec.progress.overStale": "({n} left open from earlier days)",
  "warroom.exec.progress.over": "{n} of these are past their end time",
  "warroom.exec.why.open": "In progress — not done yet (not started, or running)",
  "warroom.exec.why.done_late": "Finished late — done, but after that",
  "warroom.exec.why.on_time": "On time — finished within the expected end + grace",
  "warroom.exec.seg.open": "In progress",
  "warroom.exec.seg.done_late": "Finished late",
  "warroom.exec.seg.on_time": "On time",
  "warroom.exec.why.onTime": "On time — finished within the expected end + grace",
  "warroom.exec.why.late": "Late — finished after that",
  "warroom.exec.why.used": "Had work — at least one ticket in this range",
  "warroom.exec.why.unused": "No work in this range",
  "warroom.exec.ontime.whyN": "From {n} tickets that have left (not-yet-left ones do not count)",
  "warroom.exec.ontime.whyCmp": "Compared with {w} ({dates})",
  "warroom.exec.ontime.whyAdmin": "{a} closed by an admin for the driver — their end time may be off",
  "warroom.exec.vehicles.whyFleet": "Out of {fleet} vehicles in service (retired excluded)",
  "warroom.exec.contracts.whyWhich": "Only contracts with tickets past their end and not done, or finished late, most first",
  "warroom.exec.contracts.whyClick": "Click a name to filter the page to that contract",
  "warroom.exec.trend.volume": "Tickets per day",
  "warroom.exec.trend.todayOut": "Today, left {n}",
  "warroom.exec.trend.todayRest": "Not left {n}",
  // ADR 0399 — per-org modules
  "admin.modules.tab": "Modules",
  "admin.modules.name.cargo": "Cargo",
  "admin.modules.name.dashboard": "Dashboard",
  "admin.modules.desc.dashboard": "The dashboard's insight tabs (every tab but \"Today\") · when off nobody in the org can open them (API too) · the \"Today\" tab is open to every org · on by default",
  "admin.modules.offChip": "{name} off",
  "admin.modules.offChip.dashboard": "Insights off",
  "admin.modules.name.passengers": "Passengers",
  "admin.modules.desc.cargo": "Product list · ticket Cargo tab · cargo at stops",
  "admin.modules.column": "Modules",
  "admin.modules.neverSwitched": "Never enabled",
  "admin.modules.neverSwitchedOn": "Never switched off — on by default", // ADR 0471 — a default-ON module
  "admin.modules.lastEnabled": "Enabled {at} by {by}",
  "admin.modules.lastDisabled": "Disabled {at} by {by}",
  "admin.modules.confirmEnableTitle": "Enable {name}?",
  "admin.modules.confirmDisableTitle": "Disable {name}?",
  "admin.modules.enableEffect": "Its menus and tabs appear for everyone in the organization",
  "admin.modules.disableEffect": "Its menus and tabs disappear for everyone in the organization and can no longer be edited",
  "admin.modules.noDataLoss": "Nothing is deleted — enabling again brings everything back",
  "admin.modules.propagation": "It can take up to a minute to reach every server",
  "admin.modules.reason": "Reason (kept in the audit log)",
  "admin.modules.enable": "Enable module",
  "admin.modules.disable": "Disable module",
  // ADR 0482 — GPS (DTC) module card + connection section
  "admin.modules.name.dtc": "GPS (DTC)",
  "admin.modules.desc.dtc": "Pulls every vehicle from DTC on a schedule and stores every field · not shown anywhere yet · the map still uses the driver's phone",
  "admin.modules.enableEffect.dtc": "Starts pulling and storing DTC data within a minute · no new menu or tab yet",
  "admin.modules.disableEffect.dtc": "Stops pulling within a minute · the DTC address, token and interval are kept; switching back on resumes at once",
  // ADR 0505 — the finance module card and its switch-off dialog
  "admin.modules.name.finance": "Finance",
  "admin.modules.desc.finance": "Every finance page (transactions, bills, pay rounds, debts, requests, reports) · income and requests in the driver app · off means nobody in the org can open them (API included) · salary and bank details on the employee page stay editable",
  "admin.modules.enableEffect.finance": "The finance menu appears for the org's admins, and drivers see \"My income\" and \"Requests\" in the app",
  "admin.modules.disableEffect.finance": "The finance menu group, province rates, menu numbers, the corner alert, finance bells, the finance card on the employee page, the ticket's money card, and income and requests in the driver app disappear · salary, bank details and employee status stay · trip pay keeps being recorded from tickets in the background",
  "admin.modules.financeImpact.title": "Money still waiting in this org",
  "admin.modules.financeImpact.rows": "Money rows not in a bill yet",
  "admin.modules.financeImpact.bills_due": "Bills due (people)",
  "admin.modules.financeImpact.rounds_open": "Pay rounds not paid yet",
  "admin.modules.financeImpact.requests_open": "Requests waiting for approval",
  "admin.modules.financeImpact.note": "All of it waits where it is; nothing is cancelled. Switching back on picks it up at once.",
  "admin.modules.financeImpact.failed": "Could not load what is waiting. You can still switch off, but check the finance pages first.",
  "admin.dtc.title": "Connection",
  "admin.dtc.status.notConnected": "Not connected",
  "admin.dtc.status.connected": "Connected",
  "admin.dtc.status.errorSince": "Error since {at}",
  "admin.dtc.status.paused": "Paused (module off)",
  "admin.dtc.address": "DTC address",
  "admin.dtc.interval": "Poll every",
  "admin.dtc.minutes": "{n} min",
  "admin.dtc.lastOk": "Last successful poll",
  "admin.dtc.lastOkValue": "{at} · {n} vehicles",
  "admin.dtc.lastError": "Last error",
  "admin.dtc.none": "None",
  "admin.dtc.boxes": "Boxes in DTC",
  "admin.dtc.boxesValue": "{total} · entered by admin {entered} · not entered {left}",
  "admin.dtc.boxesUnknown": "Unknown — run Test connection",
  "admin.dtc.showNotEntered": "Show {n} gps_id not entered yet",
  "admin.dtc.col.plate": "Plate in DTC",
  "admin.dtc.col.lastSeen": "Last signal",
  "admin.dtc.downloadCsv": "Download CSV",
  "admin.dtc.test": "Test connection",
  "admin.dtc.testOk": "Test passed · DTC has {n} boxes · {s} s",
  "admin.dtc.pausedHint": "Settings are kept · stored data is not deleted · switching the module back on resumes on the next poll",
  "admin.dtc.keysMissing": "This server has no GPS_DTC_ENC_KEY / GPS_DTC_PII_HMAC_KEY yet — nothing can be pulled",
  "admin.dtc.saved": "Saved",
  "admin.dtc.setByOrg": "The address and API key are set by the organization's org_admin at Settings › GPS (DTC) · super admins can't see or change them",
  "admin.dtc.setBy": "Set by",
  "admin.dtc.saveInterval": "Save interval",
  "admin.dtc.testSaved": "Test with the saved key",
  "admin.dtc.errorHintSa": "Polling keeps trying · the organization's org_admin can fix it at Settings › GPS (DTC)",
  // ADR 0485 — settings › GPS (DTC), the org_admin sets the address + key
  "settings.dtc.nav": "GPS (DTC)",
  "settings.dtc.title": "GPS (DTC)",
  "settings.dtc.intro": "Enter the address and API key you got from DTC, then press \"Test connection\" · you can save only after a passing test",
  "settings.dtc.readOnly": "Only this organization's org_admin can change it · you can view the status only",
  "settings.dtc.keysMissing": "This server is not ready to connect to DTC · contact the system administrator",
  "settings.dtc.key": "API key",
  "settings.dtc.keyPlaceholder": "Paste the API key (api_token_key) from DTC",
  "settings.dtc.keyHint": "Characters are hidden",
  "settings.dtc.savedBy": "Saved {at} by {by}",
  "settings.dtc.intervalBySa": "(set by the system administrator)",
  "settings.dtc.notEnteredHint": "Enter the gps_id as the serial (S/N) at Vehicles › GPS, then bind it to a vehicle",
  "settings.dtc.errorHint": "Polling keeps trying · if DTC changed the key, use \"Change connection\"",
  "settings.dtc.change": "Change connection",
  "settings.dtc.changing": "Changing the connection — the current key (•••• {last4}) keeps polling until the new one is saved",
  "settings.dtc.retest": "Test again",
  "settings.dtc.disconnect": "Disconnect",
  "settings.dtc.disconnectTitle": "Disconnect DTC?",
  "settings.dtc.disconnectBody": "The API key is deleted and polling stops · stored data is kept · reconnect by entering the address and key and testing again",
  "settings.dtc.disconnected": "Disconnected",
  "settings.dtc.testing": "Testing…",
  "settings.dtc.needBoth": "Fill in both fields, then test · Save turns on after a passing test",
  "settings.dtc.testOk": "Test passed · DTC has {n} boxes · {s} s — save before {until}",
  "settings.dtc.testFail": "Test failed · {code} · {message}",
  "settings.dtc.changed": "Changed after the test — test again before saving",
  "settings.dtc.pending": "A test passed at {at} and was not saved (valid until {until}) · enter the same values and test again to save",
  "settings.dtc.err.DTC_TEST_REQUIRED": "Test these exact values and pass before saving (the test expired or the values differ)",
  "settings.dtc.err.ORG_ADMIN_ONLY": "Only this organization's org_admin can change the DTC connection",
  "settings.dtc.err.DTC_TEST_COOLDOWN": "Testing too often — wait a moment and try again",
  "settings.dtc.err.MODULE_DISABLED": "This organization's GPS (DTC) module is off",
  "settings.dtc.err.unknown": "Something went wrong",
  "errors.moduleDisabled": "This module is not enabled for this organization",
  // ADR 0400 — cargo products
  "nav.group.cargo": "Cargo",
  "nav.cargoProducts": "Products",
  "masterData.groupCargo": "Cargo",
  "masterData.cat.cargo_unit": "Cargo units",
  "masterData.cat.cargo_fail_reason": "Cargo failure reasons",
  "cargo.product.add": "Add product",
  "cargo.product.edit": "Edit product",
  "cargo.product.code": "Product code",
  "cargo.product.name": "Product name",
  "cargo.product.unit": "Unit",
  "cargo.product.locationName": "Storage location",
  "cargo.product.qty": "Quantity on hand",
  "cargo.product.pin": "Pin",
  "cargo.product.note": "Note",
  "cargo.product.search": "Code / name",
  "cargo.product.allLocations": "Storage location: all",
  "cargo.product.outOfStock": "Out of stock",
  "cargo.product.trash": "Deleted",
  "cargo.product.updated": "Updated",
  "cargo.product.deletedBy": "Deleted by · reason",
  "cargo.product.empty": "No products yet",
  "cargo.product.emptyTrash": "No deleted products",
  "cargo.product.deleted": "Deleted",
  "cargo.product.restored": "Restored",
  "cargo.product.restore": "Restore",
  "cargo.product.deleteTitle": "Delete {code} · {name}?",
  "cargo.product.deleteHint": "Hidden from lists · an org admin can restore it",
  "cargo.product.manageUnits": "Manage units",
  "cargo.product.fillRequired": "Fill every field marked *",
  "cargo.product.errRequired": "Required",
  "cargo.product.errCodeTooLong": "Code is longer than 40 characters",
  "cargo.product.errPinRequired": "Pin the storage location",
  "cargo.product.errQty": "Must be a number, 0 or more",
  // ADR 0473 — stock ledger (plan: 4-Archives/plan/tms/0473-cargo-stock-movements-from-jobs.md)
  "cargo.product.errQtyDecimals": "At most 2 decimals",
  "cargo.product.errLocationNoPin": "This location has no pin yet — pin it before saving",
  "cargo.product.pinLocation": "Pin it in Warehouses",
  "cargo.product.startQty": "Starting quantity",
  "cargo.location.noPin": "No pin yet",
  "cargo.stock.section": "Stock",
  "cargo.stock.createHint": "These two cannot be edited after saving — stock changes through \"Adjust\" · the unit is locked",
  "cargo.stock.adjust": "Adjust",
  "cargo.stock.history": "History",
  "cargo.stock.last": "Last",
  "cargo.stock.none": "No history yet",
  "cargo.stock.noPlace": "No storage place yet — jobs do not move this product's stock",
  "cargo.stock.type.opening": "Opening balance",
  "cargo.stock.type.receive": "Received",
  "cargo.stock.type.issue": "Taken out",
  "cargo.stock.type.count": "Counted",
  "cargo.stock.type.reversal": "Reversal",
  "cargo.stock.type.job_pickup": "Loaded from storage",
  "cargo.stock.type.job_deliver_in": "Delivered into storage",
  "cargo.stock.type.job_putback_in": "Put back into storage",
  "cargo.stock.kind.all": "All",
  "cargo.stock.kind.job": "From jobs",
  "cargo.stock.kind.adjust": "Adjustments",
  "cargo.stock.kind.fix": "Reversals / fixes",
  "cargo.stock.via.driver": "driver",
  "cargo.stock.via.admin": "admin",
  "cargo.stock.via.system": "system",
  "cargo.stock.adjustTitle": "Adjust · {code} {name}",
  "cargo.stock.qtyReceive": "Quantity received ({unit})",
  "cargo.stock.qtyIssue": "Quantity taken out ({unit})",
  "cargo.stock.qtyCount": "Counted ({unit})",
  "cargo.stock.reason": "Reason",
  "cargo.stock.summary": "Stock {from} → {to} {unit}",
  "cargo.stock.err.over": "At most {max} {unit} can be taken out",
  "cargo.stock.err.zero": "Must be more than 0",
  "cargo.stock.err.invalid": "Must be a number from 0",
  "cargo.stock.err.noChange": "That is already the stock",
  "cargo.stock.err.issueBlocked": "Stock is {qty} — use Received or Counted",
  "cargo.stock.err.reasonRequired": "A reason is required",
  "cargo.stock.changedTitle": "The stock changed while you were typing",
  "cargo.stock.changedOpen": "When opened",
  "cargo.stock.changedNow": "Now",
  "cargo.stock.changedWhy": "Because of",
  "cargo.stock.changedHint": "Check the number again, then save",
  "cargo.stock.saveFailed": "Could not save — try again",
  "cargo.stock.saved": "Stock adjusted · {from} → {to} {unit}",
  "cargo.stock.historyTitle": "Stock history · {code} {name}",
  "cargo.stock.saveNeeds": "Enter a quantity and a reason first",
  "cargo.stock.reasonNeeds": "Enter a reason first",
  "cargo.stock.search": "Search job no · item no · reason · name",
  "cargo.stock.col.at": "Time",
  "cargo.stock.col.item": "Movement",
  "cargo.stock.col.ref": "Reference",
  "cargo.stock.col.qty": "Change",
  "cargo.stock.col.balance": "Balance",
  "cargo.stock.col.who": "By · reason",
  "cargo.stock.reversed": "Reversed",
  "cargo.stock.correction": "Fix",
  "cargo.stock.reverse": "Reverse",
  "cargo.stock.reverseTitle": "Reverse \"{type} {qty}\"?",
  "cargo.stock.reverseConfirm": "Reverse",
  "cargo.stock.err.reverseNegative": "Cannot reverse — the stock would go below 0; count it instead",
  "cargo.stock.err.alreadyReversed": "Someone already reversed this",
  "cargo.stock.openingNote": "The balance before history started — no earlier detail",
  "cargo.stock.noMatch": "Nothing matches the search or filter",
  "cargo.stock.useMovement": "Stock now changes through \"Adjust\" — reload the page",
  "ops.cargo": "Cargo",
  "cargo.action.label": "Transport type",
  "cargo.action.pickup": "Pick up",
  "cargo.action.deliver": "Deliver",
  "cargo.action.return": "Return",
  "cargo.status.pickup.pending": "To pick up",
  "cargo.status.pickup.done": "Picked up",
  "cargo.status.pickup.partial": "Partly picked up",
  "cargo.status.pickup.failed": "Not picked up",
  "cargo.status.pickup.cancelled": "Cancelled",
  "cargo.status.deliver.pending": "To deliver",
  "cargo.status.deliver.done": "Delivered",
  "cargo.status.deliver.partial": "Partly delivered",
  "cargo.status.deliver.failed": "Not delivered",
  "cargo.status.deliver.cancelled": "Cancelled",
  "cargo.status.return.pending": "To collect",
  "cargo.status.return.done": "Collected",
  "cargo.status.return.partial": "Partly collected",
  "cargo.status.return.failed": "Not collected",
  "cargo.status.return.cancelled": "Cancelled",
  "cargo.status.generic.pending": "Pending",
  "cargo.item.title": "Cargo items",
  "cargo.item.add": "Add cargo",
  "cargo.item.empty": "No cargo on this job yet",
  "cargo.item.emptyTrash": "No deleted items",
  "cargo.item.id": "Item ID",
  "cargo.item.product": "Product",
  "cargo.item.stop": "Stop",
  "cargo.item.qty": "Qty",
  "cargo.item.updated": "Last updated",
  "cargo.item.search": "Item ID / product",
  "cargo.item.allStops": "Stop: all",
  "cargo.item.allActions": "Type: all",
  "cargo.item.allStatuses": "Status: all",
  "cargo.item.trash": "Deleted",
  "cargo.item.setStatus": "Set status…",
  "cargo.item.edit": "Edit…",
  "cargo.item.editTitle": "Edit {id}",
  "cargo.item.editPendingOnly": "Only pending items can be edited",
  "cargo.item.delete": "Delete…",
  "cargo.item.deleteTitle": "Delete {id} · {name}?",
  "cargo.item.deleteSelected": "Delete {n} items?",
  "cargo.item.deleteHint": "Hidden from the list · an org admin can restore it",
  // ADR 0459 D2/D4 — remove a photo (reason required · soft delete)
  "cargo.photo.remove": "Remove photo",
  "cargo.photo.removeTitle": "Remove this photo of {id}?",
  "cargo.photo.removeHint": "The photo is hidden everywhere. The removal, with your reason, stays in the job timeline.",
  "cargo.photo.removed": "Photo removed",
  "cargo.item.reason": "Reason",
  "cargo.item.qtyDone": "Qty done",
  "cargo.item.failReason": "Failure reason",
  "cargo.item.cancelReason": "Cancel reason",
  "cargo.item.otherReason": "Describe the reason",
  "cargo.item.progress": "Done {done}/{total}",
  "cargo.item.problems": "{n} with issues",
  "cargo.item.selected": "{n} selected",
  "cargo.item.ticketCancelled": "This job was cancelled — {n} items are still pending",
  "cargo.item.cancelAll": "Cancel all",
  "cargo.item.ticketClosed": "This job is closed — cargo can no longer change",
  "cargo.item.dupTitle": "Same product, type and stop as another row",
  "cargo.item.revConflict": "Someone changed this item first — reloaded",
  "cargo.item.stopGone": "Its stop was removed — cannot restore",
  "cargo.item.saved": "Saved",
  "cargo.item.added": "Added {n} items",
  "cargo.item.deleted": "Deleted",
  "cargo.item.restored": "Restored",
  "cargo.item.bulkSkipped": "Skipped {n} items that cannot change",
  "cargo.item.deletedBy": "Deleted by · reason",
  "cargo.item.selectAll": "Select all",
  "cargo.action.returnDeliver": "Return to",
  "cargo.item.pair": "Other end",
  "cargo.item.editPairHint": "Quantity and note change both the pickup and the delivery",
  "cargo.item.deletePairHint": "A pickup → delivery item is removed on both ends",
  "cargo.dialog.title": "Add cargo to job",
  "cargo.dialog.stopsCount": "{n} stops",
  "cargo.dialog.catalog": "Choose products",
  "cargo.dialog.addedN": "Added {n}",
  // ADR 0433 — add-cargo dialog: catalog columns · stock switch · states S3–S7
  "cargo.dialog.colProduct": "Product",
  "cargo.dialog.colStock": "In stock",
  "cargo.stockFilter.label": "Stock",
  "cargo.stockFilter.all": "All",
  "cargo.stockFilter.in": "In stock",
  "cargo.stockFilter.out": "Out of stock",
  "cargo.dialog.catalogEmpty": "No products yet",
  "cargo.dialog.catalogEmptyHint": "Create the first product to add it to jobs",
  "cargo.dialog.filterEmpty.in": "No products in stock",
  "cargo.dialog.filterEmpty.out": "No products out of stock",
  "cargo.dialog.filterEmpty.inAt": "No products in stock at {warehouse}",
  "cargo.dialog.filterEmpty.outAt": "No products out of stock at {warehouse}",
  "cargo.dialog.filterEmpty.at": "No products at {warehouse}",
  "cargo.dialog.showAll": "Show all",
  "cargo.dialog.noMatchQuery": "No product matches \"{q}\"",
  "cargo.dialog.createNamed": "+ Create product \"{q}\"",
  "cargo.dialog.firstN": "Showing the first {n} — type a name or code to narrow down",
  "cargo.dialog.addProduct": "Add {name}",
  "cargo.dialog.newProduct": "+ New product",
  "cargo.dialog.newProductTitle": "New product — also added to the product list",
  "cargo.dialog.createAndPick": "Create and add",
  "cargo.dialog.backToCatalog": "Back to products",
  "cargo.dialog.useExisting": "Use the existing one",
  "cargo.dialog.codeTaken": "This code is already in the product list",
  "cargo.dialog.rowsTitle": "Items to add",
  "cargo.dialog.sortBy": "Sort by",
  "cargo.dialog.sortStop": "Stop order",
  "cargo.dialog.noRows": "Press \"+ Add\" on a product on the left",
  "cargo.dialog.chooseStop": "Choose stop",
  "cargo.dialog.duplicate": "Duplicate",
  "cargo.dialog.remove": "Remove",
  "cargo.dialog.merge": "Merge qty",
  "cargo.dialog.addNote": "Add note",
  "cargo.dialog.dupSummary": "{n} repeated items (same product, pickup and delivery) — you can save; different lot/PO? add a note",
  "cargo.dialog.stockShort": "{n} picked up over stock: {list}", // ADR 0477 D6 (was cargo.dialog.stock0 — jobs deduct stock since ADR 0473)
  "cargo.dialog.stockShortRow": "{code} picks up {wanted} · in stock {onHand}",
  "cargo.stockNote.otherStop": "Picking up here does not deduct stock — this product is kept at {storage}", // ADR 0477 D4
  "cargo.stockNote.useStop": "Pick up at {n}. {name}",
  "cargo.stockNote.notOnRoute": "Picking up here does not deduct stock — this product is kept at {storage}, which is not on this route",
  "cargo.stockNote.addStopHint": "add that stop by choosing \"Warehouse\" in the add-stop form",
  "cargo.dialog.submit": "Add {n} items",
  "cargo.dialog.finishNewFirst": "Finish the new product first",
  "cargo.dialog.fixRows": "Complete every row (stop · qty · status)",
  "cargo.dialog.setAll": "Set all statuses",
  "cargo.dialog.summary": "{n} items",
  "cargo.dialog.lockedStop": "Adding at stop {name}",
  "timeline.created": "Job created",
  "timeline.released": "Driver released — leave approved",
  "timeline.updated": "Job updated",
  "timeline.stopsUpdated": "Stops updated",
  "timeline.chargeAdded": "Charge added",
  "timeline.chargeRemoved": "Charge removed",
  "timeline.chargeUpdated": "Charge updated",
  "timeline.arrived": "Arrived at {place}",
  "timeline.departed": "Departed {place}",
  "timeline.appOpened": "Driver opened the app",
  "timeline.appResumed": "Driver resumed the app",
  "timeline.changed": "Changed {fields}",
  "timeline.boarded": "{n} boarded",
  "timeline.loading": "Loading timeline…",
  "ops.checkedInOnPage": "{n} checked in (this page)",
  "timeline.licenseExp": "exp",
  "timeline.burst": "{n} times in a row",
  "timeline.earlierApp": "+{n} earlier app events",
  "timeline.resumeCount": "Resumed the app {n} times",
  "timeline.openCount": "Reopened the app {n} times",
  "timeline.field.label": "label",
  "timeline.field.startTime": "start time",
  "timeline.field.expectedEnd": "expected end",
  "timeline.field.acceptDeadline": "accept deadline",
  "timeline.field.revenue": "revenue",
  "timeline.cargo.item.created": "Cargo added",
  "timeline.cargo.item.updated": "Cargo edited",
  "timeline.cargo.item.status": "Cargo status changed",
  // ADR 0459 — a photo removed by hand (the office · the driver ✕)
  "timeline.cargo.item.photo_removed": "Cargo photo removed",
  "timeline.cargo.item.deleted": "Cargo deleted",
  "timeline.cargo.item.restored": "Cargo restored",
  "ops.stopEdit.addStop": "Add stop",
  "ops.stopEdit.stay": "Stay",
  "ops.stopEdit.fitAll": "Whole route",
  "ops.stopEdit.fullscreen": "Full screen",
  "ops.stopEdit.exitFullscreen": "Exit full screen",
  // ADR 0406 — stops editor: rows open in place · one save · ask before leaving unsaved edits · "ที่จุดนี้" column
  "ops.stopEdit.close": "Close",
  "ops.stopEdit.revert": "Revert this stop",
  "ops.stopEdit.edited": "Edited",
  "ops.stopEdit.cancelAll": "Discard all",
  "ops.stopEdit.pendingCount": "{n} unsaved change(s)",
  "ops.stopEdit.switchTitleStop": "{name} is not saved",
  "ops.stopEdit.switchAskTarget": "Save before going to {target}?",
  "ops.stopEdit.switchAskAdd": "Save before adding a new stop?",
  "ops.stopEdit.leaveBody": "{n} change(s) to this route are not saved",
  "ops.stopEdit.stayHere": "Stay on this page",
  "ops.stopEdit.dontSave": "Don't save",
  "ops.stopEdit.coordsPending": "Coordinates of {name} are incomplete",
  "ops.stopEdit.stopN": "Stop {n}",
  "ops.stopEdit.viaX": "Via {letter}",
  "ops.stopEdit.item.edited": "{name} — {fields}",
  "ops.stopEdit.item.moved": "Moved {name}",
  "ops.stopEdit.item.added": "Added {name}",
  "ops.stopEdit.item.addedVia": "Added a via point",
  "ops.stopEdit.item.removed": "Removed {name}",
  "ops.stopEdit.field.name": "name",
  "ops.stopEdit.field.coords": "coordinates",
  "ops.stopEdit.field.other": "other fields",
  "ops.stopSide.title": "At this stop",
  "ops.stopSide.via": "Via points only shape the route — no cargo or passengers",
  "ops.stopSide.unsaved": "Save the route first to add cargo or passengers here",
  "ops.stopSide.up": "Boarding here",
  "ops.stopSide.down": "Getting off here",
  "ops.stopSide.addPax": "Add passenger",
  "ops.stopSide.truncated": "Showing the first {n} — see all in the Passengers tab",
  "ops.stopSide.paxSavesNow": "Passengers save immediately",
  "ops.stopSide.removeHasPax": "{n} passenger(s) booked here — cancel their bookings first",
  "ops.stopEdit.more": "More actions",
  "ops.stopEdit.moveUp": "Move up",
  "ops.stopEdit.moveDown": "Move down",
  "ops.stopEdit.remove": "Remove this stop",
  "ops.storage.label": "Warehouse", // ADR 0477 D2 — the stop IS this storage location (jobs deduct stock there)
  "ops.storage.none": "Not a warehouse",
  "ops.storage.badge": "Warehouse",
  "ops.storage.locked": "Cannot change — cargo at this stop already has a result",
  "ops.stopSide.addCargo": "Add cargo",
  "ops.stopSide.tabCargo": "Cargo",
  "ops.stopSide.tabPax": "Passengers",
  "ops.stopPending.new": "New",
  "ops.stopPending.remove": "Remove",
  "ops.stopPending.undo": "Undo",
  "ops.stopPending.willCancel": "To be cancelled",
  "ops.stopPending.addToList": "Add to the list",
  "ops.stopPending.cargoAdd": "Add {name} ×{qty} ({from} → {to})",
  "ops.stopPending.cargoStatus": "{name} → {status}",
  "ops.stopPending.paxAdd": "Add {name} at {stop}",
  "ops.stopPending.paxNoShow": "{name} → no-show",
  "ops.stopPending.paxCancel": "Cancel {name}",
  "ops.stopPending.groupCargoLabel": "Cargo",
  "ops.stopPending.groupPaxLabel": "Passengers",
  "ops.stopPending.groupStops": "stops {n}",
  "ops.stopPending.groupCargo": "cargo {n}",
  "ops.stopPending.groupPax": "passengers {n}",
  "cargo.status.returnDeliver.pending": "To return",
  "cargo.status.returnDeliver.done": "Returned",
  "cargo.status.returnDeliver.partial": "Partly returned",
  "cargo.status.returnDeliver.failed": "Return failed",
  "cargo.status.returnDeliver.cancelled": "Cancelled",
  "cargo.dialog.startBar": "Items added from the left will",
  "cargo.dialog.sortPickup": "Pickup order",
  "cargo.dialog.summaryReturns": "{n} returns",
  "cargo.dialog.incomplete": "{n} not complete — press Add to go to what is missing",
  "cargo.dialog.emptyTitle": "No items yet",
  "cargo.dialog.emptyBody": "Pick a product on the left and press \"+ Add\" · not in the list yet? Press \"+ New product\"",
  "cargo.pair.qty": "Quantity",
  "cargo.pair.pickupAt": "Pick up at",
  "cargo.pair.deliverAt": "Deliver at",
  "cargo.pair.sidePickup": "Pickup",
  "cargo.pair.sideDeliver": "Delivery",
  "cargo.pair.kind": "Type",
  "cargo.pair.kindNormal": "Delivery",
  "cargo.pair.kindReturn": "Return",
  "cargo.pair.chooseStop": "Choose a stop",
  "cargo.pair.choosePickup": "Choose pickup",
  "cargo.pair.choosePickupReturn": "Choose where to collect",
  "cargo.pair.chooseDeliver": "Choose delivery",
  "cargo.pair.missingPickup": "Choose the pickup stop",
  "cargo.pair.missingPickupReturn": "Choose where to collect it",
  "cargo.pair.missingDeliver": "Choose the delivery stop — it must come after the pickup",
  "cargo.pair.badQty": "Enter a quantity above 0",
  "cargo.pair.why.before_pickup": "Before the pickup",
  "cargo.pair.why.is_pickup": "Already the pickup",
  "cargo.pair.why.after_deliver": "After the delivery",
  "cargo.pair.why.is_deliver": "Already the delivery",
  "cargo.pair.thisStop": "This stop",
  "cargo.pair.lockBar": "{name} is the",
  "cargo.pair.lockBarTail": "of the items added",
  "cargo.pair.less": "Less",
  "cargo.pair.more": "More",
  "cargo.pair.note": "Note",
  "cargo.pair.addNote": "Add a note",
  "cargo.pair.notePlaceholder": "e.g. PO no. · lot · receiver",
  "cargo.pair.removeNote": "Remove the note",
  "cargo.pair.remove": "Remove",
  "cargo.pair.dupTitle": "This item is already here",
  "cargo.pair.dupBody": "pick up at {from}, deliver at {to} — same as another item here",
  "cargo.pair.dupSavedBody": "pick up at {from}, deliver at {to} — already on this job",
  "cargo.pair.dupHint": "Different PO or lot? Add a note to keep them apart",
  "cargo.pair.dupMerge": "Make it one item…",
  "cargo.pair.dupKeep": "Keep apart (add a note)",
  "cargo.pair.mergeTitle": "Make it one item?",
  "cargo.pair.mergeProduct": "Product",
  "cargo.pair.mergeRoute": "Route",
  "cargo.pair.mergeQty": "Quantity",
  "cargo.pair.mergeNote": "Note",
  "cargo.pair.mergeWarn": "Kept apart for a different PO / lot / receiver? Do not merge — add a note to each instead",
  "cargo.pair.mergeConfirm": "Make it {n} {unit}",
  "cargo.pair.removedToast": "Removed {name} ({qty} {unit})",
  "cargo.pair.undo": "Undo",
  "ops.stopEdit.legendNew": "New",
  "ops.stopEdit.legendEdited": "Edited",
  "ops.stopEdit.staleTitle": "Someone saved this job while you were editing —",
  "ops.stopEdit.staleOverwrite": "Load the latest and put my draft on top",
  "ops.stopEdit.staleDiscard": "Throw my draft away",
  "ops.stopEdit.goTo": "Go to {name}",
  "ops.stopEdit.saveFailedNothing": "Not saved — nothing was changed",
  "ops.stopEdit.staleBody": "your draft is still here",
  "ops.stopEdit.newStop": "New stop (not saved)",
  "ops.stopCargo.search": "Search code or name",
  "ops.stopCargo.clearSearch": "Clear search",
  "ops.stopCargo.pickupHere": "Picked up here",
  "ops.stopCargo.deliverHere": "Delivered here",
  "ops.stopCargo.nOfM": "{n} of {m}",
  "ops.stopCargo.noPickup": "Nothing picked up here",
  "ops.stopCargo.noDeliver": "Nothing delivered here",
  "ops.stopCargo.noMatch": "No item matches",
  "ops.stopCargo.deliverAt": "deliver at",
  "ops.stopCargo.pickupFrom": "from",
  "ops.stopCargo.deliverBeforeHere": "now before this stop",
  "ops.stopCargo.pickupAfterHere": "now after this stop",
  "ops.stopCargo.noDeliverPair": "No delivery stop (older item)",
  "ops.stopCargo.noPickupPair": "No pickup stop (older item)",
  "ops.stopCargo.note": "Note",
  "ops.stopCargo.setStatusInCargo": "Set status in the cargo tab", // ADR 0475 D1
  "ops.stopCargo.notePlaceholder": "e.g. PO no. · lot · receiver",
  "ops.stopCargo.remove": "Remove",
  "ops.stopCargo.removeBoth": "Remove it from both {here} and {other}?",
  "ops.stopCargo.removeOne": "Remove it from this job?",
  "ops.stopCargo.reason": "Reason",
  "ops.stopCargo.reasonPlaceholder": "e.g. customer cancelled · ordered twice · out of stock",
  "ops.stopCargo.nItems": "{n} items",
  "ops.stopCargo.sumPickup": "Total up",
  "ops.stopCargo.sumDeliver": "Total down",
  "ops.stopCargo.orderWarning": "{n} delivered before pick-up — the stop order changed. You can save, but fix the order or the item's stops",
  "ops.stopCargo.pendingHere": "{n} unsaved cargo items use this stop — remove them first",
  "ops.stopPending.cargoNote": "{name} → new note",
  "ops.stopPending.cargoRemove": "Remove {name} ({reason})",
  "cargo.dialog.stage": "Add {n} to the list",
  "ops.stopSide.cancelAsk": "Cancel booking {id} · {name}?",
  "cargo.stop.empty": "No cargo at this stop",
  "cargo.stop.savesNow": "Cargo saves immediately · the stop itself saves with Save changes",
  "cargo.stop.removeHasCargo": "This stop has {n} cargo items — move or delete them first",
  "cargo.product.noMatch": "No products match these filters",
  "cargo.product.statusActive": "Active",
  "nav.cargoWarehouses": "Warehouses",
  "cargo.warehouse.manage": "Manage warehouses",
  "cargo.warehouse.warehouse": "Warehouse",
  "cargo.warehouse.location": "Location",
  "cargo.warehouse.noneHint": "No warehouse yet — go to",
  "cargo.warehouse.all": "Warehouse: all",
  "cargo.warehouse.mainPin": "Pin of the \"Main\" location",
  "cargo.warehouse.name": "Warehouse name",
  "cargo.warehouse.locations": "Storage locations",
  "cargo.warehouse.add": "Add warehouse",
  "cargo.warehouse.edit": "Edit warehouse",
  "cargo.warehouse.delete": "Delete warehouse",
  "cargo.warehouse.deleted": "Warehouse deleted",
  "cargo.warehouse.inUse": "Move the products out of this warehouse first",
  "cargo.warehouse.taken": "A warehouse with this name already exists",
  "cargo.warehouse.mainHint": "A \"Main\" location is created with it · add more later",
  "cargo.warehouse.search": "Warehouse / location",
  "cargo.warehouse.noMatch": "No warehouses match these filters",
  "cargo.warehouse.empty": "No warehouses yet",
  "cargo.warehouse.emptyTrash": "No deleted warehouses",
  "cargo.warehouse.deleteTitle": "Delete warehouse \"{name}\"?",
  "cargo.warehouse.deleteHint": "Hidden from lists and pickers together with its locations · an org admin can restore it",
  "cargo.location.default": "Default",
  "cargo.location.all": "Location: all",
  "cargo.location.inUse": "Still holds products — move them out first",
  "cargo.location.taken": "This warehouse already has a location with this name",
  "cargo.location.remove": "Remove location",
  "cargo.location.add": "Add location",
  "cargo.location.main": "Main",
  "cargo.location.reserved": "\"Main\" / \"ส่วนกลาง\" is kept for the default location",
  // ADR 0408 — warehouse detail page
  "cargo.warehouse.summary": "{n} locations · updated {date}",
  "cargo.warehouse.locationCount": "Locations",
  "cargo.warehouse.notFound": "Warehouse not found",
  "cargo.warehouse.deleteZoneNote": "All its locations go with it · an org admin can restore it from the warehouse list",
  "cargo.location.name": "Location name",
  "cargo.location.edit": "Edit location",
  "cargo.location.pin": "Place",
  "cargo.location.pinField": "Where this location is (pickup point)",
  "cargo.location.mainPinLocked": "A storage location keeps its pin — move it, but it cannot be cleared",
  "cargo.location.noPinYet": "No pin yet",
  "cargo.location.setPinFirst": "This location has no pin yet — set it before saving",
  "cargo.location.removeConfirm": "Remove \"{name}\" from this warehouse?",
  "cargo.location.removed": "Location removed",
  "cargo.location.limit": "A warehouse holds at most {max} locations",
  "cargo.location.search": "Search locations",
  "cargo.location.noMatch": "No locations match your search",
  "cargo.location.addedAt": "Added",
  "cargo.evidence.photos": "Evidence photos",
  "cargo.evidence.byDriver": "Sent by the driver",
  "cargo.evidence.noLocation": "No location",
  "cargo.evidence.at": "Taken at",
  "cargo.disposition.label": "Goods are",
  "cargo.disposition.return_to_stop": "Returned to stop",
  "cargo.disposition.lost": "Lost",
  "cargo.disposition.question": "Where did the goods go back to",
  "cargo.disposition.pickStop": "Pick a stop",
  "cargo.disposition.lostHint": "Lost / stolen — nothing to put back",
  "cargo.evidence.picked": "Picked up {picked} of {qty}",
  "cargo.evidence.returnLost": "{back} back · {lost} lost",
  "cargo.disposition.tagHere": "this stop",
  "cargo.disposition.tagOrigin": "pickup stop",
  "cargo.disposition.bulkHint": "Every delivery row gets the same return stop",
  "cargo.evidence.failPhotos": "Delivery photos",
  "cargo.evidence.returnPhotos": "Put-back photos",
  "cargo.evidence.noReturnPhoto": "No put-back photo yet",
  "settings.customerNoPrefix.title": "Customer number",
  "settings.customerNoPrefix.help": "One prefix for the whole organization · every new customer gets the next number · changing it starts new customers at 0001; existing ones keep theirs",
  "settings.customerNoPrefix.label": "Prefix",
  "settings.customerNoPrefix.sample": "Example",
  "settings.customerNoPrefix.invalid": "A–Z or 0–9, starting with a letter, up to 10, no dash, not JOB or CTR",
  "settings.dnPrefix.title": "Delivery note number",
  "settings.dnPrefix.help": "One prefix for the whole organization · the number always pairs with the job number · notes already issued keep their prefix",
  "settings.dnPrefix.label": "Delivery note prefix",
  "settings.dnPrefix.sample": "Example",
  "settings.dnPrefix.invalid": "A–Z or 0–9, starting with a letter, up to 10, no dash, not JOB",
  "settings.shopPrefix.title": "Shop code prefixes",
  "settings.shopPrefix.help": "Picked from a dropdown when a shop is created · each prefix counts on its own (BKK-0001, NTB-0001) · saved as soon as you click",
  "settings.shopPrefix.labelPh": "e.g. Bangkok",
  "settings.shopPrefix.invalid": "A–Z or 0–9, starting with a letter, up to 10, no dash",
  "settings.shopPrefix.empty": "No prefix yet — shops cannot be created until you add one",
  "settings.shopPrefix.firstCode": "First code",
  "settings.shopPrefix.setDefault": "Set as default",
  "settings.shopPrefix.deactivate": "Deactivate",
  "settings.shopPrefix.activate": "Activate",
  "settings.shopPrefix.inactive": "Inactive",
  "settings.shopPrefix.inUse": "Shops use it — it can be deactivated, not removed",
  "settings.shopPrefix.readOnly": "Only organization admins can change prefixes",
  "settings.shopPrefix.showInactive": "Inactive",
  "settings.shopPrefix.labelField": "Label",
  "cargo.party.open": "Receiver & customer ref",
  "cargo.party.search": "Find shop or receiver — shop code, shop name, name, phone",
  "cargo.party.none": "Not found — press + New shop, or type the receiver below",
  "cargo.party.pickShop": "Find the shop above",
  "cargo.party.newShop": "+ New shop",
  "cargo.party.noPeopleYet": "no receiver yet",
  "cargo.party.shop": "Shop",
  "cargo.party.clearShop": "Clear the shop",
  "cargo.party.newShopPh": "New shop name (optional)",
  "cargo.party.prefixTitle": "Shop code prefix",
  "cargo.party.noPrefix": "No shop prefix yet — add one in Settings › ID Patterns",
  "cargo.party.receiver": "Receiver",
  "cargo.party.namePh": "Name",
  "cargo.party.phonePh": "Phone",
  "cargo.party.clear": "Clear",
  "cargo.party.phoneIs": "This phone is {name}",
  "cargo.party.phoneIsTail": "saving uses that receiver (the register is not renamed)",
  "cargo.party.needBoth": "A receiver needs both a name and a phone",
  "cargo.party.refs": "Cust. ref",
  "cargo.party.refPh": "e.g. 4500123",
  "cargo.party.removeRef": "Remove this reference",
  "cargo.party.refDup": "Same number twice",
  "cargo.party.noRefTypes": "No reference types in master data yet",
  "cargo.party.addRef": "Customer reference",
  "cargo.party.pinOff": "The shop's usual stop \"{name}\" is not on this route — add it in Route & stops first",
  "masterData.cat.cargo_ref_type": "Customer reference types",
  "masterData.cat.shop_contact_type": "Shop contact channels", // ADR 0455 D2
  "cargo.dn.title": "Delivery notes",
  "cargo.dn.items": "{n} items",
  "nav.cargoShops": "Shops",
  "cargo.shop.add": "Add shop",
  "cargo.shop.code": "Code",
  "cargo.shop.name": "Shop name",
  "cargo.shop.pin": "Branch location", // ADR 0450 D11 — was "หมุดประจำ" (one pin per shop, before branches)
  "cargo.shop.pinPick": "Pick on map",
  "cargo.shop.pinChange": "Change on map",
  "cargo.shop.note": "Receiving note",
  "cargo.shop.notePh": "e.g. deliver at the back door, before 10:00",
  "cargo.shop.codeHint": "The code is issued on save and never changes",
  "cargo.shop.people": "Receivers ({n})",
  "cargo.shop.peopleCol": "Receivers",
  "cargo.shop.noPeople": "No receivers yet — they are added when a job is saved with this shop",
  "cargo.shop.removePerson": "Remove {name}",
  "cargo.shop.revConflict": "Someone changed this shop first — close and open it again",
  "cargo.shop.search": "Shop code / name",
  "cargo.shop.lastUsed": "Last used",
  // ADR 0447 D2 · D3 — shops under customers, branches
  "cargo.shop.customer": "Customer",
  "cargo.shop.pickCustomerShort": "Choose a customer",
  "cargo.shop.pickCustomer": "Choose a customer on the left to see its shops",
  "cargo.shop.noCustomer": "No customer yet",
  "cargo.shop.noCustomerHint": "Shops from before shops belonged to customers",
  "cargo.shop.noCustomerMatch": "No customer matches",
  "cargo.shop.customerSearch": "Search customers",
  "cargo.shop.customerSummary": "{shops} shops · {branches} branches",
  "cargo.shop.emptyCustomer": "This customer has no shop yet",
  "cargo.shop.backToCustomers": "All customers",
  "cargo.shop.branchCount": "{n} branches",
  "cargo.shop.autoCreated": "Created with the customer",
  "cargo.shop.noPinYet": "No pin yet",
  "cargo.shop.locations": "Branches ({n})",
  "cargo.shop.locationAdd": "Add branch",
  "cargo.shop.locationDefault": "Default",
  "cargo.shop.locationMakeDefault": "Make default",
  "cargo.shop.locationEdit": "Edit",
  "cargo.shop.locationClose": "Close",
  "cargo.shop.locationRemove": "Remove branch",
  "cargo.shop.locationLabel": "Branch name",
  "cargo.shop.locationUnnamed": "(no name yet)",
  "cargo.shop.locationsHint": "The default branch is the one picked first when cargo is added",
  "cargo.shop.noMatch": "No shops match the filters",
  "cargo.shop.empty": "No shops yet",
  "cargo.shop.emptyTrash": "No deleted shops",
  // ADR 0449 — the redesigned shops page + the shop dialog with its map
  "cargo.shop.addNeedsCustomer": "Choose a customer on the left first",
  "cargo.shop.listTitle": "Shops of {name}",
  "cargo.shop.customerMore": "Showing {n} of {total} · scroll for more",
  "cargo.shop.customerAll": "All {total} shown",
  "cargo.shop.deletedLink": "Deleted shops ({n})",
  "cargo.shop.deletedTitle": "{name} · deleted shops",
  "cargo.shop.backToActive": "Back to active shops",
  "cargo.shop.emptyCustomerNamed": "{name} has no shop yet",
  "cargo.shop.addFirst": "Add this customer's first shop",
  "cargo.shop.customerNotFound": "Customer not found",
  "cargo.shop.noCustomers": "No customers yet — add them on the Customers page",
  "cargo.shop.neverUsed": "Never used",
  "cargo.shop.more": "More",
  "cargo.shop.deleteShop": "Delete shop",
  "cargo.shop.deleteConfirmTitle": "Delete shop {code}?",
  "cargo.shop.deleteConfirmBody": "Code {code} is never reused · it can be restored from deleted shops",
  "cargo.shop.colPlace": "Location",
  "cargo.shop.colBranch": "Branch",
  "cargo.shop.recipients": "Receivers ({n})",
  "cargo.shop.noPeopleDetail": "No receivers yet — added when a receiver is named while adding cargo to a job",
  "cargo.shop.recentJobs": "Recent jobs",
  "cargo.shop.noRecentJobs": "Not used on a job yet",
  "cargo.shop.mapTitle": "Branch map",
  "cargo.shop.mapNoPins": "No branch is pinned yet",
  "cargo.shop.noCustomerBanner": "This shop has no customer — it cannot be added to a job's cargo",
  "cargo.shop.attachCustomer": "Link to a customer…",
  "cargo.shop.deletedAt": "Deleted {date}",
  "cargo.shop.deletedNotice": "This shop is deleted",
  "cargo.shop.pinsProgress": "Every branch needs a name and a pin to save ({ready} of {total})",
  "cargo.shop.pinsReady": "Every branch has a name and a pin",
  "cargo.shop.pinsShort": "{ready} of {total} branches ready",
  "cargo.shop.pickRowHint": "Pick a branch row, then pin it on the map · the selected branch is the blue pin you can drag",
  "cargo.shop.mapTip": "Click the map to pin {branch} · drag the pin to move it",
  "cargo.shop.pinFor": "Pin of branch {n} {label}",
  "cargo.shop.pinnedAt": "Pinned · {coords}",
  "cargo.shop.noPinLong": "No pin yet — click the map or search above",
  "cargo.shop.samePlace": "Branches {rows} are pinned at the same spot — check this is intended",
  "cargo.shop.pasteHint": "Paste a pair like \"13.41, 101.03\" into either box",
  // ADR 0450 — shop details table · split dialogs (info · one branch · two-step new shop)
  "cargo.shop.editInfo": "Edit shop details",
  "cargo.shop.shopChip": "Shop",
  "cargo.shop.branchAddTitle": "Add branch",
  "cargo.shop.branchEditTitle": "Edit branch {n} · {label}",
  "cargo.shop.branchRemove": "Delete this branch",
  "cargo.shop.branchRemoveTitle": "Delete branch {label} of {code}?",
  "cargo.shop.branchRemoveBody": "Cargo already on jobs keeps this branch's name",
  "cargo.shop.branchMakeDefault": "Use as the default branch",
  "cargo.shop.branchIsDefaultHint": "Picked first when cargo is added · to change it, make another branch the default",
  "cargo.shop.branchDefaultNowShort": "The default now is \"{label}\"",
  "cargo.shop.branchOthersShort": "the other branches are the numbered pins",
  "cargo.shop.branchMapTip": "Click the map to pin it · drag the pin to move it",
  "cargo.shop.branchPinAction": "Pin it",
  "cargo.shop.newShopOneBranch": "A new shop starts with one branch — add more from the shop's detail after saving",
  "cargo.shop.mainBranchHeading": "Main branch",
  // ADR 0455 — shop branch contact rows (plan 4-Archives/plan/tms/0455-shop-contact-channels.md)
  "cargo.contact.title": "Contacts",
  "cargo.contact.colChannel": "Channel",
  "cargo.contact.colValue": "Number / ID",
  "cargo.contact.colLabel": "Contact person",
  "cargo.contact.ph.label": "e.g. Receiving",
  "cargo.contact.add": "Add contact",
  "cargo.contact.full": "All {n} contacts used",
  "cargo.contact.noTypes": "No contact channels yet — add them in Master Data",
  "cargo.contact.noTypesLink": "Go to Master Data",
  "cargo.contact.inactive": "(turned off)",
  "cargo.contact.none": "No contacts yet",
  "cargo.contact.addLink": "Add a contact",
  "cargo.contact.main": "Main",
  "cargo.contact.open.phone": "Call",
  "cargo.contact.open.email": "Send e-mail",
  "cargo.contact.open.link": "Open link",
  "cargo.contact.copy": "Copy",
  "cargo.contact.copied": "Copied",
  "cargo.contact.moveUp": "Move up",
  "cargo.contact.moveDown": "Move down",
  "cargo.contact.remove": "Remove this contact",
  "cargo.contact.ph.phone": "e.g. 081-234-5678",
  "cargo.contact.ph.email": "name@shop.com",
  "cargo.contact.ph.url": "e.g. facebook.com/shopname",
  "cargo.contact.ph.line": "@id or a line.me link",
  "cargo.contact.ph.none": "ID or number",
  "cargo.contact.err.typeUnknown": "Pick a channel",
  "cargo.contact.err.valueRequired": "Enter a number, ID or link",
  "cargo.contact.err.valueTooLong": "Longer than 120 characters",
  "cargo.contact.err.phoneInvalid": "A phone number needs 9–15 digits",
  "cargo.contact.err.emailInvalid": "Incomplete e-mail — use name@shop.com",
  "cargo.contact.err.labelTooLong": "Contact person is longer than 60 characters",
  "cargo.contact.err.duplicate": "Same as a row above",
  "cargo.contact.err.tooMany": "A branch has at most 5 contacts",
  "cargo.shop.branchRemoveContacts": "This branch's {n} contact(s) will be removed too",
  "cargo.shop.colAddress": "Address",
  "cargo.shop.colCoords": "Coordinates",
  "cargo.shop.colJob": "Job",
  "cargo.shop.colStatus": "Status",
  "cargo.shop.colDate": "Date",
  "cargo.shop.colName": "Name",
  "cargo.shop.colPhone": "Phone",
  "cargo.shop.colNote": "Note",
  "cargo.shop.colLastUsed": "Last used",
  "cargo.shop.recentJobsCount": "Latest {n} jobs",
  "cargo.shop.personRemoveTitle": "Remove {name} from the receivers?",
  "cargo.shop.personRemoveBody": "Jobs already saved keep the name and phone they had",
  "cargo.shop.personRemove": "Remove",
  "cargo.tabAdd.rows": "Items to add ({n})",
  "cargo.tabAdd.chooseShopFirst": "Choose the shop first",
  "cargo.tabAdd.noShopN": "{n} items have no shop yet",
  "cargo.tabAdd.shopPin": "Shop's pin",
  "cargo.tabAdd.shopNoPin": "This shop has no pin yet — add it on the shops page first",
  "cargo.jobMap.title": "Job map",
  "cargo.jobMap.open": "Job map",
  "cargo.jobMap.all": "See every pin of the job on the map",
  "cargo.jobMap.google": "Open in Google Maps",
  "cargo.jobMap.jobStops": "Stops on this job · in route order",
  "cargo.jobMap.newPins": "New pins in this list · ordered when saved",
  "cargo.jobMap.readOnly": "View only — choose and share pins in the list",
  "cargo.tile.sharedWith": "shared with item {n}",
  "cargo.tile.jobStop": "stop {n} on this job",
  "cargo.tile.existing": "already: {list}",
  "cargo.tile.change": "Change",
  "cargo.tile.onMap": "On the map",
  "cargo.tile.shopDefault": "Default: the branch location", // ADR 0451 D2
  "cargo.tile.sendTo": "Send to",
  "cargo.tile.route": "Route",
  "cargo.tile.legPickup": "pick up",
  "cargo.tile.legDeliver": "deliver",
  "cargo.pair.collectAt": "Collect at",
  "cargo.pair.returnTo": "Return to",
  "cargo.shopPick.placeholder": "Choose a shop — code · shop name · name · phone",
  "cargo.shopPick.inJob": "On this job",
  "cargo.party.branch": "Branch",
  "cargo.party.branchOnly": "{label} (only branch)", // ADR 0451 D6
  "cargo.shopPick.noCustomer": "This job has no customer yet — add a contract with a customer in the job settings first",
  "cargo.shopPick.ofCustomer": "Shops of {name} ({contract})",
  "cargo.shopPick.recent": "Used recently",
  "cargo.shopPick.never": "Not used yet",
  "cargo.shopPick.neverShort": "never used",
  "cargo.shopPick.today": "today",
  "cargo.shopPick.daysAgo": "{n} d ago",
  "cargo.shopPick.noPin": "no pin yet",
  "cargo.shopPick.noPinWhy": "Set the shop's pin first, then it can be picked",
  "cargo.shopPick.none": "No shop found — add it with + New shop",
  "cargo.shopPick.footMore": "{n} loaded • scroll for more • type to filter",
  "cargo.shopPick.footAll": "{n} loaded · end of list",
  "cargo.party.receiverName": "Receiver",
  "cargo.party.phoneLabel": "Phone",
  "cargo.close.title": "Items not added yet",
  "cargo.close.body": "Closing throws away the {n} items you entered",
  "cargo.close.line": "item {n} {name} ×{qty}",
  "cargo.close.stay": "Stay",
  "cargo.close.discard": "Throw away and close",
  "cargo.tabAdd.newPin": "New",
  "cargo.tabAdd.shared": "Shared by {n}",
  "cargo.tabAdd.groupDraft": "New pins in this list",
  "cargo.tabAdd.groupJob": "Pins on this job",
  "cargo.tabAdd.addPin": "+ New pin…",
  "cargo.tabAdd.routeLocked": "This job's route can no longer change",
  "cargo.tabAdd.order": "The pickup must come before the delivery",
  "cargo.tabAdd.same": "Pickup and delivery are the same place",
  "cargo.tabAdd.newPinName": "New pin",
  "stopOrder.openRoute": "Optimise the whole route",
  "stopOrder.saveFirst": "Save your changes first, then optimise",
  "stopOrder.useOrder": "Use this order",
  "stopOrder.err.pairOrder": "No order works — a new pickup would have to come after a delivery the driver already passed. Go back and change that item's pickup or delivery",
  "stopOrder.err.stale": "This job was changed elsewhere meanwhile — go back and open the dialog again to see the latest",
  "stopOrder.err.editWindow": "This job's route can no longer change",
  "stopOrder.err.tooMany": "The job has more stops than one road line can be worked out for",
  "stopOrder.err.noRoad": "Google found no road route — a pin may be in the sea or off any road",
  "stopOrder.err.network": "Could not connect — check the internet and try again",
  "stopOrder.err.unknown": "Something went wrong — try again",
  "stopOrder.saveFailed": "Not saved — nothing was written",
  "stopOrder.saving": "Saving…",
  "stopOrder.retrySave": "Try saving again",
  "stopOrder.manualTitle": "The order could not be worked out automatically — please arrange it by hand",
  "stopOrder.manualGoogle": "Google did not answer",
  "stopOrder.manualUnreachable": "Could not reach the ordering service",
  "stopOrder.manualStart": "A starting order is set: the job's stops in order, new pins where they add the least (pickup before delivery)",
  "stopOrder.retryAuto": "Try the automatic order again",
  "stopOrder.retryAutoReplace": "Replace the order you arranged?",
  "stopOrder.yes": "Yes, replace it",
  "stopOrder.no": "No",
  "stopOrder.recalc": "Work out the road line",
  "stopOrder.recalcBusy": "Working it out…",
  "stopOrder.recalcFailed": "Could not work out the road line",
  "stopOrder.recalcFailedTail": "Your order is kept · saving still works",
  "stopOrder.retry": "Try again",
  "stopOrder.handOrder": "Arranged by hand",
  "stopOrder.legPickup": "pick up",
  "stopOrder.legDeliver": "deliver",
  "stopOrder.existingTag": "already",
  "stopOrder.map": "Stop order map",
  "stopOrder.start.label": "Start point",
  "stopOrder.start.none": "Not set — the system picks the first stop",
  "stopOrder.start.cleared": "Not set — the job's saved start point is removed when you save",
  "stopOrder.start.arrived": "Continues from the last stop reached — cannot be set",
  "stopOrder.start.set": "Set on the map",
  "stopOrder.start.change": "Change",
  "stopOrder.start.clear": "Clear",
  "stopOrder.start.restore": "Use the saved one",
  "stopOrder.start.new": "New",
  "stopOrder.start.saved": "Saved on the job",
  "stopOrder.start.pickTitle": "Set the start point",
  "stopOrder.start.noAddress": "The point set on the map",
  "stopOrder.start.closeItem": "The start point you set: {address}",
  // ADR 0451 D5 — the job driver's current depot is the default start
  "stopOrder.start.driverOf": "{name}'s depot ({code}) · default",
  "stopOrder.start.driverOfNoCode": "{name}'s depot · default",
  "stopOrder.start.useDriver": "Use the driver's depot",
  "stopOrder.near.mergeLead": "Merge into stop {n} · ",
  "stopOrder.near.sub": "{m} m apart",
  "stopOrder.near.passed": "The driver already passed this stop — cannot merge",
  "stopOrder.near.mergedN": "{n} pins merged",
  "stopOrder.near.newAway": "New · {m} m away",
  "stopOrder.near.existing": "Existing pin",
  "stopOrder.near.blocked": "Cannot save yet — re-order, move it by hand, or split it out",
  "stopOrder.near.split": "Split out",
  "stopOrder.near.problemOrder": "Re-order needed — {deliver} comes before {pickup}, where it is picked up",
  "stopOrder.near.problemSame": "Cannot merge — one item's pickup and delivery would be the same stop",
  "stopOrder.reorder": "Re-order",
  "stopOrder.reorderBusy": "Ordering…",
  "stopOrder.reorderAskTitle": "Re-order the stops?",
  "stopOrder.reorderAskBody": "The system orders the stops by real road distance; the list will change",
  "stopOrder.reorderAskMoved": "The order you arranged by hand will be replaced",
  "stopOrder.pending.merge": "Pins merged/split",
  "stopOrder.pending.order": "Order changed",
  "stopOrder.pending.noRoad": "No route yet",
  "stopOrder.pending.start": "Start point changed",
  "cargo.near.hint": "Near pin {name} ({m} m) — you can merge them when confirming the order",
  "cargo.near.shared": "Merged with pin {name} (chosen when confirming the order)",
  "stopOrder.title": "Confirm the stop order",
  "stopOrder.loading": "Working out the order…",
  "stopOrder.google": "Ordered by real road distance",
  "stopOrder.fallback": "Simple estimate — Google did not answer",
  // ADR 0452 D3 — why suggest-order fell back (`lib/stops/fallback-reason.ts`)
  "stopOrder.fallbackWhy": "Simple estimate — {why}",
  "stopOrder.reason.timeout": "Google took longer than 15 seconds",
  "stopOrder.reason.google_error": "Google answered with an error",
  "stopOrder.reason.network": "Could not reach Google",
  "stopOrder.reason.not_configured": "Google is not set up",
  "stopOrder.reason.skipped": "Google left some pins out",
  "stopOrder.reason.cycle": "Some items go back and forth between the same pins",
  "stopOrder.reason.order": "The order would deliver before pickup",
  "stopOrder.reason.unknown": "Google's order could not be used",
  "stopOrder.total": "{km} km · {min} min",
  "stopOrder.approx": "About {km} km (straight line)",
  "stopOrder.new": "New",
  "stopOrder.passed": "Passed",
  "stopOrder.whyPair": "Pick up before delivery ({name})",
  "stopOrder.whyArrived": "Already passed",
  "stopOrder.up": "Move up",
  "stopOrder.down": "Move down",
  "stopOrder.reset": "Back to the proposed order",
  "stopOrder.back": "Back",
  "stopOrder.confirm": "Confirm and save",
  "stopOrder.viaCleared": "{n} via segments will be cleared because the order changed",
  "stopOrder.failed": "Could not work out the order",
  "stopOrder.notePickup": "Pick up {code} {qty}",
  "stopOrder.noteDeliver": "Drop {code} {qty}",
  // the screen's own use (a form check / the lock banner) — not an HTTP refusal; the API says the same sentences as err.error.LOCATION_PIN_REQUIRED / TICKET_STATUS_UNKNOWN
  "cargo.err.locationPinRequired": "A storage location must have a pin — search a place, click the map or type the latitude/longitude",
  "cargo.err.ticketStatusUnknown": "This job status does not allow cargo changes",
  "cargo.lock.completed": "This job is completed — cargo cannot be changed (outcomes can still be recorded)", // ADR 0467
  "cargo.tabAdd.notShopPin": "Not {name}'s pin",
  "cargo.pinShare.title": "{name} is near a pin on this job",
  "cargo.pinShare.use": "Share the pin of {name} ({m} m away)",
  "cargo.pinShare.item": "item {n} {name} ×{qty}",
  "cargo.pinShare.jobStop": "stop {n} on this job",
  "cargo.pinShare.unticked": "unticked = a new pin of its own",
  "cargo.tabAdd.useShopPin": "Use the shop's pin",
  "cargo.shopChange.title": "Where should it go?",
  "cargo.shopChange.body": "Item {n} {name} — the shop changes to {shop}, but its stop was picked by hand",
  "cargo.shopChange.keep": "Keep the stop picked: {stop}",
  "cargo.shopChange.useNew": "Use the new shop's pin: {shop}",
  "cargo.shopChange.cancel": "Don't change the shop",
  "cargo.shopChange.ok": "OK",
  "cargo.shop.created": "Shop {code} added",
  "ops.searchTicketsCargo": "Search: job#, job name, route, driver, plate, contract · delivery note# (or DN-date), customer ref, shop code/name…",
  "ops.searchTruncated": "Showing the 200 newest jobs matching this shop / reference — type more of it, or filter by date",
  // ADR 0457
  "cargo.leg.short.pickup": "Pick up",
  "cargo.leg.short.deliver": "Deliver",
  "cargo.leg.short.return": "Return",
  "ops.stopCargo.attention": "{n} need attention",
  "ops.stopCargo.doneOf": "Done {done}/{total}",
  // ADR 0457
  "ops.cargoCard.title": "Cargo",
  "ops.cargoCard.allDone": "All done",
  // ADR 0457
  "ops.headerContracts": "Contracts",
  "ops.contractsMore": "+{n} contracts",
  "ops.contractsCustomers": "{n} customers",
  // ADR 0457
  "ops.stopCargo.noGoods": "Nothing to deliver",
  // ADR 0457
  "cargo.status.problem": "Has issues",
  "cargo.summary.attention": "Needs attention",
  "cargo.group.hide": "Hide",
  "cargo.group.show": "Show {n}",
  "cargo.col.evidence": "Evidence",
  "cargo.ev.key.reason": "Reason",
  "cargo.ev.key.return": "Returned",
  "cargo.ev.key.photos": "Photos",
  "cargo.ev.key.returnPhoto": "Put-back photo",
  "cargo.ev.lostN": "lost {n}",
  "cargo.item.count": "{n} items",
  // ADR 0457
  "ops.listCargo": "Cargo {done}/{total}",
  // ADR 0479 — the map stop popup · cargo.row.* = the driver app's words, kept equal by lib/map-stop-view.test.ts
  "cargo.row.pickup.pending": "To pick up {qty} {unit}",
  "cargo.row.pickup.done": "Picked up all {qty} {unit}",
  "cargo.row.pickup.partial": "Picked up {done} / {qty} {unit}",
  "cargo.row.pickup.failed": "Could not pick up {qty} {unit}",
  "cargo.row.pickup.cancelled": "Pick up cancelled · {qty} {unit}",
  "cargo.row.deliver.pending": "To deliver {qty} {unit}",
  "cargo.row.deliver.done": "Delivered all {qty} {unit}",
  "cargo.row.deliver.partial": "Delivered {done} / {qty} {unit}",
  "cargo.row.deliver.failed": "Could not deliver {qty} {unit}",
  "cargo.row.deliver.cancelled": "Deliver cancelled · {qty} {unit}",
  "cargo.row.return.pending": "To return {qty} {unit}",
  "cargo.row.return.done": "Returned all {qty} {unit}",
  "cargo.row.return.partial": "Returned {done} / {qty} {unit}",
  "cargo.row.return.failed": "Could not return {qty} {unit}",
  "cargo.row.return.cancelled": "Return cancelled · {qty} {unit}",
  "cargo.row.deliver.nogoods": "Nothing to deliver",
  "ops.mapStop.state.upcoming": "Not reached",
  "ops.mapStop.locusDone": "Delivered {time}",
  "ops.mapStop.locusNoData": "No data yet",
  "ops.mapStop.state.current": "Current stop",
  "ops.mapStop.state.arrived": "Arrived",
  "ops.mapStop.state.skipped": "Skipped",
  "ops.mapStop.late": "{n} min late",
  "ops.mapStop.sched": "Due",
  "ops.mapStop.arrivedAt": "Arrived",
  "ops.mapStop.cargoDone": "Cargo done",
  "ops.mapStop.allDone": "All done",
  "ops.mapStop.pax": "Passengers",
  "ops.mapStop.booked": "Booked",
  "ops.mapStop.boarded": "Boarded",
  "ops.mapStop.walkin": "Walk-in",
  "ops.mapStop.checkin": "Check-in by {m}",
  "ops.mapStop.checkin.qr": "QR",
  "ops.mapStop.checkin.auto": "GPS",
  "ops.mapStop.checkin.driver": "driver",
  "ops.mapStop.overflow": "~{n} over capacity",
  "ops.mapStop.cargoN": "Cargo · {n} items",
  "ops.mapStop.toCargoTab": "Open in the cargo tab →",
  "ops.mapStop.close": "Close",
  // ADR 0457
  "cargo.end.to": "To",
  "cargo.end.from": "From",
  "cargo.end.returnTo": "Back to",
  "cargo.end.atPin": "at pin {name}",
  "cargo.ev.key.note": "Note",
  "cargo.ev.key.recipient": "Receiver",
  "cargo.ev.key.ref": "Ref.",
  "cargo.ev.returnAtStop": "{n} at stop {stop}",
  "cargo.ev.lostAll": "All lost",
  "cargo.ev.notPutBack": "Not put back yet",
  "cargo.branch.noContacts": "No contact",
  "cargo.branch.contactsLabel": "Contact",
  "cargo.branch.more": "+{n} more",
  "cargo.branch.shops": "{n} shops",
  "cargo.branch.removed": "Branch removed",
  // ── ADR 0394 — insights ──
  "insights.common.overview": "Today",
  "insights.common.switcher": "Switch dashboard",
  "insights.common.period": "Period",
  "insights.common.days30": "30 days",
  "insights.common.days90": "90 days",
  "insights.common.days365": "12 months",
  "insights.common.periodNote": "{from} to {to} · compared with {pf} to {pt}",
  "insights.common.snapshotNote": "Status right now · the calendar looks 12 weeks ahead",
  "insights.common.vsPrev": "vs previous period",
  "insights.common.noPrev": "No comparison yet",
  "insights.common.asOfNow": "As of now",
  "insights.common.points": "{n} pts",
  "insights.common.noData": "No data in this period yet",
  "insights.common.calm": "All clear",
  "insights.common.target": "Target {v}",
  "insights.common.targetShort": "Target / needed",
  "insights.common.actual": "Actual",
  "insights.common.low": "Low",
  "insights.common.high": "High",
  "insights.common.today": "Today",
  "insights.common.within90": "Ends within 90 days",
  "insights.common.expiredBucket": "Expired",
  "insights.common.showingOf": "Showing {n} of {total} — most urgent first",
  "insights.unit.days": "days",
  "insights.unit.min": "min",
  "insights.unit.km": "km",
  "insights.unit.kmpl": "km/L",
  "insights.unit.vehicles": "vehicles",
  "insights.unit.people": "people",
  "insights.unit.trips": "trips",
  "insights.unit.items": "items",
  "insights.status.ok": "OK",
  "insights.status.soon": "Ends within 30 days",
  "insights.status.expired": "Expired",
  "insights.status.missing": "Missing",
  "insights.status.na": "Not applicable",
  "insights.service.title": "Service reliability",
  "insights.compliance.title": "Documents & expiry",
  "insights.fleet.title": "Fleet utilization",
  "insights.maintenance.title": "Maintenance & inspection",
  "insights.cost.title": "Cost & fuel",
  "insights.workforce.title": "Workforce",
  "insights.finance.title": "Finance & payroll",
  "insights.ridership.title": "Ridership & booking",
  "insights.drivers.title": "Drivers & safety",
  "insights.commercial.title": "Customers & contracts",
  "insights.capacity.title": "Capacity & dispatch",
  "insights.adoption.title": "Communication & app use",
  "insights.compliance.question": "What has expired or is about to, and which vehicle or driver would run uncovered today",
  "insights.compliance.kpi.expired": "Already expired",
  "insights.compliance.kpi.due_30": "Ending in 30 days",
  "insights.compliance.kpi.license_expired": "Drivers with expired licence",
  "insights.note.license_missing": "{n} more with no licence on file",
  "insights.compliance.card.calendar": "Expiry calendar, next 12 weeks",
  "insights.compliance.card.calendar.q": "Which weeks the renewals pile up · next 12 weeks (already expired is in the KPI)",
  "insights.compliance.card.gaps": "Gaps to close first",
  "insights.compliance.card.gaps.q": "What is illegal or risky if a vehicle leaves today",
  "insights.compliance.card.matrix": "Document status by vehicle",
  "insights.compliance.card.matrix.q": "Vehicles with an expired, missing or expiring document, worst first — tap a document to filter · tap a plate to open it",
  "insights.common.docsAll": "All with an issue",
  "insights.common.docExpiredDays": "expired {n} days ago",
  "insights.common.docExpiredToday": "expires today",
  "insights.common.docLeftDays": "{n} days left",
  "insights.common.docMissing": "missing",
  "insights.common.vstatus_repair": "in repair",
  "insights.common.vstatus_stored": "stored",
  "insights.common.docsNoneInFilter": "No vehicle in this list has that issue",
  "insights.common.docsKeyBad": "expired / missing",
  "insights.common.docsKeySoon": "expiring within 30 days",
  "insights.compliance.list.no_cmi": "In-service vehicles without valid CMI",
  "insights.compliance.list.untaxed": "In-service vehicles with no valid road tax",
  "insights.compliance.list.license_with_jobs": "Drivers with a lapsed licence who have jobs today or tomorrow",
  "insights.compliance.list.no_gps": "In-service vehicles with no GPS unit",
  "insights.compliance.list.docs_unverified": "Employee documents awaiting review",
  "insights.series.cmi": "CMI",
  "insights.series.insurance": "Insurance",
  "insights.series.tax": "Road tax",
  "insights.series.registration": "Registration",
  "insights.series.device": "GPS / devices",
  "insights.series.license": "Driving licence",
  "insights.series.emp_doc": "ID / employee docs",
  "insights.series.contract_doc": "Contract documents",
  "insights.col.registration": "Registration",
  "insights.col.cmi": "CMI",
  "insights.col.insurance": "Insurance",
  "insights.col.tax": "Tax",
  "insights.col.gps": "GPS",
  "insights.col.certificate": "Device cert.",
  // ── ADR 0394 — insights ──
  "insights.service.question": "Do trips run on time as promised — and when they don't, where, when and why",
  "insights.service.kpi.ontime": "On time",
  "insights.service.kpi.done": "Trips completed",
  "insights.service.kpi.cancel_rate": "Cancelled",
  "insights.service.kpi.avg_late": "Average delay (late finishes only)",
  "insights.service.card.ontime_trend": "On-time rate",
  "insights.service.card.ontime_trend.q": "Better or worse lately, against the target · long ranges are weekly",
  "insights.service.card.outcomes": "Trip outcomes",
  "insights.service.card.outcomes.q": "How trips ended (weekly / monthly)",
  "insights.service.card.late_routes": "Routes that run late most",
  "insights.service.card.late_routes.q": "Which timetable to fix first · routes with 5+ measured trips",
  "insights.service.card.late_heat": "Lateness by day and departure hour",
  "insights.service.card.late_heat.q": "% of trips late by weekday and departure hour (00–23) — is it the same slot every week",
  "insights.service.card.funnel": "Where trips drop out",
  "insights.service.card.funnel.q": "Is the problem assignment, acceptance or the run itself",
  "insights.service.card.cancel_reasons": "Why trips were cancelled",
  "insights.service.card.cancel_reasons.q": "Colour shows who cancelled",
  "insights.service.stage.created": "Created",
  "insights.service.stage.assigned": "Driver assigned",
  "insights.service.stage.accepted": "Driver accepted",
  "insights.service.stage.started": "Started",
  "insights.service.stage.done": "Completed",
  "insights.service.reason_unspecified": "No reason given",
  "insights.series.ontime": "On time",
  "insights.series.on_time": "On time",
  "insights.series.late_small": "Late ≤15 min",
  "insights.series.late_big": "Late >15 min",
  "insights.series.cancelled": "Cancelled",
  "insights.series.cancel_admin": "Cancelled by admin",
  "insights.series.cancel_driver": "Cancelled by driver",
  "insights.unavailable.no_measurable": "No measurable trips in this period",
  // ── ADR 0394 — insights ──
  "insights.maintenance.question": "How road-ready is the fleet, what is waiting for repair, is PM on schedule, and what breaks most",
  "insights.maintenance.kpi.open": "Open work orders",
  "insights.maintenance.kpi.pm_on_time": "PM plans not overdue",
  "insights.maintenance.kpi.days_off_road": "Average days off the road",
  "insights.maintenance.kpi.spend": "Repair spend in period",
  "insights.note.overdue_wo": "{n} overdue",
  "insights.maintenance.card.aging": "Age of open work orders",
  "insights.maintenance.card.aging.q": "How long jobs have been waiting (days since opened)",
  "insights.maintenance.card.failed_items": "Inspection items failing most",
  "insights.maintenance.card.failed_items.q": "% failed per item in this period's inspections (items answered 10+ times)",
  "insights.maintenance.card.repeat_vehicles": "Vehicles back in the shop",
  "insights.maintenance.card.repeat_vehicles.q": "Vehicles with 2+ work orders this period — time to retire?",
  "insights.maintenance.card.symptoms": "Most common symptoms",
  "insights.maintenance.card.symptoms.q": "From repair receipt lines",
  "insights.maintenance.basis.time": "Time-based",
  "insights.maintenance.basis.odometer": "Distance-based",
  "insights.maintenance.basis.both": "Time + distance",
  "insights.series.pm_overdue": "Overdue",
  "insights.series.pm_due_soon": "Due soon",
  "insights.series.pm_ok": "On schedule",
  "insights.series.open_wo": "Open work orders",
  "insights.series.wo_repair": "Repair",
  "insights.series.wo_maintenance": "Maintenance",
  "insights.series.wo_preventive": "Preventive",
  "insights.series.wo_inspection": "Inspection",
  "insights.series.other": "Other",
  "insights.unavailable.no_pm_plans": "No PM plans yet",
  "insights.unavailable.no_closed_wo": "No work orders closed in this period",
  // ── ADR 0394 — insights ──
  "insights.cost.question": "What a kilometre costs, where the money goes, which vehicles burn too much fuel, and what is coming due",
  "insights.cost.kpi.total": "Vehicle cost in period",
  "insights.cost.kpi.per_km": "Running cost per km (fuel + repairs)",
  "insights.cost.kpi.kmpl": "Fleet fuel economy",
  "insights.note.measured_vehicles": "Measured on {n} vehicles",
  "insights.cost.card.by_month": "Monthly cost by type",
  "insights.cost.card.by_month.q": "Where the money goes · same rule as Cost per vehicle (booked in the month it falls due)",
  "insights.cost.card.per_km": "Running cost per km above same-type vehicles",
  "insights.cost.card.per_km.q": "Fuel + repairs per km vs the median of the same vehicle type (insurance, tax and instalments land as lump sums and are left out) · only vehicles averaging ≥ 10 km/day",
  "insights.cost.card.kmpl_vs_spec": "Measured km/L vs spec",
  "insights.cost.card.kmpl_vs_spec.q": "Red = more than 10% below spec · worst first",
  "insights.cost.card.fuel_flags": "Refuels the system flagged",
  "insights.cost.card.fuel_flags.q": "Which reasons come up most — click to review",
  "insights.cost.card.cash_ahead": "Cash due over the next 12 months",
  "insights.cost.card.cash_ahead.q": "Estimate: unpaid instalments + renewing insurance/tax at the current price",
  "insights.cost.flag.off_station": "Off the approved station",
  "insights.cost.flag.no_location": "No location",
  "insights.cost.flag.odometer_backwards": "Odometer went backwards",
  "insights.cost.flag.clock_skew": "Device clock off",
  "insights.cost.flag.vehicle_not_active": "Vehicle not in service",
  "insights.series.cost_fuel": "Fuel",
  "insights.series.cost_maintenance": "Maintenance",
  "insights.series.cost_installment": "Instalments",
  "insights.series.cost_insurance_tax": "Insurance + tax",
  "insights.series.cost_devices": "GPS / devices",
  "insights.series.renew_insurance": "Insurance renewal",
  "insights.series.renew_tax": "Tax renewal",
  "insights.series.kmpl_measured": "Measured",
  "insights.series.kmpl_spec": "Spec",
  "insights.unavailable.no_distance": "No distance yet (needs refuels with odometer)",
  // ── ADR 0394 — insights ──
  "insights.fleet.question": "Is each vehicle earning its keep, which sit idle, which are broken, how old is the fleet",
  "insights.fleet.kpi.utilization": "Average daily utilization",
  "insights.note.on_road": "{n} in service",
  "insights.fleet.card.utilization_trend": "% of vehicles used each day",
  "insights.fleet.card.utilization_trend.q": "Had at least one job that day, out of all in-service vehicles",
  "insights.fleet.card.idle": "Idle vehicles",
  "insights.fleet.card.idle.q": "Days since the last trip (7+) — vehicles that never had a job are not listed",
  "insights.fleet.all": "All",
  "insights.series.utilization": "Utilization",
  "insights.series.v_active": "In service",
  "insights.series.v_repair": "In repair",
  "insights.series.v_stored": "Stored",
  "insights.series.vehicles": "Vehicles",
  "insights.vtype.van": "Van",
  "insights.vtype.minibus": "Minibus",
  "insights.vtype.bus": "Bus",
  "insights.vtype.truck": "Truck",
  "insights.vtype.other": "Other",
  "insights.vtype.pickup": "Pickup",
  "insights.vtype.sedan": "Sedan",
  "insights.vtype.motorcycle": "Motorcycle",
  "insights.x.<2": "< 2 yrs",
  "insights.x.2-4": "2–4 yrs",
  "insights.x.5-7": "5–7 yrs",
  "insights.x.8-10": "8–10 yrs",
  "insights.x.11-13": "11–13 yrs",
  "insights.x.14+": "14+ yrs",
  "insights.x.unknown": "Unknown",
  "insights.x.0-3": "0–3 days",
  "insights.x.4-7": "4–7 days",
  "insights.x.8-14": "8–14 days",
  "insights.x.15-30": "15–30 days",
  "insights.x.31+": "31+ days",
  // ── ADR 0394 — insights ──
  "insights.workforce.question": "Who is on leave when, whose probation ends soon, and where hiring stands",
  "insights.workforce.kpi.active": "Active employees",
  "insights.workforce.kpi.on_leave_today": "On leave today",
  "insights.workforce.kpi.leave_pending": "Leave awaiting approval",
  "insights.note.drivers": "{n} drivers",
  "insights.workforce.card.leave_calendar": "Leave calendar, next 5 weeks",
  "insights.workforce.card.leave_calendar.q": "People on approved leave per day — which days pile up",
  "insights.workforce.card.hiring": "Hiring funnel",
  "insights.workforce.card.hiring.q": "Candidates who applied this period — where they drop out",
  "insights.workforce.card.leave_types": "Leave days per month by type",
  "insights.workforce.card.leave_types.q": "Is sick leave rising (counted by start month · half day = 0.5)",
  "insights.workforce.stage.applied": "Applied",
  "insights.workforce.stage.screening": "Screening",
  "insights.workforce.stage.interview": "Interview",
  "insights.workforce.stage.offer": "Offer",
  "insights.workforce.stage.onboarding": "Onboarding",
  "insights.series.emp_company_driver": "Company driver",
  "insights.series.emp_contractor": "Contract driver",
  "insights.series.emp_office_staff": "Office",
  "insights.series.emp_mechanic": "Mechanic",
  "insights.series.emp_part_time": "Part-time",
  "insights.series.leave_annual": "Annual",
  "insights.series.leave_sick": "Sick",
  "insights.series.leave_personal": "Personal",
  "insights.series.leave_other": "Other",
  // ── ADR 0394 — insights ──
  "insights.finance.question": "How revenue and driver pay are trending", // ADR 0471 — เหลือเฉพาะรายรับ/ค่าเที่ยว
  "insights.finance.kpi.revenue": "Revenue in period",
  "insights.finance.kpi.expense": "Driver pay + manual entries",
  "insights.finance.card.rev_exp": "Monthly revenue vs driver pay",
  "insights.finance.card.rev_exp.q": "The spend line is driver pay and manual entries only — no fuel, repairs, insurance or payroll yet",
  "insights.st.open": "Open",
  "insights.st.in_progress": "Month still running",
  "insights.st.no_trips": "No trips",
  "insights.st.locked": "Locked",
  "insights.st.paid": "Paid",
  "insights.st.none": "No tickets",
  "insights.st.draft": "Draft",
  "insights.st.calculated": "Calculated",
  "insights.st.verified": "Verified",
  "insights.series.revenue": "Revenue",
  "insights.series.expense": "Driver pay + manual",
  "insights.series.pay_base": "Base salary",
  "insights.series.pay_ot": "Overtime",
  "insights.series.pay_allowance": "Allowances",
  // ── ADR 0394 — insights ──
  "insights.ridership.question": "Are seats filled, do people who book actually ride, and are passengers happy",
  "insights.ridership.kpi.seat_util": "Seat utilization",
  "insights.ridership.kpi.bookings": "Bookings",
  "insights.ridership.kpi.no_show": "No-show rate",
  "insights.ridership.kpi.rating": "Average rating",
  "insights.ridership.card.seat_by_route": "Fullest and emptiest routes",
  "insights.ridership.card.seat_by_route.q": "Finished trips, including those nobody booked · against a 75% target — red means the vehicle is too big",
  "insights.ridership.card.daily": "Bookings per day by outcome",
  "insights.ridership.card.daily.q": "How many who booked actually rode (by trip date · weekly for long ranges)",
  "insights.ridership.card.boarding_heat": "When people ride",
  "insights.ridership.card.boarding_heat.q": "Riders by weekday and departure hour (00–23)",
  "insights.series.ridden": "Rode",
  "insights.series.no_show_b": "No-show",
  "insights.series.cancelled_b": "Cancelled",
  "insights.series.ch_self": "Self-service (app/portal/QR)",
  "insights.series.ch_staff": "Staff / driver",
  "insights.series.ch_walkin": "Walk-in",
  "insights.unavailable.no_capacity": "No trips in this period have a seat capacity",
  "insights.unavailable.no_ratings": "No ratings in this period",
  // ── ADR 0394 — insights ──
  "insights.drivers.question": "Which drivers need attention, what incidents recur, and how fast emergencies are answered",
  "insights.drivers.kpi.open_cases": "Open cases",
  "insights.drivers.kpi.sos_ack": "SOS — minutes to acknowledge",
  "insights.drivers.card.scorecard": "Drivers to talk to first",
  "insights.drivers.card.scorecard.q": "Ranked by cases, complaints and late trips (on time counts from 10 trips) · complaints = passenger feedback linked to that driver's trips",
  "insights.drivers.card.cases_monthly": "Cases per month by type",
  "insights.drivers.card.cases_monthly.q": "Cases by category per month (by incident date)",
  "insights.drivers.col.name": "Driver",
  "insights.drivers.col.branch": "Branch",
  "insights.drivers.col.ontime": "On time",
  "insights.drivers.col.trips": "Trips",
  "insights.drivers.col.cases": "Cases",
  "insights.drivers.col.complaints": "Complaints",
  "insights.ax.cat_accident": "Accident",
  "insights.ax.cat_violation": "Violation",
  "insights.ax.cat_complaint": "Complaint",
  "insights.ax.cat_inspection": "Inspection",
  "insights.ax.sev_low": "Low",
  "insights.ax.sev_medium": "Medium",
  "insights.ax.sev_high": "High",
  "insights.ax.sev_critical": "Critical",
  "insights.series.case_accident": "Accident",
  "insights.series.case_violation": "Violation",
  "insights.series.case_complaint": "Complaint",
  "insights.series.case_inspection": "Inspection",
  "insights.series.drivers": "Drivers",
  "insights.x.<60": "< 60",
  "insights.x.60-69": "60–69",
  "insights.x.70-79": "70–79",
  "insights.x.80-89": "80–89",
  "insights.x.90-100": "90–100",
  "insights.unavailable.no_sos": "No SOS in this period",
  "insights.unavailable.sos_unanswered": "SOS raised but nobody has answered yet",
  // ── ADR 0394 — insights ──
  "insights.commercial.question": "Which customers the revenue comes from, which routes make or lose money, which contracts end soon (money figures are estimates)",
  "insights.commercial.kpi.monthly_value": "Monthly contract value (est.)",
  "insights.commercial.kpi.expiring": "Contracts ending within 30 days",
  "insights.commercial.kpi.margin": "Gross margin (est.)",
  "insights.commercial.card.by_customer": "Contract value by customer",
  "insights.commercial.card.by_customer.q": "THB per month from active contracts — how concentrated we are",
  "insights.commercial.card.route_margin": "Total margin by route (est.)",
  "insights.commercial.card.route_margin.q": "Revenue − cost at the prices on each trip · the 8 routes with the biggest impact (+ or −) · hover for per trip",
  "insights.commercial.card.timeline": "Contract timeline",
  "insights.commercial.card.timeline.q": "Soonest to end on top — when to start renewal talks",
  "insights.commercial.no_customer": "No customer set",
  "insights.commercial.other_customers": "Other customers",
  "insights.unavailable.no_route_econ": "Routes run in this period have no revenue/cost per job set",
  // ── ADR 0394 — insights ──
  "insights.capacity.question": "Are there enough vehicles and drivers for the next two weeks, who needs cover tomorrow, and is work shared fairly",
  "insights.capacity.kpi.unassigned": "Trips not fully assigned (14 days)",
  "insights.note.available_vehicles": "of {n} ready vehicles",
  "insights.capacity.card.demand_supply": "Vehicles needed vs ready (next 14 days)",
  "insights.capacity.card.demand_supply.q": "A bar above the line means too few vehicles · an unassigned trip counts as one vehicle",
  "insights.capacity.card.teams_tomorrow": "Drivers tomorrow, by team",
  "insights.capacity.card.teams_tomorrow.q": "Drivers with a job tomorrow who are on leave need cover first · spare = not on leave and not assigned",
  "insights.capacity.col.spare": "Spare",
  "insights.capacity.col.assigned_on_leave": "Assigned but on leave",
  "insights.capacity.col.assigned": "Assigned",
  "insights.capacity.col.name": "Team",
  "insights.capacity.card.workload": "Driver workload",
  "insights.capacity.card.workload.q": "Trips per week per driver this period — a long right tail means some carry too much",
  "insights.series.needed": "Vehicles needed",
  "insights.series.available": "Vehicles ready",
  "insights.series.overflow": "Overflow",
  "insights.x.0": "0",
  "insights.x.1-9": "1–9",
  "insights.x.10-19": "10–19",
  "insights.x.20-29": "20–29",
  "insights.x.30-39": "30–39",
  "insights.x.40+": "40+",
  "insights.unavailable.no_driver_trips": "No trips assigned to drivers in this period",
  // ── ADR 0394 — insights ──
  "insights.unavailable.no_future_trips": "No trips scheduled in the next 14 days",
  // ── ADR 0394 — insights ──
  "insights.adoption.question": "Do drivers close trips in the app, do announcements reach people, and are emails or invites stuck",
  "insights.adoption.kpi.latest_read": "Readers of the latest announcement",
  "insights.adoption.kpi.email_failed": "Emails that failed",
  "insights.adoption.kpi.invites_pending": "Invitations not yet accepted",
  "insights.note.of_drivers": "of {n} drivers",
  "insights.adoption.card.driver_logins": "% of drivers logging in per day",
  "insights.adoption.card.driver_logins.q": "Distinct drivers per day, as a share of all drivers",
  "insights.adoption.card.announcements": "Announcement read rate",
  "insights.adoption.card.announcements.q": "Latest 6 announcements · out of everyone who can see each one (drivers / passengers with an account) · passenger reads recorded since 2 Oct 2026",
  "insights.series.drivers_active": "Drivers active",
  "insights.series.login_failed": "Failed logins",
  "insights.unavailable.no_announcements": "No published announcements",
  // ── ADR 0394 — insights ──
  "insights.common.noDataNow": "Nothing here right now",
  "insights.common.emptyHintPeriod": "Nothing in the selected period — try a longer range",
  "insights.common.emptyHintLongest": "Nothing on this in the last 12 months",
  "insights.common.emptyHintSnapshot": "This card fills in once the data exists in the system",
  "insights.common.widen": "View 12 months",
  "insights.common.unavailableHint": "This needs data the system doesn't have yet — changing the period won't help",
  // ── ADR 0394 — insights ──
  "insights.fleet.kpi.idle": "Idle 7+ days",
  "insights.note.never_used": "{n} with no job in 180 days",
  "insights.note.sos_unacked": "{n} not acknowledged",
  "insights.note.unrecorded_bookings": "{n} bookings never marked",
  "insights.note.overflow_resolved": "{n} marked resolved",
  "insights.common.moreItems": "and {n} more — see the full list",
  // ── ADR 0394 — insights ──
  "insights.common.filters": "Filters",
  "insights.common.all": "All",
  "insights.common.filter_contract_id": "Contract",
  "insights.common.filter_vehicle_type": "Vehicle type",
  "insights.common.filter_branch": "Branch",
  "insights.common.moreCount": "and {n} more",
  "insights.common.seeAll": "See all",
  "insights.unavailable.filter_unsupported": "This can't be split by the selected filter — clear it to see",
  "insights.fleet.card.days_used": "Share of days each vehicle ran",
  "insights.fleet.card.days_used.q": "The whole fleet in one chart — the left bars are vehicles barely used",
  "insights.x.use_0": "Never",
  "insights.x.use_1-25": "1–25%",
  "insights.x.use_26-50": "26–50%",
  "insights.x.use_51-75": "51–75%",
  "insights.x.use_76-100": "76–100%",
  "insights.x.wk_0": "None",
  "insights.x.wk_<2": "< 2",
  "insights.x.wk_2-4": "2–4",
  "insights.x.wk_5-9": "5–9",
  "insights.x.wk_10-14": "10–14",
  "insights.x.wk_15+": "15+",
  "insights.x.pay_lt50": "< 50%",
  "insights.x.pay_50-79": "50–79%",
  "insights.x.pay_80-120": "80–120%",
  "insights.x.pay_121-150": "121–150%",
  "insights.x.pay_gt150": "> 150%",
  // ── ADR 0394 — insights ──
  "insights.adoption.kpi.staff_7d": "Staff who signed in in 7 days",
  "insights.adoption.card.staff_logins": "Staff signing in per day",
  "insights.adoption.card.staff_logins.q": "Distinct people per day · recorded since 2 Oct 2026, so earlier days are zero · people in several orgs are not counted yet",
  "insights.series.staff_active": "Staff",
  // ── ADR 0394 — insights ──
  "insights.series.aud_drivers": "To drivers",
  "insights.series.aud_passengers": "To passengers",
  "insights.series.aud_all": "To everyone",
  // ── ADR 0394 — insights ──
  "insights.common.partialMonth": "Faded / dashed = latest period still running (counted to {to})",
  // ── ADR 0394 — insights ──
  "insights.focal.margin": "Left after driver pay, {x}",
  "insights.focal.busiest_week": "Busiest week · from {x}",
  "insights.focal.above_avg": "{x} · {n}% above average",
  "insights.focal.below_avg": "{x} · {n}% below average",
  "insights.focal.series_share": "{series} share · {x}",
  "insights.focal.barely_used": "Vehicles barely used (≤ 25% of days)",
  "insights.focal.stale_wo": "Work orders open > 30 days",
  "insights.focal.overloaded": "Drivers on 15+ trips a week",
  "insights.focal.pay_outliers": "Pay outside the normal range — check before locking",
  "insights.focal.short_days": "Days short of vehicles, next 14 days",
  "insights.focal.next_months": "Due in the next {n} months",
  "insights.focal.top": "#1 · {label}",
  "insights.focal.top_share": "{label} · of the total",
  "insights.focal.loss_routes": "Loss-making routes",
  "insights.focal.below_target": "Below target",
  "insights.focal.above_target": "Above target",
  "insights.focal.hot": "Hotspot · {row} {col}",
  "insights.focal.must_fix": "Legal gaps to close first",
  "insights.focal.worst_gap": "{label} · furthest below spec",
  "insights.focal.below_target_day": "Days below the {target} target",
  "insights.focal.below_target_week": "Weeks below the {target} target",
  "insights.focal.below_target_month": "Months below the {target} target",
  "insights.focal.lowest_at": "Lowest · {x}",
  "insights.focal.off_target": "Routes under target or overcrowded",
  "insights.focal.assigned_on_leave": "Drivers with a job tomorrow who are on leave",
  // ── viz audit 7 ต.ค. 2026 — การ์ด/KPI ที่เพิ่มหรือแทนที่ ──
  "insights.fleet.kpi.spare_peak": "Spare vehicles at peak",
  "insights.fleet.kpi.down": "Vehicles down now",
  "insights.fleet.card.down": "Vehicles off the road now",
  "insights.fleet.card.down.q": "Which vehicle, how many days, and why — from its oldest open work order",
  "insights.fleet.col.name": "Plate",
  "insights.fleet.col.days_down": "Days down",
  "insights.fleet.col.reason": "Reason",
  "insights.note.peak_used": "Peak day used {n}",
  "insights.note.longest_down_days": "Longest {n} days",
  "insights.maintenance.card.spend_mix": "Planned vs unplanned workshop spend",
  "insights.maintenance.card.spend_mix.q": "Is unplanned work taking a bigger share · 12 months (no fuel or inspection — totals live on the cost page)",
  "insights.maintenance.card.pm_overdue": "Overdue PM",
  "insights.maintenance.card.pm_overdue.q": "Which vehicle, which job, how many days / km late — worst first",
  "insights.maintenance.col.name": "Plate",
  "insights.maintenance.col.service": "Job",
  "insights.maintenance.col.days_over": "Days late",
  "insights.maintenance.col.km_over": "Km late",
  "insights.series.wo_planned": "Planned (PM/upgrades)",
  "insights.series.wo_unplanned": "Unplanned repairs",
  "insights.cost.kpi.overdue_installments": "Overdue instalments today",
  "insights.note.overdue_payments": "{n} payments",
  "insights.series.flag_pending": "Awaiting review",
  "insights.series.flag_reviewed": "Reviewed",
  "insights.workforce.kpi.trial_ending": "Probation ending in 30 days",
  "insights.note.on_trial": "{n} on probation",
  "insights.series.pnl_trip_pay": "Driver trip pay",
  "insights.series.pnl_payroll": "Payroll",
  "insights.series.pnl_vehicle": "Vehicle cost",
  "insights.focal.pnl_margin": "Margin after known costs · {x}",
  "insights.ridership.kpi.overflow": "Overbooked trips",
  "insights.ridership.card.overflow_hours": "Overbooked trips by hour",
  "insights.ridership.card.overflow_hours.q": "Departure hour of trips booked past their seats — where an extra vehicle would help",
  "insights.note.complaints": "{n} complaints",
  "insights.focal.peak_overflow": "Most overbooked departure · {x}:00",
  "insights.drivers.kpi.accident_rate": "Accidents per 1,000 trips",
  "insights.drivers.kpi.with_case": "Drivers with a case this period",
  "insights.drivers.card.serious_open": "Serious cases still open",
  "insights.drivers.card.serious_open.q": "High/critical severity, oldest first — close these before anything else",
  "insights.drivers.col.case": "Case",
  "insights.drivers.col.category": "Category",
  "insights.drivers.col.severity": "Severity",
  "insights.drivers.col.days_open": "Days open",
  "insights.note.accidents": "{n} accidents",
  "insights.unit.per_1k": "per 1k",
  "insights.unavailable.no_trips": "No trips ran in this period",
  "insights.st.cat_accident": "Accident",
  "insights.st.cat_violation": "Violation",
  "insights.st.cat_complaint": "Complaint",
  "insights.st.cat_inspection": "Inspection",
  "insights.st.sev_high": "High",
  "insights.st.sev_critical": "Critical",
  "insights.commercial.kpi.revenue_vs_value": "Actual revenue ÷ contract value",
  "insights.note.contracts_with_value": "{n} contracts have a value",
  "insights.unavailable.no_contract_value": "No live contract has a monthly value",
  "insights.capacity.kpi.spare_tomorrow": "Spare drivers tomorrow",
  "insights.capacity.kpi.schedule_horizon": "Schedule published ahead",
  "insights.note.assigned_on_leave": "{n} assigned but on leave",
  "insights.note.of_days": "of {n} days looked at",
  "insights.adoption.kpi.closed_by_driver": "Trips closed by the driver in the app",
  "insights.note.closed_by_admin": "{n} closed by an admin",
  "insights.unavailable.no_completed_trips": "No trips finished in this period",
  "insights.compliance.kpi.legal_blocked": "Vehicles not road-legal today",
  "insights.series.doc_vehicle": "Vehicle documents",
  "insights.series.doc_people": "Licences / staff documents",
  "insights.series.doc_contract": "Contract documents",
  "insights.unavailable.no_paid_drivers": "No driver was paid in this period — no median to compare against",
  "insights.series.pm_unknown": "Unknown (no odometer or due date)",
  "insights.note.pm_unknown": "{n} plans can't be judged (no odometer)",
  "insights.status.unknown": "Expiry unknown",
  "insights.series.ontime_7d": "7-day average",
  "insights.series.ontime_daily": "Daily",
  // ── insp — ตรวจสภาพ / inspections (ADR 0367) ──────────────────────────
  "insp.pageTitle": "Vehicle inspections",
  "insp.pageSubtitle": "Inspections, results and checklist templates for every vehicle",
  "insp.tabResults": "Results",
  "insp.tabTemplates": "Templates",
  "insp.templates": "Templates",
  "insp.statusDraft": "Draft",
  "insp.statusInProgress": "In progress",
  "insp.statusSubmitted": "Awaiting review",
  "insp.statusClosed": "Closed",
  "insp.statusCancelled": "Cancelled",
  "insp.roleOwner": "Owner",
  "insp.roleInspector": "Inspector",
  "insp.roleDriver": "Driver",
  "insp.roleReviewer": "Reviewer",
  "insp.roleWatcher": "Watcher",
  "insp.typePassFail": "Pass/fail",
  "insp.typeText": "Text",
  "insp.typeNumber": "Number",
  "insp.typeChoice": "Choice",
  "insp.typePhoto": "Photo",
  "insp.pass": "Pass",
  "insp.fail": "Fail",
  "insp.na": "N/A",
  "insp.notChecked": "Not checked",
  "insp.required": "Required",
  "insp.resultHasFail": "Has failures",
  "insp.resultFailOpen": "Failures without a ticket",
  "insp.resultIncomplete": "Not fully checked",
  "insp.resultAllPass": "All passed",
  "insp.resultAny": "Result: any",
  "insp.resultAria": "{pass} passed, {fail} failed of {total}",
  "insp.blockRequired": "Required item not answered",
  "insp.blockNote": "A failure needs a note",
  "insp.blockPhoto": "A failure needs a photo",
  "insp.blockMinPhotos": "Not enough photos yet",
  "insp.statToday": "Inspected today",
  "insp.statNotInspected": "{n} vehicles not inspected today",
  "insp.statAwaiting": "Awaiting review",
  "insp.statOldest": "Oldest waiting {n} days",
  "insp.statFail7d": "With failures (7 days)",
  "insp.statFailOpen": "Failures without a repair ticket",
  "insp.searchPlaceholder": "Search number, plate, sheet, people, notes…",
  "insp.createButton": "New inspection",
  "insp.filterPerson": "Person involved",
  "insp.mine": "Mine",
  "insp.dateRangeActive": "Dates {from} – {to}",
  "insp.colCode": "Number",
  "insp.colResult": "Result",
  "insp.colFail": "Failed",
  "insp.colPeople": "People",
  "insp.colClosedBy": "Signed off by",
  "insp.colFiles": "Files",
  "insp.columns": "Columns",
  "insp.nFail": "{n} failed",
  "insp.ticketsOpened": "Repair tickets opened",
  "insp.noTicketYet": "No ticket yet",
  "insp.openNewTab": "Open in new tab",
  "insp.copyLink": "Copy link",
  "insp.linkCopied": "Link copied",
  "insp.linkCopyFailed": "Could not copy the link",
  "insp.createFromThis": "New inspection from this one",
  "insp.loadFailed": "Could not load inspections",
  "insp.emptyNone": "No inspections yet",
  "insp.emptyNoneHint": "Create the first inspection and pick the sheets it uses — one inspection can hold several.",
  "insp.emptyFiltered": "No inspections match these filters",
  "insp.emptyFilteredHint": "Try fewer filters or a wider date range",
  "insp.inspectedAt": "Inspected at",
  "insp.submittedAt": "Submitted",
  "insp.closedAt": "Signed off",
  "insp.purpose": "Purpose",
  "insp.sheets": "Sheets",
  "insp.createTitle": "New inspection",
  "insp.createAndOpen": "Create and start",
  "insp.created": "Created {code}",
  "insp.createFailed": "Could not create the inspection",
  "insp.lastInspection": "Last inspected {date} · {code}",
  "insp.openDuplicate": "This vehicle already has {code} for the same purpose, not yet submitted",
  "insp.openThatOne": "Open it",
  "insp.suggestedForType": "Suggested for this vehicle type",
  "insp.pickVehicleForSuggestions": "Pick a vehicle to see suggestions",
  "insp.showOtherTemplates": "Show {n} more templates",
  "insp.showSuggestedOnly": "Suggested only",
  "insp.nItems": "{n} items",
  "insp.minutesShort": "min",
  "insp.noActiveTemplates": "No active templates",
  "insp.inspector": "Inspector",
  "insp.inspectorDefaultYou": "Defaults to you",
  "insp.createHint": "You will own this inspection. Add people, photos and answers on the next page.",
  "insp.notFound": "Inspection not found, or you are not part of it",
  "insp.backToList": "Back to the list",
  "insp.checked": "Checked",
  "insp.itemsTotal": "Items total",
  "insp.files": "Files",
  "insp.ticketsShort": "Tickets",
  "insp.activity": "Activity",
  "insp.print": "Print",
  "insp.saving": "Saving…",
  "insp.savedAt": "Saved {time}",
  "insp.saveRetry": "Not saved — retry",
  "insp.saveFailed": "Could not save",
  "insp.actionFailed": "That did not work",
  "insp.submit": "Submit",
  "insp.submitBlocked": "Submit ({n} to go)",
  "insp.jumpToBlock": "Go to the first item that still needs work",
  "insp.return": "Send back",
  "insp.close": "Sign off & close",
  "insp.reopen": "Reopen",
  "insp.cancel": "Cancel inspection",
  "insp.deleteDraft": "Delete draft",
  "insp.draftDeleted": "Draft deleted",
  "insp.dialog_submit": "Submit inspection",
  "insp.dialog_return": "Send back for changes",
  "insp.dialog_close": "Sign off and close",
  "insp.dialog_reopen": "Reopen inspection",
  "insp.dialog_cancel": "Cancel inspection",
  "insp.confirm_submit": "Submit",
  "insp.confirm_return": "Send back",
  "insp.confirm_close": "Sign off",
  "insp.confirm_reopen": "Reopen",
  "insp.confirm_cancel": "Cancel it",
  "insp.done_submit": "Submitted",
  "insp.done_return": "Sent back",
  "insp.done_close": "Signed off",
  "insp.done_reopen": "Reopened",
  "insp.done_cancel": "Cancelled",
  "insp.submitUncheckedNote": "{n} optional items are unchecked — you can still submit",
  "insp.failOpenWarning": "{n} failed items have no repair ticket yet",
  "insp.submitHint": "After submitting, answers lock and the reviewer is notified",
  "insp.reason": "Reason",
  "insp.reviewNote": "Reviewer note",
  "insp.cancelledBecause": "Cancelled: {reason}",
  "insp.returnedBecause": "Sent back: {reason}",
  "insp.addSheet": "Add sheet",
  "insp.addNSheets": "Add {n} sheets",
  "insp.alreadyAttached": "Already added",
  "insp.searchTemplates": "Search templates…",
  "insp.sheetsAdded": "Added {n} sheets",
  "insp.sheetRemoved": "Sheet removed",
  "insp.removePerson": "Remove from inspection",
  "insp.sheetHandedOff": "Handed back",
  "insp.removeSheet": "Remove sheet",
  "insp.removeSheetQ": "Remove \"{name}\"?",
  "insp.removeSheetConfirm": "{n} answers and their photos will be discarded. This cannot be undone.",
  "insp.assignSheet": "Who answers this sheet",
  "insp.anyoneMayAnswer": "Any inspector",
  "insp.sheetAssignee": "Assigned: {name}",
  "insp.noSheetsYet": "No sheets on this inspection yet",
  "insp.noSheetsHint": "Use \"Add sheet\" to pick templates — several if needed",
  "insp.noSheetsReadOnly": "Nobody has added a sheet yet",
  "insp.filterUnchecked": "Unchecked",
  "insp.filterFail": "Failed",
  "insp.passRemaining": "Pass the rest",
  "insp.passGroup": "Pass group",
  "insp.passRemainingConfirm": "Marks {n} unanswered pass/fail items as passed (answered items, readings and photos are left alone)",
  "insp.passNItems": "Pass {n} items",
  "insp.newerVersion": "A newer version of this template exists (v{from} → v{to}) and this sheet has no answers yet",
  "insp.upgradeNow": "Update to latest",
  "insp.upgraded": "Sheet updated to the latest version",
  "insp.shortcuts": "Keyboard shortcuts",
  "insp.shortcutsHelp": "J / K next/previous · P pass · F fail · N not applicable · C note",
  "insp.noFailures": "No failures in this sheet",
  "insp.allChecked": "Every item is checked",
  "insp.howToCheck": "How to check",
  "insp.passRange": "Passes between {min} – {max} {unit}",
  "insp.flipVerdict": "Click to flip the verdict",
  "insp.photosOfMin": "{n}/{min} photos",
  "insp.addPhoto": "Add photo",
  "insp.viewPhoto": "View photo",
  "insp.removePhoto": "Remove photo",
  "insp.maxPhotos": "Up to {n} photos per item",
  "insp.uploadFailed": "Upload failed",
  "insp.failNotePlaceholder": "What is wrong…",
  "insp.addNoteOrPhoto": "Add a note or photo",
  "insp.openTicket": "Open repair ticket",
  "insp.ticketOpened": "Ticket opened",
  "insp.ticketOpening": "Opening ticket…",
  "insp.answeredBy": "Answered by {name}",
  "insp.photoViewer": "Photo viewer",
  "insp.openTicketN": "Open ticket ({n} items)",
  "insp.ticketHint": "Pick the failed items to put on one repair ticket. Their photos and notes go with them.",
  "insp.ticketTitle": "Ticket title",
  "insp.ticketTitleAuto": "Leave empty to name it after the items",
  "insp.ticketCreated": "Opened {code}",
  "insp.ticketFailed": "Could not open the ticket",
  "insp.generalInfo": "General",
  "insp.editInfo": "Edit inspection",
  "insp.location": "Location",
  "insp.createdBy": "Created by",
  "insp.titleOptional": "Title (optional)",
  "insp.people": "People",
  "insp.noReviewerHint": "No reviewer yet — an admin can sign off, or add someone as \"Reviewer\"",
  "insp.review": "Sign-off",
  "insp.closedByOn": "Signed off by {name} on {date}",
  "insp.awaitingReviewBy": "Submitted by {by} on {date} · waiting for {name}",
  "insp.reviewPending": "Once submitted, {name} will be asked to sign off",
  "insp.anyAdmin": "an admin",
  "insp.vehicleHistory": "This vehicle's inspections",
  "insp.noOtherInspections": "No other inspections for this vehicle",
  "insp.seeAll": "See all",
  "insp.scopeInspection": "Inspection",
  "insp.scopeItems": "Items",
  "insp.attachFiles": "Attach files",
  "insp.dropHint": "Drop files anywhere in this box · photos, PDFs, documents",
  "insp.filesAttached": "Attached {n} files",
  "insp.removeFile": "Remove file",
  "insp.fileOpenFailed": "Could not open the file",
  "insp.inspectionPhoto": "Inspection photo",
  "insp.document": "Document",
  "insp.noFiles": "No files yet",
  "insp.noActivity": "Nothing recorded yet",
  "insp.actCreate": "created the inspection",
  "insp.actUpdate": "edited the details",
  "insp.actDelete": "deleted the draft",
  "insp.actSheetAdd": "added sheets",
  "insp.actSheetRemove": "removed a sheet",
  "insp.actSheetUpgrade": "updated a sheet version",
  "insp.actSheetUpdate": "changed a sheet",
  "insp.actAnswer": "changed results",
  "insp.actParticipantAdd": "added a person",
  "insp.actParticipantRemove": "removed a person",
  "insp.actAttachmentAdd": "attached files",
  "insp.actAttachmentRemove": "removed a file",
  "insp.actStatus": "changed the status",
  "insp.actWorkOrder": "opened a repair ticket",
  "insp.nAnswersDropped": "{n} answers discarded",
  "insp.nFiles": "{n} files",
  "insp.printTitle": "Vehicle inspection",
  "insp.item": "Item",
  "insp.value": "Value",
  "insp.failPhotos": "Photos of failed items",
  "insp.signInspector": "Inspector",
  "insp.signReviewer": "Reviewer",
  "insp.tplActive": "Active",
  "insp.tplInactive": "Off",
  "insp.tplOff": "Off",
  "insp.category": "Category",
  "insp.allCategories": "All categories",
  "insp.sortBy": "Sort by",
  "insp.sortUsage": "Most used",
  "insp.sortName": "Name",
  "insp.sortUpdated": "Recently edited",
  "insp.sortLastUsed": "Recently used",
  "insp.nGroupsItems": "{g} groups · {n} items",
  "insp.usedN": "used {n}×",
  "insp.lastUsedOn": "last used {date}",
  "insp.pickTemplate": "Pick a template on the left",
  "insp.discardChangesQ": "Discard changes?",
  "insp.discardChanges": "The template you are editing is not saved. Switching now loses the changes.",
  "insp.discard": "Discard",
  "insp.tplActiveLabel": "Active",
  "insp.tplInactiveLabel": "Off",
  "insp.tplTurnedOn": "Template switched on",
  "insp.tplTurnedOff": "Template switched off",
  "insp.tplTurnOffQ": "Switch off \"{name}\"?",
  "insp.tplTurnOffBody": "New inspections cannot pick it. The {n} inspections that used it keep their copy. You can switch it back on any time.",
  "insp.tplTurnOff": "Switch off",
  "insp.tplDuplicated": "Copied (switched off until you are ready)",
  "insp.editedBy": "edited {date} by {name}",
  "insp.fromInspection": "From an inspection →",
  "insp.editedOn": "edited {date}",
  "insp.previewAsInspector": "Preview as inspector",
  "insp.duplicate": "Duplicate",
  "insp.structure": "Structure",
  "insp.stats90": "Last 90 days",
  "insp.statsBasis": "from {n} inspections",
  "insp.failRate": "{pct}% failed ({fail}/{n})",
  "insp.noData": "no data",
  "insp.photoOnFail": "Photo required on failure",
  "insp.previewTitle": "Preview: {name}",
  "insp.previewHint": "Try it — nothing is saved",
  "insp.itemType": "Answer type",
  "insp.allowNa": "Allow \"not applicable\"",
  "insp.unit": "Unit",
  "insp.min": "Min to pass",
  "insp.max": "Max to pass",
  "insp.rangeHint": "Outside the range fails automatically · either side may be empty",
  "insp.options": "Options",
  "insp.optionLabel": "Option",
  "insp.countsAsFail": "Counts as fail",
  "insp.addOption": "Add option",
  "insp.minPhotos": "Minimum photos",
  "insp.instruction": "How to check",
  "insp.instructionPlaceholder": "e.g. measure the inner tread; at least 1.6 mm",
  "insp.examplePhotos": "Example photos",
  "insp.addExample": "Add example photo",
  "insp.whenFailRequire": "On failure, require:",
  "insp.requireNote": "a note",
  "insp.requirePhoto": "a photo",
  "insp.itemSettings": "Item settings",
  "insp.moveUp": "Move up",
  "insp.moveDown": "Move down",
  "insp.tplNamePlaceholder": "e.g. Exterior walk-around",
  "insp.tplCode": "Short code",
  "insp.tplCodePlaceholder": "e.g. TPL-EXT",
  "insp.vehicleTypes": "Vehicle types",
  "insp.allVehicleTypes": "All types",
  "insp.nTypes": "{n} types",
  "insp.estimatedMinutes": "Estimated minutes",
  "insp.errName": "The template needs a name",
  "insp.errGroupName": "Every group needs a name (or remove the empty one)",
  "insp.errChoice": "\"{label}\" needs at least two options",
  "insp.errRange": "\"{label}\": min is above max",
  "insp.nChanges": "{n} unsaved changes",
  "insp.noChanges": "No changes",
  "insp.saveAsVersionHint": "Saves as v{v} · inspections already answered keep their version",
  "insp.tplSaved": "Template saved (v{v})",
  "insp.tplCreated": "Template created",
  // plan/fuel-cards (2026-10-15) — the company's fuel cards, held by people
  "nav.fuelCards": "Fuel cards",
  "hr.fuelCardsTab": "Fuel cards",
  "fuelCard.title": "Fuel cards",
  "fuelCard.subtitle": "{n} company cards · {held} held",
  "fuelCard.add": "Add card",
  "fuelCard.search": "Search provider, number or holder",
  "fuelCard.filter.all": "All",
  "fuelCard.filter.held": "Held",
  "fuelCard.filter.free": "Free",
  "fuelCard.filter.over": "Over limit",
  "fuelCard.filter.closed": "Closed",
  "fuelCard.col.card": "Card",
  "fuelCard.col.holder": "Holder",
  "fuelCard.col.limit": "Limit",
  "fuelCard.col.added": "Added",
  "fuelCard.col.used": "Used",
  "fuelCard.col.left": "Left",
  "fuelCard.col.since": "Held since",
  "fuelCard.free": "Free — nobody holds it",
  "fuelCard.since": "Held since {date}",
  "fuelCard.over": "Over limit",
  "fuelCard.closedTag": "Closed",
  "fuelCard.emptyTitle": "No fuel cards yet",
  "fuelCard.emptyBody": "Add the company's cards here first, then give them to people",
  "fuelCard.action.give": "Give",
  "fuelCard.action.swap": "Swap",
  "fuelCard.action.withdraw": "Withdraw",
  "fuelCard.action.topup": "Add money",
  "fuelCard.action.edit": "Edit",
  "fuelCard.action.close": "Close",
  "fuelCard.action.reopen": "Reopen",
  "fuelCard.addTitle": "Add a fuel card",
  "fuelCard.editTitle": "Edit card",
  "fuelCard.f.provider": "Provider",
  "fuelCard.f.providerHint": "e.g. PTT, Shell, Bangchak",
  "fuelCard.f.number": "Card number",
  "fuelCard.f.note": "Note",
  "fuelCard.f.person": "Holder",
  "fuelCard.f.personPick": "Search a name or code",
  "fuelCard.f.limit": "Limit",
  "fuelCard.f.limitHint": "Fill-ups paid with this card come off the limit; past it, the card turns red",
  "fuelCard.f.amount": "Amount to add",
  "fuelCard.f.card": "Card",
  "fuelCard.giveTitle": "Give card {card}",
  "fuelCard.swapTitle": "Swap card {card}",
  "fuelCard.swapFrom": "{name} holds it now; the new holder starts at 0 used",
  "fuelCard.topupTitle": "Add money to {card}",
  "fuelCard.topupNow": "{left} baht left now",
  "fuelCard.withdrawQ": "Take {card} back from {name}?",
  "fuelCard.withdrawBody": "The card becomes free; what was used stays in its history",
  "fuelCard.closeQ": "Close card {card}?",
  "fuelCard.closeBody": "It will not be offered again; old fill-ups paid with it still open",
  "fuelCard.saved": "Saved",
  "fuelCard.lead": "The company's cards — added here, then given to someone; past the limit they turn red",
  "fuelCard.noMatch": "No card matches the search or filters",
  "fuelCard.f.holderType": "Holder type",
  "fuelCard.f.leftMax": "Left at most",
  "fuelCard.f.leftMaxHint": "Held cards only; 0 = cards used up or over",
  "fuelCard.f.leftMaxChip": "Left at most {amount} baht",
  "fuelCard.action.history": "Holder history",
  "fuelCard.sort.provider": "Provider",
  "fuelCard.sort.card_number": "Card number",
  "fuelCard.sort.holder": "Holder",
  "fuelCard.sort.since": "Held since",
  "fuelCard.sort.limit": "Limit",
  "fuelCard.sort.added": "Added",
  "fuelCard.sort.used": "Used",
  "fuelCard.sort.left": "Left",
  "fuelCard.history.title": "Who held {card}",
  "fuelCard.history.none": "Nobody has held this card",
  "fuelCard.history.now": "Holds it now",
  "fuelCard.history.range": "{from} – {to}",
  "fuelCard.history.since": "Since {from}",
  "fuelCard.history.endedSwap": "Swapped to someone else",
  "fuelCard.history.endedWithdraw": "Withdrawn",
  "fuelCard.history.money": "Limit {limit} · added {added} · used {used} · left {left} baht",
  "fuelCard.action.limit": "Change limit",
  "fuelCard.action.delete": "Delete",
  "fuelCard.limitTitle": "Change the limit of {card}",
  "fuelCard.limitNow": "{used} baht used · {added} baht added — these stay",
  "fuelCard.f.limitEdit": "Starting limit",
  "fuelCard.deleteQ": "Delete card {card}?",
  "fuelCard.deleteBody": "Nobody has held it; it disappears from the list — for a card added by mistake",
  "fuelCard.deleted": "Card deleted",
  "fuelCard.baht": "{amount} baht",
  "fuelCard.person.title": "Fuel cards held",
  "fuelCard.person.none": "Holds no fuel card",
  "fuelCard.person.noFree": "No free card — add one in the Fuel cards menu first",
  "fuelCard.person.addTitle": "Add a card for {name}",
  "fuelCard.person.topups": "Money added {n} times",
  "fuelCard.person.topupLine": "{date} · +{amount} baht",
  "fuelCard.person.goMenu": "Open Fuel cards",
  "fuelCard.overChip": "Fuel card over limit",
  "fuelCard.legacy": "Old-system card",
  "fuelCard.pickCard": "Choose a card",
  "fuelCard.pickPersonFirst": "Choose who filled first, then one of their cards",
  "fuelCard.noneHeld": "This person holds no fuel card",
  // plan/holiday-pay — the holiday list and the multiplied trip pay
  "nav.financeHolidays": "Holidays",
  "holiday.title": "Holidays",
  "holiday.lead": "Tickets that start inside these windows have their trip pay multiplied. The rate on the ticket does not change.",
  "holiday.add": "Add holiday",
  "holiday.addTitle": "Add holiday",
  "holiday.editTitle": "Edit holiday",
  "holiday.saved": "Saved. Tickets in this window follow.",
  "holiday.deleted": "Deleted. Its tickets are back to their trip rate.",
  "holiday.search": "Search name, note",
  "holiday.view.all": "All",
  "holiday.view.upcoming": "Upcoming",
  "holiday.view.past": "Past",
  "holiday.col.date": "Date",
  "holiday.col.name": "Name",
  "holiday.col.window": "Time",
  "holiday.col.multiplier": "Multiplier",
  "holiday.col.tickets": "Tickets",
  "holiday.sort.date": "Date",
  "holiday.sort.name": "Name",
  "holiday.sort.start": "Start time",
  "holiday.sort.multiplier": "Multiplier",
  "holiday.sort.tickets": "Tickets",
  "holiday.wholeDay": "All day",
  "holiday.windowNext": "{start}–{end} next day",
  "holiday.ticketsN": "{n} tickets",
  "holiday.action.edit": "Edit",
  "holiday.action.delete": "Delete",
  "holiday.noMatch": "No holiday matches",
  "holiday.emptyTitle": "No holidays yet",
  "holiday.emptyBody": "Add a holiday with its time and multiplier. Holidays are never guessed.",
  "holiday.f.date": "Date",
  "holiday.f.name": "Name",
  "holiday.f.nameHint": "e.g. Songkran, special night shift",
  "holiday.f.window": "Time",
  "holiday.f.wholeDay": "All day (00:00–24:00)",
  "holiday.f.wholeDayHint": "Tickets starting any time this day",
  "holiday.f.windowHint": "Tickets starting from the start time until before the end time",
  "holiday.f.nextDay": "Ends {end} the next day",
  "holiday.f.start": "Start time",
  "holiday.f.end": "End time",
  "holiday.f.multiplier": "Trip pay multiplier",
  "holiday.f.multiplierHint": "e.g. 2.5 turns a 500 trip rate into 1,250 · ticket reimbursements/deductions are not multiplied",
  "holiday.f.multiplierBad": "More than 1, at most 10, two decimals",
  "holiday.f.note": "Note",
  "holiday.f.range": "Dates",
  "holiday.f.from": "From",
  "holiday.f.to": "To",
  "holiday.f.fromChip": "from {d}",
  "holiday.f.toChip": "until {d}",
  "holiday.settledWarn": "{n} tickets in this window are in a pay round or paid. A change goes to the next bill as a difference.",
  "holiday.deleteQ": "Delete holiday \"{name}\"?",
  "holiday.deleteBody": "No ticket falls in it yet.",
  "holiday.deleteBodyTickets": "{n} tickets go back to their trip rate.",
  "holiday.deleteBodySettled": "{n} tickets are in a pay round or paid; the multiplied part is taken back in the next bill.",
  "holiday.ticketsLead": "Trip pay {mult}",
  "holiday.noTickets": "No completed ticket falls in this window yet",
  "holiday.tag": "{base} {mult} · {name}",
  "holiday.tagShort": "{mult} · {name}",
  "holiday.onTicket": "Holiday \"{name}\": trip rate {base} {mult} = {total}",
  "holiday.onTicketNoRate": "Holiday \"{name}\": trip pay is multiplied {mult}",
  "audit.fin.finance_holiday_create": "Add holiday",
  "audit.fin.finance_holiday_update": "Edit holiday",
  "audit.fin.finance_holiday_delete": "Delete holiday",
  "holiday.sumTitle": "What \"{name}\" did",
  "holiday.sum.tickets": "Tickets multiplied",
  "holiday.sum.people": "People",
  "holiday.sum.base": "Trip pay before",
  "holiday.sum.extra": "Added by the holiday",
  "holiday.sum.after": "After {after}",
  "holiday.sum.states": "Not in a round {open} · In a round {inRound} · Paid {paid}",
  "holiday.sum.byPerson": "By person (most money first)",
  "holiday.sum.page": "Page {page}/{pages} · {total} people",
  "holiday.sum.all": "See all {n} in the money list",
  "financeTxn.f.holiday": "Holiday",
  "financeTxn.f.noPerson": "No employee",
  "nav.group.requests": "Requests",
  // G5 — the request list as a standard list screen
  "requests.leadShort": "What staff send from the app — any matter. Take it, work it, then close or say no with a reason. If it is about money, make the money row from the request's page.",
  "requests.col.no": "No.",
  "requests.col.title": "Subject",
  "requests.col.employee": "Filed by",
  "requests.col.status": "Status",
  "requests.col.owner": "Taken by",
  "requests.col.filed": "Filed",
  "requests.col.updated": "Updated",
  "requests.col.money": "Money tied",
  "requests.sort.display_id": "No.",
  "requests.sort.title": "Subject",
  "requests.sort.employee": "Filed by",
  "requests.sort.status": "Status",
  "requests.sort.owner": "Taken by",
  "requests.sort.created": "Filed",
  "requests.sort.updated": "Updated",
  "requests.sort.money": "Money tied",
  "requests.f.employeePick": "Pick who filed",
  "requests.f.anyone": "Anyone",
  "requests.f.from": "Filed from",
  "requests.f.to": "To",
  "requests.f.filed": "Filed",
  "requests.f.fromChip": "from {d}",
  "requests.f.toChip": "until {d}",
  "requests.f.moneyYes": "Money tied",
  "requests.f.moneyNo": "No money tied",
  "requests.moneyRows": "{n} money rows",
  // 2026-10-19 — pick the bill when adding a row
  "billPick.title": "Into bill",
  "billPick.box": "Accumulated box",
  "billPick.mustPick": "This person has a bill open — pick the bill, or pick the accumulated items (someone must pick them into a round)",
  "addMoney.need.bill": "which bill it goes into",
  "billPick.monthly": "Monthly bill",
  "billPick.placeFailed": "Saved, but it could not go into the picked bill — it is in the automatic one",
  "billPick.autoTag": "Automatic",
  "billPick.round": "{from}–{to}",
  "billPick.pay": "paid {day}",
  "billPick.thisRound": "This round",
  "billPick.nextRound": "Next round",
  "billPick.notYet": "In no bill yet",
  "billPick.autoLater": "In no bill until the row's day",
  "billPick.loading": "Loading this person's bills…",
  "billPick.emptyShort": "no rows yet",
  "billPick.empty": "No rows yet — starts with this one",
  "billPick.inBill": " · into {bill}",
  // plan/review-tools — checking bills
  "billCheck.open": "Check bills",
  "billCheck.openCount": "Check bills ({n})",
  "billCheck.openCountHint": "{n} bills in ended rounds not checked yet (one person can have several — one per contract)",
  "billCheck.title": "Check bills",
  "billCheck.lead": "Pick a contract and a round, then check person by person. \"Checked\" keeps the bill as you saw it; if anything changes later the bill says it needs a look again, and what changed.",
  "billCheck.back": "Back to bills",
  "billCheck.pickContract": "Pick a contract",
  "billCheck.scope.monthly": "Salary (everyone's monthly bills)",
  "billCheck.scope.box": "Accumulated items (no contract)",
  "billCheck.pickFirst": "Pick a contract first — its people for that round show here",
  "billCheck.pickPerson": "Pick a person to see their bill",
  "billCheck.window": "{from}–{to} · paid {pay}",
  "billCheck.progress": "Checked {done}/{all} people · {pct}% of the money",
  "billCheck.progressMoney": "before deductions",
  "billCheck.progressMoneyHint": "The rows of the bills before what the system deducts (such as withholding tax) — the list shows each bill after it",
  "billCheck.filter": "Show",
  "billCheck.filter.all": "All",
  "billCheck.filter.unchecked": "Not checked",
  "billCheck.filter.stale": "Look again",
  "billCheck.filter.flagged": "Problem",
  "billCheck.filter.checked": "Checked",
  "billCheck.filter.unacked": "Checked past a point",
  "billCheck.next": "Next not checked",
  "billCheck.none": "Nobody here",
  "billCheck.allDone": "Nobody left to check in this round · {checked} checked · {flagged} waiting on a problem",
  "billCheck.rows": "{n} rows",
  "billCheck.checkedBy": "checked by {name} {day}",
  "billCheck.state.unchecked": "Not checked",
  "billCheck.state.checked": "Checked",
  "billCheck.state.flagged": "Problem",
  "billCheck.state.stale": "Look again",
  "billCheck.state.unacked": "Checked · points open",
  "billCheck.box": "Accumulated box",
  "billCheck.staleLead": "{name} checked it on {day}. Since then:",
  "billCheck.why.changed": "a row was edited",
  "billCheck.why.new": "new rows came in",
  "billCheck.why.left": "rows left",
  "billCheck.why.remade": "the system re-made a row",
  "billCheck.sealedBy": "Checked by {name} on {day}",
  "billCheck.row.changed": "edited after the check (was {was})",
  "billCheck.row.new": "came in after the check",
  "billCheck.row.remade": "re-made by the system (was {was})",
  "billCheck.flag": "Problem",
  "billCheck.flagPh": "What is wrong",
  "billCheck.flagSave": "Save",
  "billCheck.flagClose": "Close",
  "billCheck.flagFirst": "Close the problem notes before checking",
  "billCheck.noRows": "No rows in this bill",
  "billCheck.leftTitle": "{n} rows left this bill after the check",
  "billCheck.leftTo": "now in {bill}",
  "billCheck.leftKind.voided": "voided",
  "billCheck.leftKind.hold": "held",
  "billCheck.leftKind.settled": "settled outside",
  "billCheck.leftKind.unknown": "in no bill",
  "billCheck.movedBy": "moved by {name}",
  "billCheck.movedItself": "moved by the system",
  "billCheck.computedTitle": "Deducted by the system (worked out for you, nothing to tick)",
  "billCheck.computedWas": "was {was}",
  "billCheck.seal": "Checked",
  "check.chip.remove": "Remove this filter",
  "check.chip.payout": "Pay day {date}",
  "check.chip.nearest": "(nearest)",
  "check.chip.round": "Round {from}–{to}",
  "check.chip.days": "Worked {from}–{to}",
  "check.chip.time": "Starts {from}–{to}",
  "check.add.payout": "Pay day",
  "check.add.person": "Person",
  "check.add.contract": "Contract / round",
  "check.add.round": "Round of the contract",
  "check.add.range": "Work days",
  "check.add.menu": "Filters",
  "check.pick.personHint": "Type a name or code — pick as many as you like",
  "check.pick.round": "Pick a round of this contract",
  "check.pick.contractSearch": "Search a contract by name or code",
  "check.pick.noContract": "No contract with bills left to pay",
  "check.pick.timeHint": "Ticket start time (optional) — a night shift can be 22:00 to 06:00",
  "check.pick.timeFrom": "From",
  "check.pick.timeTo": "Before",
  "check.pick.apply": "Use this range",
  "check.nPeople": "{n} people",
  "check.nBills": "{n} bills",
  "check.tag.flags": "{n} problems",
  "check.tag.changed": "{n} changed",
  "check.tag.progress": "{x}/{y} checked",
  "check.workPeriod": "Work period",
  "check.round.none": "Not in a pay round yet",
  "check.round.draft": "In a draft round",
  "check.round.frozen": "Frozen, awaiting approval",
  "check.round.approved": "Approved, awaiting transfer",
  "check.round.paid": "Paid",
  "check.round.name": "Round “{name}”",
  "check.todo.flagged": "Has a problem, waiting to be closed",
  "check.todo.stale": "Look again: rows changed since the check",
  "check.todo.signals": "{n} suspicious points to look at",
  "check.todo.rows": "{n} more rows to check",
  "check.todo.unchecked": "Not checked yet",
  "check.todo.unacked": "Checked, but {n} points not acknowledged",
  "check.todo.done": "All done",
  "check.sig.unpaid": "Trips with no pay",
  "check.sig.stuck": "Tickets stuck",
  "check.queue": "Check queue",
  "check.queueSearch": "Search the queue — name, code, contract",
  "check.order": "Order: needs a look first, then bigger money",
  "check.pile.problem": "Problems — an open problem note",
  "check.pile.changed": "Changed since checked",
  "check.pile.odd": "Signals to look at",
  "check.pile.todo": "Not checked — bigger money first",
  "check.pile.done": "Checked",
  "check.doneFolded": "Folded",
  "check.shown": "{n} of {m}",
  "check.show": "Show",
  "check.loadMore": "Load {n} more",
  "check.empty": "No bills match the filter",
  "check.workMeta": "{n} bills in this set · total",
  "check.position": "{at} of {of} still to do",
  "check.openPerson": "This person's bills",
  "check.billsOf": "This person's bills in the set",
  "check.footProgress": "This person: {x}/{y} rows checked",
  "check.keys": "J/K next/previous · N next person · Enter checked",
  "check.nextBill": "Their next bill →",
  "check.nextPerson": "Next person →",
  "check.totals": "{people} people · {bills} bills · ฿{amount}",
  "check.allDone": "Everyone in this set is checked",
  "check.reloadFailed": "Saved, but the list could not be reloaded — refresh the page to see the latest",
  "check.totalsBeforeFee": "(bill amounts, before the round's transfer fee — the transfer total is on the round page)",
  "check.done": "{n} people checked ({pct}%)",
  "check.roundTools": "This round's tools:",
  "check.byPerson": "Person by person",
  "check.chip.batch": "Pay round {name}",
  "check.add.batch": "Pay round",
  "payBatches.checkPeople": "Check this round person by person",
  "check.viewing": "Also looking at this person: {names} — avoid checking twice",
  "check.sets": "Check sets",
  "check.sets.saved": "Check set saved",
  "check.sets.forMe": "Given to me",
  "check.sets.for": "Given to {name}",
  "check.sets.by": "Saved by {name}",
  "check.sets.remove": "Remove {name}",
  "check.sets.none": "No check set yet",
  "check.sets.saveTitle": "Save the current filter as a set",
  "check.sets.namePh": "A name, e.g. East routes, pay day 31",
  "check.sets.assignPh": "Give it to (optional)",
  "check.sets.save": "Save set",
  "check.sets.needFilter": "Pick at least one filter first",
  "check.checkers": "Last checked by: {list}",
  "check.checker": "{name} {n} bills ({rows} rows)",
  "search.placeholder": "Search people, tickets, plates, contracts, amounts…",
  "search.title": "Find anything",
  "search.esc": "Esc to close",
  "search.hint": "Type 2 or more — a ticket number, a name or code, a plate, a contract, or an amount",
  "search.none": "Nothing matches",
  "search.keys": "↑↓ pick · Enter open · Tab next group · Esc close · searches the whole organisation",
  "search.tab.all": "All",
  "search.tab.tickets": "Tickets",
  "search.tab.vehicles": "Vehicles",
  "search.tab.people": "People",
  "search.tab.contracts": "Contracts",
  "search.tab.amounts": "Amounts",
  "search.inBill": "Bill {title} {from}–{to}",
  "search.inBox": "Accumulated items (no pay day yet)",
  "search.noBill": "Not in a bill yet",
  "search.checkedBy": "Checked · {name} {day}",
  "search.notChecked": "Not checked",
  "search.noPayDay": "No pay day yet",
  "search.drivers": "Drivers lately: {names}",
  "search.noDrivers": "No driver in the last 45 days",
  "search.checkThem": "Check its drivers",
  "search.personSub": "Open this person's check",
  "billCheck.tickRange": "Range checked ({n} items)",
  "billCheck.rangeDone": "This range is all checked",
  "billCheck.rangeTicked": "The rows in this range are ticked",
  "billCheck.rangeShown": "Only the rows in the filtered range: {n} of this bill's {m} · \"Range checked\" ticks these alone",
  "billCheck.rangeOutside": "{n} outside the range",
  "billCheck.rangeSeeWhole": "See the whole bill",
  "billCheck.rangeBack": "Back to the range",
  "billCheck.rangeWhole": "Showing the whole bill — \"Checked\" closes the whole bill",
  "billCheck.sealAgain": "Checked (again)",
  "billCheck.sealed": "Marked checked",
  "billCheck.unseal": "Uncheck",
  "billCheck.unsealed": "Unchecked",
  "review.signal.unchecked": "{n} people's bills were not checked before freezing",
  "review.signal.stale": "{n} people's bills changed after their check",
  "review.signal.flagged": "{n} people have an open problem note",
  "review.check.unchecked": "Bill not checked",
  "review.check.checked": "Bill checked",
  "review.check.stale": "Changed after check",
  "review.check.flagged": "Problem",
  "billCheck.onlySignalsPeople": "People to look at",
  "billCheck.signalsN": "{n} to look at",
  "billCheck.pointsOpen": "{n} points nobody said yes to",
  "billCheck.pointsAcked": "{n} points · all said yes to",
  "billCheck.ack.title": "Before “Checked”, look at each point the system raised ({n}) and tick it as fine",
  "billCheck.ack.titleAfter": "{name} checked this on {day} but nobody said yes to these {n} points — look at each and tick it",
  "billCheck.ack.notePh": "Why they are fine (optional), e.g. the line supervisor confirmed the trip",
  "billCheck.ack.bad": "Tick each point on its row · one that is not fine: flag that row instead",
  "billCheck.ack.bar": "{n} points the system raised · {x}/{n} ticked",
  "billCheck.ack.barAfter": "{name} checked it on {day} but nobody has answered these {n} points · {x}/{n} ticked",
  "billCheck.ack.next": "Next point",
  "billCheck.ack.rowYes": "Looked — it’s right",
  "billCheck.ack.show": "Show row",
  "billCheck.ack.go": "Checked · {n} points said yes to",
  "billCheck.ack.goAgain": "Say yes and confirm the check",
  "billCheck.ack.needAll": "Tick every point first",
  "billCheck.ack.done": "Said yes to by {who}",
  "billCheck.rate.off": "Not the rate set — {src} {expected}",
  "billCheck.rate.src.standard": "the route’s standard rate",
  "billCheck.rate.src.route_driver": "this person’s rate on this route",
  "billCheck.rate.src.contract_driver": "this person’s rate on this contract",
  "billCheck.rate.src.driver_default": "the driver’s own rate",
  "billCheck.rate.src.manual": "typed on the ticket (against the standard rate)",
  "billCheck.edit.line": "Changed after closing: was {from} → {to} · {who} · {day}",
  "billCheck.edit.noWho": "no record of who",
  "billCheck.clash.line": "Ran at the same time as {trips}",
  "billCheck.sig.edited": "pay changed after closing",
  "billCheck.sig.overlap": "overlaps another trip",
  "billCheck.sig.dup_slot": "same route and time twice",
  "billCheck.sig.dup_item": "same item twice this month",
  "billCheck.sig.on_leave": "trip on a leave day",
  "billCheck.sig.after_leave": "after leaving",
  "billCheck.sig.negative": "bill below zero",
  "billCheck.sig.unpaid": "{n} closed trips with no trip pay",
  "billCheck.sig.time_broken": "ticket times wrong (ended before start, or no start)",
  "billCheck.sig.ran_short": "ran under 5 minutes",
  "billCheck.sig.off_plan": "ran over 3 hours off the plan",
  "billCheck.sig.stops_missed": "under half the stops reached",
  "billCheck.sig.no_pair": "no paired leg that day",
  "billCheck.sig.stuck": "{n} tickets still \"running\" after 24 hours",
  "billCheck.sig.vs_last": "More than 20% off last month",
  "issues.stuck": "Tickets still \"running\" after 24 hours",
  "billCheck.export.file": "bill-check-{contract}-{end}",
  "billCheck.export.people": "People",
  "billCheck.export.points": "Points raised",
  "billCheck.export.person": "Name",
  "billCheck.export.state": "State",
  "billCheck.export.rows": "Rows",
  "billCheck.export.amount": "Before deductions (THB)",
  "billCheck.export.checkedBy": "Checked by",
  "billCheck.export.checkedOn": "Checked on",
  "billCheck.export.signals": "Points",
  "billCheck.export.unacked": "Not acknowledged",
  "billCheck.export.day": "Day",
  "billCheck.export.time": "Time",
  "billCheck.export.route": "Route",
  "billCheck.export.money": "Amount (THB)",
  "billCheck.export.what": "What",
  "billCheck.export.status": "Point state",
  "billCheck.export.note": "Reason / note",
  "billCheck.export.by": "By",
  "billCheck.export.part.past": "Checked past, not acknowledged",
  "billCheck.export.part.open": "Not checked yet",
  "billCheck.export.part.flagged": "Problem, waiting to close",
  "billCheck.export.part.acked": "Acknowledged",
  "roundEdit.open": "Edit in round",
  "roundEdit.tag": "Editing",
  "roundEdit.title": "{n} bill(s) open for editing — the round cannot be approved yet",
  "roundEdit.hint": "Finish the bill's rows, then press \"Done\": the bill goes back into this round and is paid with it",
  "roundEdit.by": "opened by {who}",
  "roundEdit.go": "Edit the bill",
  "roundEdit.done": "Done",
  "roundEdit.doneSame": "The bill is back in the round, total unchanged",
  "roundEdit.doneChanged": "The bill is back in the round · this person {before} → {after}",
  "roundEdit.doneReset": "The bill is back in the round · this person {before} → {after} · approval starts again at step 1",
  "roundEdit.dialogTitle": "Edit this bill in the round",
  "roundEdit.dialogBody1": "This bill opens for editing; the other bills of the round stay locked",
  "roundEdit.dialogBody2": "When done, press \"Done\": the bill goes back into this round and is paid with it. A new total restarts the approval at step 1",
  "roundEdit.reason": "Why",
  "roundEdit.reasonPlaceholder": "e.g. one trip pay is wrong",
  "roundEdit.opened": "The bill is open for editing",
  "roundEdit.approveBlocked": "A bill is open for editing — press \"Done\" before approving",
  "roundEdit.billBanner": "This bill is open for editing in {name} — press \"Done\" to put it back into that round",
  "payBatches.event.bill_open": "Bill opened for editing",
  "payBatches.event.bill_done": "Bill edited and put back",
  "roundEdit.seeRound": "See the round",
  "billCheck.by": "Group by",
  "billCheck.by.person": "Person",
  "billCheck.by.route": "Route",
  "billCheck.by.day": "Day",
  "billCheck.by.kind": "Money kind",
  "billCheck.group.noRoute": "No job ticket",
  "billCheck.group.meta": "{n} rows · {people} people · {done} checked",
  "billCheck.group.partly": "{pct}% checked",
  "billCheck.group.pick": "Pick a group to see its rows",
  "billCheck.group.layout": "Layout",
  "billCheck.group.layout.list": "List",
  "billCheck.group.layout.grid": "Day × time",
  "billCheck.group.tick": "Check the whole group ({n} rows)",
  "billCheck.group.tickShown": "Check the {n} rows shown",
  "billCheck.group.confirm": "Mark {n} rows checked · {total}",
  "billCheck.group.skipped": "{n} rows with a problem left out",
  "billCheck.group.pointed": "{n} rows the system points at left out — look at them in each person’s bill",
  "billCheck.group.tickGo": "Confirm",
  "billCheck.group.ticked": "{n} rows checked",
  "billCheck.group.undo": "Undo the last tick",
  "billCheck.group.undone": "Tick undone",
  "billCheck.group.cell": "{day} at {time}",
  "billCheck.group.cellLabel": "{day} at {time}: {n} rows · {state}",
  "billCheck.group.day": "Day",
  "billCheck.group.noTimes": "No trips with a start time in this group",
  "billCheck.group.more": "Showing {shown} of {n} rows — pick a cell in Day × time to narrow it",
  "roundCheck.title": "Bills checked: {done}/{all} people · {pct}% of the money",
  "roundCheck.amountBase": "(money checked / to check — every row in or out counted, not the transfer total)",
  "roundCheck.advice": "The round can be frozen without every check, but the approver sees these figures — tap a name to check it if there is time",
  "roundCheck.issues": "{n} suspicious points in this pay round · {contract} · work to {end}",
  "roundCheck.issuesNone": "No suspicious points in this pay round · {contract} · work to {end}",
  "roundCheck.issuesScope": "Counts only the bills in this pay round, but the page it opens shows the whole contract and work round, including people not in this pay round, so it may show more. No suspicious points does not mean everything is checked.",
  "roundCheck.who": "Who is not checked ({n})",
  "roundCheck.more": "and {n} more — carry on in the bill check",
  "billCheck.note.title": "Handoff note",
  "billCheck.note.placeholder": "e.g. checked up to person 38 · the flagged ones wait for HR",
  "billCheck.note.save": "Save note",
  "billCheck.note.saved": "Note saved",
  "billCheck.note.removed": "Note removed",
  "billCheck.note.by": "{name} · {day}",
  "billCheck.note.edit": "Edit",
  "billCheck.note.add": "Leave a handoff note",
  "fileCheck.open": "Check the file before sending",
  "fileCheck.title": "Check the bank file before sending",
  "fileCheck.nothingRead": "No transfers could be read from this file — it needs an account number and an amount column",
  "fileCheck.failed": "The file could not be checked",
  "fileCheck.ok": "The file matches the round",
  "fileCheck.notOk": "The file does not match the round — do not send it yet",
  "fileCheck.counts": "File: {fn} lines {ft} · round: {rn} people {rt}",
  "fileCheck.missing": "In the round but not in the file: {n} (would not be paid)",
  "fileCheck.diff": "Amount differs: {n} accounts",
  "fileCheck.diffRow": "round {round} · file {file}",
  "fileCheck.extra": "In the file but not in the round: {n} accounts",
  "fileCheck.repeated": "One account on several lines: {n}",
  "fileCheck.lines": "{n} lines",
  "fileCheck.privacy": "The file is read on this device only; just account numbers and amounts are sent to compare, and the check is logged",
  "payBatches.sharedAccount": "{n} accounts take transfers for more than one person in this round — check before the files",
  "payBatches.allContracts": "All contracts",
  "billPanel.trips": "{n} trips",
  "billPanel.rateOk": "✓ every trip at the rate set ({src})",
  "billPanel.rateOkSome": "✓ {n} trips at the rate set",
  "billPanel.rateOff": "⚠ {n} trips paid {paid} but the rate set is {expected}",
  "billPanel.days": "{n} days with trips",
  "billPanel.leave": "{n} days of leave",
  "billPanel.oddDays": "{n} days to look at",
  "billPanel.computed": "Deducted by the system",
  "billPanel.computedHint": "worked out for you, nothing to tick",
  "billPanel.prev": "Last window {n} rows · {total}",
  "billPanel.noPrev": "First round for this person in this contract — nothing before to compare",
  "billPanel.usual": "Usual: {n} trips a day",
  "billPanel.leg.outbound": "in",
  "billPanel.leg.return": "out",
  "billPanel.dayLabel": "{day}: {n} trips",
  "billPanel.dayMissing": "{day}: a working day with no trip",
  "billPanel.legend.checked": "Checked",
  "billPanel.legend.unusual": "To look at (unlike usual, or something to look at)",
  "billPanel.legend.flagged": "Problem",
  "billPanel.legend.missing": "Missing",
  "billPanel.legend.leave": "Leave",
  "billPanel.legend.empty": "Day off / no trip",
  "billPanel.nothingToLook": "Every day is as usual — nothing to look at specially",
  "billPanel.dayChecked": "{day} checked",
  "billPanel.tickDay": "Mark {day} checked",
  "billPanel.dayTrips": "{n} trips",
  "billPanel.dayOther": "{n} other rows",
  "billPanel.onLeave": "on leave",
  "billPanel.notTicked": "ticked {done}/{n}",
  "billPanel.peek.plan": "Planned",
  "billPanel.peek.ran": "Ran",
  "billPanel.peek.stops": "Stops {done}/{n}",
  "billPanel.peek.noStops": "no stop arrivals recorded",
  "billPanel.peek.rate": "Trip pay on the ticket",
  "billPanel.peek.open": "See the ticket",
  "billPanel.view": "View",
  "billPanel.view.days": "By day",
  "billPanel.view.routes": "By route",
  "billPanel.view.all": "Every row",
  "billPanel.onlyLook": "Days to look at {n}",
  "billPanel.keys": "J/K next day · Space tick day · F flag · Enter checked · N next person",
  "billPanel.keysShort": "Enter checked · N next person",
  "billPanel.keysLabel": "Keys",
  "billPanel.more": "Details",
  "billPanel.fold": "Less",
  "billPanel.otherRows": "{n} other rows",
  "billPanel.missingShort": "{n} working days missed",
  "billPanel.rateOffShort": "⚠ {n} trips off the rate",
  "billPanel.rateOkShort": "✓ every trip at its rate",
  "billPanel.prevShort": "last round {total}",
  "billPanel.calendar": "The bill’s days",
  "billPanel.calendarOpen": "Calendar",
  "billPanel.calendarFold": "Fold the calendar",
  "billPanel.missing": "{n} working days missing: {days}",
  "billPanel.noMissing": "No working day missing",
  "billPanel.offWeekdays": "Usual days off: {days}",
  "compare.pick": "Compare with someone in this round…",
  "compare.pickShort": "Compare with…",
  "compare.same": "The same money every day",
  "compare.less": "{me} got {amount} less than {peer}",
  "compare.more": "{me} got {amount} more than {peer}",
  "compare.onlyPeer": "{n} days the other ran and this person did not ({days}): {amount}",
  "compare.onlyMine": "{n} days this person ran and the other did not ({days}): {amount}",
  "compare.both": "{n} days both ran for different money ({days}): {amount}",
  "compare.day": "Day",
  "compare.me": "This person",
  "compare.peer": "The other",
  "compare.diff": "Difference",
  "compare.trips": "{n} trips",
  "bulk.open": "Check everyone with nothing to look at ({n})",
  "bulk.title": "These {n} have no point raised — look at the money once more, then check them in one go",
  "bulk.unusual": "not like most",
  "bulk.total": "Total {amount}",
  "bulk.go": "Confirm: {n} checked",
  "bulk.done": "{n} checked",
  "bulk.skipped": "{done} checked · {n} bills left out (changed, or a new point) — open them one by one",
  "billPanel.ahead": "{n} days still to come",
  "billPanel.legend.ahead": "Still to come",
  "glance.title": "This round at a glance",
  "glance.toCheck": "Compare with others and check it, on the bill check",
  "glance.worked": "Trips on {n} days",
  "glance.leave": "Leave: {days}",
  "glance.noLeave": "No leave in this round",
  "glance.noTrip": "No ticket — a working day missing",
  "readiness.title": "Bills to check before paying",
  "readiness.lead": "Rounds that have ended and are not paid — how far their bills are checked, and what is left to clear before approving",
  "readiness.window": "Round {from}–{to} · pay {pay} · {n} people",
  "readiness.windowNoStart": "Round to {to} · pay {pay} · {n} people",
  "readiness.noWindow": "No cutoff · {n} people",
  "readiness.checked": "checked {done}/{all}",
  "readiness.ready": "Ready to approve",
  "readiness.open": "{n} points open · {amount}",
  "readiness.flagged": "{n} with a problem",
  "readiness.left": "{n} not checked",
  "issues.open.btn": "Suspicious points",
  "issues.btn.open": "{n} suspicious points",
  "issues.btn.answered": "Suspicious points · {n} answered",
  "issues.btn.none": "No suspicious points",
  "issues.btn.noneHint": "The system sees nothing odd in this round. That does not mean everything is checked — see the progress bar above.",
  "issues.what": "A suspicious point is something the system finds odd, such as a trip with no trip pay or a time that does not add up. Someone has to look at it, then say yes to it or raise a problem if it is wrong.",
  "issues.noneLeft": "No suspicious points left — the {n} the system raised have all been answered (below)",
  "issues.checkLeft": "But {left} of {all} people are not checked yet",
  "issues.checkDone": "And everyone is checked ({all} people)",
  "issues.goCheck": "Check the people left",
  "issues.open": "Points still open",
  "issues.flagged": "Problems waiting to be closed",
  "issues.acked": "Said yes to",
  "issues.points": "{n} points",
  "issues.pastCheck": "{n} of them on bills already checked",
  "issues.none": "No suspicious points in this round",
  "issues.unpaid": "Closed trips with no trip pay",
  "issues.part.past": "Checked past, nobody said yes ({n})",
  "issues.part.open": "Not checked yet ({n})",
  "issues.part.flagged": "Problem, waiting to be closed ({n})",
  "issues.part.acked": "Said yes to ({n})",
  // ADR 0489 — แท็บการจัดการเงินของพนักงาน + หน้าต่างเรท/เงินเดือน/เบี้ยเลี้ยง (4-Archives/plan/tms/0489-hr-money-tab-thai-text.md)
  "hrMoney.monthlyHint": "Monthly salary, OT, tax & pay schedule plus recurring allowances/deductions — changes carry an effective window (date & time) with history",
  "hrMoney.perMonth": "/mo",
  "hrMoney.perHour": "/hr",
  "hrMoney.componentsLead": "Added or taken every month — each with its own effective dates",
  "hrMoney.netLine": "Net each month now {sign}฿{amount}",
  "hrMoney.historyShow": "Show salary history ({n})",
  "hrMoney.historyHide": "Hide salary history",
  "hrMoney.tripsLead": "The system checks each step in order and uses the first one set:",
  "hrMoney.tripsStandardWhere": "The last step is set on the route page (internal / external driver pay) and applies to everyone on that route",
  "hrMoney.componentsTitle": "Allowances & Deductions",
  "hrMoney.componentsHint": "Recurring monthly components applied on top of base salary — each list has its own editor",
  "hrMoney.noAllowances": "No allowances",
  "hrMoney.noDeductions": "No deductions",
  "hrMoney.effectiveAlways": "always",
  "hrMoney.listEffective": "Effective {from} → {to}",
  "hrMoney.resolutionOrder": "· resolution order (first match wins):",
  "hrMoney.noDefaultRateYet": "No personal default rate yet — the route's standard pay is used",
  "hrMoney.contractRateHint": "This driver's per-trip rate on a specific contract — beats driver default; a route rate still wins",
  "hrMoney.noContractRatesYet": "No personal contract rate yet — the next step is used",
  "hrMoney.routeRateHint": "This driver's per-trip rate on one specific route — the most specific recurring level; beats contract and default",
  "hrMoney.route": "Route",
  "hrMoney.contract": "Contract",
  "hrMoney.effectiveWindow": "Effective",
  "hrMoney.voidRateAria": "Void rate ฿{amount}",
  "hrMoney.voidRateConfirm": "Void the ฿{amount} rate ({from} → {to})? The row stays in history, and affected tickets in open periods will be re-priced.",
  "hrMoney.voidRateConfirmLabel": "Void",
  "hrMoney.voidRateFailed": "Void failed",
  "hrMoney.rate.titleDefault": "Set personal default rate",
  "hrMoney.rate.titleRoute": "Set personal route rate",
  "hrMoney.rate.titleContract": "Set personal contract rate",
  "hrMoney.rate.driverPrefix": "Driver: {name} · ",
  "hrMoney.rate.ledgerHint": "A new version auto-closes the previous one (ledger — history is kept)",
  "hrMoney.rate.appliesTo": "Applies to",
  "hrMoney.rate.perTrip": "Rate per trip (฿)",
  "hrMoney.rate.amountPh": "e.g. 350",
  "hrMoney.rate.backdated": "⚠ Backdated — tickets already run in this window (open periods only) will be re-priced automatically, with an audit log",
  "hrMoney.rate.reasonPh": "e.g. New contract agreement",
  "hrMoney.rate.save": "Save New Rate",
  "hrMoney.window.from": "Effective From (date & time)",
  "hrMoney.window.to": "Effective To (date & time)",
  "hrMoney.window.blankOpenEnded": "— blank = open-ended",
  "hrMoney.window.backdated": "Back-dated — applies retroactively from this moment.",
  "hrMoney.window.scheduled": "Scheduled — takes effect at this future moment.",
  "hrMoney.salary.baseLabel": "Base Salary (฿/month)",
  "hrMoney.salary.diffFromCurrent": "{diff} ฿ from current ({current} ฿)",
  "hrMoney.salary.otHourly": "OT Hourly Rate (฿)",
  "hrMoney.salary.save": "Save Base Compensation",
  "hrMoney.comp.manageAllowances": "Manage allowances",
  "hrMoney.comp.manageDeductions": "Manage deductions",
  "hrMoney.comp.windowHint": "Each item has its own effective window",
  "hrMoney.comp.namePhAllowance": "e.g. Fuel allowance",
  "hrMoney.comp.namePhDeduction": "e.g. Uniform",
  "hrMoney.comp.amountPh": "฿ / month",
  "hrMoney.comp.removeRow": "Remove {name}",
  "hrMoney.comp.rowN": "row {n}",
  "hrMoney.comp.noEnd": "No end = open-ended",
  "hrMoney.comp.addAllowance": "Add allowance",
  "hrMoney.comp.addDeduction": "Add deduction",
  "hrMoney.comp.totalAllowances": "Total allowances ({n} items, all windows)",
  "hrMoney.comp.totalDeductions": "Total deductions ({n} items, all windows)",
  "hrMoney.comp.reasonPhAllowance": "e.g. Added fuel allowance per new route assignment",
  "hrMoney.comp.reasonPhDeduction": "e.g. Uniform deduction per company policy",
  "hrMoney.comp.saveAllowances": "Save Allowances",
  "hrMoney.comp.saveDeductions": "Save Deductions",
  // IN-PLACE T5
  "payBatchList.search": "Search a round name, or a person the round pays",
  "payBatchList.filterStatus": "Round status",
  "payBatchList.range": "Payout day",
  "payBatchList.from": "Payout from",
  "payBatchList.to": "Payout to",
  "payBatchList.fromChip": "From {d}",
  "payBatchList.toChip": "To {d}",
  "payBatchList.noMatch": "No pay round matches",
  "billsList.unassigned": "{n} money entries name no employee, so they are in no one's bill —",
  "billsList.unassignedLink": "check them on the Transactions page",
  "billsList.sort.nameAsc": "Name A → Z",
  "billsList.sort.nameDesc": "Name Z → A",
  "billsList.sort.dueDesc": "Due, highest first",
  "billsList.sort.dueAsc": "Due, lowest first",
  "billsList.sort.openDesc": "Unpaid total, highest first",
  "billsList.sort.openAsc": "Unpaid total, lowest first",
  // IN-PLACE T4
  "financeTxn.searchPh": "FTX-… / JOB-… or a name",
  "financeTxn.perPage": "Rows per page",
  "financeTxn.export": "Export Excel",
  "financeTxn.exportDone": "Exported {n} rows",
  "financeTxn.exportCapped": "Exported the first {max} of {total} rows — one file holds at most {max}. Narrow the dates or filters for the rest",
  "financeTxn.exportEmpty": "Nothing to export",
  "financeTxn.exportFailed": "Export failed",
  "financeTxn.exportHint": "Every page of this filter and order, up to {max} rows",
  "financeTxn.col.status": "Status",
  "financeTxn.status.normal": "Normal",
  "financeTxn.historyOpen": "History",
  "financeTxn.historyAria": "History of {doc}",
  "financeTxn.historyNone": "This row has not changed since it came in",
  "financeTxn.historyFailed": "Could not load the history",
  "financeTxn.historyNote": "Reason: {note}",
  "financeTxn.revField.status": "Status",
  "financeTxn.revField.voided_at": "Voided on",
  "financeTxn.revField.deferred_from": "Moved to the bill holding",
  "financeTxn.rev.sync": "Re-read from its source",
  "financeTxn.rev.manual_create": "Recorded",
  "financeTxn.rev.manual_edit": "Edited",
  "financeTxn.rev.manual_clear_override": "Edit cleared",
  "financeTxn.rev.manual_relink": "Linked document changed",
  "financeTxn.rev.manual_void": "Voided",
  "financeTxn.rev.manual_reverse": "Reversed",
  "financeTxn.rev.source_disappeared": "Its source document is gone",
  "financeTxn.rev.period_locked": "Month closed",
  "financeTxn.rev.rebuild": "Rebuilt by the system",
  "financeTxn.rev.bill_move": "Moved into a bill",
  "financeTxn.rev.bill_return": "Returned from a bill",
  "financeTxn.rev.bill_defer": "Moved to a later bill",
  "financeTxn.rev.manual_status": "Status changed",
  // IN-PLACE T1
  "billPanel.openBill": "Open the bill page",
  "billPanel.readOnly": "This bill is paid: read only",
  // IN-PLACE T3
  "roundBill.switch": "This person's bills in the round: {n}",
  "review.details": "Round details",
  // IN-PLACE T6
  "flagThread.replyPh": "Reply…",
  "flagThread.send": "Send",
  "flagThread.closePh": "Why it is closed",
  "flagThread.needReason": "Say why before closing",
  "flagThread.closedBy": "Closed by {who} · {when}",
  "flagThread.noReason": "No reason given",
  "issues.part.closed": "Closed ({n})",
  "billCheck.export.part.closed": "Closed",
  // IN-PLACE T7
  "history.btn": "Check history",
  "history.everyone": "Everyone",
  "history.kinds": "Kind of event",
  "history.kind.all": "All",
  "history.kind.check": "Checks",
  "history.kind.flag": "Problems",
  "history.kind.changed": "Needs another look",
  "history.ev.check": "checked",
  "history.ev.uncheck": "unchecked",
  "history.ev.group_tick": "checked as a group",
  "history.ev.group_untick": "group check undone",
  "history.ev.flag": "flagged a problem on",
  "history.ev.flag_reply": "replied on",
  "history.ev.flag_close": "closed the problem on",
  "history.ev.changed": "needs another look",
  "history.acked": "{n} points acknowledged",
  "history.rows": "{n} rows",
  "history.system": "System",
  "history.none": "Nobody has checked this round yet",
  "history.noneShown": "Nothing matches the filter",
  "history.shown": "Showing {n} of {all}",
  "history.more": "Show older",
  // IN-PLACE T8
  "sample.title": "Spot check {x}/{y} (optional)",
  "sample.lead": "Ordinary rows nothing points at, picked at random to look at — approving does not wait for them",
  "sample.by": "Picked by {name} · {time}",
  "sample.drawing": "Picking rows…",
  "sample.drawFailed": "Could not pick the rows",
  "sample.none": "No ordinary rows to pick — everyone is already in what to look at first",
  "sample.ok": "Right",
  "sample.bad": "Wrong",
  "sample.changed": "Changed after picking",
  "sample.flagged": "Marked as a problem",
  "sample.openBill": "Open {name}'s bill",
  "sample.badTitle": "What is wrong",
  "sample.badWhat": "The row gets a problem note with this text for the bill checkers to fix",
  "sample.badPh": "e.g. the amount does not match the ticket",
  "sample.badSave": "Save as wrong",
  "sample.failed": "Could not save the spot check",
  "sample.bar": "Spot check {x}/{y}",
  "sample.open": "Open the sampled rows",
  "sample.fold": "Fold",
  "history.kind.sample": "Spot check",
  "history.ev.sample": "Spot check",
  "history.ev.spotOk": "Spot check: right",
  "history.ev.spotBad": "Spot check: wrong",
  // ADR 0492 — the Makro Locus card on the employee page · list filter · ADR 0493 vehicle history source (import)
  "hr.locus.filter": "Makro Locus",
  "hr.locus.filter.all": "All",
  "hr.locus.filter.on": "Pulling",
  "hr.locus.filter.off": "Linked, not pulling",
  "hr.locus.filter.none": "Not linked",
  "vb.import": "Imported from the driver file",
  // ADR 0490 — the /locus-sync page (keys built from lists: lib/locus-sync-page.ts LOCUS_PAGE_LIST_KEYS)
  "locus.title": "Pull work from Makro Locus",
  "locus.syncNow": "Pull now",
  "locus.syncing": "Pulling… {t}",
  "locus.readOnly": "Read only · only this organization's org_admin can pull",
  "locus.why.running": "You can close this page — the run goes on to the end",
  "locus.why.cooldown": "You can press again at {time}",
  "locus.why.login": "Log in to Locus again first",
  "locus.why.blocked": "Locus login paused until {time}",
  "locus.why.noRiders": "No employee has a Locus code yet",
  "locus.why.notConnected": "This organization is not connected to Locus",
  "locus.badge.running": "Pulling",
  "locus.badge.ok": "Connected",
  "locus.badge.error": "Last run failed",
  "locus.badge.login": "Locus login needed",
  "locus.badge.blocked": "Login paused",
  "locus.badge.notConnected": "Not connected",
  "locus.badge.off": "Pull off",
  "locus.badge.noContract": "No contract",
  "locus.bar.login": "The Locus login has expired — tickets will not update until someone logs in again (the dev team does this for now)",
  "locus.bar.blocked": "Locus login paused until {time} after repeated failures",
  "locus.bar.running": "Pulling — started by {name} at {time} · the result shows in the history when it ends",
  "locus.bar.noRiders": "No employee has a Locus code — nothing is pulled from Locus",
  "locus.bar.toHr": "Go to employees →",
  "locus.toast.done": "Pull finished · tickets new {c} · changed {u} · same {s}",
  "locus.toast.failed": "The pull failed — {reason}",
  "locus.toast.runNotFound": "Run not found",
  "locus.timeline.title": "Today's pulls · {date}",
  "locus.timeline.next": "next {time}",
  "locus.timeline.now": "now {time}",
  "locus.timeline.empty": "Nothing pulled today yet",
  "locus.timeline.planned": "scheduled",
  "locus.timeline.running": "pulling",
  "locus.timeline.manual": "A square on top means it was pulled by hand",
  // ADR 0516 D5 — the minute labels of the timeline grid (the slow mark and half of it)
  "locus.timeline.gridMin": "{n} min",
  "locus.timeline.aria": "Today's pull runs",
  "locus.timeline.openHint": "click for details",
  "locus.track.aria": "Pull tracking",
  "locus.track.status": "Status",
  "locus.track.nextRun": "Next run {time}",
  "locus.track.manualOnly": "By hand only",
  "locus.track.runningHead": "Started {time} · {t} so far",
  "locus.track.runningSub": "This run can take up to {n} min",
  "locus.track.stale": "No progress for {n} min",
  "locus.track.failedHead": "Last run failed {time}",
  "locus.track.streak": "{n} failed in a row",
  "locus.track.lastOk": "Last good pull",
  "locus.track.never": "never",
  "locus.track.login": "Locus login",
  "locus.track.loginHours": "{n} h left",
  "locus.track.loginMinutes": "{n} min left",
  "locus.track.loginExpiring": "about to expire",
  "locus.track.loginGone": "expired",
  "locus.track.loginNone": "never logged in",
  "locus.track.loginBlocked": "paused until {time}",
  "locus.track.loginsToday": "Logins today",
  "locus.track.loginsFails": "failed {n}",
  "locus.track.today": "Today's work · {date}",
  "locus.track.trips": "Trips in Locus",
  "locus.track.tickets": "Tickets in our system",
  "locus.track.tripsDone": "Finished",
  "locus.track.ordersDone": "Drops delivered (orders)",
  "locus.track.carriedOver": "Yesterday's tickets not finished",
  "locus.track.todayNone": "No numbers for today yet — no good pull so far",
  "locus.track.riders": "Drivers",
  "locus.track.pulled": "Being pulled",
  "locus.track.withWork": "With work today",
  "locus.track.noTrips": "No trip in Locus",
  "locus.track.noVehicle": "No bound vehicle",
  "locus.ago.min": "{n} min ago",
  "locus.ago.hour": "{n} h ago",
  "locus.ago.day": "{n} days ago",
  "locus.step.queued": "Waiting to start…",
  "locus.step.login": "Locus login",
  "locus.step.tours": "Find the drivers' trips",
  "locus.step.orders": "Read the orders of changed trips",
  "locus.step.write": "Write tickets + stops",
  "locus.stepSub.login": "{riders} drivers",
  "locus.stepSub.tours": "{trips} trips · {requests} requests",
  "locus.stepSub.orders": "{orders} of {planned} stores read",
  "locus.stepSub.ordersNoPlan": "{orders} stores read",
  "locus.stepSub.write": "{tickets} trips",
  "locus.col.at": "Time",
  "locus.col.by": "By",
  "locus.col.status": "Status",
  "locus.col.ms": "Took (s)",
  "locus.col.trips": "Trips",
  "locus.col.orders": "Orders read",
  "locus.col.tickets": "Tickets",
  "locus.col.ticketsNew": "New",
  "locus.col.ticketsUpdated": "Changed",
  "locus.col.ticketsSame": "Same",
  "locus.seconds": "{n} s",
  "locus.filter.status": "Status",
  "locus.empty.noMatch": "No run matches the filters",
  "locus.empty.noneYet": "No pull runs yet",
  "locus.dialog.title": "Run details",
  "locus.dialog.errors": "Error",
  "locus.dialog.what": "What happened",
  "locus.dialog.request": "Failed request",
  "locus.dialog.requestVal": "{endpoint} · HTTP {status} · {n} tries",
  "locus.dialog.requestNet": "{endpoint} · no connection · {n} tries",
  "locus.dialog.code": "Error code",
  "locus.dialog.raw": "System message",
  "locus.dialog.todo": "What to do",
  "locus.dialog.result": "Result",
  "locus.dialog.created": "New tickets",
  "locus.dialog.updated": "Changed tickets",
  "locus.dialog.unchanged": "Same tickets",
  "locus.dialog.trips": "Trips from Locus",
  "locus.dialog.skippedCompleted": "Finished trips (not pulled)",
  "locus.dialog.orders": "Orders read",
  "locus.dialog.requests": "Requests to Locus",
  "locus.dialog.noTrips": "Codes with no trip in Locus",
  "locus.dialog.noVehicle": "Drivers without a bound vehicle",
  "locus.dialog.nobody": "no employee holds this code now",
  "locus.group.ok": "OK",
  "locus.group.error": "Failed",
  "locus.group.skipped": "Skipped",
  "locus.group.login": "Locus login", // ADR 0499 D7
  "locus.outcome.ok": "OK",
  "locus.outcome.skipped_locked": "Skipped",
  "locus.outcome.not_configured": "Not connected",
  "locus.outcome.no_riders": "Skipped — no linked employee",
  "locus.outcome.login_needed": "Locus login needed",
  "locus.outcome.login_blocked": "Login paused",
  "locus.outcome.locus_error": "Locus error",
  "locus.outcome.budget_exceeded": "Request limit hit",
  "locus.outcome.error": "Our error",
  "locus.trigger.manual": "System",
  "locus.trigger.loop": "System",
  "locus.trigger.schedule": "Automatic",
  "locus.trigger.web": "Pressed here",
  "locus.error.no_riders": "No employee has a Locus code with pulling on",
  "locus.error.skipped_locked": "Another run was going on",
  "locus.error.not_configured": "This organization is not connected to Locus",
  "locus.error.login_needed": "The Locus login expired and the server cannot log in by itself yet",
  "locus.error.login_blocked": "Login paused after repeated failures",
  "locus.error.http_401": "Locus no longer accepts this login (HTTP 401)",
  "locus.error.http_4xx": "Locus refused the request (HTTP {http})",
  "locus.error.http_5xx": "Locus did not answer or failed (HTTP {http}), retried",
  "locus.error.network": "Could not reach Locus, retried",
  "locus.error.budget_exceeded": "Too many requests to Locus in one run, stopped before writing",
  "locus.error.error": "Something failed on our side",
  "locus.error.unknown": "An error this page does not know",
  "locus.errorTodo.no_riders": "Link Locus codes on the employee page",
  "locus.errorTodo.skipped_locked": "Nothing — the result is in the run that was going on",
  "locus.errorTodo.not_configured": "Ask the dev team to connect Locus",
  "locus.errorTodo.login_needed": "Ask the dev team to log in to Locus again",
  "locus.errorTodo.login_blocked": "Wait until it may try again, then ask the dev team to check the account",
  "locus.errorTodo.http_401": "Ask the dev team to log in to Locus again",
  "locus.errorTodo.http_4xx": "Tell the dev team — Locus may have changed what it accepts",
  "locus.errorTodo.http_5xx": "Wait for the next run or press \"Pull now\" again · tell the dev team if it fails for over an hour",
  "locus.errorTodo.network": "Wait for the next run or press \"Pull now\" again",
  "locus.errorTodo.budget_exceeded": "Tell the dev team — today has more trips than planned for",
  "locus.errorTodo.error": "Tell the dev team with this run's time",
  "locus.errorTodo.unknown": "Tell the dev team with the error code",
  // ADR 0491 — the Locus account + the server login
  "locus.outcome.login_started": "Locus login started",
  "locus.outcome.login_failed": "Login failed",
  "locus.error.login_rejected": "Locus refused the saved User ID or password",
  "locus.error.login_shape_changed": "The Locus login page changed (e.g. a password change or two-step check is required)",
  "locus.error.login_timeout": "The Locus login did not finish in time",
  "locus.errorTodo.login_rejected": "Open \"Locus settings\" and enter the password again",
  "locus.errorTodo.login_shape_changed": "Log in on the Locus site yourself first, then enter the new password in \"Locus settings\"",
  "locus.errorTodo.login_timeout": "Press \"Pull now\" again · tell the dev team if it repeats",
  "locus.badge.loggingIn": "Logging in to Locus",
  "locus.bar.loggingIn": "Logging in to Locus — started by {name} at {time} · the pull follows by itself",
  "locus.bar.noAccount": "The Locus login has expired and no Locus account is saved — tickets will not update",
  "locus.bar.settingsLink": "Locus settings →",
  "locus.bar.askOrgAdmin": "Ask this organization's org_admin to enter the Locus account",
  "locus.why.loginCap": "Locus logins used up for today (6)",
  "locus.loggingIn": "Logging in to Locus… {t}",
  "locus.toast.loginStarted": "Logging in to Locus · the pull follows by itself",
  "locus.track.loginAuto": "expired · logs in by itself",
  "locus.creds.settings": "Locus settings",
  "locus.creds.userId": "User Id",
  "locus.creds.userIdPh": "The User ID you log in to Locus with",
  "locus.creds.password": "Password",
  "locus.creds.testSave": "Test and save",
  "locus.creds.testing": "Testing…",
  "locus.creds.testingBox": "Testing the Locus login · can take up to 3 minutes · you can close this panel",
  "locus.creds.none": "No Locus account yet",
  "locus.creds.noneRead": "No Locus account yet · this organization's org_admin enters it",
  "locus.creds.setBy": "Set by",
  "locus.creds.lastTest": "Last test",
  "locus.creds.triesToday": "Logins today",
  "locus.creds.triesLeft": "{n} left (max {max})",
  "locus.creds.change": "Change account",
  "locus.creds.delete": "Remove account",
  "locus.creds.deleteTitle": "Remove the Locus account?",
  "locus.creds.deleteMsg": "The account and its login are removed at once; pulls stop until a new account is entered",
  "locus.creds.deleted": "Locus account removed",
  "locus.creds.failed": "Test failed — {reason} · the account in use (if any) stays · {n} tries left today",
  "locus.creds.notReady": "The server cannot store an account yet — tell the dev team",
  "locus.creds.lockedBlocked": "Logins paused until {time}",
  "locus.creds.lockedCap": "All 6 tries used today · try again tomorrow",
  "locus.creds.passed": "Test passed · account saved",
  "locus.creds.sent": "Account test started",
  "locus.creds.errUserId": "Enter the User ID (up to 200 characters)",
  "locus.creds.errPassword": "Enter the password (up to 200 characters)",
  "locus.creds.state.testing": "Testing",
  "locus.creds.state.ok": "Passed",
  "locus.creds.state.failed": "Failed",
  "locus.creds.account": "Locus account",
  "locus.creds.badge.saved": "Saved",
  "locus.creds.badge.testing": "Testing",
  "locus.creds.badge.failed": "Test failed",
  "locus.creds.badge.locked": "Paused",
  "locus.creds.badge.none": "No account",
  "locus.creds.badge.not_ready": "Not ready",
  "locus.settings.details": "Details",
  "locus.settings.every": "Automatic runs",
  "locus.settings.everyValue": "Every {n} min",
  "locus.settings.scheduleOff": "Off",
  "locus.settings.next": "Next run",
  "locus.settings.changes": "List changes",
  "locus.settings.changesValue": "Apply from the next run",
  "locus.riders.title": "Drivers pulled from Locus",
  "locus.riders.count": "· pulling {on} of {of}",
  "locus.riders.countLoading": "· pulling – of –",
  "locus.riders.search": "Search name · EMP · ADT code",
  "locus.riders.filter": "Driver filter",
  "locus.riders.group.all": "All",
  "locus.riders.group.on": "Pulling",
  "locus.riders.group.off": "Not pulling",
  "locus.riders.group.none": "No code",
  "locus.riders.group.stopped": "Stopped",
  "locus.riders.col.name": "Name",
  "locus.riders.col.code": "Locus rider code",
  "locus.riders.col.status": "Status",
  "locus.riders.col.setBy": "Set by",
  "locus.riders.headCheck": "Select everyone shown",
  "locus.riders.rowCheck": "Pull work for {name}",
  "locus.riders.edit": "Edit or remove the code",
  "locus.riders.codePh": "Code from Locus",
  "locus.riders.setByImport": "Driver file import",
  "locus.riders.unsaved": "Not saved",
  "locus.riders.otherEmployee": "another employee",
  "locus.riders.stop.resigned": "Stopped · resigned",
  "locus.riders.stop.inactive": "Stopped · deactivated",
  "locus.riders.stop.not_driver": "Stopped · not a driver",
  "locus.riders.err.missing": "Enter the Locus rider code before saving",
  "locus.riders.err.LOCUS_RIDER_BAD_CODE": "Not a rider code · e.g. ADT001534",
  "locus.riders.err.LOCUS_RIDER_TAKEN": "This code is linked to {emp}",
  "locus.riders.err.LOCUS_RIDER_NOT_DRIVER": "Only a driver can carry a Locus code",
  "locus.riders.err.LOCUS_RIDER_DUPLICATE": "Same code as {emp}",
  "locus.riders.err.LOCUS_RIDER_NOT_FOUND": "This employee is gone · close and reopen the panel",
  "locus.riders.err.ORG_ADMIN_ONLY": "Only this organization's org_admin can change the list",
  "locus.riders.readOnly": "Only this organization's org_admin can change the list",
  "locus.riders.loadError": "Could not load the drivers · try again",
  "locus.riders.retry": "Try again",
  "locus.riders.empty": "No drivers yet",
  "locus.riders.emptyLink": "Add them on the employees page",
  "locus.riders.noMatch": "No driver matches",
  "locus.riders.summary": "{n} changed:",
  "locus.riders.sumAdded": "{n} added",
  "locus.riders.sumStopped": "{n} stopped",
  "locus.riders.sumCode": "{n} codes changed",
  "locus.riders.discardChanges": "Discard changes",
  "locus.riders.save": "Save {n}",
  "locus.riders.saving": "Saving…",
  "locus.riders.savingN": "Saving {n}…",
  "locus.riders.saved": "Saved · applies from the next run at {time}",
  "locus.riders.savedNoTime": "Saved · applies from the next run",
  "locus.riders.saveFailed": "Not saved · {n} rows to fix · nothing was saved",
  "locus.riders.tooMany": "Up to {max} people per save",
  "locus.riders.discardTitle": "Discard {n} changes?",
  "locus.riders.discardMsg": "The ticks and codes you changed are not saved; closing now drops them all",
  "locus.riders.discardKeep": "Keep editing",
  "locus.riders.discardConfirm": "Discard",
  // refusals of /organizations/locus* (ADR 0490 · 0491)
  "locus.syncErr.LOCUS_SYNC_RUNNING": "Another pull is going on",
  "locus.syncErr.LOCUS_LOGIN_NEEDED": "Log in to Locus again first",
  "locus.syncErr.LOCUS_SYNC_COOLDOWN": "Wait a little before pressing again",
  "locus.syncErr.LOCUS_RUNS_BAD_RESULT": "That status filter is not valid",
  "locus.syncErr.LOCUS_RUNS_BAD_DATE": "Those dates are not valid",
  "locus.syncErr.ORG_ADMIN_ONLY": "Only this organization's org_admin can do this",
  "locus.syncErr.LOCUS_LOGIN_BLOCKED": "Locus logins are paused",
  "locus.syncErr.LOCUS_LOGIN_DAILY_CAP": "Locus logins used up for today",
  "locus.syncErr.LOCUS_LOGIN_RUNNING": "A Locus login is going on",
  "locus.syncErr.LOCUS_LOGIN_RUNNER_FAILED": "The Locus login could not start — tell the dev team",
  "locus.syncErr.LOCUS_CREDS_BAD_INPUT": "The account details are incomplete",
  "locus.syncErr.LOCUS_CREDS_NOT_READY": "The server cannot store an account yet — tell the dev team",
  "locus.syncErr.LOCUS_SYNC_DISPATCH_FAILED": "The pull could not be started (the scheduler could not be told) — tell the dev team; the next automatic run still goes",
  "locus.syncErr.LOCUS_NO_CONTRACT": "No customer and active contract chosen yet. Choose them in \"Locus settings\"",
  "locus.syncErr.LOCUS_NOT_READY": "Test the Locus account and choose the customer and contract before turning the pull on",
  "locus.syncErr.LOCUS_SETUP_INVALID": "This contract cannot be used. Choose an active contract of the chosen customer",
  // ADR 0494 — what the pull did to ticket statuses
  "locus.dialog.cancelled": "Cancelled (Locus)",
  "locus.dialog.restored": "Back to waiting",
  "locus.dialog.cancelSkippedStarted": "Cancelled in Locus, driver already started",
  "locus.dialog.skippedCancelled": "Cancelled trips (not pulled)",
  "locus.dialog.ordersShort": "Trips with orders missing",
  // ADR 0499 — /locus-sync small fixes
  "locus.track.running": "On the road",
  "locus.track.notStarted": "Not started",
  "locus.track.ticketsExtra": "{n} more than the trips in Locus — trips Locus removed; cancelled by itself after 24 h.",
  "locus.newTab": "(opens in a new tab)",
  "locus.timeline.sumPulls": "Today {n} pulls · ok {ok} · failed {err}",
  "locus.timeline.sumSkipped": "skipped {n} (another run was pulling)",
  "locus.timeline.sumManual": "by hand {n}",
  "locus.timeline.sumLogins": "logged in to Locus {n}×",
  "locus.timeline.tipTickets": "new {created} · changed {updated}",
  "locus.timeline.legendGrey": "Skipped / login",
  "locus.dialog.titleRun": "Run {time}",
  "locus.dialog.took": "took {n} s",
  "locus.dialog.listCreated": "New tickets",
  "locus.dialog.listChanged": "Changed tickets",
  "locus.dialog.listTruncated": "Not all listed — over 300 tickets changed in this run; see the ticket list",
  "locus.dialog.tech": "Fetch numbers (technical)",
  "locus.kpi.created": "New tickets",
  "locus.kpi.started": "Started",
  "locus.kpi.completed": "Completed",
  "locus.kpi.cancelled": "Cancelled",
  "locus.kpi.other": "Updated",
  "locus.dialog.updatedSum": "{n} tickets changed = started + completed + cancelled + updated (the table's \"changed\" column)",
  "locus.dialog.loginNote": "This row is a new Locus login (the token had run out); nothing was pulled yet — the pull follows as its own row.",
  "locus.change.created": "New",
  "locus.change.started": "Started",
  "locus.change.completed": "Completed",
  "locus.change.cancelled": "Cancelled",
  "locus.change.restored": "Back to waiting",
  "locus.change.closed_superseded": "Closed (newer trip)",
  "locus.change.cancelled_missing": "Cancelled (gone from Locus)",
  "locus.change.updated": "Changed",
  "locus.change.part.stops": "stops changed",
  "locus.change.part.schedule": "times changed",
  "locus.change.part.driver": "driver changed",
  "locus.change.part.vehicle": "vehicle changed",
  "locus.change.part.progress": "progress from Locus",
  "locus.bar.passwordWarn": "The Locus password expires {when} (in {n} days)",
  "locus.bar.passwordUrgent": "The Locus password expires {when} (in {h} h)",
  "locus.bar.passwordExpired": "The Locus password expired ({when}) — pulls stop until it is changed",
  "locus.bar.passwordChange": "Open Locus to change it",
  "locus.bar.passwordThenPre": "then press", // ADR 0500 D4
  "locus.bar.passwordThenPost": "and choose \"Change account\" to enter the new password",
  "locus.bar.noContract": "Pulling from Locus has stopped because contract {contract} is no longer active",
  "locus.bar.noContractNone": "Pulling from Locus has stopped because no contract is chosen",
  "locus.bar.chooseContract": "Choose a new contract",
  "locus.bar.off": "Automatic pulling is off. The tickets pulled before are all still here",
  "locus.bar.offNew": "Automatic pulling is not on yet",
  "locus.bar.turnOn": "Turn pulling on",
  "locus.bar.tempVehicles": "{n} temporary vehicles are in use because these drivers have no vehicle of their own:",
  "locus.bar.and": "and",
  "locus.bar.andMore": "and {n} more",
  "locus.bar.tempVehiclesBind": "Press a name to give the driver a vehicle",
  "locus.tempVehicle": "Temporary vehicle (Locus)",
  "locus.tempVehicleHint": "The driver has no vehicle of their own, so the system uses a temporary one. Bind a vehicle on the employee page",
  "locus.pull.title": "Pulling",
  "locus.pull.switch": "Pull automatically",
  "locus.pull.switchHint": "Every {n} minutes",
  "locus.pull.customer": "Customer",
  "locus.pull.contract": "Contract",
  "locus.pull.customerPh": "Choose a customer",
  "locus.pull.noCustomers": "This organization has no customers yet",
  "locus.pull.createCustomer": "Create a customer",
  "locus.pull.contractPickCustomer": "Choose the customer first",
  "locus.pull.contractPh": "Choose a contract",
  "locus.pull.noContracts": "This customer has no active contract yet",
  "locus.pull.createContract": "Create a contract",
  "locus.pull.changeNote": "A new contract applies to new tickets only",
  "locus.pull.needAccount": "Test the Locus account first",
  "locus.pull.needSetup": "Choose the customer and contract first",
  "locus.pull.contractGone": "Contract {contract} is no longer active. Choose a new one",
  "locus.pull.saved": "Contract saved",
  "locus.pull.turnedOn": "Automatic pulling is on",
  "locus.pull.turnedOff": "Automatic pulling is off",
  "locus.pull.loadFailed": "Could not load the customers and contracts",
  "locus.creds.passwordExpires": "Password expires",
  "locus.confirm.title": "Pull from Locus now?",
  "locus.confirm.body": "The latest trips and statuses from Locus will update the tickets.",
  "locus.confirm.withLogin": "Locus needs a new login first (used today: {n}/{max}); the pull follows by itself.",
  "locus.confirm.go": "Pull",
  "locus.dialog.startedByLocus": "Started per Locus",
  "locus.dialog.completedByLocus": "Completed per Locus",
  "locus.dialog.closedSuperseded": "Closed — driver started a newer trip",
  "locus.dialog.openDays": "Days with open tickets",
  "locus.dialog.openMissing": "Open tickets Locus did not return",
  "locus.dialog.cancelledMissing": "Cancelled — trip gone from Locus",
  "locus.dialog.slow": "Slow run — over {min} minutes (if every run is this slow, split the runs)",
  "locus.toast.queued": "Pull started — you can close this page; the result shows in the history when it is done",
  "locus.bar.scheduleSkip": "The every-{every}-minute run does not pull this organization yet: {reason}",
  "locus.skip.not_configured": "Locus is not connected",
  "locus.skip.module_off": "The Makro Locus module is off",
  "locus.skip.disabled": "The Locus connection is off",
  "locus.skip.no_account": "No tested Locus account and the token has run out — add one in \"Locus settings\"",
  "locus.skip.no_contract": "No customer and active contract chosen",
  "locus.skip.login_blocked": "Locus login is not possible right now (paused or the daily limit is used up)",
  "locus.skip.login_running": "A Locus login is going on",
  "locus.skip.no_riders": "No employee has a Locus code yet",
  "locus.skip.locked": "Another run is going on",
  "locus.skip.not_due": "Not its turn yet",
  "emp.st.pending_start": "Not started",
  "emp.st.trial": "Probation",
  "emp.st.active": "Working",
  "emp.st.resigned": "Resigned",
  "emp.st.terminated": "Dismissed",
  "emp.st.contract_end": "Contract ended",
  "emp.why.trial": "Counts as a probation period",
  "emp.why.active": "Passed probation, or back to normal work",
  "emp.why.resigned": "The employee leaves by choice",
  "emp.why.terminated": "The company lets them go",
  "emp.why.contract_end": "The contract ran out and is not renewed",
  "emp.err.generic": "Could not save the status. Try again.",
  "emp.sheet.until": "Trial until",
  "emp.sheet.untilPh": "Not set",
  "emp.sheet.untilHint": "Optional. If set, HR is reminded a week before it ends.",
  "emp.sheet.untilBad": "The trial's last day must be after the effective day",
  "emp.history.until": "until {day}",
  "emp.chip.label": "Employment status",
  "emp.chip.later": "{next} from {moment}",
  "emp.action.change": "Change status",
  "emp.menu.label": "More for this employee",
  "emp.menu.off": "Turn account off (made by mistake)",
  "emp.menu.on": "Turn account on again",
  "emp.off.note": "This account is off. They can't sign in or take jobs. Turning an account off is not leaving work.",
  "emp.off.noteAdmin": "Turn it on again from the ⋯ menu at the top right.",
  "emp.off.confirmTitle": "Turn this account off?",
  "emp.off.confirmBody": "Only for an account made by mistake. They disappear from the list and can't sign in, but this is not leaving work: no last working day and no leaving pay. If they really resigned, use \"Change status\" instead.",
  "emp.off.confirmBtn": "Turn off",
  "emp.on.confirmTitle": "Turn the account on again?",
  "emp.on.confirmBody": "They come back to the list and can sign in. Their employment status does not change.",
  "emp.on.confirmBtn": "Turn on",
  "emp.toast.off": "Account turned off",
  "emp.toast.on": "Account turned on",
  "emp.toast.failed": "That did not work. Try again.",
  "emp.sheet.title": "Employment status",
  "emp.sheet.changeTo": "Change to",
  "emp.sheet.date": "Effective date",
  "emp.sheet.time": "Time",
  "emp.sheet.futureJobs": "{n} jobs are still on this person after leaving — give them to another driver (nothing is cancelled, and this person cannot be put on a job after leaving)",
  "emp.sheet.futureJobsMore": "and {n} more",
  "emp.sheet.timeHint": "No time: today = from now · another day = from 00:00 that day",
  "emp.sheet.now": "now (on save)",
  "emp.sheet.reason": "Reason",
  "emp.sheet.reasonPh": "A few words on why",
  "emp.sheet.save": "Save change",
  "emp.sheet.saved": "Status saved",
  "emp.sheet.sentenceLeave": "Last working day {day} · cannot sign in or take jobs from {moment}",
  "emp.sheet.sentenceStart": "Effective {moment}",
  "emp.sheet.dailyNote": "Money counted by the day, such as pro-rated salary and daily wage, uses the new status from the next day.",
  "emp.sheet.backMsg": "The moment you chose has already passed. The new status takes effect at once and may change bills or pay already worked out. Save anyway?",
  "emp.sheet.waitingTitle": "A change is waiting",
  "emp.sheet.waitingHint": "Cancel this change before setting another.",
  "emp.sheet.cancel": "Cancel this change",
  "emp.sheet.cancelled": "Cancelled",
  "emp.sheet.cancelTitle": "Cancel this change?",
  "emp.sheet.cancelMsg": "{status} set for {moment} will not happen.",
  "emp.sheet.cancelKeep": "Keep it",
  "emp.sheet.noNext": "There is no status to change to right now.",
  "emp.sheet.settled": "The money for this leaving is settled, so the leaving can't be cancelled or changed. To change it, undo the settlement on this person's finance page first.",
  "emp.history.startHint": "Change the start day on the employee form (the hire date field)",
  "emp.sheet.settledLink": "Go to this person's finance page",
  "emp.sheet.leavingLine": "{status} from {moment} · last working day {day}",
  "emp.sheet.lastDay": "Last working day {day}",
  "emp.sheet.leaveLink": "See what is owed or to be collected, and settle",
  "emp.history.title": "Status history",
  "emp.history.empty": "No history yet",
  "emp.history.by": "Set by {name} · {at}",
  "emp.history.byUnknown": "Set {at}",
  "emp.history.mistake": "Remove (recorded by mistake)",
  "emp.history.mistakeTitle": "Remove a change recorded by mistake",
  "emp.history.mistakeMsg": "Remove {status} from {moment} as if never recorded — the status, last working day and held trip pay go back to before. The audit log keeps it.",
  "emp.history.mistakeGo": "Remove it",
  "emp.history.fromOld": "From the old record (before status history)",
  "emp.badge.pending": "Waiting",
  "emp.badge.current": "Current",
  "emp.badge.past": "Past",
  "emp.loadFailed": "Could not load the status",
  "salary.badge.pending": "Waiting",
  "salary.badge.current": "In use",
  "salary.badge.past": "Earlier",
  "salary.history.from": "From {day}",
  "salary.cancelled": "Cancelled",
  "salary.cancel.title": "Cancel this salary change?",
  "salary.cancel.msg": "The salary of ฿{amount} set to start on {day} will not happen. The salary in use now stays the same.",
  "salary.cancel.go": "Cancel the change",
  "salary.mistake.title": "Delete a change made by mistake",
  "salary.mistake.msg": "Delete the salary of ฿{amount} that started on {day}, as if it was never saved. The salary goes back to ฿{prev}. Use this only for a change saved by mistake; if the salary really changed, set a new one instead.",
  "salary.effective.label": "Starts from",
  "salary.effective.note": "Salary counts by the day, from 00:00 of the chosen day. If that day falls inside a pay period, the days before it are paid at the old salary and the days from it at the new one.",
  "salary.effective.future": "Set ahead. The new salary starts when this day arrives.",
  "salary.diffOnDay": "{diff} ฿ from the salary in use on that day ({current} ฿)",
  "salary.noneOnDay": "No salary is in effect on that day yet",
  "salary.err.generic": "Could not save the salary. Try again.",
  "salary.err.salary_before_history": "A later salary change has already taken effect. Choose a later day.",
  "salary.err.salary_pending_after": "A salary change is already waiting after that day. Cancel it in the history first.",
  "salary.err.effective_in_effect": "This change is already in effect and cannot be cancelled. If it was saved by mistake, use \"Remove (recorded by mistake)\" on the latest one.",
  "salary.err.effective_not_latest": "Only the latest change can be deleted.",
  "salary.err.salary_only_change": "This is the first salary of this person and cannot be deleted. Set a new salary instead.",
  // plan/employment-lifecycle §5.4 — ค่าดำเนินการ 5 ขั้น (opfee.*)
  "opfee.title": "Operating fee",
  "opfee.alias": "(commission)",
  "opfee.notSet": "Not set",
  "opfee.what": "Taken from the trip pay on tickets only, before tax. Not from salary, daily wage or other items.",
  "opfee.order": "The system looks from the most specific: person, route, contract, customer, company default",
  "opfee.level.employee": "Level 1 of 5 · Person. A special deal with this person. Beats every other level.",
  "opfee.level.route": "Level 2 of 5 · Route. Applies to trips on this route.",
  "opfee.level.contract": "Level 3 of 5 · Contract. Applies to everyone who runs this contract.",
  "opfee.level.customer": "Level 4 of 5 · Customer. Applies to all contracts of this customer.",
  "opfee.level.company": "Level 5 of 5 · Company default. Used when no other level is set (starts at 0%).",
  "opfee.since": "Set, in effect since {moment}",
  "opfee.notSetHint": "Not set at this level, so the next level is used. This is different from 0%, which means taking nothing on purpose.",
  "opfee.ifNot": "If not set here, {pct} is used, from {source}",
  "opfee.usingNow": "Right now {pct} is used, from {source}",
  "opfee.change": "Change",
  "opfee.inline.since": "since {moment}",
  "opfee.inline.pending": "becomes {value} from {moment}",
  "opfee.changeLink": "Change…",
  "opfee.fieldLabel": "Operating fee (commission)",
  "opfee.loadFailed": "Could not load the operating fee",
  "opfee.retry": "Try again",
  "opfee.pendingTitle": "Set ahead (waiting)",
  "opfee.pendingRow": "{value} · from {moment}",
  "opfee.cancel": "Cancel",
  "opfee.history": "History",
  "opfee.historyHide": "Hide history",
  "opfee.historyEmpty": "Never set at this level",
  "opfee.value": "{pct}",
  "opfee.valueNone": "Not set at this level (use the next)",
  "opfee.from": "From {moment}",
  "opfee.by": "Saved by {name} · {at}",
  "opfee.byUnknown": "Saved {at}",
  "opfee.fromOld": "From the old data in the system",
  "opfee.badge.pending": "Waiting",
  "opfee.badge.current": "In use",
  "opfee.badge.past": "Past",
  "opfee.mistake": "Remove (recorded by mistake)",
  "opfee.mistakeTitle": "Remove a mistaken record",
  "opfee.mistakeMsg": "Remove {value} from {moment}, as if never saved. The value goes back to the one before. Open bills are worked out again; frozen or paid bills do not change.",
  "opfee.mistakeGo": "Remove this",
  "opfee.cancelTitle": "Cancel the value set ahead",
  "opfee.cancelMsg": "Cancel {value} due from {moment}. The value in use now does not change.",
  "opfee.cancelKeep": "Keep it",
  "opfee.sheet.title": "Operating fee",
  "opfee.sheet.choose": "Value at this level",
  "opfee.sheet.setTo": "Set to",
  "opfee.sheet.pct": "Percent",
  "opfee.sheet.unset": "Not set at this level (use the next level)",
  "opfee.sheet.unsetWhy": "This level takes nothing of its own; {pct} from {source} applies instead",
  "opfee.sheet.zeroNote": "0% means taking nothing on purpose and beats the lower levels. It is different from \"not set\".",
  "opfee.sheet.date": "Effective date",
  "opfee.sheet.time": "Time (empty = 00:00)",
  "opfee.sheet.timeHint": "Tickets whose trip starts from this day and time use the new value. Thai time.",
  "opfee.sheet.reason": "Reason (optional)",
  "opfee.sheet.reasonPh": "e.g. new agreement with the customer",
  "opfee.sheet.save": "Save",
  "opfee.sheet.now": "from now",
  "opfee.sheet.sentence": "{what}, effective {moment}",
  "opfee.sheet.sentenceSet": "Set to {pct}",
  "opfee.sheet.sentenceUnset": "Not set at this level",
  "opfee.sheet.saved": "Operating fee saved",
  "opfee.sheet.cancelled": "Cancelled",
  "opfee.sheet.backMsg": "The day and time you chose have already passed. Open bills will work out the operating fee again with this value. Frozen or paid bills do not change. Save anyway?",
  "opfee.err.generic": "Could not save the operating fee. Try again.",
  "opfee.rates.title": "Rates in use",
  "opfee.rates.show": "Show the rate taken on each contract (last 90 days)",
  "opfee.rates.hide": "Hide the rates taken",
  "opfee.rates.lead": "Which contract and route this person runs and how much operating fee is taken (trips in the last 90 days)",
  "opfee.rates.contract": "Contract",
  "opfee.rates.route": "Route",
  "opfee.rates.trips": "Trips (90 days)",
  "opfee.rates.rate": "Rate",
  "opfee.rates.source": "From",
  "opfee.rates.empty": "No trips in the last 90 days",
  "opfee.rates.own": "Set on this person: {pct}",
  "opfee.legacy.title": "{n} customers have an old commission still waiting. It is not being taken yet.",
  "opfee.legacy.lead": "This value was typed on the customer but never taken. Pick the ones you want and confirm. The system then takes it as the customer-level operating fee from the day you choose. Nothing is changed on its own.",
  "opfee.legacy.date": "Effective from",
  "opfee.legacy.confirm": "Confirm as operating fee",
  "opfee.legacy.all": "Select all",
  "opfee.legacy.done": "Confirmed {n}",
  "opfee.legacy.failed": "Could not confirm. Try again.",
  "opfee.bill.line": "Operating fee {rate}% of {base} · from {source}",
  "opfee.row.tag": "Operating fee {pct} (from {source}) = {fee}",
  "opfee.companyEffect": "Used for every ticket where person, route, contract and customer have nothing set",
  "opfee.companyNever": "Never set. Starts at 0%, which takes nothing.",
  "hr.hireDateSynced": "Changing this day also moves the start in the status history",
  "pdv.err.generic": "Could not save. Try again.",
  "pdv.when": "Effective from",
  "pdv.whenNow": "Now",
  "pdv.time": "Time",
  "pdv.laterNote": "The new value waits and takes effect from {moment}. Until then the current value is used.",
  "pdv.payeeNote": "A pay round's transfer file uses the account in effect on that round's pay day. No day = effective now.",
  "pdv.roleNote": "No day = effective when saved. One change can wait at a time.",
  "pdv.typeReleasesVehicleNow": "This type is not a driver: the assigned vehicle {plate} is returned when you save. Jobs that hold it are not touched.",
  "pdv.typeReleasesVehicleLater": "This type is not a driver: the assigned vehicle {plate} is returned at {moment}. Jobs that hold it are not touched.",
  "pdv.typeNote": "No day = effective when saved. One change can wait at a time.",
  "pdv.reason": "Reason (optional)",
  "pdv.reasonPh": "For example: account changed as the employee reported",
  "pdv.waitingTitle": "Waiting, effective {moment}",
  "pdv.cancelWaiting": "Cancel",
  "pdv.cancelled": "Cancelled. The current value is unchanged.",
  "payChange.colTicket": "Ticket",
  "payChange.colDay": "Day",
  "payChange.colFrom": "Was",
  "payChange.colTo": "Becomes",
  "payChange.more": "and {n} more",
  "payChange.loading": "Checking which tickets' trip pay changes…",
  "payChange.loadFailed": "Could not list the tickets whose trip pay changes",
  "payChange.typeTitle": "Changing the type changes trip pay",
  "payChange.typeCancelTitle": "Cancelling puts trip pay back",
  "payChange.typeQuiet": "No ticket's trip pay changes",
  "payChange.typeIntro": "{n} tickets from {day} are priced again with that day's type, {delta} in total",
  "payChange.closedNote": "{n} tickets are in a frozen or paid pay round and are not changed; add a money item in the next round if it needs adjusting",
  "payChange.closedShould": "Unchanged (should be {to})",
  "payChange.manualNote": "Tickets with a typed trip pay, cancelled tickets and tickets with no driver are not touched",
  "payChange.typeGo": "Save and price trips again",
  "payChange.typeCancelGo": "Cancel and price trips again",
  "reprice.button": "Price again",
  "reprice.title": "Price trips again with current values",
  "reprice.intro": "Prices the chosen tickets again with what is in effect for each ticket's day: the route's pay (the version in effect), the person's trip rates and the person's type that day",
  "reprice.summary": "{n} tickets change, {delta} in total",
  "reprice.nothing": "No ticket's trip pay changes",
  "reprice.go": "Use the new pay on {n} tickets",
  "reprice.done": "Priced {n} tickets again",
  "reprice.progress": "Saving {done}/{total} tickets",
  "reprice.failed": "Pricing again failed",
  "reprice.tooMany": "At most {max} tickets at a time",
  "reprice.outcome.CHANGE": "Changes",
  "reprice.outcome.SAME": "Same pay",
  "reprice.outcome.MANUAL": "Typed by hand, not touched",
  "reprice.outcome.IN_CLOSED_ROUND": "In a frozen or paid round, not touched",
  "reprice.outcome.CANCELLED": "Cancelled",
  "reprice.outcome.NO_DRIVER": "No driver yet",
  "reprice.outcome.NOT_PRICED": "No trip pay yet (see tickets without trip pay)",
  "reprice.outcome.UNPRICEABLE": "Cannot be priced",
  "reprice.outcome.NOT_FOUND": "Ticket not found",
  "ops.versionApplyExisting": "Apply to existing tickets",
  "ops.versionStaleBanner": "{n} tickets in this version's window still use an older version",
  "ops.versionApplyTitle": "Apply version {n} to existing tickets",
  "ops.versionRowClosed": "Cannot change: in a frozen or paid pay round",
  "ops.versionCode.IN_CLOSED_ROUND": "This ticket's trip pay is in a frozen or paid pay round; the system leaves it",
  "pdv.cancelTitle": "Cancel the waiting change",
  "pdv.cancelMsg": "Cancel \"{text}\" due {moment}. The current value stays.",
  "pdv.keep": "Keep it",
  "pdv.mistake": "Mistake",
  "pdv.mistakeTitle": "Take back the latest row",
  "pdv.mistakeMsg": "Take back \"{text}\" (effective {moment}) to the previous value. Use only when it was recorded by mistake.",
  "pdv.mistakeGo": "Take it back",
  "pdv.mistakeDone": "Taken back",
  "pdv.showHistory": "Show history ({n})",
  "pdv.hideHistory": "Hide history",
  "pdv.from": "In use from {moment}",
  "pdv.payeeHistory": "Payee history",
  "pdv.tab": "History",
  "pdv.tabLead": "Every change to this person, newest first",
  "pdv.topic.label": "Choose a subject",
  "pdv.topic.all": "All",
  "pdv.topic.status": "Status",
  "pdv.topic.salary": "Salary",
  "pdv.topic.payee": "Payee",
  "pdv.topic.finance": "Finance",
  "pdv.topic.role": "Role / type",
  "pdv.topic.vehicle": "Vehicle",
  "pdv.topic.depot": "Depot",
  "pdv.topic.other": "Other",
  "pdv.timelineFailed": "Could not load the history. Try again.",
  "pdv.timelineEmpty": "No changes on this subject yet",
  "pdv.byName": "by {name}",
  "pdv.byUnknown": "by unknown",
  "pdv.effective": "effective {moment}",
  "pdv.loadMore": "Load more",
  "settings.searchOrgsCount": "Search {n} organizations",
  "payBatches.x.debitOption": "KBank {account}",
  "portalScope.status.expired": "Expired",
  "portalScope.status.suspended": "Suspended",
  "portalScope.status.draft": "Draft",
  "portalScope.saved": "Scope saved",
  "portalScope.searchPlaceholder": "Search contracts or routes",
  "portalScope.contractsCount": "Contracts: {n}",
  "portalScope.pendingAdds": "+{n} unsaved",
  "portalScope.collapseAll": "Collapse all",
  "portalScope.expandAll": "Expand all",
  "portalScope.addContract": "Add contracts",
  "portalScope.colCode": "Code",
  "portalScope.colCustomerName": "Customer / name",
  "portalScope.colRoutes": "Routes",
  "portalScope.emptySearch": "No contracts or routes in this scope match your search",
  "portalScope.empty": "This portal has no contracts yet. Press “Add contracts” to start",
  "portalScope.contractRange": "Contracts {from}–{to} of {total}",
  "portalScope.unsavedPrefix": "Unsaved:",
  "portalScope.draftAdd": "add {n} contracts",
  "portalScope.draftRemove": "remove {n} contracts",
  "portalScope.draftRoutes": "{n} route changes",
  "portalScope.discard": "Discard changes",
  "portalScope.pendingBadge": "Unsaved",
  "portalScope.noRoutesOpen": "⚠ No routes are open, so riders cannot see this contract",
  "portalScope.removeContract": "Remove contract from portal",
  "portalScope.toggleRoutesPage": "Turn {n} routes on this page on or off",
  "portalScope.toggleRoutesAll": "Turn all routes of this contract on or off",
  "portalScope.toggleRoutesPageHint": "Applies to the {n} routes shown on this page",
  "portalScope.routeSearchPlaceholder": "Search routes in this contract",
  "portalScope.openOf": "{open} of {total} open",
  "portalScope.noRoutesMatch": "No routes in this contract match your search",
  "portalScope.stops": "{n} stops",
  "portalScope.routeRange": "Routes {range} of {total}",
  "portalScope.pick.title": "Add contracts to portal",
  "portalScope.pick.placeholder": "Code, customer or route name",
  "portalScope.pick.matchedRoutes": "Matches route {routes}",
  "portalScope.pick.routesN": "{n} routes",
  "portalScope.pick.allListed": "All matching contracts are already in the list",
  "portalScope.pick.none": "No contracts match your search",
  "portalScope.pick.alreadyIn": "Already in the list:",
  "portalScope.pick.selected": "{n} selected · {routes} routes will become visible",
  "portalScope.pick.nothing": "Nothing selected",
  "hrLeave.cal.noOne": "No one is on leave in {month}",
  "hrLeave.cal.cellTitle": "{name} · {type} ({status}) {days} · {from}–{to}",
  "hrLeave.cal.hasFiles": " · 📎 has attachment",
  "hrLeave.cal.legendHalf": "Full cell = whole day · left half = morning · right half = afternoon",
  "hrLeave.rejected": "Request rejected",
  "hrLeave.rejectFailed": "Could not reject",
  "hrLeave.failed": "Failed",
  "hrLeave.bulkApproved": "{n} requests approved",
  "hrLeave.bulkMixed": "{ok} approved · {failed} failed — {first}",
  "hrLeave.cancelled": "Request cancelled",
  "hrLeave.cancelFailed": "Could not cancel",
  "hrLeave.bulkTitle": "Approve {n} requests?",
  "hrLeave.bulkTotal": "{days} days in total",
  "hrLeave.bulkPerson": "· {name} · {days} days",
  "hrLeave.bulkMore": "· and {n} more",
  "hrLeave.bulkClash": "⚠ {n} clash with jobs already assigned ({names})",
  "hrLeave.bulkNote": "Each request is checked again for balance and overlaps. Any that fail are skipped and reported back.",
  "hrLeave.bulkConfirm": "Approve all",
  "hrLeave.rejectReason": "Reason for rejecting (optional)",
  "hrLeave.rejectConfirm": "Confirm rejection",
  "hrLeave.selectedN": "{n} selected",
  "hrLeave.selectedTotal": " · {days} days in total",
  "hrLeave.clearSelection": "Clear selection",
  "hrLeave.approveSelected": "Approve selected",
  "hrLeave.selectRow": "Select {name}'s request",
  "hrLeave.overEntitlement": "Over balance",
  "hrLeave.decidedBy": "by {name}",
  "hrLeave.cancelMessage": "{name} · {type} {days} — the days go back to the balance right away",
  "hrLeave.cancelConfirm": "Cancel request",
  "hrLeave.rowMenu": "Menu for {name}'s request",
  "hrLeave.openProfile": "Open employee profile",
  "hrLeave.fileTypeNotAllowed": "This file type cannot be attached",
  "hrLeave.fileTooBig": "File is larger than {mb} MB",
  "hrLeave.uploadFailed": "Upload failed",
  "hrLeave.endBeforeStart": "End date must be on or after the start date",
  "hrLeave.singleDayHalfOnly": "A single-day leave can only be a morning or afternoon half",
  "hrLeave.leaveDate": "Leave date",
  "hrLeave.firstDay": "First day",
  "hrLeave.lastDay": "Last day",
  "hrLeave.totalDays": "{days} days in total",
  "hrLeave.sickNeedsDoc": "Sick leave of {n} days or more needs a document",
  "hrLeave.chooseFile": "Choose file",
  "hrLeave.removeFile": "Remove {name}",
  "hrLeave.otherNeedsReason": "“Other” needs a reason, since the type name says nothing",
  "hrLeave.decider": "Decided by",
  "hrLeave.pendingBadge": "{days} days pending",
  "hrLeave.usedSummary": "Used {used} · Pending {pending} · Available {available}",
  "hrLeave.patternTitle": "Leave pattern {year}",
  "hrLeave.monthTitle": "{month} · {days} days",
  "hrLeave.loadingBalance": "Loading leave balance…",
  "hrLeave.decisionApproveMsg": "{type} leave {from} – {to} will be approved and the balance deducted.",
  "hrLeave.decisionRejectMsg": "{type} leave {from} – {to} will be rejected.",
  "hrLeave.submitRequest": "Submit request",
  "err.title": "Something went wrong",
  "err.body": "Something went wrong. Our team has been notified. Please try again.",
  "err.reference": "Reference: {ref}",
  "err.toDashboard": "Back to dashboard",
  "notFound.title": "Page not found",
  "notFound.body": "The link may be broken or the page may have been moved.",
  "routeTip.outbound": "Outbound",
  "routeTip.return": "Return",
  "routeTip.pairedWith": "Paired with",
  "routeTip.stopN": "Stop {n} · {name}",
  "tracking.parkedSuffix": "Parked",
  "optimizer.routeCreated": "Route \"{code}\" created",
  "optimizer.saveFailed": "Could not save",
  "roles.emptyTitle": "No roles yet",
  "roles.emptyBody": "Create a role to set what each member can do, for example Fleet Manager, Driver or Accountant.",
  "hr.noEmployeesFound": "No employees found",
  "hr.licenseTypeExample": "e.g. ท.1, ท.2, ท.4",
  "hr.licenseIssuerExample": "e.g. Department of Land Transport",
  "detour.title": "Move passengers to {vehicle}",
  "detour.step1": "1. Pickup",
  "detour.step2": "2. Confirm",
  "detour.moving": "Moving passengers",
  "detour.countPeople": "{n} passengers",
  "detour.overCapacity": "{n} selected, more than the {seats} free seats on {vehicle}",
  "detour.choosePickup": "Choose where {vehicle} picks them up",
  "detour.stop": "Stop",
  "detour.walk": "Passenger walks",
  "detour.vehiclePath": "Vehicle route",
  "detour.unchanged": "Unchanged",
  "detour.pickupPoint": "Pickup point",
  "detour.walkNone": "0 m (boards at the same place)",
  "detour.pathAdded": "Added to route",
  "detour.pathAddedValue": "{dist} · ~{min} min (estimate)",
  "detour.customHint": "Pick a spot on the map (available once routing is connected)",
  "detour.origBoarding": "Original boarding point",
  "detour.newPickup": "New pickup point",
  "detour.vehicleStops": "Vehicle stops here",
  "detour.legendRoute": "━ {vehicle} route",
  "detour.legendOrig": "● Original boarding point",
  "detour.legendPickup": "● Pickup point",
  "detour.fromTrip": "From trip",
  "detour.toTrip": "To trip",
  "detour.method": "Pickup method",
  "detour.notify": "Notify",
  "detour.notifyPax": "{n} passengers",
  "detour.notifyPaxMsg": "\"Your vehicle is now {vehicle}. Same boarding point and time.\"",
  "detour.notifyDriver": "Driver of {vehicle}",
  "detour.notifyDriverMsg": "\"A pickup for {n} passengers has been added.\"",
  "detour.previewNote": "Preview only · saves once the system is connected",
  "comms.ann.published": "Announcement published",
  "comms.ann.editSaved": "Changes saved",
  "comms.ann.saveFailed": "Could not save",
  "comms.ann.audAll": "Everyone",
  "comms.ann.audDrivers": "Drivers",
  "comms.ann.audPassengers": "Passengers",
  "comms.ann.prioNormal": "Normal",
  "comms.ann.prioImportant": "Important",
  "comms.ann.prioUrgent": "Urgent",
  "comms.ann.back": "Back",
  "comms.ann.edit": "Edit",
  "comms.ann.editTitle": "Edit announcement",
  "comms.ann.read": "Read",
  "comms.ann.readRate": "Read rate",
  "comms.ann.formNew": "New announcement",
  "comms.ann.subject": "Subject",
  "comms.ann.subjectEx": "e.g. Special holiday closure...",
  "comms.ann.body": "Message",
  "comms.ann.bodyPh": "Announcement details...",
  "comms.ann.image": "Image (URL)",
  "comms.ann.audience": "Audience",
  "comms.ann.priority": "Priority",
  "comms.ann.schedule": "Schedule publishing",
  "comms.ann.saveAndPublish": "Save and publish",
  "comms.ann.publishNow": "Publish now",
  "comms.ann.save": "Save",
  "comms.ann.saveDraft": "Save draft",
  "comms.ann.none": "No announcements",
  "audit.act.re_resolve": "Re-resolve",
  "audit.act.pay_decision": "Pay Decision",
  "audit.act.lock": "Lock",
  "audit.act.auto_void_on_cancel": "Auto Void",
  "audit.act.salary_profile_update": "Salary Update",
  "audit.act.login": "Login",
  "audit.act.login_failed": "Login Failed",
  "audit.act.logout": "Logout",
  "audit.act.fuel_draft_created": "Fuel Entry Started",
  "audit.act.fuel_step_captured": "Fuel Photo Taken",
  "audit.act.fuel_step_failed": "Fuel Photo Refused",
  "audit.act.fuel_submitted": "Fuel Entry Sent",
  "audit.act.fuel_submit_failed": "Fuel Entry Refused",
  "audit.act.fuel_discarded": "Fuel Entry Discarded",
  "audit.act.fuel_log_approved": "Fuel Approved",
  "audit.act.fuel_log_rejected": "Fuel Rejected",
  "audit.act.fuel_log_suspicious": "Fuel Marked Suspicious",
  "audit.sum.signedIn": "{who} signed in",
  "audit.sum.signInFailed": "{who} failed to sign in",
  "audit.sum.signedOut": "Signed out",
  "audit.sum.revokeFailed": "token revoke failed",
  "audit.sum.repriced": "Re-priced {n} trips · total {delta}",
  "audit.sum.locked": "Locked {period} · payable {payable} ({paid} trips, {excluded} excluded)",
  "audit.sum.opCostVoided": "Op-cost charge {amount} voided (ticket cancelled)",
  "audit.sum.baseSalary": "Base {from} → {to}",
  "audit.sum.rateVoided": "Voided rate {amount} ({from} → {to})",
  "audit.sum.openEnded": "open-ended",
  "audit.sum.rateSet": "Set {scope} rate {amount} from {from}",
  "audit.sum.rateTo": "to {to}",
  "audit.sum.rateTrimmed": "(closed {n} previous version)",
  "audit.sum.payRun": "Pay run {period} — {n} tickets, net {net}",
  "audit.sum.dirReimburse": "Reimburse",
  "audit.sum.dirDeduct": "Deduct",
  "audit.sum.dirCharge": "Charge",
  "audit.sum.chargeAdded": "{dir} {amount}{type} added",
  "audit.sum.chargeRemoved": "{dir} {amount}{type} removed",
  "audit.sum.chargeApproved": "Charge approved",
  "audit.sum.chargeRevoked": "Charge approval revoked",
  "audit.sum.via": "(via {via})",
  "audit.sum.updated": "Updated {fields}{more}",
  "audit.sum.moreFields": "+{n} more",
  "audit.sum.ticketCreated": "Ticket created",
  "audit.exported": "Exported {n} entries to .xlsx",
  "audit.loadFailed": "Failed to load audit log: {error}",
  "audit.beforeAfter": "Before → After",
  "audit.client": "Client",
  "common.editNamed": "Edit {name}",
  "common.deleteNamed": "Delete {name}",
  "hr.deleteRateBtn": "Delete Rate",
  "ticketPay.amountBaht": "Amount (฿)",
  "ticketPay.chargeAdded": "Charge added: {type} ฿{amount}",
  "ticketPay.dirReimburseSub": "Reimburse — adds to driver pay",
  "ticketPay.dirDeductSub": "Deduct — subtracts from driver pay",
  "ticketPay.editChargeMenu": "Edit charge…",
  "ticketPay.compCharges": "Fare ฿{comp}{mult} {sign} lines ฿{charges}",
  // plan/distance-pay audit — the lines here pay into (or come off) the driver's net: not "operating expenses"
  "ticketPay.linesTitle": "This ticket's add and deduct lines",
  "ticketPay.linesEmpty": "No add or deduct lines on this ticket yet ·",
  "ticketPay.stopOption": "Stop {n}: {name}",
  "ticketPay.stopOptionSub": "Scheduled {time} • {booked} booked",
  "rateConfirm.title": "Driver changed — compensation rate?",
  "rateConfirm.body": "The current rate was set manually to {current}. The new driver's {tier} rate is {next}.",
  "rateConfirm.keep": "Keep ฿{amount}",
  "rateConfirm.use": "Use {tier} rate ฿{amount}",
  "hr.unit.perMonth": "฿{amount}/mo",
  "hr.unit.perDay": "฿{amount}/day",
  "hr.unit.perTrip": "฿{amount}/trip",
  "hr.unit.perHour": "฿{amount}/hr ({mult}×)",
  "empImport.uploadFirst": "Upload a file first",
  "empImport.noValidRows": "No valid rows to import",
  "empImport.done": "Imported {n} employees successfully",
  "empImport.checking": "Checking...",
  "empImport.preview": "Preview",
  "empImport.importing": "Importing...",
  "empImport.import": "Import",
  "empImport.importRows": "Import ({n} rows)",
  "empImport.browse": "or click to browse — export Excel to .csv first",
  "empImport.required": "Required:",
  "empImport.optional": "Optional:",
  "empImport.totalRows": "Total rows:",
  "empImport.valid": "Valid:",
  "empImport.errors": "Errors:",
  "empImport.rowError": "Row {row}{field}: {message}",
  "nav.comingSoonTitle": "{label} — Coming soon",
  "hr.createPosting": "Create Posting",
  "hr.confirmReject": "Confirm Reject",
  "hr.advanceStage": "Advance Stage",
  "hr.resumeNotUploaded": "Not uploaded",
  "reports.custom.nameRequired": "Name is required",
  "reports.custom.runFailed": "Run failed",
  "reports.custom.run": "Run",
  "reports.custom.newTemplate": "+ New Template",
  "reports.custom.fields": "Fields (comma-separated, empty = all)",
  "reports.custom.addFilter": "+ Add filter",
  "reports.custom.noFilters": "No filters. Report runs against all rows in {source}.",
  "reports.custom.opNone": "— none —",
  "reports.custom.opEq": "equals",
  "reports.custom.opNe": "not equals",
  "reports.custom.opIn": "in [a,b,c]",
  "reports.custom.opRegex": "matches",
  "reports.d1": "Trip summary - total trips, on-time rate, passengers",
  "reports.d2": "Route analysis - time, distance, booking utilization",
  "reports.d3": "Driver evaluation - on-time, fuel efficiency, completion rate",
  "reports.d4": "Fleet status - active, repair, stored, total km",
  "reports.d5": "Maintenance history - work orders, costs, pending",
  "reports.d6": "Insurance and registration expiry report",
  "reports.d7": "Booking summary - per route, utilization trend",
  "reports.d8": "Overflow report - frequency, pattern, resolution",
  "reports.d9": "Fuel summary - per vehicle, km/L trend, anomaly flags",
  "reports.d10": "Revenue and cost - per contract, per route, margin",
  "reports.d11": "Contract billing summary - trips x rate = total",
  "hr.employeesCount": "{n} employees",
  "audit.fin.bill_check": "Bill checked",
  "audit.fin.bill_uncheck": "Bill check undone",
  "audit.fin.bill_flag": "Problem noted",
  "audit.fin.bill_flag_reply": "Problem replied to",
  "audit.fin.bill_flag_close": "Problem closed",
  "audit.fin.bill_spot": "Sample checked",
  "audit.fin.opening_history": "History carried over",
  "audit.fin.opening_undo": "Carry-over undone",
  "audit.fin.pay_batch_bill_open": "Bill opened for editing",
  "audit.fin.pay_batch_bill_done": "Bill editing done",
  "audit.fin.pay_batch_file_check": "Bank file checked",
  "audit.fin.pay_batch_hold_person": "Person held in round",
  "audit.fin.pay_batch_method": "Payment method changed",
  "audit.fin.pay_batch_sample": "Round sampled",
  "audit.fin.pay_batch_transfer_failed": "Transfer failed",
  "audit.fin.person_hold": "Person held",
  "audit.fin.person_release": "Person released",
  "audit.fin.staff_request_create": "Request filed",
  "audit.fin.staff_request_move": "Request moved",
  "audit.module.finance": "Finance",
  "audit.module.hr": "HR",
  "audit.module.jobs": "Jobs",
  "audit.module.routes": "Routes",
  "audit.module.cargo": "Cargo",
  "audit.module.contracts": "Contracts",
  "audit.module.dispatch": "Dispatch",
  "audit.module.admin": "Admin",
  "audit.module.export": "Export",
  "audit.module.auth": "Sign-in",
  "audit.module.fuel": "Fuel",
  "audit.module.fleet": "Fleet",
  "audit.module.settings": "Settings",
  // plan/distance-pay — pay by the km a ticket ran: the sheet, the rate field, the ticket's real stats
  "distpay.open": "Pay by distance",
  "distpay.title": "Pay by distance",
  "distpay.lineType": "Pay by distance",
  "distpay.listOpen": "Pay by km",
  "distpay.listPaid": "By km ฿{amount}",
  "distpay.listPaidHint": "Paid by distance · click to see or change",
  "distpay.loadFailed": "Could not load this ticket's distance",
  "distpay.saved": "Added ฿{amount} pay by distance to the ticket",
  "distpay.removed": "Pay by distance removed",
  "distpay.err.generic": "Could not save. Try again",
  "distpay.notRun": "This ticket has not run yet. Pay by distance once it has started",
  "distpay.noDriver": "This ticket has no driver yet, so there is nobody to pay",
  "distpay.roundClosed": "This ticket's trip pay is in a frozen or paid round. What you save now goes to the next bill as an adjustment",
  "distpay.km": "Distance driven",
  "distpay.src.box": "GPS box odometer · {km} km",
  "distpay.src.boxBody": "{from} → {to} ({span})",
  "distpay.src.phone": "Driver's phone trace · {km} km",
  "distpay.src.phoneBody": "{n} points ({span})",
  "distpay.src.phoneGaps": "{n} gaps in the signal, counted as straight lines",
  "distpay.src.odometer": "Type the odometer",
  "distpay.src.odometerBody": "Enter the odometer before and after the run",
  "distpay.odoBefore": "Before",
  "distpay.odoAfter": "After",
  "distpay.unitKm": "km",
  "distpay.unitRate": "THB/km",
  "distpay.noReadings": "No GPS box or phone reading while this ticket ran. Type the odometer",
  "distpay.odoWrong": "The odometer after the run must not be below the one before",
  "distpay.planned": "Planned {km} km",
  "distpay.rate": "Rate per km",
  "distpay.noRate": "{name} has no rate per km yet",
  "distpay.noRateBoth": "{name} and contract {contract} have no rate per km yet",
  "distpay.rateFromPerson": "{name}'s rate (from {day}) · a change here applies to this ticket only",
  "distpay.rateFromContract": "Contract {name}'s rate (from {day}) · a change here applies to this ticket only",
  "distpay.rateTyped": "Typed for this ticket (the set rate is {rate} THB/km)",
  "distpay.keep": "Keep this rate for next time as the rate of",
  "distpay.keepPerson": "{name}",
  "distpay.keepContract": "contract {name}",
  "distpay.keepFrom": "From {day} (this ticket's day) · change it later on that page",
  "distpay.addsTo": "Added to the ticket's money",
  "distpay.approvedNote": "Approved on save and goes to the driver's bill as usual. A ticket has one such line",
  "distpay.current": "This ticket is paid ฿{amount} by distance now",
  "distpay.remove": "Remove this line",
  "distpay.removeTitle": "Remove the pay by distance?",
  "distpay.removeMsg": "฿{amount} leaves this ticket's money",
  "distpay.save": "Save",
  "distrate.label": "Rate per km",
  "distrate.hintPerson": "Used when a ticket is paid by the distance driven · before the contract's rate",
  "distrate.hintContract": "Used when a ticket is paid by the distance driven · a driver's own rate comes first",
  "distrate.value": "{rate} THB/km",
  "distrate.notSet": "Not set",
  "distrate.unsetPerson": "Not set on this person (use the contract's rate)",
  "distrate.unsetContract": "Not set on this contract",
  "distrate.since": "from {day}",
  "distrate.change": "Change…",
  "distrate.setFirst": "Set…",
  "distrate.sheetTitleFirst": "Set the rate per km",
  "distrate.pending": "{value} from {day}",
  "distrate.loadFailed": "Could not load the rate per km · try again",
  "distrate.err.generic": "Could not save. Try again",
  "distrate.saved": "Rate per km saved",
  "distrate.cancelled": "The waiting rate was cancelled",
  "distrate.cancel": "Cancel",
  "distrate.cancelTitle": "Cancel the waiting rate?",
  "distrate.cancelMsg": "{value} from {day} will be cancelled",
  "distrate.sheetTitle": "Change the rate per km",
  "distrate.save": "Save",
  "distrate.whatPerson": "Baht per km this person gets when a ticket is paid by the distance driven. Without one, the ticket's contract rate is used",
  "distrate.whatContract": "Baht per km for this contract's tickets paid by the distance driven. A driver's own rate comes first",
  "distrate.choose": "Set to",
  "distrate.setTo": "Rate",
  "distrate.rate": "Rate per km",
  "distrate.day": "Effective from",
  "distrate.dayHint": "Tickets run from this day use the new rate; earlier ones keep the old",
  "distrate.reason": "Reason (optional)",
  "distrate.history": "History",
  "distrate.historyHide": "Hide history",
  "distrate.historyEmpty": "Never set",
  "distrate.from": "From {day}",
  "distrate.by": "Set by {name} · {at}",
  "ticket.real.running": "running",
  "ticket.real.planKm": "Plan {km} km",
  "ticket.real.fromBox": "Actual distance from the GPS box odometer, start to finish",
  "ticket.real.fromPhone": "Actual distance from the driver's phone trace (no odometer from this vehicle's GPS box)",
  "ticket.real.skipped": "{n} skipped",
  "ticket.real.label": "Actual",
  "ticket.real.timeHint": "When the driver really started and finished",
};
